const { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } = React;

// ============================================================================
// API client
// ============================================================================

// Every call goes through apiFetch: a network failure (offline, server
// restarting) becomes a translated sentence instead of the browser's raw
// "Failed to fetch" / "Load failed".
async function apiFetch(path, init, opts) {
  let res;
  try {
    res = await fetch(path, { credentials: 'include', ...init });
  } catch (_) {
    const err = new Error(currentCatalog['errors.network']
      || 'Can’t reach the server. Check your connection and try again.');
    err.code = 'network_error';
    throw err;
  }
  if (!res.ok) throw await errorFrom(res, opts);
  return res.json();
}

const jsonInit = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json', ...tenantHeader() },
  body: JSON.stringify(body || {}),
});

const api = {
  get(path) { return apiFetch(path, { headers: tenantHeader() }); },
  // opts.noUpgradeModal: don't pop the "upgrade your plan" dialog for a
  // tier-cap error on this call (e.g. an invitee can't upgrade anything).
  post(path, body, opts) { return apiFetch(path, jsonInit('POST', body), opts); },
  patch(path, body) { return apiFetch(path, jsonInit('PATCH', body)); },
  put(path, body) { return apiFetch(path, jsonInit('PUT', body)); },
  del(path) { return apiFetch(path, { method: 'DELETE', headers: tenantHeader() }); },
  // Alias so `api.delete(...)` works alongside `api.del(...)`. Some call
  // sites use the natural HTTP-verb name; others use `del`. Keeping both
  // avoids a class of "delete is not a function" bugs at runtime.
  delete(path) { return this.del(path); },
  // Multipart upload. Caller passes a FormData body; we don't set
  // Content-Type — the browser sets it with the right boundary marker.
  // Tenant + cookies still ride along, which is the WHOLE POINT (raw
  // fetch() loses X-Tenant-Id and the request 400s with tenant_required
  // even though the user is authenticated).
  upload(path, formData) { return apiFetch(path, { method: 'POST', headers: tenantHeader(), body: formData }); },
};

function tenantHeader() {
  const id = window.__currentTenantId;
  return id ? { 'X-Tenant-Id': id } : {};
}

// fetchPdfBlobWithRetry — wraps a PDF fetch with a soft timeout and one
// auto-retry. Built for the "5 KB PDF occasionally takes 10 s" symptom:
// either S3 (presigned URL path) or our own /pdf endpoint can stall on a
// stale HTTP/2 keepalive connection. Aborting at 5 s and retrying forces
// the browser to open a fresh connection — the retry is almost always
// instant.
//
// `sameOrigin` toggles credential behaviour:
//   false (default) — presigned URL to S3 / CDN. credentials:'omit'
//                     because adding auth headers breaks the signature
//                     check on some S3-compatible backends.
//   true            — our own /api/.../pdf endpoint. credentials:
//                     'same-origin' so the session cookie / Bearer
//                     header rides along to satisfy requireAuth.
async function fetchPdfBlobWithRetry(url, { perAttemptMs = 5000, maxRetries = 1, sameOrigin = false } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), perAttemptMs);
    try {
      const res = await fetch(url, {
        signal: controller.signal,
        credentials: sameOrigin ? 'same-origin' : 'omit',
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`pdf fetch: status ${res.status}`);
      return await res.blob();
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      // Distinguish "user navigated away" (real cancellation we should
      // propagate) from "timeout-driven abort" (which we should retry).
      // AbortController surfaces both as AbortError; the controller we
      // signal ourselves only fires on timeout, so any AbortError here
      // is from our timer.
      if (attempt === maxRetries) break;
    }
  }
  throw lastErr || new Error('pdf fetch failed');
}

// Module-scope mirror of the current i18n catalog. The I18nProvider keeps
// this in sync via a useEffect (see below). Lets non-React code paths
// like errorFrom translate without going through a hook.
let currentCatalog = {};
// Active locale, mirrored the same way — drives Intl.NumberFormat in the
// money/number helpers so currencies render per-locale conventions
// ("€0.10" in en, "0,10 €" in de) without threading the locale through
// every call site.
let currentLocale = 'en';

// EUR cents → locale-formatted currency string via Intl. The single
// source for ALL user-visible euro amounts; never hand-build "X €" or
// "€X" strings — symbol placement and decimal separator are locale
// conventions, not constants.
//   formatEur(10)            → "€0.10"  (en) / "0,10 €" (de)
//   formatEur(2900, {whole: true}) → "€29" / "29 €"

// Every template in a workspace. The v1 list pages at 50 by default (max
// 100), so a single request silently hid older templates on plans that
// allow more — in the browser, search and every template picker.
async function fetchAllTemplates(wsSlug) {
  const all = [];
  for (let offset = 0, page = 0; page < 100; page++) {
    const res = await api.get(`/api/v1/${wsSlug}/templates?limit=100&offset=${offset}`);
    const rows = res.templates || [];
    all.push(...rows);
    if (!(res.meta && res.meta.hasMore) || rows.length === 0) break;
    offset += rows.length;
  }
  return { templates: all };
}

function formatEur(cents, { whole = false } = {}) {
  if (cents == null) return '—';
  const opts = { style: 'currency', currency: 'EUR' };
  if (whole) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
  try {
    return new Intl.NumberFormat(currentLocale, opts).format(cents / 100);
  } catch (_) {
    // Unknown locale string — fall back to a neutral en format.
    return new Intl.NumberFormat('en', opts).format(cents / 100);
  }
}

function translateError(code, detail) {
  if (!code) return null;
  // Two-step lookup: prefer a code-specific key, fall back to a default.
  const tpl = currentCatalog[`errors.${code}`];
  if (!tpl) return null;
  if (!detail) return tpl;
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => (detail[k] != null ? String(detail[k]) : `{{${k}}}`));
}

// ============================================================================
// Data-shape helpers (AcroForm auto-binding + CSV bulk import) live in a
// shared module so the SAME source is bundled here AND unit-tested under
// node. esbuild (bundle:true) inlines this require at build time.
// ============================================================================
const { normalizeJsonKey, acroNameToPath, setPathDeep, buildInlineSchemaFromFields,
        parseCsvRows, flattenDotKeys, csvEscapeCell, csvToJobItems,
        sampleFromSchema, schemaForTemplate, jobSampleText,
        fieldKeyFromName, nextFieldName, uniqueDataKey, fillFormFromFields, initialFillValues, buildFillData,
        previewFieldValue, numberInputChars, normalizeNumberInput,
        JSON_SAMPLE, CSV_SAMPLE } = require('../../src/shared/data-shape');

// Errors that mean "your plan can't do this — upgrade to continue."
// Dispatching a CustomEvent the AppShell listens for keeps the api wrapper
// decoupled from the modal component and lets ANY call site benefit
// without the caller knowing the modal exists.
const UPGRADE_REQUIRED_CODES = new Set([
  'pdf_too_large',
  'pdf_too_many_pages',
  'quota_exceeded',
  'template_limit_reached',
  'seat_limit_reached',
  'user_seat_limit_reached',
  'workspace_limit_reached',
  // Image-library caps (hosted only).
  'image_limit_reached',
  'image_too_large',
  // Hit the per-tier open-ticket cap when opening a new ticket.
  // Free = 1 concurrent, paid = 3, enterprise unlimited.
  'open_ticket_cap_reached',
]);

async function errorFrom(res, opts) {
  try {
    const j = await res.json();
    const code = j.error;
    // Use the localized message if the catalog has one for this code,
    // otherwise fall back to the raw code so toasts at least say
    // SOMETHING actionable instead of "HTTP 402".
    // Fallback chain: localized template, then the server-supplied
    // `detail` string (server responses often carry a complete English
    // sentence ready to surface), then the bare code, then a generic
    // HTTP status line.
    // Interpolate the localized template from the structured detail
    // object when present, else from the whole response body (so
    // top-level fields like `signerIndex` / `max` fill {{placeholders}}
    // even when `detail` is a ready-made English string).
    const interpSource = (j.detail && typeof j.detail === 'object') ? j.detail : j;
    // A bare code ("paid_tier_required") means nothing to a user, so the
    // last resort is a plain sentence that still carries the code for
    // support.
    const generic = (currentCatalog['errors.generic'] || 'Something went wrong ({{code}}). Please try again.')
      .replace('{{code}}', code || `HTTP ${res.status}`);
    const message = translateError(code, interpSource)
      || (typeof j.detail === 'string' ? j.detail : null)
      || (typeof j.message === 'string' ? j.message : null)
      || generic;
    const err = new Error(message);
    err.code = code;
    // Surface the structured detail so callers can branch on, e.g.,
    // a wrong-email invite (`{ error: "wrong_email", expected, actual }`)
    // or a seat-cap hit (`{ error: "user_seat_limit_reached", detail: {...} }`).
    err.detail = j.detail || j;
    // The whole body too: top-level fields (e.g. signedDocuments) are
    // lost from err.detail when detail is a sentence.
    err.body = j;
    err.status = res.status;
    // If this is a tier-cap rejection, signal the AppShell to pop the
    // upgrade modal. The throw still happens — callers can also handle
    // it locally for inline messaging if they want.
    if (UPGRADE_REQUIRED_CODES.has(code) && !(opts && opts.noUpgradeModal)) {
      try {
        window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
          detail: { code, message, body: j },
        }));
      } catch (_) {}
    }
    return err;
  } catch {
    // Non-JSON error body (proxy error page, plain-text 404): still give
    // the user a sentence, not "HTTP 502".
    const err = new Error((currentCatalog['errors.generic'] || 'Something went wrong ({{code}}). Please try again.')
      .replace('{{code}}', `HTTP ${res.status}`));
    err.status = res.status;
    return err;
  }
}

// ============================================================================
// i18n — fetches the catalog for the active locale once on boot, exposes a
// t(key, params) hook everywhere. Initial locale comes from the
// pluma_lang cookie (set by the server's localeMiddleware) so first render
// is already in the right language even before /spa/i18n resolves.
// ============================================================================

const I18nCtx = createContext({ t: (k) => k, locale: 'en', setLocale: () => {}, locales: [] });

function readCookieLocale() {
  const m = document.cookie.match(/(?:^|;\s*)pluma_lang=([a-z]{2})/);
  return (m && m[1]) || 'en';
}

// Tiny URL state shim so a refresh restores what the user was doing
// (selected template, current PDF page, etc) instead of jumping to the
// first template / page 1. We use query params keyed under a single
// path (/app) so this works without any deep-routing library — every
// state-bearing component owns one param and writes it via
// replaceState (no history-spam from clicking around).
function readUrlParam(key) {
  try { return new URLSearchParams(window.location.search).get(key); }
  catch { return null; }
}
function writeUrlParam(key, value) {
  try {
    const params = new URLSearchParams(window.location.search);
    if (value == null || value === '') params.delete(key);
    else params.set(key, String(value));
    const qs = params.toString();
    const next = window.location.pathname + (qs ? '?' + qs : '');
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', next);
    }
  } catch { /* private mode etc — silently no-op */ }
}

// Which app section the shell is showing, encoded in the `?nav` query
// param so a refresh lands back on the same page instead of the templates
// editor. Three shapes:
//   (absent)              → editor view, templates page (the clean default)
//   nav=<page>            → editor view, a secondary page (jobs, billing, …)
//   nav=settings.<id>     → settings view, that settings section
// The templates editor's own sub-state (?template, ?page) is owned
// separately by TemplatesPage / TemplateEditor.
const EDITOR_PAGES = ['templates', 'renders', 'sign-requests', 'billing', 'license', 'account', 'help', 'support'];
// Keep this list aligned with the `items` array inside SettingsShell —
// any section id that's missing here gets silently dropped from `?nav`
// on reload and the user lands on General instead of the page they were
// viewing. New sections must be added to BOTH places.
const SETTINGS_SECTIONS = ['general', 'notifications', 'users', 'workspaces', 'templates',
  'signatures', 'image-library', 'api-keys', 'saved-schemas', 'docker-registries',
  'webhooks', 'fonts', 'backup', 'branding'];
function readNav() {
  const raw = readUrlParam('nav');
  if (!raw) return { view: 'editor', page: 'templates', settingsSection: null };
  if (raw === 'settings' || raw.startsWith('settings.')) {
    const id = raw.includes('.') ? raw.slice('settings.'.length) : null;
    return {
      view: 'settings',
      page: 'templates',
      settingsSection: SETTINGS_SECTIONS.includes(id) ? id : null,
    };
  }
  // API keys moved from a top-level page into Settings; old links follow.
  if (raw === 'api-keys') return { view: 'settings', page: 'templates', settingsSection: 'api-keys' };
  if (EDITOR_PAGES.includes(raw)) return { view: 'editor', page: raw, settingsSection: null };
  return { view: 'editor', page: 'templates', settingsSection: null };
}

// Theme: "auto" (default) | "light" | "dark". Persisted in localStorage
// keyed by `pluma_theme`; applied as a data-theme attribute on <html>
// so the CSS rules in <style> can pick it up. Auto defers to the OS
// via prefers-color-scheme (handled by the CSS @media block).
const THEME_STORAGE_KEY = 'pluma_theme';
function readStoredTheme() {
  try { return localStorage.getItem(THEME_STORAGE_KEY) || 'auto'; }
  catch { return 'auto'; }
}
// Resolves "auto" against the OS preference so the meta theme-color
// reflects what the user actually sees. Safari / mobile PWAs paint
// the URL bar (and the iOS status bar inside a PWA) with whatever the
// active <meta name="theme-color"> carries; without this sync the bar
// stays the dark default forever even after the user picks light.
function resolveAppliedTheme(theme) {
  if (theme === 'light' || theme === 'dark') return theme;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}
// Same hex values the CSS uses for `--bg` in each theme (see index.css
// :root + :root[data-theme="light"]). Kept in sync by hand because the
// CSS variable isn't readable until after styles apply, and we want
// the meta tag to flip BEFORE the first paint.
const THEME_COLOR_HEX = { dark: '#12121a', light: '#f5f5fa' };
function applyThemeColorMeta(theme) {
  if (typeof document === 'undefined') return;
  const applied = resolveAppliedTheme(theme);
  let tag = document.querySelector('meta[name="theme-color"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'theme-color');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', THEME_COLOR_HEX[applied] || THEME_COLOR_HEX.dark);
}
function applyThemeAttr(theme) {
  // dark gets no attribute (matches the :root default), so a removed
  // attribute reads as "dark" with no FOUC. light/auto are explicit.
  if (theme === 'dark') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
  applyThemeColorMeta(theme);
}
const ThemeCtx = createContext({ theme: 'auto', setTheme: () => {} });
function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme());
  useEffect(() => { applyThemeAttr(theme); }, [theme]);
  // In "auto" mode, keep the meta theme-color in sync with the OS
  // preference. Without this the URL bar / iOS PWA status bar would
  // stay frozen on whichever colour was current at page load, even
  // after the user flips system Dark/Light Mode.
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => applyThemeColorMeta('auto');
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, [theme]);
  const setTheme = (next) => {
    setThemeState(next);
    try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch {}
  };
  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}
const useTheme = () => useContext(ThemeCtx);

function I18nProvider({ children }) {
  // Seed the module-scope mirror inside the lazy initializer so money
  // formatters are locale-correct from the very first render, not only
  // after the first effect pass.
  const [locale, setLocaleState] = useState(() => {
    const l = readCookieLocale();
    currentLocale = l;
    return l;
  });
  const [catalog, setCatalog] = useState({});
  const [locales, setLocales] = useState([]);

  // Load catalog + supported list on first mount and on any locale change.
  useEffect(() => {
    let aborted = false;
    fetch(`/spa/i18n/${locale}`).then((r) => r.json()).then((d) => {
      if (!aborted && d.catalog) setCatalog(d.catalog);
    }).catch(() => {});
    return () => { aborted = true; };
  }, [locale]);

  // Mirror the catalog into module scope so non-React code (errorFrom,
  // imperative toast callers) can translate without a hook. The locale
  // mirror feeds the Intl-based money/number formatters.
  useEffect(() => { currentCatalog = catalog; }, [catalog]);
  // Keep <html lang> in sync so screen readers pronounce the UI in the
  // right language (index.html ships lang="en").
  useEffect(() => { currentLocale = locale; document.documentElement.lang = locale; }, [locale]);

  useEffect(() => {
    fetch('/spa/i18n').then((r) => r.json()).then((d) => setLocales(d.locales || [])).catch(() => {});
  }, []);

  // Set a new locale: update local state, persist to backend if logged in
  // (best effort — the cookie itself is also written so reload picks it up).
  // { persist: false } applies a locale that came FROM the server, so a
  // stale read can never be written back over the user's newer choice.
  const setLocale = useCallback(async (newLocale, opts = {}) => {
    setLocaleState(newLocale);
    document.cookie = `pluma_lang=${newLocale}; max-age=${365*24*60*60}; path=/; samesite=lax${location.protocol === 'https:' ? '; secure' : ''}`;
    document.documentElement.lang = newLocale;
    if (opts.persist === false) return;
    try { await fetch('/spa/i18n/me', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale: newLocale }),
    }); } catch { /* unauth users just keep the cookie */ }
  }, []);

  // The translation function. Two-arg signature is overloaded for
  // ergonomics:
  //   t('foo.bar')                  → catalog['foo.bar']   ?? 'foo.bar'
  //   t('foo.bar', 'English fb')    → catalog['foo.bar']   ?? 'English fb'
  //   t('foo.bar', { name: 'X' })   → catalog['foo.bar'] with {{name}} expanded
  //   t('foo.bar', 'English fb', { name: 'X' })
  //                                 → English fb with {{name}} expanded (when key missing)
  // The overload lets developers ship UI with a sensible English
  // default inline instead of waiting for a locale-catalog round
  // trip, while keeping the {{param}} interpolation contract intact.
  const t = useCallback((key, fallbackOrParams, params) => {
    const fallbackIsString = typeof fallbackOrParams === 'string';
    const fallback = fallbackIsString ? fallbackOrParams : null;
    const paramObj = fallbackIsString
      ? (params && typeof params === 'object' ? params : null)
      : (fallbackOrParams && typeof fallbackOrParams === 'object' ? fallbackOrParams : null);
    let s = catalog[key];
    if (s == null) s = fallback != null ? fallback : key;
    if (paramObj) {
      s = s.replace(/\{\{(\w+)\}\}/g, (_, k) => (paramObj[k] != null ? String(paramObj[k]) : `{{${k}}}`));
    }
    return s;
  }, [catalog]);

  return (
    <I18nCtx.Provider value={{ t, locale, setLocale, locales }}>
      {children}
    </I18nCtx.Provider>
  );
}
const useI18n = () => useContext(I18nCtx);
const useT = () => useI18n().t;

// ============================================================================
// Toast system
// ============================================================================

// Icon set ported from the original PDF Microservice Builder. Plain SVG
// inlined as React components so the bundle stays self-contained — no
// icon-font / external pack required. Sizes are prescriptive (sized to
// their typical usage spot) but every icon accepts a fill / stroke from
// `currentColor`, so styling is just a wrapping CSS rule.
const Icons = {
  Plus: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
  // Warning triangle — paired with form-input syntax-error feedback.
  // Filled triangle outline + exclamation glyph reads as "something
  // is wrong here" at favicon-row sizes without needing colour.
  AlertTriangle: (props) => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  // Layers — represents the editor's overlay layer that sits on top of
  // the rendered PDF. Paired with LayersOff for the show/hide toggle.
  Layers: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  LayersOff: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19.785 14.5l2.215-1.5-10-7-1.5 1.05"/><path d="M2 7l10 7 2.85-2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 4.5 13.75"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  Trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  // Pencil — affordance for inline rename actions (folder header,
  // future field-name edits). Sized to match Trash so paired toolbar
  // buttons read as a set.
  Pencil: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>,
  Copy: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  // Eraser — used by the "clear field" action in the properties
  // toolbar. Stroke-only so it themes via currentColor like the rest.
  Eraser: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7L3 16a2 2 0 0 1 0-2.8L13.2 3a2 2 0 0 1 2.8 0l5 5a2 2 0 0 1 0 2.8L10 20"/><path d="M14 6l4 4"/></svg>,
  // Info — small "i" used by LabelWithInfo to hide long explanations
  // behind a tooltip instead of letting them dominate the panel.
  Info: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><circle cx="12" cy="8" r="0.7" fill="currentColor"/></svg>,
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Folder: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  FolderPlus: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>,
  ChevronDown: (props) => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" {...props}><polyline points="6 9 12 15 18 9"/></svg>,
  ChevronLeft: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>,
  File: () => <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  Settings: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  // Padlock — closed/open. Closed glyph for "locked, click to unlock",
  // open glyph for "unlocked, click to lock". Stroke-only so it themes
  // via currentColor like every other icon.
  Lock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  LockOpen: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>,
  // Curved-arrow undo/redo. Mirror images of each other so they read as
  // a paired affordance in the toolbar.
  Undo: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>,
  Redo: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 15-6.7L21 13"/></svg>,
};

// Indeterminate progress bar — a thin sliding-gradient affordance used
// in place of "Loading…" text and rotating spinners across the SPA.
// Stateless; the visible animation lives entirely in .progress-bar CSS,
// which auto-themes via CSS vars and falls back to a static fill under
// prefers-reduced-motion. Accept className so callers can size it.
function ProgressBar({ className = '', style }) {
  return (
    <div className={`progress-bar ${className}`}
         role="progressbar"
         aria-label="Loading"
         aria-busy="true"
         style={style} />
  );
}

// LoadingFrame — full-area placeholder used while a major asset (the
// editor PDF, a settings card content) is loading. Labelled so the user
// knows *what* is loading; the embedded ProgressBar gives the motion
// affordance. Pair with the route the user is on, e.g.:
//   <LoadingFrame label="Loading PDF…" />
function LoadingFrame({ label, className = '' }) {
  return (
    <div className={`loading-frame ${className}`} role="status" aria-live="polite">
      {label ? <div className="loading-frame__label">{label}</div> : null}
      <ProgressBar />
    </div>
  );
}

// ─────────────────────────── Icons ───────────────────────────
// One inline-SVG set, replacing the emoji and Unicode dingbats the UI used to
// render (🔒 ⬇ 🗑 ⚠️ 🏷️ ☁ 👋 ✕ ✓ ✎ ▦ ◇ ✱ ● ■).
//
// Why emoji had to go:
//   - they render as a DIFFERENT picture on every OS (and as a blank tofu box
//     on the Linux servers/kiosks a self-hosted install often runs on),
//   - they are full-colour, so they ignore the theme and clash with the accent,
//   - their metrics vary wildly, which is why they needed per-site font-size
//     and margin hacks to line up with text,
//   - a screen reader announces the emoji's own name unless every use is
//     hand-labelled.
// Inline SVG fixes all four: identical everywhere, `currentColor` so it themes
// itself, `1em` so it sits on the text baseline, and aria-hidden by default
// (decorative) unless the caller passes a title.
//
// No icon-font, no library: it stays inside the "runs offline, no external
// services" promise and adds ~0 bytes of network cost. Paths are 24×24,
// stroke-based, 1.75 weight so they read at 14–16px.
const ICON_PATHS = {
  close:     'M6 6l12 12M18 6L6 18',
  check:     'M4 12.5l5 5L20 6.5',
  lock:      'M6 10.5h12v9H6zM8.5 10.5V7.5a3.5 3.5 0 017 0v3',
  download:  'M12 4v11m0 0l-4.5-4.5M12 15l4.5-4.5M5 19.5h14',
  trash:     'M4.5 7h15M9.5 7V4.5h5V7M7 7l1 12.5h8L17 7M10.5 10.5v6M13.5 10.5v6',
  warning:   'M12 4.5L2.5 20h19zM12 10v4.5M12 17.4v.1',
  tag:       'M3.5 11.2V4.5h6.7L20 14.3l-6.7 6.7zM7.6 8.1v.1',
  cloud:     'M7.5 18.5a4 4 0 01-.4-8 5.5 5.5 0 0110.6 1.4 3.6 3.6 0 01-.7 6.6z',
  wave:      'M8 13V5.5a1.5 1.5 0 013 0V12m0-.5V4.5a1.5 1.5 0 013 0V12m0-.5v-1a1.5 1.5 0 013 0V15a6 6 0 01-6 6h-1a6 6 0 01-6-6v-3a1.5 1.5 0 013 0',
  pen:       'M4 20l4.5-1 10-10a2.1 2.1 0 10-3-3l-10 10z',
  users:     'M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2.5 20a6.5 6.5 0 0113 0M16 4.7a3.5 3.5 0 010 6.6M18 14.2a6.5 6.5 0 013.5 5.8',
  grid:      'M4 4h16v16H4zM4 9.3h16M4 14.7h16M9.3 4v16M14.7 4v16',
  diamond:   'M12 3l9 9-9 9-9-9z',
  asterisk:  'M12 4v16M4.9 8l14.2 8M19.1 8L4.9 16',
  circle:    'M12 12m-8 0a8 8 0 1016 0 8 8 0 10-16 0',
  square:    'M4.5 4.5h15v15h-15z',
  text:      'M5 5.5h14M12 5.5v13M9 18.5h6',
  copy:      'M9 9h11v11H9zM15 9V4H4v11h5',
  external:  'M14 4h6v6M20 4l-9 9M18 14v5.5H4.5V6H10',
  chevron:   'M9 5l7 7-7 7',
  alignLeft:    'M4 6h16M4 10h10M4 14h16M4 18h10',
  alignCenter:  'M4 6h16M7 10h10M4 14h16M7 18h10',
  alignRight:   'M4 6h16M10 10h10M4 14h16M10 18h10',
  valignTop:    'M4 4h16M7.5 7.5h3.5v12H7.5zM13 7.5h3.5V15H13z',
  valignMiddle: 'M4 12h16M7.5 5h3.5v14H7.5zM13 8h3.5v8H13z',
  valignBottom: 'M4 20h16M7.5 4.5h3.5v12H7.5zM13 9h3.5v7.5H13z',
  page:      'M6.5 3.5h7.5l4 4v13h-11.5zM14 3.5V8h4M9.5 12.5h5M9.5 16h5',
  list:      'M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.5M4.5 12h.5M4.5 17.5h.5',
  dots:      'M4.4 12a1.6 1.6 0 103.2 0 1.6 1.6 0 10-3.2 0M10.4 12a1.6 1.6 0 103.2 0 1.6 1.6 0 10-3.2 0M16.4 12a1.6 1.6 0 103.2 0 1.6 1.6 0 10-3.2 0',
  arrowDown: 'M12 5v14M6.5 13.5L12 19l5.5-5.5',
  bell:      'M6 16.5V11a6 6 0 0112 0v5.5l1.5 2h-15zM10 20.5a2 2 0 004 0',
};
// Icons whose shape needs a filled interior rather than a stroked outline.
const ICON_FILLED = new Set(['circle', 'square', 'diamond', 'dots']);

// Provider marks for the "Continue with …" buttons. Inline (the app loads
// nothing from third parties) and small: Google's four-colour G as its
// brand guidelines want it, Apple's logo in the button's text colour as
// Apple's do.
function GoogleMark() {
  return (
    <svg className="sso-mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
function AppleMark() {
  return (
    <svg className="sso-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function Icon({ name, title, className = '', size }) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  const filled = ICON_FILLED.has(name);
  return (
    <svg
      className={`icon icon-${name} ${className}`.trim()}
      viewBox="0 0 24 24"
      width={size || '1em'} height={size || '1em'}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round" strokeLinejoin="round"
      // Decorative unless the caller gives it a name — otherwise every icon
      // next to a text label would be read out twice.
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={d} />
    </svg>
  );
}

const ToastCtx = createContext();
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((message, type = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, message, type }]);
    // Errors usually need reading (and often acting on) — keep them up longer.
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), type === 'error' ? 8000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {/* Live region: most feedback in the app is a toast, so screen
          readers must hear it. */}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`} role={t.type === 'error' ? 'alert' : undefined}>{t.message}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
const useToast = () => useContext(ToastCtx);

// ============================================================================
// Confirm dialog — replaces window.confirm everywhere with a styled modal
// that matches the rest of the UI. Supports a "danger" variant for
// destructive actions and an optional second-step typed confirmation
// (e.g. "type DELETE to confirm").
// ============================================================================

const ConfirmCtx = createContext();
function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const t = useT();

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || t('common.areYouSure', 'Are you sure?'),
        body: opts.body || '',
        confirmLabel: opts.confirmLabel || t('common.confirm', 'Confirm'),
        cancelLabel: opts.cancelLabel || t('common.cancel', 'Cancel'),
        danger: !!opts.danger,
        typeToConfirm: opts.typeToConfirm || null,
        promptLabel: opts.promptLabel || null,
        promptDefault: opts.promptDefault || '',
        resolve,
      });
    });
  }, [t]);

  const close = (value) => {
    state?.resolve(value);
    setState(null);
  };

  // Local state for the typed-confirmation input + prompt input.
  const [typedConfirm, setTypedConfirm] = useState('');
  const [promptValue, setPromptValue] = useState('');
  useEffect(() => {
    setTypedConfirm('');
    setPromptValue(state?.promptDefault || '');
  }, [state]);

  // Escape cancels. Enter is handled by the <form> below, so it only
  // confirms when focus is inside the dialog (an input or the confirm
  // button). The old window-level Enter handler confirmed even when the
  // user had tabbed to Cancel — running the delete they meant to abort.
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Give focus back to whatever opened the dialog.
  const returnFocusRef = useRef(null);
  const isOpen = !!state;
  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement;
    } else if (returnFocusRef.current) {
      try { returnFocusRef.current.focus(); } catch (_) { /* element gone */ }
      returnFocusRef.current = null;
    }
  }, [isOpen]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div className="modal-overlay" data-confirm-open="true" onClick={() => close(false)}>
          <form className="modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title"
                onClick={(e) => e.stopPropagation()} style={{maxWidth: 460}}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (state.typeToConfirm && typedConfirm !== state.typeToConfirm) return;
                  close(state.promptLabel ? promptValue : true);
                }}>
            <h2 id="confirm-dialog-title" style={{marginBottom: 8}}>{state.title}</h2>
            {state.body && <p style={{color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16}}>{state.body}</p>}
            {state.promptLabel && (
              <div style={{marginBottom: 16}}>
                <label className="label" htmlFor="confirm-dialog-input">{state.promptLabel}</label>
                <input id="confirm-dialog-input" className="input" autoFocus value={promptValue}
                       onChange={(e) => setPromptValue(e.target.value)} />
              </div>
            )}
            {state.typeToConfirm && (
              <div style={{marginBottom: 16}}>
                <label className="label" htmlFor="confirm-dialog-input" style={{textTransform: 'none', letterSpacing: 0}}>
                  {t('confirm.typePrefix', 'Type')}{' '}
                  <code style={{background: 'var(--bg-elevated)', padding: '1px 6px', borderRadius: 4}}>{state.typeToConfirm}</code>{' '}
                  {t('confirm.typeSuffix', 'to confirm')}
                </label>
                <input id="confirm-dialog-input" className="input" autoFocus value={typedConfirm}
                       autoComplete="off" onChange={(e) => setTypedConfirm(e.target.value)} />
              </div>
            )}
            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8}}>
              {/* Destructive dialogs start on Cancel, others on the
                  confirm button — so Enter does the safe thing. */}
              <button type="button" className="btn" onClick={() => close(false)}
                      autoFocus={!state.promptLabel && !state.typeToConfirm && state.danger}>
                {state.cancelLabel}
              </button>
              <button
                type="submit"
                className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`}
                autoFocus={!state.promptLabel && !state.typeToConfirm && !state.danger}
                disabled={state.typeToConfirm && typedConfirm !== state.typeToConfirm}>
                {state.confirmLabel}
              </button>
            </div>
          </form>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}
const useConfirm = () => useContext(ConfirmCtx);

// ============================================================================
// Auth context
// ============================================================================

// Runtime config from the server — tells the SPA which surfaces the
// current deployment exposes (commerce, admin, help center, marketing).
// Fetched once on app boot; cached for the session. Defaults err on the
// side of "hide the feature" so we never render a button that 404s.
// Shared modal keyboard behaviour: Escape closes the dialog. Bails when
// a confirm dialog is stacked on top (its own handler wins) and when the
// caller marks itself busy (so Escape can't abandon an in-flight submit).
// Autofocus is handled per-modal via the returned ref on the first field.
// Keyboard activation for elements that are visually buttons but render
// as <div> (menus, tabs, nav rows). Mirrors the click handler onto
// Enter/Space so they're operable without a mouse. Pair with
// role + tabIndex={0} on the element.
function keyActivate(fn) {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      fn(e);
    }
  };
}

function useModalDismiss(onClose, { busy = false } = {}) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || busy) return;
      if (document.querySelector('[data-confirm-open]')) return; // confirm on top wins
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
}

const RuntimeCtx = createContext();
function RuntimeProvider({ children }) {
  const [runtime, setRuntime] = useState(null);
  useEffect(() => {
    fetch('/spa/runtime', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setRuntime(d);
        // Lazy-inject Plausible when the operator configured a domain.
        // Cookieless + no fingerprinting, so no consent banner needed.
        // Idempotent on the data-domain attr so a refresh-trigger from
        // a re-render doesn't double-load the script.
        const dom = d.analytics?.plausibleDomain;
        if (dom && !document.querySelector(`script[data-domain="${dom}"]`)) {
          const s = document.createElement('script');
          s.defer = true;
          s.setAttribute('data-domain', dom);
          s.src = d.analytics.plausibleScriptUrl || 'https://plausible.io/js/script.js';
          document.head.appendChild(s);
        }
      })
      .catch(() => setRuntime({ mode: 'selfhosted', features: {} }));
  }, []);
  return <RuntimeCtx.Provider value={runtime}>{children}</RuntimeCtx.Provider>;
}
const useRuntime = () => useContext(RuntimeCtx) || { features: {} };

const AuthCtx = createContext();
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // True when a login (typically OAuth) is parked waiting for its TOTP
  // code — /auth/me reports { twoFactorPending: true } and no user. The
  // AuthPage opens straight on the code step.
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      if (data.twoFactorPending) {
        setTwoFactorPending(true);
        setUser(null);
      } else {
        setTwoFactorPending(false);
        setUser(data.user);
      }
    } catch {
      setUser(null);
      setTwoFactorPending(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
    setTwoFactorPending(false);
    window.__currentTenantId = null;
    // Clear the persisted tenant choice so the next user signing in on
    // this browser doesn't start scoped to the previous user's org.
    writePersistedTenantId(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, twoFactorPending, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
const useAuth = () => useContext(AuthCtx);

// ============================================================================
// Auth page (login + register)
// ============================================================================

function AuthPage() {
  const runtime = useRuntime();
  // Self-hosted is single-operator, local-account only: no OAuth, no OTP-by-
  // email login, no email verification, no password-reset-by-email link (there's
  // no transactional mail). The matching server routes 404. `localAuthOnly`
  // collapses the two-step hosted flow into one email+password form.
  const localAuthOnly = !!(runtime.features && runtime.features.localAuthOnly);
  // Per-provider OAuth availability. Each flag is true ONLY when the
  // operator has populated both the client id and the client secret
  // for that provider (see builder/src/runtime/routes.js); falsy when
  // unconfigured or when localAuthOnly is in force. Buttons gate on
  // these independently so a deployment that wires only Google still
  // shows the Google button without an orphaned Apple one.
  const googleAuthAvailable = !!(runtime.oauth && runtime.oauth.google && runtime.oauth.google.enabled);
  const appleAuthAvailable = !!(runtime.oauth && runtime.oauth.apple && runtime.oauth.apple.enabled);
  const anySsoAvailable = googleAuthAvailable || appleAuthAvailable;
  // Fresh install with zero users → register the admin (first user wins, see
  // auth/routes.js). Once anyone exists this is false.
  const needsFirstUser = !!runtime.needsFirstUser;

  // Two-step hosted login flow:
  //   1. mode='email'    — user types email, hits Continue.
  //   2a. hasPassword    → mode='password' → submit email+password.
  //   2b. !hasPassword   → /auth/otp/request fires automatically and we
  //                        jump to mode='otp_verify' for the 6-digit code.
  // Self-hosted: start straight in 'password' (email field stays editable).
  // 'reset' is driven by a ?reset_token= URL param (hosted only).
  const params = new URLSearchParams(window.location.search);
  const resetToken = (!localAuthOnly && params.get('reset_token')) ? params.get('reset_token') : null;
  // An invite link lands the user here with ?invite_token=…. Default them
  // to the register form (most invitees are new), passing the token so
  // /auth/register both creates the account and joins the account in one
  // shot. They can still switch to "sign in" if they already have a login.
  // The token also re-opens the register form on a self-hosted install
  // where open sign-up is otherwise closed.
  const inviteToken = params.get('invite_token') || null;
  // Marketing "Get started" / "Start free" CTAs link to /app?signup=1 so a
  // new visitor lands on the sign-up form instead of "Welcome back".
  // Self-hosted sign-up is invite-only, so the flag is ignored there.
  const wantsSignup = !localAuthOnly && (params.has('signup') || params.get('mode') === 'signup');
  const { refresh, twoFactorPending } = useAuth();
  const initialMode = twoFactorPending ? 'twoFactor'
    : resetToken ? 'reset'
    : inviteToken ? 'register'
    : needsFirstUser ? 'register'
    : wantsSignup ? 'register'
    : localAuthOnly ? 'password'
    : 'email';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 6-digit TOTP code or a recovery code, entered at the 2FA login step.
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // First + last name asked separately on email registration. OAuth
  // (Google / Facebook) populates User.name from the provider's
  // displayName and skips this prompt entirely. Salutation is no longer
  // asked here — every extra sign-up field costs conversions, and it's
  // still editable in Account → Profile.
  const [firstName, setFirstName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [lastName, setLastName] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [busy, setBusy] = useState(false);
  // First-run only: holds whether the restore-from-backup modal is open.
  // Eligibility (is the install genuinely empty?) is checked inside the
  // modal so we don't fire an extra fetch on every auth-page mount; the
  // text link itself is gated on `firstUser` which is the same signal.
  const [restoreOpen, setRestoreOpen] = useState(false);
  // Tenant name from the invite, shown as a "join X" hint once resolved.
  const [inviteTenantName, setInviteTenantName] = useState(null);
  const toast = useToast();
  const t = useT();

  // Invite links default to the REGISTER form (most invitees are new) —
  // but an invitee who already has an account should be greeted with
  // SIGN IN and their email prefilled, not a register form that will
  // reject their email as taken. Resolve the invite's metadata and
  // switch the form accordingly. Errors keep the register default.
  useEffect(() => {
    if (!inviteToken) return;
    let aborted = false;
    (async () => {
      try {
        const r = await fetch(`/accept-invite/${encodeURIComponent(inviteToken)}/info`, { credentials: 'include' });
        if (!r.ok) return; // invalid/expired/accepted — POST after auth reports the real error
        const info = await r.json();
        if (aborted) return;
        if (info.tenantName) setInviteTenantName(info.tenantName);
        if (info.email) setEmail(info.email);
        if (info.userExists) {
          // Existing account → sign-in. Self-hosted is password-only;
          // hosted goes through the email step (prefilled, one click)
          // which routes to password or OTP as appropriate.
          setMode(localAuthOnly ? 'password' : 'email');
        }
      } catch (_) { /* network hiccup — register default stands */ }
    })();
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goSignIn = () => { setMode(localAuthOnly ? 'password' : 'email'); setPassword(''); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'email') {
        // Decide whether to ask for a password or send a one-time code.
        const { hasPassword } = await api.post('/auth/email-check', { email });
        if (hasPassword) {
          setMode('password');
        } else {
          await api.post('/auth/otp/request', { email });
          toast(t('auth.otp.requestSent'), 'success');
          setMode('otp_verify');
        }
      } else if (mode === 'password') {
        const r = await api.post('/auth/login', { email, password });
        // Account has 2FA — the server parked the login; ask for the code.
        if (r.twoFactorRequired) { setMode('twoFactor'); setPassword(''); }
        else await refresh();
      } else if (mode === 'twoFactor') {
        await api.post('/auth/2fa/login-verify', { code: twoFactorCode.trim() });
        await refresh();
      } else if (mode === 'register') {
        // Backend stores three structured fields; the denormalised `name`
        // is server-side derived (composeName) — we never send it directly.
        await api.post('/auth/register', {
          email, password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          inviteToken: inviteToken || undefined,
        });
        // Drop ?signup so a later reload doesn't reopen the form.
        if (wantsSignup) {
          const p = new URLSearchParams(window.location.search);
          p.delete('signup'); p.delete('mode');
          const qs = p.toString();
          window.history.replaceState(null, '', '/app' + (qs ? '?' + qs : ''));
        }
        await refresh();
      } else if (mode === 'forgot') {
        await api.post('/auth/password-reset/request', { email });
        toast(t('auth.resetEmailSent'), 'success');
        setMode('email');
      } else if (mode === 'reset') {
        await api.post('/auth/password-reset/confirm', { token: resetToken, password });
        toast(t('auth.passwordUpdated'), 'success');
        // Strip the token from the URL so browser refresh doesn't replay.
        window.history.replaceState(null, '', '/app');
        setMode('email');
        setPassword('');
      } else if (mode === 'otp_verify') {
        const r = await api.post('/auth/otp/verify', { email, code: otpCode });
        if (r.twoFactorRequired) { setMode('twoFactor'); setOtpCode(''); }
        else await refresh();
      }
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const firstUser = needsFirstUser && mode === 'register';
  const productName = (runtime.brand && runtime.brand.productName) || 'Pluma';
  const titles = {
    email: t('auth.emailStepTitle', 'Sign in to {{product}}', { product: productName }),
    password: t('auth.welcomeBack'),
    register: firstUser ? t('auth.firstUserTitle') : t('auth.createAccount'),
    forgot: t('auth.resetPassword'),
    reset: t('auth.chooseNewPassword'),
    otp_verify: t('auth.otp.title'),
    twoFactor: t('auth.2fa.title'),
  };
  const subs = {
    email: t('auth.emailStepSubtitle', 'Enter your email. New here? We’ll create your free account.'),
    password: t('auth.signInSubtitle'),
    register: firstUser ? t('auth.firstUserSubtitle') : t('auth.signUpSubtitle'),
    forgot: t('auth.forgotSubtitle'),
    reset: t('auth.resetSubtitle'),
    otp_verify: t('auth.otp.subtitle'),
    twoFactor: t('auth.2fa.subtitle'),
  };

  const emailEditable = localAuthOnly || (mode !== 'password' && mode !== 'otp_verify');

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Brand mark — links back to the website (self-hosted "/"
            redirects to /app, so the link is safe everywhere). */}
        <a className="auth-brand" href="/">
          <img src={(runtime.brand && runtime.brand.faviconUrl) || '/static/favicon.svg'} alt="" width="28" height="28" />
          <span>{productName}</span>
        </a>
        <h1>{titles[mode]}</h1>
        <p className="sub">{subs[mode]}</p>

        {/* Invite context banner — tells the visitor WHY they're here and
            which workspace they're joining, for both the sign-in and the
            register variant of the invite flow. */}
        {inviteToken && inviteTenantName && (
          <div style={{
            padding: '10px 12px', marginBottom: 14, borderRadius: 8, fontSize: 13,
            background: 'rgba(108,92,231,0.08)', border: '1px solid rgba(108,92,231,0.35)',
          }}>
            {t('invite.banner', 'You’ve been invited to join {{tenant}}. Sign in or create your account to accept.', { tenant: inviteTenantName })}
          </div>
        )}

        {!localAuthOnly && anySsoAvailable && (mode === 'email' || mode === 'register') && (
          <>
            <div className="oauth-row">
              {googleAuthAvailable && (
                <a href="/auth/google" className="btn btn-sso"><GoogleMark />{t('auth.continueWithGoogle')}</a>
              )}
              {appleAuthAvailable && (
                <a href="/auth/apple" className="btn btn-sso"><AppleMark />{t('auth.continueWithApple', 'Continue with Apple')}</a>
              )}
            </div>
            {/* A first Google/Apple login creates the account right here,
                so the same age confirmation + agreement sits under the
                buttons. In register mode the form's own line covers it. */}
            {mode === 'email' && (
              <div className="auth-legal auth-legal-sso">
                {t('auth.legalPrefixSso', 'By continuing with Google or Apple you confirm that you are at least 18 years old (or act for a company) and agree to the')}{' '}
                <a href="/terms" target="_blank" rel="noopener">{t('auth.legalTerms', 'Terms')}</a>{' '}
                {t('auth.legalAnd', 'and')}{' '}
                <a href="/privacy" target="_blank" rel="noopener">{t('auth.legalPrivacy', 'Privacy Policy')}</a>.
              </div>
            )}
            <div className="divider">{t('auth.or')}</div>
          </>
        )}

        <form className="form-row" onSubmit={submit}>
          {mode === 'register' && (
            <div className="auth-name-row">
              <div>
                <label className="label" htmlFor="auth-first-name">{t('auth.firstName', 'First name')}</label>
                <input id="auth-first-name" className="input" required autoFocus autoComplete="given-name"
                       value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="auth-last-name">{t('auth.lastName', 'Last name')}</label>
                <input id="auth-last-name" className="input" required autoComplete="family-name"
                       value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
          )}
          {mode !== 'reset' && mode !== 'twoFactor' && (
            <div>
              <label className="label" htmlFor="auth-email">{t('auth.email')}</label>
              <input id="auth-email" className="input" type="email" required autoComplete="email"
                     disabled={!emailEditable}
                     autoFocus={mode === 'email' || (localAuthOnly && mode === 'password')}
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          )}
          {(mode === 'password' || mode === 'register' || mode === 'reset') && (
            <div>
              <label className="label" htmlFor="auth-password">{mode === 'reset' ? t('auth.newPassword') : t('auth.password')}</label>
              <div className="auth-password-wrap">
                <input id="auth-password" className="input" type={showPassword ? 'text' : 'password'} required minLength={8}
                       autoComplete={mode === 'password' ? 'current-password' : 'new-password'}
                       autoFocus={mode === 'password' && !localAuthOnly}
                       value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="auth-password-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-pressed={showPassword}>
                  {showPassword ? t('auth.hidePassword', 'Hide') : t('auth.showPassword', 'Show')}
                </button>
              </div>
              {mode !== 'password' && (
                <div className="form-hint">{t('auth.passwordHint', 'At least 8 characters.')}</div>
              )}
            </div>
          )}
          {mode === 'otp_verify' && (
            <div>
              <label className="label" htmlFor="auth-otp">{t('auth.otp.codeLabel')}</label>
              <input id="auth-otp" className="input" required pattern="[0-9]{6}" maxLength={6} autoFocus
                     inputMode="numeric" autoComplete="one-time-code"
                     style={{letterSpacing: '0.4em', fontFamily: 'var(--font-mono)', fontSize: 18, textAlign: 'center'}}
                     value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} />
            </div>
          )}
          {mode === 'twoFactor' && (
            <div>
              <label className="label" htmlFor="auth-2fa">{t('auth.2fa.codeLabel')}</label>
              {/* Accepts a 6-digit TOTP code OR a recovery code — the
                  server's verifier handles both, so no strict pattern. */}
              <input id="auth-2fa" className="input" required autoFocus autoComplete="one-time-code"
                     style={{letterSpacing: '0.2em', fontFamily: 'var(--font-mono)', fontSize: 18, textAlign: 'center'}}
                     value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} />
              <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 6}}>{t('auth.2fa.codeHint')}</div>
            </div>
          )}
          <button className="btn btn-primary" disabled={busy} type="submit">
            {busy ? (
              mode === 'register' ? t('common.creating', 'Creating…')
              : mode === 'forgot' ? t('common.sending', 'Sending…')
              : t('auth.signingIn', 'Signing in…')
            ) : (
              mode === 'email' ? t('common.continue')
              : mode === 'password' ? t('auth.signIn')
              : mode === 'register' ? t('auth.signUp')
              : mode === 'forgot' ? t('auth.sendResetLink')
              : mode === 'otp_verify' ? t('auth.otp.verifyCta')
              : mode === 'twoFactor' ? t('auth.2fa.verify')
              : t('auth.updatePassword')
            )}
          </button>
          {/* Hosted sign-up: make the agreement explicit, with links. */}
          {mode === 'register' && !localAuthOnly && (
            <div className="auth-legal">
              {t('auth.legalPrefix', 'By creating an account you agree to the')}{' '}
              <a href="/terms" target="_blank" rel="noopener">{t('auth.legalTerms', 'Terms')}</a>{' '}
              {t('auth.legalAnd', 'and')}{' '}
              <a href="/privacy" target="_blank" rel="noopener">{t('auth.legalPrivacy', 'Privacy Policy')}</a>.
            </div>
          )}
        </form>

        <div className="auth-switch">
          {mode === 'email' && (
            <div>{t('auth.noAccount')} <button type="button" className="btn-link" onClick={() => setMode('register')}>{t('auth.signUp')}</button></div>
          )}
          {mode === 'password' && (
            <>
              {!localAuthOnly && <div><button type="button" className="btn-link" onClick={() => setMode('forgot')}>{t('auth.forgotPassword')}</button></div>}
              {!localAuthOnly && <div style={{marginTop: 8}}><button type="button" className="btn-link" onClick={() => { setMode('email'); setPassword(''); }}>{t('auth.useDifferentEmail')}</button></div>}
              {/* Self-hosted sign-up is invite-only — only offer "create account"
                  here when the visitor actually arrived with an invite link. */}
              {localAuthOnly && inviteToken && <div>{t('auth.noAccount')} <button type="button" className="btn-link" onClick={() => setMode('register')}>{t('auth.signUp')}</button></div>}
            </>
          )}
          {mode === 'otp_verify' && (
            <button type="button" className="btn-link" onClick={() => { setMode('email'); setOtpCode(''); }}>{t('auth.useDifferentEmail')}</button>
          )}
          {mode === 'twoFactor' && (
            <button type="button" className="btn-link"
               onClick={async () => {
                 // Drop the parked 2FA state server-side, then restart.
                 try { await api.post('/auth/logout'); } catch (_) {}
                 window.location.assign('/app');
               }}>{t('auth.backToSignIn')}</button>
          )}
          {mode === 'register' && !needsFirstUser && (
            <>{t('auth.haveAccount')} <button type="button" className="btn-link" onClick={goSignIn}>{t('auth.signIn')}</button></>
          )}
          {/* Fresh-install only, SELF-HOSTED only: subtle "restore from
              backup" link under the Create account CTA. The /api/restore
              endpoints are mounted only inside the `!isHosted` branch in
              server.js — `localAuthOnly` is the SPA's signal for
              "this build is self-hosted", so we re-use it here to keep
              the link from appearing on a fresh hosted tenant signup. */}
          {mode === 'register' && firstUser && localAuthOnly && (
            <div style={{marginTop: 14, fontSize: 13, color: 'var(--text-secondary)'}}>
              {t('restore.linkPreamble', 'Migrating an existing install?')}{' '}
              <button type="button" className="btn-link" onClick={() => setRestoreOpen(true)}>
                {t('restore.link', 'Restore from backup')}
              </button>
            </div>
          )}
          {mode === 'forgot' && (
            <button type="button" className="btn-link" onClick={() => setMode('email')}>{t('auth.backToSignIn')}</button>
          )}
          {mode === 'reset' && (
            <button type="button" className="btn-link" onClick={() => setMode('email')}>{t('auth.backToSignIn')}</button>
          )}
        </div>
      </div>

      {/* Fresh-install restore modal — SELF-HOSTED only. Triggered by
          the subtle text link in the auth-switch block above. The
          backend refuses the upload once any user exists (409
          install_not_empty), but the /api/restore router itself is
          mounted only inside the `!isHosted` branch of server.js, so a
          hosted build can't even reach the modal. We belt-and-suspender
          gate on `localAuthOnly` too in case future changes split the
          two signals. */}
      {firstUser && localAuthOnly && restoreOpen && (
        <RestoreFromBackupModal onClose={() => setRestoreOpen(false)} />
      )}
    </div>
  );
}

// Restore-from-backup modal. Opened from a subtle text link below the
// "Create account" CTA on the first-run signup screen — kept off the
// main signup flow because the vast majority of fresh installs are
// genuinely fresh, not migrations.
//
// Defends against a stale-open page race by re-checking
// GET /api/restore/state on mount and refusing if the install has filled
// up between the link being clicked and the modal opening.
function RestoreFromBackupModal({ onClose }) {
  const [eligible, setEligible] = useState(null); // null = checking, true/false = result
  const [file, setFile] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [schemaDrift, setSchemaDrift] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const t = useT();

  useEffect(() => {
    fetch('/api/restore/state', { credentials: 'include' })
      .then((r) => r.json()).then((j) => setEligible(!!j.empty))
      .catch(() => setEligible(false));
  }, []);

  // Read the bundle's manifest BEFORE committing — gives the operator a
  // "this is what's in the zip" preview so they don't accidentally
  // restore last quarter's snapshot.
  const previewManifest = async (f) => {
    setError(''); setManifest(null); setSchemaDrift(false); setFile(f);
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('bundle', f);
      const res = await fetch('/api/restore/manifest', { method: 'POST', body: fd, credentials: 'include' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || j.error || 'preview_failed');
      setManifest(j.manifest);
      setSchemaDrift(!!j.schemaDrift);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    if (!file) return;
    setError(''); setBusy(true);
    try {
      const fd = new FormData(); fd.append('bundle', file);
      const res = await fetch('/api/restore', { method: 'POST', body: fd, credentials: 'include' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || j.error || 'restore_failed');
      setDone(j);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // Body is one of: checking / not-eligible / done / form. We keep the
  // outer modal-overlay + modal shell stable across states so the close
  // button stays in the same place — no layout jump as the operator
  // moves through the steps.
  let body;
  if (eligible === null) {
    body = <div className="sub">{t('common.loading')}</div>;
  } else if (!eligible) {
    body = (
      <>
        <p className="sub">
          {t('restore.notEligible',
             'This install already has users — restore is only available on a fresh, empty install. Wipe the database and try again, or use a clean container.')}
        </p>
        <div style={{display:'flex', justifyContent:'flex-end'}}>
          <button className="btn" onClick={onClose}>{t('common.close', 'Close')}</button>
        </div>
      </>
    );
  } else if (done) {
    body = (
      <>
        <p className="sub">
          {t('restore.done.body', 'Restored {{models}} record types and {{blobs}} files. Sign in with the credentials from the source install.', {
            models: Object.keys(done.db?.models || {}).length,
            blobs: done.blobs?.count || 0,
          })}
        </p>
        <div style={{display:'flex', justifyContent:'flex-end'}}>
          <button className="btn btn-primary" onClick={() => window.location.assign('/app')}>
            {t('restore.done.cta', 'Continue to sign-in')}
          </button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <p className="sub" style={{marginTop: 0}}>
          {t('restore.body', 'Skip the signup and reload from a backup .zip. Database, uploaded files, and settings will be replaced with the bundle’s contents. The previous self-hosted licence activation is not transferred — re-activate after sign-in.')}
        </p>
        <input type="file" accept=".zip,application/zip"
               onChange={(e) => previewManifest(e.target.files?.[0] || null)} />
        {manifest && (
          <div className="card" style={{marginTop: 12, padding: 12, fontSize: 13}}>
            <div>
              <strong>{t('restore.source', 'Source')}:</strong>{' '}
              {manifest.dbDialect || 'unknown'} ·{' '}
              {formatDateTime(manifest.createdAt)}
            </div>
            <div style={{marginTop: 4}}>
              <strong>{t('restore.contents', 'Contents')}:</strong>{' '}
              {Object.keys(manifest.counts?.models || {}).length} {t('restore.modelTypes', 'record types')},{' '}
              {manifest.counts?.blobs || 0} {t('restore.files', 'files')}
            </div>
            {/* Product version only. Internal tooling versions (ORM etc.)
                stay in the manifest for the restore engine's drift checks
                but are NOT surfaced — we don't expose the tech stack in
                the UI. */}
            {manifest.appVersion && (
              <div style={{marginTop: 4, color:'var(--text-muted)', fontSize: 12}}>
                {t('restore.version', 'Version')}: Pluma {manifest.appVersion}
              </div>
            )}
          </div>
        )}
        {schemaDrift && (
          <div className="card" style={{
            marginTop: 12, padding: 12, fontSize: 13,
            borderColor: 'var(--warning)', background: 'rgba(255,165,2,0.07)',
          }}>
            <strong>{t('restore.schemaDrift.title', 'Schemas don’t match')}</strong>
            <div style={{marginTop: 4}}>
              {t('restore.schemaDrift.body',
                'This bundle was created on a different Pluma version. The restore will still run, but rows referencing renamed or removed columns may be skipped. Review the result before relying on the install.')}
            </div>
          </div>
        )}
        {error && <div className="err" style={{marginTop: 10}}>{error}</div>}
        <div style={{display:'flex', gap: 8, marginTop: 16, justifyContent:'flex-end'}}>
          <button className="btn" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
          <button className="btn btn-primary" disabled={!manifest || busy} onClick={restore}>
            {busy ? t('common.loading') : t('restore.confirm', 'Restore now')}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 520}}>
        <div className="modal-title">{t('restore.title', 'Restore from backup')}</div>
        <div style={{marginTop: 12}}>{body}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Tenant context — list of tenants the user belongs to + currently active one
// ============================================================================

const TenantCtx = createContext();
// Persist the user's chosen organisation across reloads. Without this, the
// "Switch" action would set state in-memory + window globals, the page
// would reload after switchTo, and the new mount would pick `tenants[0]`
// again — bouncing the user back to whichever org happens to come first
// in the /spa/tenants response.
const TENANT_PERSIST_KEY = 'pluma_current_tenant_id';
function readPersistedTenantId() {
  try { return localStorage.getItem(TENANT_PERSIST_KEY); } catch (_) { return null; }
}
function writePersistedTenantId(id) {
  try {
    if (id) localStorage.setItem(TENANT_PERSIST_KEY, id);
    else localStorage.removeItem(TENANT_PERSIST_KEY);
  } catch (_) { /* private mode / disabled storage */ }
}

function TenantProvider({ children }) {
  const [tenants, setTenants] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  // A failed load must not look like "you have no organisation": AppShell
  // would offer to create one (a duplicate) or show "access removed".
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { tenants } = await api.get('/spa/tenants');
      setError(null);
      setTenants(tenants);
      if (tenants.length === 0) return;
      // Two jobs at once:
      //   - First load (no current yet): pick from persisted id or fall
      //     through to tenants[0] and remember it.
      //   - Subsequent refreshes: re-point `current` at the FRESH copy
      //     of the same tenant from the just-fetched list so any field
      //     mutated by a Settings page Save (defaultCurrency,
      //     deliveryEmail, etc.) propagates to consumers. The previous
      //     refresh() updated `tenants` but kept the pre-save snapshot
      //     under `current`, so re-mounting a settings page after Save +
      //     Navigate read the old values from useState initialisers.
      if (!current) {
        const persistedId = readPersistedTenantId();
        // Prefer an organisation the user can open (not paused).
        const pick = (persistedId && tenants.find((t) => t.id === persistedId)) || tenants.find((t) => !t.suspended) || tenants[0];
        setCurrent(pick);
        window.__currentTenantId = pick.id;
        // If we fell back to tenants[0] because the persisted id is gone
        // (member removed, organisation deleted), rewrite the storage so a
        // subsequent reload doesn't keep chasing the same dead id.
        writePersistedTenantId(pick.id);
      } else {
        const fresh = tenants.find((t) => t.id === current.id);
        if (fresh) setCurrent(fresh);
        else {
          // The current tenant disappeared (member removed, org
          // deleted). Fall back to tenants[0] so the SPA doesn't get
          // stuck on a dead reference.
          setCurrent(tenants[0]);
          window.__currentTenantId = tenants[0].id;
          writePersistedTenantId(tenants[0].id);
        }
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [current]);

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const switchTo = (tenant) => {
    setCurrent(tenant);
    window.__currentTenantId = tenant.id;
    writePersistedTenantId(tenant.id);
  };

  return (
    <TenantCtx.Provider value={{ tenants, current, loading, error, refresh, switchTo }}>
      {children}
    </TenantCtx.Provider>
  );
}
const useTenant = () => useContext(TenantCtx);

// ============================================================================
// Workspace context — workspaces inside the current tenant + active one.
// Active selection persists in localStorage as `pluma_workspace:<tenantId>`
// so refreshing the page lands you back where you were. On tenant switch
// the active workspace falls back to the tenant's first.
// ============================================================================

const WorkspaceCtx = createContext();
function WorkspaceProvider({ children }) {
  const { current: tenant } = useTenant();
  const [workspaces, setWorkspaces] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lsKey = tenant ? `pluma_workspace:${tenant.id}` : null;

  const refresh = useCallback(async () => {
    if (!tenant) { setWorkspaces([]); setCurrent(null); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const { workspaces } = await api.get('/spa/workspaces');
      setWorkspaces(workspaces);
      const remembered = lsKey ? localStorage.getItem(lsKey) : null;
      const pick = workspaces.find((w) => w.slug === remembered) || workspaces[0] || null;
      setCurrent(pick);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [tenant, lsKey]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchTo = (workspace) => {
    setCurrent(workspace);
    if (lsKey && workspace) localStorage.setItem(lsKey, workspace.slug);
  };

  return (
    <WorkspaceCtx.Provider value={{ workspaces, current, loading, error, refresh, switchTo }}>
      {children}
    </WorkspaceCtx.Provider>
  );
}
const useWorkspace = () => useContext(WorkspaceCtx) || { workspaces: [], current: null };

// Maps a workspace publish state (from /spa/workspaces/:slug/publish, or
// fallback to the persisted lastPublishStatus on the row) to the dot
// class used by the top-bar pill + the workspace switcher.
//
// Inputs we accept:
//   - a state string returned by the publish status endpoint
//     ('never' | 'in_sync' | 'drift' | 'building' | 'failed')
//   - a workspace row (uses lastPublishStatus + lastPublishedAt)
// Tracks whether the viewport is in mobile-portrait territory. Drives
// the editor's layout switch from a 3-pane desktop view to a single-
// pane stacked view with a bottom switcher (templates / canvas /
// properties), matching the original PDF Microservice Builder.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// usePullToRefresh — touch-driven pull-to-refresh on a scrollable element.
// Pass a ref to the scroll container, an indicator ref (positioned just
// above the container), and an async refresh function. Direct DOM mutation
// during the gesture so we don't trigger React re-renders 60×/sec.
//
// Mobile-only: bails out if pointerType isn't touch, so a desktop user with
// a touchscreen scrolling the templates list doesn't accidentally trigger
// a refresh on every overscroll.
function usePullToRefresh(scrollRef, indicatorRef, onRefresh, { enabled = true, threshold = 64 } = {}) {
  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    const ind = indicatorRef.current;
    if (!el || !ind) return;

    const state = { startY: 0, pulling: false, refreshing: false };
    let rafId = 0;

    const setIndicator = (dy, transitioning) => {
      ind.style.transition = transitioning ? 'transform 0.35s cubic-bezier(.2,.9,.3,1), opacity 0.35s ease' : 'none';
      ind.style.transform = `translateY(${dy}px)`;
      ind.style.opacity = Math.min(1, dy / threshold);
      ind.dataset.refreshing = state.refreshing ? '1' : '0';
      ind.dataset.armed = (!state.refreshing && dy >= threshold) ? '1' : '0';
    };

    const onStart = (e) => {
      if (el.scrollTop <= 0 && !state.refreshing) {
        state.startY = e.touches[0].clientY;
        state.pulling = true;
        ind.style.willChange = 'transform, opacity';
      }
    };
    const onMove = (e) => {
      if (!state.pulling) return;
      const rawDy = e.touches[0].clientY - state.startY;
      if (rawDy > 0) {
        e.preventDefault();
        cancelAnimationFrame(rafId);
        // Diminishing resistance — feels like a spring, not free pull.
        const dy = threshold * Math.pow(rawDy / threshold, 0.55);
        rafId = requestAnimationFrame(() => setIndicator(Math.min(dy, threshold * 1.6), false));
      } else if (rawDy < -5) {
        state.pulling = false;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => setIndicator(0, true));
      }
    };
    const onEnd = () => {
      if (!state.pulling) return;
      state.pulling = false;
      ind.style.willChange = '';
      cancelAnimationFrame(rafId);
      const currentDy = parseFloat((ind.style.transform || '').replace(/[^0-9.-]/g, '')) || 0;
      if (currentDy >= threshold) {
        state.refreshing = true;
        setIndicator(40, true);
        Promise.resolve()
          .then(() => onRefresh && onRefresh())
          .catch(() => {})
          .finally(() => {
            state.refreshing = false;
            setIndicator(0, true);
          });
      } else {
        setIndicator(0, true);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, scrollRef, indicatorRef, onRefresh, threshold]);
}

function publishStatusFor(arg) {
  if (typeof arg === 'string') {
    if (arg === 'in_sync') return 'clean';
    if (arg === 'drift') return 'dirty';
    if (arg === 'building') return 'building';
    if (arg === 'failed') return 'failed';
    return ''; // 'never' → bare gray
  }
  const w = arg;
  if (!w) return '';
  if (w.lastPublishStatus === 'running') return 'building';
  if (w.lastPublishStatus === 'failed') return 'failed';
  if (w.lastPublishStatus === 'succeeded' && w.lastPublishedAt) return 'clean';
  return '';
}

// Hook that drives the top-bar pill from the live status endpoint.
// Polls every 5s while a build is in flight; otherwise refreshes on
// workspace switch + when refresh() is called explicitly (e.g. after
// the user kicks off a publish).
function usePublishStatus(workspace, enabled) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!enabled || !workspace?.slug) { setStatus(null); return; }
    setLoading(true);
    try {
      const data = await api.get(`/spa/workspaces/${workspace.slug}/publish`);
      setStatus(data);
    } catch (_) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [workspace?.slug, enabled]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh while building. Five-second cadence is fast enough that
  // the status pill animates while the user watches and slow enough not
  // to spam the server.
  useEffect(() => {
    if (!enabled || status?.state !== 'building') return;
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [status?.state, fetchStatus, enabled]);

  return { status, loading, refresh: fetchStatus };
}

// ============================================================================
// Create-workspace modal — slug + name. Used from the top-bar workspace
// switcher's "+ New workspace" entry.
// ============================================================================

// Workspace name → URL-safe slug. Transliterates instead of dropping
// non-ASCII letters ("Anträge" → "antraege", not "antr-ge"), and never
// returns empty (a Cyrillic or emoji name used to leave a required field
// blank and fail validation on a field the user never saw).
function slugifyName(name) {
  const base = String(name || '').toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 63).replace(/-+$/g, '');
  return base || ('workspace-' + Math.random().toString(36).slice(2, 6));
}

function CreateWorkspaceModal({ onClose, onCreated }) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const toast = useToast();
  const t = useT();

  // Auto-generate a slug from the name as the user types — only until
  // they manually edit the slug field, then we leave it alone.
  const [slugTouched, setSlugTouched] = useState(false);
  useEffect(() => {
    if (slugTouched) return;
    setSlug(name.trim() ? slugifyName(name) : '');
  }, [name, slugTouched]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { workspace } = await api.post('/spa/workspaces', { slug, name });
      onCreated(workspace);
    } catch (err) {
      toast(err.detail?.detail || err.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{t('workspace.create.title')}</div>
        <form onSubmit={submit} className="form-row" style={{marginTop: 16}}>
          <div>
            <label className="label" htmlFor="ws-create-name">{t('workspace.create.name')}</label>
            <input id="ws-create-name" className="input" required autoFocus
                   value={name} onChange={(e) => setName(e.target.value)}
                   placeholder={t('workspace.create.namePlaceholder', 'e.g. Contracts')} />
          </div>
          {/* The URL/API identifier is generated from the name; most people
              never need to touch it, so it lives under "Advanced". */}
          <details>
            <summary style={{cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)'}}>
              {t('workspace.create.advanced', 'Advanced')}
            </summary>
            <div style={{marginTop: 10}}>
              <label className="label" htmlFor="ws-create-slug">{t('workspace.create.slugFriendly', 'Short name (used in links and the API)')}</label>
              <input id="ws-create-slug" className="input" required
                     value={slug}
                     onChange={(e) => { setSlug(e.target.value.toLowerCase()); setSlugTouched(true); }}
                     pattern="[a-z0-9][a-z0-9-]{0,62}"
                     placeholder={slugifyName(t('workspace.create.namePlaceholderBare', 'Contracts'))} />
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
                {t('workspace.create.slugHint')}
              </div>
            </div>
          </details>
          <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('common.creating', 'Creating…') : t('workspace.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// App-wide banner shown whenever the signed-in user has a pending account
// deletion. `deletionScheduledFor` rides on the user object (publicUser).
// Abort options match the server: "Resume subscription" un-cancels the
// Stripe sub; "Keep my account" just clears the deletion (a paid sub stays
// cancelling → drops to free).
// /spa/account/me wrapper. Surfaces the "tenant" payload (limits,
// justReactivatedAt, overCap) added in src/account/routes.js. Cached at
// AppShell scope so the welcome-back banner + upgrade modal + lock badges
// all read from a single source without each refetching independently.
// Refetches when `scopeKey` (tenant + plan) changes — the payload is
// tenant-scoped (X-Tenant-Id) — and on explicit refresh(). Waits for a
// tenant: a fetch before one is picked has no tenant payload and would
// be thrown away a moment later.
function useAccountTenantInfo(scopeKey) {
  const [info, setInfo] = useState(null);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    if (!scopeKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get('/spa/account/me');
        if (!cancelled) setInfo(d.tenant || null);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [version, scopeKey]);
  return { info, refresh };
}

// React context for the tenant snapshot. Subcomponents (template list,
// upgrade-blocked surfaces) read via useTenantInfo() instead of a window
// global — keeps the data flow inspectable in React DevTools and lets us
// add new consumers without touching plumbing.
const TenantInfoCtx = createContext({ info: null, refresh: () => {} });
function useTenantInfo() { return useContext(TenantInfoCtx); }

// Convenience: opens the upgrade modal for a known cap-reason from any
// component, without re-implementing the dispatch shape each time. The
// modal listener in AppShell handles the rest.
function requestUpgrade({ code, detail, body }) {
  try {
    window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
      detail: {
        code,
        message: detail || code,
        body: body || { error: code, detail },
      },
    }));
  } catch (_) {}
}

// One-time "you came back — your old templates are gone" banner. Reads
// req.tenantContext.justReactivatedAt (set by requireTenant middleware
// on the first login after inactivity-deletion). Dismissal persists in
// localStorage keyed by the reactivation timestamp, so the user doesn't
// see it again across reloads.
function WelcomeBackBanner({ tenantInfo }) {
  const t = useT();
  const ts = tenantInfo?.justReactivatedAt;
  const key = ts ? `pluma:welcomeback:${ts}` : null;
  // Derived per render, keyed by the timestamp: the banner first renders
  // before tenantInfo has loaded (key === null), and the old one-time
  // useState initialiser froze `dismissed` at true — so the notice never
  // appeared, and the server only reports justReactivatedAt once.
  const [dismissedKey, setDismissedKey] = useState(null);
  let stored = false;
  try { stored = !!(key && localStorage.getItem(key)); } catch (_) { /* storage blocked */ }
  if (!ts || dismissedKey === key || stored) return null;
  const dismiss = () => { try { localStorage.setItem(key, '1'); } catch (_) {} setDismissedKey(key); };
  return (
    <div style={{
      background: 'linear-gradient(90deg, #6c5ce7 0%, #a29bfe 100%)', color: '#fff',
      padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 12,
      alignItems: 'center', justifyContent: 'center', fontSize: 13,
    }}>
      <span style={{flex: '1 1 auto', maxWidth: 720}}>
        <strong>{t('inactivity.welcomeBackTitle', 'Welcome back!')}</strong>{' '}
        {t('inactivity.welcomeBackBody', 'Your workspace was inactive for 12 months, so we deleted the old templates and reactivated your account on the Free plan. Start a fresh template anytime.')}
      </span>
      <button className="btn" onClick={dismiss}
              style={{ padding: '4px 12px', fontSize: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>
        {t('common.dismiss', 'Dismiss')}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Tier-gating UI primitives
//
// Two helpers used by every "this feature needs a higher plan" surface:
//
//   <TierBadge tier="pro" />            inline pill rendered next to a
//                                       section title. Pure cosmetic.
//
//   <FeatureLocked feature="publish"    wraps a card / page section.
//                  tier="pro"           Renders the children disabled +
//                                       a tinted overlay with an Upgrade
//                                       button that dispatches the same
//                                       pluma:upgrade-required event the
//                                       backend cap rejections use, so
//                                       UpgradeRequiredModal handles it.
//
// Both read the current plan / features via useTenantInfo(). They do NOT
// gate anything by themselves — backend checks are still authoritative.
// These are purely the discoverability layer.
// ──────────────────────────────────────────────────────────────────────

// Map a feature flag → the lowest tier that includes it (per
// builder/src/billing/plan-limits.js). Used by FeatureLocked to pick the
// right badge label without each caller hardcoding it.
const FEATURE_MIN_TIER = {
  s3Backup:    'advanced',
  publish:     'pro',         // self-hosted only; UI mirrors that
  customFonts: 'enterprise',
};

const TIER_LABEL = { free: 'Free', advanced: 'Advanced', pro: 'Pro', enterprise: 'Enterprise' };

function TierBadge({ tier }) {
  const label = TIER_LABEL[tier] || tier;
  return <span className={`badge badge-${tier}`}>{label}</span>;
}

// `feature` matches an entry in FEATURE_MIN_TIER. Falls back to the explicit
// `tier` prop if no feature flag applies. Returns the children un-wrapped
// when the current plan already has the feature, so callers can use it
// liberally without thinking about it.
function FeatureLocked({ feature, tier, code, children, hint }) {
  const t = useT();
  const { info } = useTenantInfo();
  const requiredTier = tier || FEATURE_MIN_TIER[feature];
  const hasFeature = !!(feature && info?.limits?.features?.[feature]);
  if (hasFeature || !requiredTier) return children;
  const open = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
      detail: { code: code || `feature_${feature || 'unknown'}`, requiredTier },
    }));
  };
  return (
    <div className="tier-locked" onClick={open}>
      <div className="tier-locked-inner">{children}</div>
      <div className="tier-locked-cta">
        <TierBadge tier={requiredTier} />
        <span>{hint || t('tierGate.requires', { tier: TIER_LABEL[requiredTier] })}</span>
        <button className="btn btn-primary" onClick={open}>{t('upgrade.cta', 'See plans')}</button>
      </div>
    </div>
  );
}

// Global "upgrade required" modal. Triggered by the pluma:upgrade-required
// CustomEvent fired from errorFrom() when the backend returns a tier-cap
// rejection (413 pdf_too_large / pdf_too_many_pages, 402 quota_exceeded,
// 403 *_limit_reached) OR from <FeatureLocked> when the operator clicks
// a tier-locked card. Single modal handles every cap reason via the
// payload it receives.
function UpgradeRequiredModal({ navigateToBilling }) {
  const t = useT();
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const onPrompt = (e) => setPrompt(e.detail || { code: 'generic' });
    window.addEventListener('pluma:upgrade-required', onPrompt);
    return () => window.removeEventListener('pluma:upgrade-required', onPrompt);
  }, []);
  if (!prompt) return null;
  const close = () => setPrompt(null);
  // Per-reason headline + body. Falls back to the server's detail string
  // for unknown codes so we never show a totally blank modal.
  const body = prompt.body || {};
  const reasons = {
    pdf_too_large: {
      title: t('upgrade.pdfTooLarge.title', 'This PDF is bigger than your plan allows'),
      body: t('upgrade.pdfTooLarge.body', body.detail || 'Your current tier limits how large a rendered PDF can be. Upgrade to render larger documents.'),
    },
    pdf_too_many_pages: {
      title: t('upgrade.pdfTooManyPages.title', 'This template has more pages than your plan allows'),
      body: t('upgrade.pdfTooManyPages.body', body.detail || 'Your current tier caps templates at a fixed page count. Upgrade to use multi-page templates.'),
    },
    quota_exceeded: {
      title: t('upgrade.quota.title', 'You\'ve used all your renders this period'),
      body: t('upgrade.quota.body', 'Upgrade to a higher monthly quota — on paid plans you can also top up your prepaid balance to keep going.'),
    },
    template_limit_reached: {
      title: t('upgrade.templateLimit.title', 'You\'ve hit your template cap'),
      body: t('upgrade.templateLimit.body', 'Your current tier limits how many templates a workspace can have. Upgrade to add more, or delete one you no longer need.'),
    },
    seat_limit_reached: {
      title: t('upgrade.seatLimit.title', 'You\'ve hit your team-member cap'),
      body: t('upgrade.seatLimit.body', 'Your current tier limits how many members a workspace can have. Upgrade to invite more.'),
    },
    user_seat_limit_reached: {
      title: t('upgrade.seatLimit.title', 'You\'ve hit your team-member cap'),
      body: t('upgrade.seatLimit.body', 'Your current tier limits how many members a workspace can have. Upgrade to invite more.'),
    },
    workspace_limit_reached: {
      title: t('upgrade.workspaceLimit.title', 'You\'ve hit your workspace cap'),
      body: t('upgrade.workspaceLimit.body', 'Your current tier limits how many workspaces you can create. Upgrade to add more.'),
    },
    image_limit_reached: {
      title: t('upgrade.imageLimit.title', 'You\'ve hit your image-library cap'),
      body: t('upgrade.imageLimit.body', body.current != null && body.max != null
        ? `You have ${body.current} of ${body.max} images on the ${body.tier || 'current'} plan. Upgrade to upload more, or delete one you no longer need.`
        : 'Your current tier limits how many images you can store. Upgrade to upload more, or delete one you no longer need.'),
    },
    image_too_large: {
      title: t('upgrade.imageSize.title', 'This image is bigger than your plan allows'),
      body: t('upgrade.imageSize.body', body.maxImageBytes != null
        ? `Your ${body.tier || 'current'} plan caps single images at ${(body.maxImageBytes / 1024 / 1024).toFixed(1)} MB. Upgrade to upload larger images.`
        : 'Your current tier limits how large a single image can be. Upgrade to upload larger files.'),
    },
    open_ticket_cap_reached: {
      title: t('upgrade.openTicketCap.title', 'You already have an open support ticket'),
      body: t('upgrade.openTicketCap.body', body.cap === 1
        ? 'Your Free plan allows one open ticket at a time. Wait for staff to resolve the current one, or upgrade for more concurrent tickets.'
        : `Your plan allows ${body.cap || 'a limited number of'} open tickets at a time. Resolve one or upgrade for unlimited concurrent tickets.`),
    },
    feature_publish: {
      title: t('upgrade.publish.title', 'Publishing workspaces is a Pro feature'),
      body: t('upgrade.publish.body', 'Publish builds a self-contained renderer image you can run anywhere. Available on Pro and Enterprise self-hosted plans.'),
    },
    feature_s3Backup: {
      title: t('upgrade.s3Backup.title', 'S3 backups need at least Advanced'),
      body: t('upgrade.s3Backup.body', 'Configure off-site backups to any S3-compatible bucket. Available on Advanced and up.'),
    },
    feature_dockerRegistries: {
      title: t('upgrade.dockerRegistries.title', 'Docker registries need a Pro plan'),
      body: t('upgrade.dockerRegistries.body', 'Registries store the images your Publish flow pushes to. Available on Pro and Enterprise self-hosted plans.'),
    },
    feature_customFonts: {
      title: t('upgrade.customFonts.title', 'Custom fonts are an Enterprise feature'),
      body: t('upgrade.customFonts.body', 'Upload TTF/OTF font files for use in your templates. Available on the Enterprise plan.'),
    },
  };
  const r = reasons[prompt.code] || {
    title: t('upgrade.generic.title', 'Upgrade required'),
    body: t('upgrade.generic.body', body.detail || prompt.message || 'This action isn\'t available on your current plan.'),
  };
  const goBilling = () => { close(); if (navigateToBilling) navigateToBilling(); };
  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            flex: '0 0 auto', width: 40, height: 40, borderRadius: 10,
            background: 'rgba(108,92,231,0.15)', color: '#6c5ce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 700,
          }}>↑</div>
          <h3 style={{margin: 0, fontSize: 17, fontWeight: 700}}>{r.title}</h3>
        </div>
        <p style={{color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.55, margin: '0 0 18px'}}>{r.body}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={close}>{t('common.notNow', 'Not now')}</button>
          <button className="btn btn-primary" onClick={goBilling}>{t('upgrade.cta', 'See plans')}</button>
        </div>
      </div>
    </div>
  );
}

// Public announcement banners — fetched from /spa/announcements?audience=app.
// Server-filtered to currently-active rows for this audience (schedule
// window + active flag). Renders one strip per announcement at the top of
// the AppShell. Dismissible banners persist their dismissal in
// localStorage keyed by announcement id; non-dismissible banners (incident,
// maintenance) stay until the operator pulls them.
const SEVERITY_COLORS = {
  info:    'linear-gradient(90deg, #2d7df0 0%, #6aa3ff 100%)',
  success: 'linear-gradient(90deg, #1f8a4d 0%, #34c759 100%)',
  warning: 'linear-gradient(90deg, #c87a06 0%, #f5b400 100%)',
  danger:  'linear-gradient(90deg, #b8331f 0%, #ff4757 100%)',
  promo:   'linear-gradient(90deg, #6c5ce7 0%, #a29bfe 100%)',
};

// "We've updated our Terms / Privacy Policy" strip. Shown while the user
// hasn't accepted the current versions (src/legal/policies.js); the admin
// console emails the same people. Hosted only — the documents live on the
// marketing site.
function PolicyUpdateBanner() {
  const t = useT();
  const runtime = useRuntime();
  const enabled = !!runtime?.features?.marketingSite;
  const [outdated, setOutdated] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    api.get('/spa/account/policies')
      .then((d) => { if (!cancelled) setOutdated(d.outdated || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);
  if (!enabled || outdated.length === 0) return null;
  const accept = async () => {
    setBusy(true);
    try { await api.post('/spa/account/policies/accept', {}); setOutdated([]); }
    catch (_) { setBusy(false); }
  };
  const message = outdated.length === 2
    ? t('policyBanner.both', 'We’ve updated our Terms of Service and Privacy Policy.')
    : outdated[0] === 'terms'
      ? t('policyBanner.terms', 'We’ve updated our Terms of Service.')
      : t('policyBanner.privacy', 'We’ve updated our Privacy Policy.');
  const linkStyle = { color: '#fff', textDecoration: 'underline', fontWeight: 600 };
  return (
    <div style={{
      background: SEVERITY_COLORS.info, color: '#fff', padding: '10px 16px',
      display: 'flex', flexWrap: 'wrap', gap: 12,
      alignItems: 'center', justifyContent: 'center', fontSize: 13,
    }}>
      <span style={{ textAlign: 'center' }}>{message}</span>
      {outdated.includes('terms') && <a href="/terms" target="_blank" rel="noopener" style={linkStyle}>{t('auth.legalTerms', 'Terms')}</a>}
      {outdated.includes('privacy') && <a href="/privacy" target="_blank" rel="noopener" style={linkStyle}>{t('auth.legalPrivacy', 'Privacy Policy')}</a>}
      <button className="btn" onClick={accept} disabled={busy}
              style={{ padding: '4px 12px', fontSize: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>
        {t('policyBanner.accept', 'I agree')}
      </button>
    </div>
  );
}

// Invitations addressed to the signed-in account (src/account/invites.js):
// one row per open invitation, with Accept / Decline. Rendered above every
// screen — including the no-workspace ones, where an invitation is usually
// the whole reason the person is here — and refreshed when a notification
// arrives, when the window regains focus, and when the bell entry is opened.
function PendingInvitesBanner() {
  const t = useT();
  const toast = useToast();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get('/spa/account/invites').then((d) => setInvites(d.invites || [])).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    const onRefresh = () => { load(); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const onSw = (e) => { if (e.data && e.data.type === 'pluma:notification') load(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pluma:invites-refresh', onRefresh);
    const sw = navigator.serviceWorker;
    if (sw && sw.addEventListener) sw.addEventListener('message', onSw);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pluma:invites-refresh', onRefresh);
      if (sw && sw.removeEventListener) sw.removeEventListener('message', onSw);
    };
  }, [load]);
  if (invites.length === 0) return null;

  const accept = async (inv) => {
    setBusy(inv.id);
    try {
      const r = await api.post(`/spa/account/invites/${inv.id}/accept`, {}, { noUpgradeModal: true });
      setInvites((cur) => cur.filter((x) => x.id !== inv.id));
      toast(t('invite.accept.success', { tenantName: (r.tenant && r.tenant.name) || (inv.tenant && inv.tenant.name) || '' }), 'success');
      if (r.tenant && r.tenant.id) {
        // Reload into the joined organisation (see the invite_token handoff):
        // switching in place left the old org's data on screen.
        writePersistedTenantId(r.tenant.id);
        setTimeout(() => window.location.assign('/app'), 1200);
      }
    } catch (e) {
      // Branch on the error CODE — e.message is the translated text.
      if (e.code === 'expired') toast(t('invite.accept.expired'), 'error');
      else if (e.code === 'user_seat_limit_reached') toast(t('invite.accept.teamFull'), 'error');
      else if (e.code === 'network_error') toast(e.message, 'error');
      else toast(t('invite.accept.invalid'), 'error');
      load();
    } finally { setBusy(null); }
  };
  const decline = async (inv) => {
    setBusy(inv.id);
    try {
      await api.post(`/spa/account/invites/${inv.id}/decline`, {});
      setInvites((cur) => cur.filter((x) => x.id !== inv.id));
      toast(t('invites.declined', 'Invitation declined.'), 'success');
    } catch (e) {
      toast(e.code === 'network_error' ? e.message : t('common.error', 'Something went wrong.'), 'error');
      load();
    } finally { setBusy(null); }
  };
  const btnStyle = { padding: '4px 12px', fontSize: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' };
  return (
    <>
      {invites.map((inv) => (
        <div key={inv.id} className="invite-banner" role="status" style={{
          background: SEVERITY_COLORS.info, color: '#fff', padding: '10px 16px',
          display: 'flex', flexWrap: 'wrap', gap: 12,
          alignItems: 'center', justifyContent: 'center', fontSize: 13,
        }}>
          <span style={{ textAlign: 'center' }}>
            {t('invites.banner.text', '{{name}} invited you to join {{tenant}} as {{role}}.', {
              name: (inv.invitedBy && inv.invitedBy.name) || '',
              tenant: (inv.tenant && inv.tenant.name) || '',
              role: t(`members.role.${inv.role}`, inv.role),
            })}
          </span>
          <button type="button" className="btn" onClick={() => accept(inv)} disabled={busy === inv.id} style={btnStyle}>
            {t('invites.banner.accept', 'Accept')}
          </button>
          <button type="button" className="btn" onClick={() => decline(inv)} disabled={busy === inv.id} style={btnStyle}>
            {t('invites.banner.decline', 'Decline')}
          </button>
        </div>
      ))}
    </>
  );
}

function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('pluma:announce:dismissed') || '[]')); }
    catch { return new Set(); }
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get('/spa/announcements?audience=app');
        if (!cancelled) setItems(d.announcements || []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);
  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    try { localStorage.setItem('pluma:announce:dismissed', JSON.stringify([...next])); } catch (_) {}
  };
  const visible = items.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;
  return (
    <>
      {visible.map((a) => (
        <div key={a.id} style={{
          background: SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.info,
          color: '#fff', padding: '10px 16px',
          display: 'flex', flexWrap: 'wrap', gap: 12,
          alignItems: 'center', justifyContent: 'center', fontSize: 13,
        }}>
          <span style={{ flex: '1 1 auto', maxWidth: 920, textAlign: 'center' }}>{a.message}</span>
          {a.link && (
            <a href={a.link} target={/^https?:\/\//i.test(a.link) ? '_blank' : undefined}
               rel="noopener" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}>
              {a.linkLabel || 'Learn more'}
            </a>
          )}
          {a.dismissible && (
            <button className="btn" onClick={() => dismiss(a.id)}
                    title="Dismiss"
                    style={{ padding: '4px 10px', fontSize: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>
              ✕
            </button>
          )}
        </div>
      ))}
    </>
  );
}

// Membership paused — the organisation's paid plan ended (Free allows fewer
// users; billing/plan-end.js) or its seat payment is overdue. The server
// answers 402 membership_suspended for this organisation; the page explains
// why and offers the user's other organisations.
function SuspendedOrgPage({ tenant }) {
  const t = useT();
  const { tenants, switchTo } = useTenant();
  const others = (tenants || []).filter((o) => o.id !== tenant.id && !o.suspended);
  const signOut = async () => {
    try { await api.post('/auth/logout'); } catch { /* best-effort */ }
    window.location.href = '/app';
  };
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>{t('access.suspended.title', { tenant: tenant.name })}</h1>
        <p className="sub">{t(tenant.suspendedReason === 'plan_ended' ? 'access.suspended.planEnded' : 'access.suspended.seatPayment')}</p>
        {others.length > 0 && (
          <div style={{marginTop: 16}}>
            <div className="label" style={{marginBottom: 6}}>{t('access.suspended.switch', 'Switch organisation')}</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
              {others.map((o) => <button key={o.id} className="btn" onClick={() => switchTo(o)}>{o.name}</button>)}
            </div>
          </div>
        )}
        <button className="btn btn-primary" onClick={signOut} style={{marginTop: 16}}>{t('access.denied.signOut', 'Sign out')}</button>
      </div>
    </div>
  );
}

// Notices about a subscription that is set to end can be hidden while the
// end is more than 30 days away (user decision 2026-10-16). In the last 30
// days they show again and can't be hidden. The choice is remembered in
// this browser per place (banner / billing page), organisation and end
// date, so a new end date shows the notice again.
const ENDING_NOTICE_QUIET_DAYS = 30;
function useEndingNoticeDismissal(place, tenantId, endsAt) {
  const [hiddenKey, setHiddenKey] = useState(null);
  const end = endsAt ? new Date(endsAt) : null;
  const showAgainAt = end ? new Date(end.getTime() - ENDING_NOTICE_QUIET_DAYS * 24 * 3600 * 1000) : null;
  const dismissible = !!(tenantId && showAgainAt && Date.now() < showAgainAt.getTime());
  const key = dismissible ? `pluma:ending-notice:${place}:${tenantId}:${end.toISOString()}` : null;
  let stored = false;
  try { stored = !!(key && localStorage.getItem(key)); } catch (_) { /* storage blocked */ }
  const hidden = dismissible && (stored || hiddenKey === key);
  const dismiss = () => { try { localStorage.setItem(key, '1'); } catch (_) {} setHiddenKey(key); };
  return { dismissible, hidden, dismiss, showAgainAt };
}

// Ownership handover: the previous owner's subscription runs out on
// handover.endsAt and the new owner has not chosen a plan yet
// (billing/plan-end.js handoverState). Owners only; gone once a plan is
// chosen or the old subscription has ended.
function HandoverBanner({ info, onChoose }) {
  const t = useT();
  const h = info && info.handover;
  const notice = useEndingNoticeDismissal('banner', info && info.id, h && h.endsAt);
  if (!h || notice.hidden) return null;
  return (
    <div style={{
      background: 'var(--warning, #f0a020)', color: '#1a1a1a',
      padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 10,
      alignItems: 'center', justifyContent: 'center', fontSize: 13,
    }}>
      <span>{t('billing.handoverBanner', { tenant: h.tenantName || '', date: formatDateOnly(new Date(h.endsAt)) })}</span>
      <button className="btn" onClick={onChoose} style={{padding: '3px 10px', fontSize: 12}}>{t('billing.handoverBannerCta', 'Choose a plan')}</button>
      {notice.dismissible && (
        <button className="btn" onClick={notice.dismiss}
                style={{padding: '3px 10px', fontSize: 12, background: 'transparent', color: '#1a1a1a', border: '1px solid rgba(0,0,0,0.3)'}}>
          {t('billing.hideUntil', { date: formatDateOnly(notice.showAgainAt) })}
        </button>
      )}
    </div>
  );
}

// The billing page's notice for a subscription that is set to end. Can be
// hidden while the end is more than 30 days away (useEndingNoticeDismissal).
function SubscriptionEndingNotice({ tenantId, endsAt, action, children }) {
  const t = useT();
  const notice = useEndingNoticeDismissal('billing', tenantId, endsAt);
  if (notice.hidden) return null;
  return (
    <div className="card" style={{
      borderColor: 'var(--warning)', background: 'rgba(255,165,2,0.07)',
      marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <Icon name="warning" size={20} />
      <span style={{flex: 1, minWidth: 220, fontSize: 13}}>{children}</span>
      {action}
      {notice.dismissible && (
        <button className="btn" onClick={notice.dismiss}>{t('billing.hideUntil', { date: formatDateOnly(notice.showAgainAt) })}</button>
      )}
    </div>
  );
}

function AccountDeletionBanner() {
  const { user, refresh } = useAuth();
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!user || !user.deletionScheduledFor) return null;
  const date = formatDateOnly(new Date(user.deletionScheduledFor));
  const abort = async (resumeSubscription) => {
    setBusy(true);
    try {
      await api.post('/spa/account/cancel-deletion', resumeSubscription ? { resumeSubscription: true } : {});
      toast(t('account.deletionCancelled'), 'success');
      await refresh();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{
      background: 'var(--danger, #ff4757)', color: '#fff',
      padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 10,
      alignItems: 'center', justifyContent: 'center', fontSize: 13,
    }}>
      <span>{t('account.globalDeletionBanner', { date })}</span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button className="btn" disabled={busy} onClick={() => abort(true)}
                style={{ padding: '3px 10px', fontSize: 12 }}>{t('account.resumeSubscription')}</button>
        <button className="btn" disabled={busy} onClick={() => abort(false)}
                style={{ padding: '3px 10px', fontSize: 12 }}>{t('account.keepAccount')}</button>
      </span>
    </div>
  );
}

// ============================================================================
// Organisation switcher — hosted-only modal. A user can belong to several
// tenants (we call them "organisations" in the UI to keep DB jargon out of
// the user's face); this popup lets them pick which one the current
// session works against. Tenants the user OWNS are listed first.
// ============================================================================

function OrgSwitcherModal({ onClose }) {
  useModalDismiss(onClose);
  const { tenants, current, switchTo } = useTenant();
  const t = useT();

  const owned  = (tenants || []).filter((o) => o.role === 'owner');
  const member = (tenants || []).filter((o) => o.role !== 'owner');

  const choose = (tenant) => {
    if (tenant.id === current?.id) { onClose(); return; }
    switchTo(tenant);
    // Workspaces, templates, schemas, jobs are all tenant-scoped — re-
    // seeding every cache by hand is brittle. A clean reload puts the
    // SPA back into the new tenant's context from scratch.
    window.location.assign('/app');
  };

  const renderRow = (tenant) => {
    const isCurrent = tenant.id === current?.id;
    const style = {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8,
      background: isCurrent ? 'var(--accent-glow)' : 'var(--bg-card)',
      borderColor: isCurrent ? 'var(--accent)' : 'var(--border)',
      cursor: isCurrent ? 'default' : 'pointer',
    };
    return (
      <button type="button" key={tenant.id} style={{...style, width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit'}}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => choose(tenant)}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{tenant.name}</div>
          <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            <span className={`badge badge-${tenant.plan}`}>{t('plan.' + tenant.plan)}</span>
            <span>{t('members.role.' + tenant.role, tenant.role)}</span>
          </div>
        </div>
        {isCurrent && <span className="pill pill-active">{t('orgSwitcher.current', 'Current')}</span>}
      </button>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
        <h2>{t('avatar.changeOrg', 'Change organisation')}</h2>
        <p className="sub" style={{marginTop: 4}}>
          {t('orgSwitcher.help', 'Pick which organisation you want to work in.')}
        </p>

        {owned.length > 0 && (
          <>
            <div className="card-title" style={{marginTop: 14, marginBottom: 6}}>
              {t('orgSwitcher.owned', 'Owned by you')}
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
              {owned.map(renderRow)}
            </div>
          </>
        )}

        {member.length > 0 && (
          <>
            <div className="card-title" style={{marginTop: 14, marginBottom: 6}}>
              {t('orgSwitcher.member', 'Member of')}
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
              {member.map(renderRow)}
            </div>
          </>
        )}

        <div className="modal-actions" style={{marginTop: 16}}>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// App shell — topbar + sidebar + page outlet
// ============================================================================

// Shown when the account or its workspaces couldn't be loaded (network
// blip, server restart). Without it a failed load rendered as an empty
// account — "create your first workspace" — inviting duplicates.
function LoadErrorScreen({ message, onRetry }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  return (
    <div className="crash-wrap" style={{minHeight: '60vh'}}>
      <div className="crash-card" role="alert">
        <div className="crash-icon" aria-hidden="true">!</div>
        <h2>{t('loadError.title', 'Couldn’t load your account')}</h2>
        <p>{t('loadError.body', 'This is usually a temporary connection problem. Your data is safe.')}</p>
        <div className="crash-actions">
          <button className="btn btn-primary" disabled={busy}
                  onClick={async () => { setBusy(true); try { await onRetry(); } finally { setBusy(false); } }}>
            {busy ? t('common.loading', 'Loading…') : t('crash.retry', 'Try again')}
          </button>
        </div>
        {message && <details className="crash-details"><summary>{t('crash.details', 'Technical details')}</summary><pre>{String(message)}</pre></details>}
      </div>
    </div>
  );
}

function AppShell() {
  // All hook destructures live at the top of the function. The
  // earlier layout had `useWorkspace()` declared 180 lines below a
  // useEffect that referenced `currentWorkspace` in its dep array.
  // babel-standalone's runtime transform was lenient (treated the
  // reference as hoisted-undefined); esbuild's ES2020 output enforces
  // strict TDZ semantics, so accessing the const before its
  // declaration throws `Cannot access 'F' before initialization` at
  // render time. Keeping every hook at the top makes this whole class
  // of bug impossible regardless of which transformer is downstream.
  const { user, logout, refresh: refreshAuth } = useAuth();
  const { tenants, current, refresh, loading: tenantsLoading, error: tenantsError } = useTenant();
  // workspace:create is an owner/admin permission (acl/index.js); editors and
  // viewers never see the entry — the server answers 403 anyway.
  const canCreateWorkspace = !!current && ['owner', 'admin'].includes(current.role);
  const { workspaces, current: currentWorkspace, switchTo: switchWorkspace, refresh: refreshWorkspaces, error: workspacesError } = useWorkspace();
  const toast = useToast();
  const t = useT();
  const { locale, setLocale } = useI18n();
  const runtime = useRuntime();
  const features = runtime.features || {};
  const { status: publishStatus, refresh: refreshPublishStatus } =
    usePublishStatus(currentWorkspace, !!features.publish);
  // Apply install-wide branding to the live SPA chrome. Reads
  // runtime.brand (populated by /spa/runtime from the install_settings
  // singleton) and writes:
  //   - --accent CSS variable so every accent-driven element retints
  //   - document.title prefix to use the configured productName
  //   - <link rel="icon"> swap to the configured favicon
  // Listens for the `pluma:brand-updated` event the Branding settings
  // page emits on save, refetches /spa/runtime, and re-applies — so
  // operators see the new brand without a full reload.
  useEffect(() => {
    const apply = (brand) => {
      if (!brand) return;
      const root = document.documentElement;
      if (brand.accentColor) root.style.setProperty('--accent', brand.accentColor);
      else root.style.removeProperty('--accent');
      const productName = brand.productName || 'Pluma';
      // Only override the suffix to keep page-specific prefixes (e.g.
      // "Templates · Pluma") intact when individual pages set their
      // own document.title.
      if (productName !== 'Pluma') document.title = productName;
      if (brand.faviconUrl) {
        let link = document.querySelector('link[rel="icon"]');
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = brand.faviconUrl;
      }
    };
    apply(runtime.brand);
    const onUpdated = () => {
      // Refetch + re-apply. We dispatch a synthetic runtime fetch
      // because useRuntime's internal store doesn't expose a refresh
      // hook; reading directly is fine for a one-shot DOM apply.
      api.get('/spa/runtime').then((next) => apply(next?.brand)).catch(() => { /* swallow — preview unchanged */ });
    };
    window.addEventListener('pluma:brand-updated', onUpdated);
    return () => window.removeEventListener('pluma:brand-updated', onUpdated);
  }, [runtime.brand]);
  // Per-source notification counters. Each entry is `{ count, urgent }`
  // where count is the number of unread items and urgent is true when
  // at least one of them is flagged urgent/priority. The avatar dot
  // and favicon badge aggregate every entry — total > 0 paints them,
  // any urgent flag flips the colour from yellow to amber. New sources
  // (billing notices, system alerts, …) just add another key to this
  // object without touching the renderer below.
  const [notifSources, setNotifSources] = useState({});
  const setNotifSource = useCallback((key, value) => {
    setNotifSources((prev) => {
      const next = { ...prev };
      if (!value || value.count <= 0) delete next[key];
      else next[key] = { count: value.count, urgent: !!value.urgent };
      return next;
    });
  }, []);

  // Support tickets — unread staff replies on the user's own tickets.
  // Polled every 60s; skipped entirely on self-hosted where there's
  // no SaaS staff to reply. The endpoint returns both a total and a
  // subset of those that are urgent priority.
  //
  // Gated on current?.id because the endpoint sits behind
  // requireTenant — without the X-Tenant-Id header (set when the
  // tenant is resolved) the server returns 400 tenant_required. Re-
  // runs on tenant switch so the badge reflects the active org.
  const supportUnread = notifSources.support?.count || 0;
  useEffect(() => {
    if (!features.supportTickets) return;
    if (!current?.id) return;
    let cancelled = false;
    const refresh = () => {
      api.get('/spa/support/tickets/notifications')
        .then((d) => {
          if (cancelled) return;
          setNotifSource('support', {
            count: d.unreadReplies || 0,
            urgent: (d.unreadUrgent || 0) > 0,
          });
        })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    // Re-poll immediately on focus so a user who reads the email and
    // clicks back into the tab sees the badge clear without waiting
    // for the next interval.
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [features.supportTickets, current?.id, setNotifSource]);

  // Clear the support badge entirely on tenant switch so the previous
  // org's count doesn't briefly carry over before the new fetch lands.
  useEffect(() => {
    setNotifSource('support', { count: 0, urgent: false });
  }, [current?.id, setNotifSource]);

  // Aggregate across every source — the badge is generic and doesn't
  // care which surface raised the notification. Totals drive the
  // visibility threshold (any > 0 paints the dot); the urgent flag
  // drives the colour (amber when set, yellow otherwise).
  const notifTotal = Object.values(notifSources).reduce((s, n) => s + n.count, 0);
  const notifHasUrgent = Object.values(notifSources).some((n) => n.urgent);
  const notifColour = notifHasUrgent ? '#f97316' : '#f5b400';

  // Dynamic document.title: "<workspace> — <org> — Pluma".
  // Falls back gracefully when the workspace or tenant isn't loaded
  // yet (a fresh login or workspace-switch in-flight). The bare
  // "Pluma" fallback matches the static <title> in the HTML head so
  // SEO/manifest copy doesn't briefly read something different.
  useEffect(() => {
    const parts = [];
    if (currentWorkspace?.name) parts.push(currentWorkspace.name);
    if (current?.name) parts.push(current.name);
    parts.push('Pluma');
    document.title = parts.join(' — ');
  }, [currentWorkspace?.name, current?.name]);

  // Swap the SVG favicon between three committed assets depending on
  // unread state: plain mark / yellow-dot / amber-dot. No canvas, no
  // data URLs — the browser fetches a different file when href changes.
  useEffect(() => {
    const link = document.querySelector('link[rel="icon"][type="image/svg+xml"]');
    if (!link) return;
    const href = notifTotal <= 0
      ? '/static/favicon.svg'
      : notifHasUrgent
        ? '/static/favicon-notify-urgent.svg'
        : '/static/favicon-notify.svg';
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  }, [notifTotal, notifHasUrgent]);
  // page + view are restored from the `?nav` query param so a refresh
  // keeps the user on the section they were viewing (jobs, billing,
  // settings, …) instead of snapping back to the templates editor.
  // view: 'editor' (the templates editor + things you do day-to-day) or
  // 'settings' (the tenant settings shell). The avatar dropdown switches
  // between them. Held in the AppShell so a workspace switch from the
  // top-bar pill doesn't yank a user out of mid-edit settings.
  const [page, setPage] = useState(() => readNav().page);
  const [view, setView] = useState(() => readNav().view);

  // Mirror view+page into the `?nav` param. Skipped while in settings —
  // SettingsShell owns the param then (it also encodes the active
  // settings section). On a clean editor/templates default the param is
  // dropped entirely so the URL stays tidy.
  useEffect(() => {
    if (view === 'settings') return;
    writeUrlParam('nav', page === 'templates' ? null : page);
  }, [view, page]);

  // Cross-shell navigation via window event. Components deep in the
  // editor (PropertiesPanel etc.) can ask the AppShell to switch view
  // without threading a callback through every layer. Detail shapes:
  //   { to: 'settings', section: 'signatures' }  → settings shell @ section
  //   { to: 'page',     page: 'billing' }        → editor view @ page
  //   { to: 'editor' }                           → editor / templates
  // SettingsShell reads its initial active section from readNav() on
  // mount, so writing the URL param BEFORE flipping view lets it pick
  // up the right section without a reload.
  useEffect(() => {
    const onNavigate = (e) => {
      const d = e.detail || {};
      // API keys live in Settings now (they used to be a page).
      const section = d.to === 'page' && d.page === 'api-keys' ? 'api-keys' : d.section;
      if (d.to === 'settings' || section === 'api-keys') {
        writeUrlParam('nav', section ? `settings.${section}` : 'settings');
        setView('settings');
      } else if (d.to === 'page' && d.page) {
        setPage(d.page);
        setView('editor');
      } else if (d.to === 'editor') {
        setPage('templates');
        setView('editor');
      }
    };
    window.addEventListener('pluma:navigate', onNavigate);
    return () => window.removeEventListener('pluma:navigate', onNavigate);
  }, []);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  // Organisation (tenant) switcher modal — only meaningful on hosted, where
  // a user can belong to multiple tenants. Opened from the avatar dropdown.
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  // Cmd/Ctrl+K command palette. Bound at the shell so it's reachable from
  // any page; ignores the shortcut while typing in an input/textarea.
  const [showPalette, setShowPalette] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        const el = document.activeElement;
        const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
        if (typing && !showPalette) return;
        e.preventDefault();
        setShowPalette((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPalette]);
  // (useWorkspace, useToast, useT, useI18n, usePublishStatus moved
  // to the top of AppShell to avoid TDZ on early useEffect deps —
  // see the comment block at the top.)

  // After login, prefer the user's stored locale over the cookie if they
  // differ — keeps device-portability nice when someone signs in on a new
  // browser. Fires once per locale-mismatch.
  useEffect(() => {
    if (user?.locale && user.locale !== locale) setLocale(user.locale, { persist: false });
  }, [user?.locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time URL-flag pickups: email-verification result lands at /app?verified=ok
  // (or invalid). Strip the param so a refresh doesn't replay the toast.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const v = p.get('verified');
    if (v) {
      toast(v === 'ok' ? t('auth.emailVerified') : t('auth.emailVerifyInvalid'), v === 'ok' ? 'success' : 'error');
      p.delete('verified');
      const qs = p.toString();
      window.history.replaceState(null, '', '/app' + (qs ? '?' + qs : ''));
    }
  }, [toast, t]);

  // Returning from Stripe Checkout / the billing portal lands at
  // /app?billing=… . Stripe applies the change server-side via the
  // /billing/webhook handler; this open tab still holds the stale cached
  // `user`/tenant, so re-fetch them — that's what makes the account-
  // deletion banner clear after the subscription is resumed, and the plan
  // pill update. One delayed retry covers the second or two the webhook
  // can lag behind the portal redirect.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (!p.get('billing')) return;
    p.delete('billing');
    const qs = p.toString();
    window.history.replaceState(null, '', '/app' + (qs ? '?' + qs : ''));
    // GET /billing/summary triggers the server's reconcile-from-Stripe
    // self-heal — if the checkout webhook was dropped, this call applies
    // the new plan directly from the subscription, so the refreshes below
    // pick up the real tier instead of a stale "free".
    const heal = () => api.get('/billing/summary').catch(() => {});
    heal().then(() => { refreshAuth(); refresh(); });
    const t2 = setTimeout(() => { heal().then(() => { refreshAuth(); refresh(); }); }, 4000);
    return () => clearTimeout(t2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Invite token handoff: when the user lands on /app?invite_token=... they've
  // clicked an emailed invite. Try to accept it; on success switch to the
  // newly-joined workspace; on a wrong-email error tell them to sign out.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tok = p.get('invite_token');
    if (!tok || !user) return;
    (async () => {
      try {
        const r = await api.post('/accept-invite/' + encodeURIComponent(tok), {}, { noUpgradeModal: true });
        toast(t('invite.accept.success', { tenantName: r.tenant?.name || '' }), 'success');
        if (r.tenant?.id) {
          // Reload into the joined organisation (see OrgSwitcherModal) —
          // switching in place left the old org's data on screen.
          writePersistedTenantId(r.tenant.id);
          p.delete('invite_token');
          const qs2 = p.toString();
          setTimeout(() => window.location.assign('/app' + (qs2 ? '?' + qs2 : '')), 1200);
        } else {
          await refresh();
        }
      } catch (e) {
        // Branch on the error CODE — e.message is the translated text.
        if (e.detail?.expected) toast(t('invite.accept.wrongEmail', { expected: e.detail.expected, actual: e.detail.actual || user.email }), 'error');
        else if (e.code === 'expired') toast(t('invite.accept.expired'), 'error');
        else if (e.code === 'user_seat_limit_reached') toast(t('invite.accept.teamFull', 'This team has no free seats. Ask the person who invited you to free one up or upgrade their plan.'), 'error');
        else if (e.code === 'network_error') toast(e.message, 'error');
        else toast(t('invite.accept.invalid'), 'error');
      } finally {
        p.delete('invite_token');
        const qs = p.toString();
        window.history.replaceState(null, '', '/app' + (qs ? '?' + qs : ''));
      }
    })();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tenant info (limits, justReactivatedAt, overCap) for global banners +
  // the upgrade modal + per-template lock badges. Refetched on workspace
  // switch; exposed via TenantInfoCtx so any descendant can subscribe.
  // ⚠ These three hooks MUST run on every render — they sit ABOVE the
  // FirstTenantPrompt early return below. React's rules-of-hooks count
  // mismatches between renders cause minified error #310 in production.
  // Refetched whenever the tenant OR its plan changes. The plan is the
  // load-bearing one for FeatureLocked / tier-gated buttons —
  // `info.limits.features` is derived from it, so an in-place
  // free→enterprise transition (license activation, Stripe webhook,
  // license clear) must refetch or every upgrade-CTA stays in place
  // until a full reload.
  const { info: tenantInfo, refresh: refreshTenantInfo } = useAccountTenantInfo(
    current?.id ? `${current.id}:${current.plan || ''}` : null);
  const tenantInfoCtx = useMemo(() => ({ info: tenantInfo, refresh: refreshTenantInfo }), [tenantInfo, refreshTenantInfo]);

  // While the tenants list is being fetched, render a loading frame
  // instead of FirstTenantPrompt. The prompt's condition
  // (`!current && tenants.length === 0`) is TRIVIALLY true during
  // mount — initial state is `tenants = []`, `current = null` — so
  // without the loading guard the prompt flashes for a single tick
  // before the API response arrives and the real shell renders.
  // That flash read as a phantom "you have no workspace, please create
  // one" to anyone reloading an existing install. The guard collapses
  // the flash window to a quiet labelled spinner that matches the
  // editor's loading aesthetic everywhere else in the app.
  if (tenantsLoading) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <LoadingFrame label={t('common.loading')} />
      </div>
    );
  }
  if (!current && tenants.length === 0 && tenantsError) {
    return <LoadErrorScreen message={tenantsError.message} onRetry={() => refresh()} />;
  }
  if (!current && tenants.length === 0) {
    // Two distinct zero-tenant cases on selfhosted:
    //   1. Fresh install — the first user just registered and now
    //      needs to provision the install's only tenant. The
    //      install has ZERO tenants overall. → FirstTenantPrompt.
    //   2. Removed user — selfhosted install already has its one
    //      tenant; this signed-in user has zero memberships because
    //      an admin removed them. The server would 403 the create
    //      attempt with `single_tenant_only`; rendering the form
    //      would trap them at the same wall on every reload. → NoAccessPage.
    //
    // The disambiguating signal is `runtime.installHasTenant`
    // (cheap COUNT in /spa/runtime). needsFirstUser is NOT a
    // reliable proxy — it flips on first registration, well before
    // any tenant exists, so the first user themselves would hit
    // the wrong branch with it.
    // Either way an open invitation is shown first: accepting it is the
    // way out of both screens.
    if (runtime.mode === 'selfhosted' && runtime.installHasTenant) {
      return <><PendingInvitesBanner /><NoAccessPage /></>;
    }
    return <><PendingInvitesBanner /><FirstTenantPrompt onCreated={() => refresh()} /></>;
  }
  if (current && current.suspended) {
    return <><PendingInvitesBanner /><SuspendedOrgPage tenant={current} /></>;
  }

  // Primary navigation model — shared by the desktop top bar and the
  // phone-sized avatar menu. Signatures follows the same gate as the
  // server's requireSignatureFeature middleware.
  const signaturesAvailable = !!features.signatures
    && (!features.signaturesRequireEnterpriseLicense || current?.plan === 'enterprise');
  const navItems = [
    { id: 'templates', label: t('nav.templates', 'Templates') },
    { id: 'renders', label: t('nav.documents', 'Documents') },
    signaturesAvailable && { id: 'sign-requests', label: t('nav.signatures', 'Signatures') },
  ].filter(Boolean);
  const activeNav = view === 'settings' ? null : page;
  const goNav = (id) => { setView('editor'); setPage(id); };

  return (
    <TenantInfoCtx.Provider value={tenantInfoCtx}>
    <div className="app">
      <AnnouncementBanner />
      <PolicyUpdateBanner />
      <PendingInvitesBanner />
      <AccountDeletionBanner />
      <HandoverBanner info={tenantInfo} onChoose={() => { setView('editor'); setPage('billing'); }} />
      <WelcomeBackBanner tenantInfo={tenantInfo} />
      {/* "See plans" routes to the Billing page on hosted (where plans
          live) and to the License page on self-hosted (where the
          operator activates a licence to unlock paid features —
          Billing doesn't exist on self-hosted, and navigating there
          previously returned 404 from /billing/summary). The
          features.billingPage flag is the canonical "is billing
          available here" signal, same one the avatar dropdown uses to
          gate its Billing menu entry. */}
      <UpgradeRequiredModal navigateToBilling={() => {
        setView('editor');
        setPage(features.billingPage ? 'billing' : 'license');
      }} />
      <div className="topbar">
        <div className="topbar-left">
          <a className="topbar-logo" href="/app"
             onClick={(e) => { e.preventDefault(); goNav('templates'); }}>
            <img src={runtime.brand?.faviconUrl || '/static/favicon.svg'} alt="" width="22" height="22"
                 style={{display:'block', borderRadius: 5, flexShrink: 0}} />
            <span>{runtime.brand?.productName || 'Pluma'}</span>
          </a>
          {/* Primary navigation. These sections used to be reachable only
              through the avatar dropdown, so new users never discovered
              Documents, Signatures or the API. On phones the list moves
              into the avatar menu instead (.topbar-nav is hidden there). */}
          <nav className="topbar-nav" aria-label={t('nav.primary', 'Main navigation')}>
            {navItems.map((item) => (
              <button key={item.id} type="button"
                      className={'topbar-nav-item' + (activeNav === item.id ? ' active' : '')}
                      aria-current={activeNav === item.id ? 'page' : undefined}
                      onClick={() => goNav(item.id)}>
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="topbar-actions">
          {currentWorkspace && (
            <WorkspaceSwitcher
              workspaces={workspaces}
              current={currentWorkspace}
              showPublishDots={!!features.publish}
              currentPublishState={publishStatusFor(publishStatus?.state) || publishStatusFor(currentWorkspace)}
              onSwitch={(w) => switchWorkspace(w)}
              onCreate={canCreateWorkspace ? () => setShowCreateWorkspace(true) : null}
            />
          )}
          {features.publish && (() => {
            // Runtime allows publish (self-hosted). The TIER gate is
            // separate: if the current plan doesn't include the publish
            // feature, render the button with a PRO badge that opens the
            // upgrade modal instead of the publish modal.
            const tierAllows = !!tenantInfo?.limits?.features?.publish;
            if (tierAllows) {
              return (
                <PublishButton
                  onClick={() => setShowPublish(true)}
                  status={publishStatusFor(publishStatus?.state) || publishStatusFor(currentWorkspace)}
                  tooltip={publishStatus?.state ? t('publish.state.' + publishStatus.state) : ''}
                />
              );
            }
            return (
              <button
                className="btn"
                style={{display: 'inline-flex', alignItems: 'center', gap: 6}}
                onClick={() => window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
                  detail: { code: 'feature_publish', requiredTier: 'pro' },
                }))}
                title={t('upgrade.publish.title', 'Publishing workspaces is a Pro feature')}
              >
                {t('publish.cta', 'Publish')}
                <span className="badge badge-pro" style={{padding: '1px 6px', fontSize: 10}}>Pro</span>
              </button>
            );
          })()}
          <NotificationBell
            onNavigate={(link) => {
              // In-app links ("/app?nav=…") stay inside the shell; anything
              // else (admin console, licence portal) is a real navigation.
              const m = /^\/app\?nav=([\w.-]+)$/.exec(link || '');
              if (!m) { window.location.assign(link || '/app'); return; }
              const target = m[1];
              if (target === 'invites') {
                // Invitations live in the banner above the shell, not on a page.
                window.dispatchEvent(new CustomEvent('pluma:invites-refresh'));
                return;
              }
              const detail = target.startsWith('settings')
                ? { to: 'settings', section: target.split('.')[1] || null }
                : { to: 'page', page: target };
              window.dispatchEvent(new CustomEvent('pluma:navigate', { detail }));
            }}
          />
          <AvatarDropdown
            user={user}
            features={features}
            isSuperAdmin={user?.isSuperAdmin}
            tenantsCount={tenants?.length || 0}
            supportUnread={supportUnread}
            notifTotal={notifTotal}
            notifColour={notifColour}
            navItems={navItems}
            activeNav={activeNav}
            onNavigate={(target) => {
              if (target === 'logout') { logout(); return; }
              if (target === 'apiDocs') { window.open('/docs', '_blank', 'noopener'); return; }
              if (target === 'settings') { setView('settings'); return; }
              if (target === 'editor') { goNav('templates'); return; }
              if (target === 'change-org') { setShowOrgSwitcher(true); return; }
              goNav(target);
            }}
          />
        </div>
      </div>

      {/* Crashes inside one section stay inside it: the top bar keeps
          working, and switching section (resetKey) clears the error. */}
      <ErrorBoundary resetKey={view === 'settings' ? 'settings' : page}>
      {view === 'settings' ? (
        <SettingsShell
          features={features}
          isSuperAdmin={user?.isSuperAdmin}
          backToEditor={() => { setView('editor'); setPage('templates'); }}
        />
      ) : (workspacesError && !currentWorkspace) ? (
        <LoadErrorScreen message={workspacesError} onRetry={() => refreshWorkspaces()} />
      ) : page === 'templates' ? (
        // Templates is the editor home and runs full-bleed with its own
        // 3-pane (browser / canvas / right rail). No outer sidebar.
        <TemplatesPage />
      ) : (
        // Secondary pages (api keys, jobs, billing, license, account,
        // help, support) reached from the avatar dropdown. They share a
        // centered .main container — no left nav. The SaaS-operator
        // admin console lives at /admin (standalone page, not part of
        // the tenant SPA) so superadmins jump out via window.location.
        <div className="main main-secondary">
          {page === 'renders' && <RendersPage />}
          {page === 'sign-requests' && <SignRequestsPage />}
          {page === 'billing' && <BillingPage />}
          {page === 'license' && <LicensePage />}
          {page === 'account' && <AccountPage />}
          {features.helpCenter && page === 'help' && <HelpCenterPage />}
          {features.supportTickets && page === 'support' && <SupportPage />}
        </div>
      )}
      </ErrorBoundary>

      {showPalette && <CommandPalette onClose={() => setShowPalette(false)} />}

      {showCreateWorkspace && (
        <CreateWorkspaceModal
          onClose={() => setShowCreateWorkspace(false)}
          onCreated={(ws) => {
            setShowCreateWorkspace(false);
            refreshWorkspaces();
            if (ws) switchWorkspace(ws);
          }}
        />
      )}

      {showPublish && currentWorkspace && (
        <PublishModal
          workspace={currentWorkspace}
          status={publishStatus}
          onClose={() => setShowPublish(false)}
          onPublished={() => { refreshPublishStatus(); refreshWorkspaces(); }}
        />
      )}

      {showOrgSwitcher && (
        <OrgSwitcherModal onClose={() => setShowOrgSwitcher(false)} />
      )}
    </div>
    </TenantInfoCtx.Provider>
  );
}

// ============================================================================
// Top-bar Publish button (selfhosted Pro+ only — gated by features.publish).
// In phase 6 the click opens the publish modal; for now the AppShell wires
// it to a "coming soon" toast so the affordance exists at the right spot
// in the top bar without pretending to do anything.
// ============================================================================

// Cmd/Ctrl+K command palette — the power-user navigation surface expected
// of a Linear/Stripe-class dashboard. Aggregates page navigation, settings
// sections, workspace + organisation switching, and quick "new" actions
// into one fuzzy-filtered, arrow-navigable list. Self-contained: it reads
// the workspace/tenant contexts directly and drives navigation through the
// same `pluma:navigate` event the rest of the shell already listens on.
function CommandPalette({ onClose }) {
  const t = useT();
  const { workspaces, current: curWs, switchTo: switchWs } = useWorkspace();
  const { tenants, current: curOrg, switchTo: switchOrg } = useTenant();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = React.useRef(null);
  React.useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);

  const nav = (detail) => { window.dispatchEvent(new CustomEvent('pluma:navigate', { detail })); onClose(); };

  // Static destinations. Labels reuse existing nav.* strings where they
  // exist so the palette never drifts from the sidebar wording.
  // Only offer destinations this install actually has — the same feature
  // flags the top bar and avatar menu use (Billing doesn't exist on
  // self-hosted, Backup/Branding only for self-hosted super-admins, …).
  const runtime = useRuntime();
  const features = runtime.features || {};
  const { user } = useAuth() || {};
  const isSuperAdmin = !!(user && user.isSuperAdmin);
  const go = t('cmd.group.go', 'Go to');
  const PAGE_CMDS = [
    { id: 'p.templates', label: t('nav.templates', 'Templates'), hint: go, run: () => nav({ to: 'editor' }) },
    { id: 'p.renders', label: t('nav.documents', 'Documents'), hint: go, run: () => nav({ to: 'page', page: 'renders' }) },
    features.signatures && { id: 'p.sign', label: t('nav.signatures', 'Signatures'), hint: go, run: () => nav({ to: 'page', page: 'sign-requests' }) },
    { id: 'p.apikeys', label: t('nav.apiKeys', 'API keys'), hint: go, run: () => nav({ to: 'settings', section: 'api-keys' }) },
    features.billingPage && { id: 'p.billing', label: t('avatar.planBilling', 'Plan & billing'), hint: go, run: () => nav({ to: 'page', page: 'billing' }) },
    features.licensePage && { id: 'p.license', label: t('nav.license', 'License'), hint: go, run: () => nav({ to: 'page', page: 'license' }) },
    { id: 'p.account', label: t('avatar.editProfile', 'Profile'), hint: go, run: () => nav({ to: 'page', page: 'account' }) },
    features.supportTickets && { id: 'p.support', label: t('avatar.getHelp', 'Get help'), hint: go, run: () => nav({ to: 'page', page: 'support' }) },
    features.helpCenter && { id: 'p.help', label: t('nav.helpCenter', 'Help center'), hint: go, run: () => nav({ to: 'page', page: 'help' }) },
  ].filter(Boolean);
  const SETTINGS_CMDS = [
    ['general', t('settings.general', 'General')],
    ['users', t('members.title', 'Members')],
    ['workspaces', t('nav.workspaces', 'Workspaces')],
    ['templates', t('nav.templates', 'Templates')],
    ['signatures', t('settings.signatures', 'Signatures')],
    ['image-library', t('settings.imageLibrary', 'Image library')],
    ['saved-schemas', t('nav.schemas', 'Schemas')],
    ['webhooks', t('nav.webhooks', 'Webhooks')],
    ['fonts', t('nav.fonts', 'Fonts')],
    (features.backupPage && isSuperAdmin) && ['backup', t('nav.backup', 'Backup')],
    (features.brandingPage && isSuperAdmin) && ['branding', t('settings.nav.branding', 'Branding')],
  ].filter(Boolean).map(([section, label]) => ({
    id: 's.' + section, label, hint: t('cmd.group.settings', 'Settings'),
    run: () => nav({ to: 'settings', section }),
  }));
  const WS_CMDS = (workspaces || [])
    .filter((w) => !curWs || w.slug !== curWs.slug)
    .map((w) => ({
      id: 'ws.' + w.id, label: w.name || w.slug, hint: t('cmd.group.switchWs', 'Switch workspace'),
      run: () => { switchWs(w); onClose(); },
    }));
  const ORG_CMDS = (tenants || []).length > 1 ? (tenants || [])
    .filter((o) => !curOrg || o.id !== curOrg.id)
    .map((o) => ({
      id: 'org.' + o.id, label: o.name, hint: t('cmd.group.switchOrg', 'Switch organisation'),
      // Full reload, like OrgSwitcherModal: every cache is tenant-scoped
      // and the first workspace slug ("default") is the same in every org,
      // so an in-place switch kept showing the previous org's templates.
      run: () => { switchOrg(o); onClose(); window.location.assign('/app'); },
    })) : [];

  const all = [...PAGE_CMDS, ...SETTINGS_CMDS, ...WS_CMDS, ...ORG_CMDS];
  const q = query.trim().toLowerCase();
  const results = q
    ? all.filter((c) => c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q))
    : all;
  const clamped = Math.min(active, Math.max(0, results.length - 1));

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); results[clamped] && results[clamped].run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: 'flex-start', paddingTop: '12vh' }}>
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 560, width: '92vw', padding: 0, overflow: 'hidden' }}>
        <input ref={inputRef} className="input" value={query} onKeyDown={onKeyDown}
               onChange={(e) => { setQuery(e.target.value); setActive(0); }}
               placeholder={t('cmd.placeholder', 'Search actions, pages, workspaces…')}
               style={{ border: 'none', borderBottom: '1px solid var(--border)', borderRadius: 0, fontSize: 15, padding: '14px 16px' }} />
        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: 6 }}>
          {results.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>{t('cmd.noResults', 'No matching actions.')}</div>
          ) : results.map((c, i) => (
            <button key={c.id} type="button"
                    onClick={c.run} onMouseEnter={() => setActive(i)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 8, font: 'inherit',
                      background: i === clamped ? 'var(--bg-elev)' : 'transparent',
                      color: 'var(--text)',
                    }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
              <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--text-muted)' }}>{c.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PublishButton({ onClick, status, tooltip }) {
  const t = useT();
  const dotCls = status || 'clean';
  return (
    <button className="publish-btn" onClick={onClick} title={tooltip || ''}>
      <span className={`publish-status-dot ${dotCls}`} />
      {t('publish.cta')}
    </button>
  );
}

// ============================================================================
// Publish modal — kicks off a build, polls progress while building, shows
// the last few historical jobs underneath. Three states share the same
// modal so the user doesn't lose context: idle (form), running (live
// logs), done (digest + history).
// ============================================================================

function PublishModal({ workspace, status, onClose, onPublished }) {
  const t = useT();
  const toast = useToast();
  const [registries, setRegistries] = useState([]);
  const [registryId, setRegistryId] = useState(status?.defaults?.registryId || '');
  const [repo, setRepo] = useState(status?.defaults?.repo || '');
  const [tag, setTag] = useState(status?.defaults?.tag || '');
  const [platforms, setPlatforms] = useState(status?.defaults?.platforms || ['linux/amd64']);
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [activeJob, setActiveJob] = useState(status?.inFlight || null);
  const [history, setHistory] = useState([]);

  // Load registries (for the select) + recent jobs.
  useEffect(() => {
    api.get('/spa/docker-registries').then(({ registries }) => setRegistries(registries || [])).catch(() => {});
    api.get(`/spa/workspaces/${workspace.slug}/publish/jobs`)
      .then(({ jobs }) => setHistory(jobs || [])).catch(() => {});
  }, [workspace.slug]);

  // Poll the active job while it's running. The status endpoint already
  // polls in the background for the pill, but the modal needs the per-
  // job log buffer which only the /jobs/:id endpoint returns.
  useEffect(() => {
    if (!activeJob || !['queued', 'running'].includes(activeJob.status)) return;
    const id = setInterval(async () => {
      try {
        const { job } = await api.get(`/spa/workspaces/${workspace.slug}/publish/jobs/${activeJob.id}`);
        setActiveJob(job);
        if (['succeeded', 'failed', 'cancelled'].includes(job.status)) {
          onPublished?.();
          // Refresh history once the job lands.
          api.get(`/spa/workspaces/${workspace.slug}/publish/jobs`)
            .then(({ jobs }) => setHistory(jobs || [])).catch(() => {});
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(id);
  }, [activeJob?.id, activeJob?.status, workspace.slug, onPublished]);

  const togglePlatform = (p) => {
    setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const submit = async () => {
    if (!registryId) { toast(t('publish.errors.registryRequired'), 'error'); return; }
    if (!repo.trim()) { toast(t('publish.errors.repoRequired'), 'error'); return; }
    if (platforms.length === 0) { toast(t('publish.errors.platformRequired', 'Pick at least one platform.'), 'error'); return; }
    setBusy(true);
    try {
      const { job } = await api.post(`/spa/workspaces/${workspace.slug}/publish`, {
        registryId, repo: repo.trim(), tag: tag.trim() || undefined, platforms,
      });
      setActiveJob(job);
      onPublished?.();
    } catch (err) {
      const msg = err.code === 'publish_already_in_flight'
        ? t('publish.errors.alreadyInFlight') : err.message;
      toast(msg, 'error');
    } finally {
      setBusy(false);
    }
  };

  const isRunning = activeJob && ['queued', 'running'].includes(activeJob.status);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth: 720}} onClick={(e) => e.stopPropagation()}>
        <h2>{t('publish.title')}</h2>
        <p style={{color: 'var(--text-muted)', fontSize: 13, marginBottom: 16}}>
          {t('publish.subtitle')}
        </p>

        {status?.state && (
          <div className={`publish-state-banner publish-state-${status.state}`}>
            <span className={`publish-status-dot ${publishStatusFor(status.state)}`} />
            {t('publish.state.' + status.state)}
            {status.lastPublishedAt && status.state !== 'never' && (
              <span style={{marginLeft: 8, opacity: 0.7}}>
                · {t('publish.lastPublishedAt', { when: formatDate(status.lastPublishedAt) })}
                {status.lastPublishedTag ? ` (${status.lastPublishedTag})` : ''}
              </span>
            )}
          </div>
        )}

        {!isRunning && (
          <div className="card" style={{marginBottom: 16}}>
            <div className="form-row">
              <label>{t('publish.registry')}</label>
              {registries.length === 0 ? (
                <div style={{fontSize: 12, color: 'var(--text-muted)'}}>{t('publish.registryEmpty')}</div>
              ) : (
                <select className="input" value={registryId} onChange={(e) => setRegistryId(e.target.value)}>
                  <option value="">—</option>
                  {registries.map((r) => (
                    <option key={r.id} value={r.id}>{r.name} · {r.url}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-row">
              <label>{t('publish.repo')}</label>
              <input className="input" value={repo}
                     onChange={(e) => setRepo(e.target.value)}
                     placeholder="namespace/image-name" />
              <div className="form-hint">{t('publish.repoHint')}</div>
            </div>

            <div className="form-row">
              <label>{t('publish.tag')}</label>
              <input className="input" value={tag}
                     onChange={(e) => setTag(e.target.value)}
                     placeholder={status?.currentHash ? status.currentHash.slice(0, 12) : 'auto'} />
              <div className="form-hint">{t('publish.tagHint')}</div>
            </div>

            <div className="form-row">
              <label>{t('publish.platforms')}</label>
              <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0}}>
                <input type="checkbox" checked={platforms.includes('linux/amd64')}
                       onChange={() => togglePlatform('linux/amd64')} />
                {t('publish.platformAmd64')}
              </label>
              <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0}}>
                <input type="checkbox" checked={platforms.includes('linux/arm64')}
                       onChange={() => togglePlatform('linux/arm64')} />
                {t('publish.platformArm64')}
              </label>
            </div>
          </div>
        )}

        {activeJob && (
          <div className="card" style={{marginBottom: 16}}>
            <div className="card-title">{t('publish.logs')}</div>
            <div style={{fontSize: 12, color: 'var(--text-muted)', marginBottom: 8}}>
              {activeJob.repo}:{activeJob.tag} · {activeJob.status}
              {activeJob.imageDigest && ` · ${activeJob.imageDigest.slice(0, 19)}…`}
            </div>
            <pre className="publish-log-buffer">
              {(activeJob.logs || []).map((l) => (
                `[${l.ts.slice(11, 19)}] ${l.level.toUpperCase().padEnd(5)} ${l.msg}`
              )).join('\n') || '(no log lines yet)'}
            </pre>
            {activeJob.error && (
              <div style={{color: 'var(--danger)', fontSize: 12, marginTop: 8}}>
                {activeJob.error}
              </div>
            )}
          </div>
        )}

        <div className="card">
          <div className="card-title">{t('publish.history')}</div>
          {history.length === 0 ? (
            <div style={{fontSize: 13, color: 'var(--text-muted)'}}>{t('publish.historyEmpty')}</div>
          ) : (
            <table className="table">
              <tbody>
                {history.slice(0, 10).map((j) => (
                  <tr key={j.id}>
                    <td>
                      <span className={`pill pill-${j.status === 'succeeded' ? 'active' : (j.status === 'failed' ? 'past_due' : 'incomplete')}`}>
                        {j.status}
                      </span>
                    </td>
                    <td><code style={{fontSize: 12}}>{j.repo}:{j.tag}</code></td>
                    <td style={{fontSize: 12, color: 'var(--text-muted)'}}>
                      {(j.platforms || []).join(', ')}
                    </td>
                    <td style={{fontSize: 12, color: 'var(--text-muted)'}}>
                      {formatDate(j.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-actions" style={{marginTop: 16}}>
          <button className="btn" onClick={onClose}>{t('publish.cancel')}</button>
          {!isRunning && (
            <button className="btn btn-primary" onClick={submit} disabled={busy || registries.length === 0}>
              {busy ? t('publish.starting') : t('publish.start')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Avatar dropdown — replaces the inline language switcher + sign-out
// buttons that used to live directly in the top bar. Per design:
//   header (name + email)
//   ── divider
//   Edit profile / Settings / API docs / Jobs
//   ── divider
//   (hosted only) Billing / Help center / Support
//   (super-admin) Admin
//   ── divider
//   Language picker
//   ── divider
//   Logout
// ============================================================================

// Workspace switcher (top bar, right). A compact pill + dropdown. Closes
// on outside click / Escape — the old inline version stayed open until
// the pill itself was clicked again.
function WorkspaceSwitcher({ workspaces, current, showPublishDots, currentPublishState, onSwitch, onCreate }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className="ws-switcher" ref={wrapRef}>
      <button type="button" className="tenant-pill" onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu" aria-expanded={open}
              title={t('workspace.switchTitle', 'Workspace: {{name}}', { name: current.name })}>
        {showPublishDots && (
          <span className={`publish-status-dot ${currentPublishState || ''}`}
                style={{marginRight: 2, flexShrink: 0}} />
        )}
        <Icon name="grid" className="tenant-pill-icon" />
        <span className="tenant-pill-name">{current.name}</span>
        <span aria-hidden="true" style={{opacity:0.6, flexShrink: 0}}>▾</span>
      </button>
      {open && (
        <div className="tenant-dropdown" role="menu">
          <div className="tenant-dropdown-label">{t('workspace.menuLabel', 'Workspaces')}</div>
          {workspaces.map((w) => (
            <button type="button" key={w.id} role="menuitem"
                    className={`tenant-item ${w.id === current.id ? 'active' : ''}`}
                    onClick={() => { onSwitch(w); setOpen(false); }}>
              <span style={{display:'flex', alignItems:'center', gap:8, minWidth: 0}}>
                {showPublishDots && <span className={`publish-status-dot ${publishStatusFor(w)}`} />}
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{w.name}</span>
              </span>
              {w.id === current.id && <Icon name="check" />}
            </button>
          ))}
          {onCreate && (<>
            <div className="avatar-dropdown-divider" />
            <button type="button" role="menuitem" className="tenant-item"
                    onClick={() => { setOpen(false); onCreate(); }}>
              + {t('workspace.add', 'New workspace')}
            </button>
          </>)}
        </div>
      )}
    </div>
  );
}

// One avatar-menu row. Real <button>s so every entry is reachable by
// keyboard (the old rows were divs, and "Sign out" had no tabindex).
function MenuItem({ onClick, className = '', children }) {
  return (
    <button type="button" role="menuitem"
            className={'avatar-dropdown-item ' + className}
            onClick={onClick}>
      {children}
    </button>
  );
}

// "3 minutes ago" / "yesterday" / a date — for the bell's entries.
function timeAgo(iso, locale, t) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (!Number.isFinite(diff)) return '';
  if (diff < 60) return t('notif.time.justNow', 'just now');
  try {
    const rtf = new Intl.RelativeTimeFormat(locale || 'en', { numeric: 'auto' });
    if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
    if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
    if (diff < 7 * 86400) return rtf.format(-Math.round(diff / 86400), 'day');
  } catch (_) { /* old browser — fall through to a date */ }
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// The VAPID public key as the PushManager wants it.
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Top-bar bell: unread count + a dropdown of the latest entries from
// /spa/notifications (one inbox across all the user's organisations). Polls
// every 45 s and on focus, and refreshes the moment the service worker
// reports a push arriving, so the badge moves together with the OS
// notification. Opening an entry marks it read and follows its link.
// One bell entry. The body is clamped to two lines; when that actually
// cuts text off, a chevron next to the × expands it in place — the row
// itself still opens the link. Measured after render (and again when the
// text changes) so the chevron only shows for entries that need it.
function NotifItem({ n, locale, t, onOpen, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const textRef = useRef(null);
  useEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [n.body, expanded]);
  const showToggle = clamped || expanded;
  return (
    <div className={`notif-item ${n.readAt ? '' : 'is-unread'}`}>
      <button type="button" role="menuitem" className="notif-open" onClick={() => onOpen(n)}>
        <span className="notif-dot" aria-hidden="true" />
        <span className="notif-body">
          <span className="notif-item-title">{n.title}</span>
          {n.body && <span ref={textRef} className={`notif-item-text ${expanded ? 'is-expanded' : ''}`}>{n.body}</span>}
          <span className="notif-meta">
            {n.tenant && n.tenant.name ? `${n.tenant.name} · ` : ''}{timeAgo(n.createdAt, locale, t)}
          </span>
        </span>
      </button>
      <div className="notif-item-actions">
        <button type="button" className="notif-remove"
                onClick={(e) => { e.stopPropagation(); onRemove(n); }}
                aria-label={t('notif.bell.remove', 'Remove notification')}
                title={t('notif.bell.remove', 'Remove notification')}>
          ×
        </button>
        {showToggle && (
          <button type="button" className="notif-more"
                  onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                  aria-expanded={expanded}
                  aria-label={expanded ? t('notif.bell.showLess', 'Collapse message') : t('notif.bell.showMore', 'Show full message')}
                  title={expanded ? t('notif.bell.showLess', 'Collapse message') : t('notif.bell.showMore', 'Show full message')}>
            {expanded ? '⌃' : '⌄'}
          </button>
        )}
      </div>
    </div>
  );
}

function NotificationBell({ onNavigate }) {
  const t = useT();
  const { locale } = useI18n();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const wrapRef = useRef(null);
  const openRef = useRef(false);
  openRef.current = open;

  const refreshCount = useCallback(() => {
    api.get('/spa/notifications/unread-count').then((d) => setUnread(d.unread || 0)).catch(() => {});
  }, []);
  const load = useCallback(async () => {
    try {
      const d = await api.get('/spa/notifications?limit=20');
      setItems(d.notifications || []);
      setUnread(d.unread || 0);
    } catch (_) { setItems([]); }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 45_000);
    const onFocus = () => refreshCount();
    const onSw = (e) => {
      if (!e.data || e.data.type !== 'pluma:notification') return;
      refreshCount();
      if (openRef.current) load();
    };
    window.addEventListener('focus', onFocus);
    const sw = navigator.serviceWorker;
    if (sw && sw.addEventListener) sw.addEventListener('message', onSw);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      if (sw && sw.removeEventListener) sw.removeEventListener('message', onSw);
    };
  }, [refreshCount, load]);
  useEffect(() => { if (open) load(); }, [open, load]);
  // Close on outside click / Escape — same behaviour as the avatar menu.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', handler, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', handler, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.readAt) {
      setItems((cur) => (cur || []).map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/spa/notifications/${n.id}/read`, {}).catch(() => {});
    }
    if (n.link) onNavigate(n.link);
  };
  const markAll = async () => {
    try {
      await api.post('/spa/notifications/read-all', {});
      const now = new Date().toISOString();
      setItems((cur) => (cur || []).map((x) => ({ ...x, readAt: x.readAt || now })));
      setUnread(0);
    } catch (_) { /* the next poll corrects the count */ }
  };
  // The × on a row: gone from the list at once; a failed delete reloads
  // the list so the row comes back rather than silently lingering server-side.
  const removeItem = (n) => {
    setItems((cur) => (cur || []).filter((x) => x.id !== n.id));
    if (!n.readAt) setUnread((u) => Math.max(0, u - 1));
    api.del(`/spa/notifications/${n.id}`).catch(() => load());
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button type="button" className="notif-btn" onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu" aria-expanded={open}
              aria-label={unread > 0 ? t('notif.bell.unreadLabel', '{{n}} unread notifications', { n: unread }) : t('notif.bell.title', 'Notifications')}
              title={t('notif.bell.title', 'Notifications')}>
        <Icon name="bell" />
        {unread > 0 && <span className="notif-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="avatar-dropdown notif-dropdown" role="menu">
          <div className="notif-head">
            <span className="notif-title">{t('notif.bell.title', 'Notifications')}</span>
            {unread > 0 && (
              <button type="button" className="notif-link" onClick={markAll}>{t('notif.bell.markAll', 'Mark all as read')}</button>
            )}
          </div>
          <div className="notif-list">
            {items === null && <div className="notif-empty">…</div>}
            {items && items.length === 0 && <div className="notif-empty">{t('notif.bell.empty', 'No notifications yet.')}</div>}
            {(items || []).map((n) => (
              <NotifItem key={n.id} n={n} locale={locale} t={t} onOpen={openItem} onRemove={removeItem} />
            ))}
          </div>
          <div className="avatar-dropdown-divider" />
          <button type="button" role="menuitem" className="avatar-dropdown-item"
                  onClick={() => { setOpen(false); onNavigate('/app?nav=settings.notifications'); }}>
            {t('notif.bell.settings', 'Notification settings')}
          </button>
        </div>
      )}
    </div>
  );
}

function AvatarDropdown({ user, features, isSuperAdmin, tenantsCount = 0, supportUnread = 0, notifTotal = 0, notifColour = '#f5b400', navItems = [], activeNav = null, onNavigate }) {
  const [open, setOpen] = useState(false);
  const t = useT();
  // Surface the active organisation in the header so the user can see at a
  // glance which one their actions will apply to — important once they
  // belong to multiple orgs and use "Change organisation" to switch.
  const { current: currentOrg } = useTenant();
  // Two-letter initials from name fallback to email; picks the first
  // codepoint of each token so it works for non-ASCII names too.
  const initials = (() => {
    const src = (user?.name || user?.email || '?').trim();
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.slice(0, 2) || '??').toUpperCase();
  })();

  // Close on outside click (capture, so it fires before child handlers
  // re-open the menu) and on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('.avatar-wrap')) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', handler, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', handler, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (target) => { setOpen(false); onNavigate(target); };
  // A personal signup gets an organisation named after the user, which
  // made the header read "Anna Becker / Anna Becker". Only show the org
  // line when it adds information.
  const showOrg = currentOrg && currentOrg.name
    && currentOrg.name.trim().toLowerCase() !== (user?.name || '').trim().toLowerCase();

  return (
    <div className="avatar-wrap" style={{position: 'relative'}}>
      <button type="button" className="avatar-btn" onClick={() => setOpen(v => !v)}
              aria-haspopup="menu" aria-expanded={open}
              aria-label={t('avatar.menuLabel', 'Account menu')}
              title={user?.email || ''}>
        {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
        {/* Bottom-right notification dot. Driven by the aggregate
            notification state — visible whenever any source has unread
            items, coloured amber when at least one of them is
            urgent/priority, yellow otherwise. Single dot regardless of
            count; the dropdown itself shows the numeric per-source
            badges on the relevant rows. */}
        {notifTotal > 0 && (
          <span className="avatar-notif-dot"
                style={{background: notifColour}}
                aria-label={`${notifTotal} unread notification${notifTotal === 1 ? '' : 's'}`} />
        )}
      </button>
      {open && (
        <div className="avatar-dropdown" role="menu">
          <div className="avatar-dropdown-header">
            {showOrg && <div className="org">{currentOrg.name}</div>}
            {user?.name && <div className="name">{user.name}</div>}
            <div className="email">{user?.email}</div>
          </div>
          {/* Phones: the top-bar navigation is hidden, so the primary
              sections live at the top of this menu instead. */}
          {navItems.length > 0 && (
            <div className="avatar-dropdown-nav">
              <div className="avatar-dropdown-divider" />
              {navItems.map((item) => (
                <MenuItem key={item.id} onClick={() => go(item.id)}
                      className={activeNav === item.id ? 'is-current' : ''}>
                  {item.label}
                </MenuItem>
              ))}
            </div>
          )}
          <div className="avatar-dropdown-divider" />
          <MenuItem onClick={() => go('account')}>{t('avatar.editProfile')}</MenuItem>
          <MenuItem onClick={() => go('settings')}>{t('avatar.settings')}</MenuItem>
          {/* "Change organisation" — hosted only AND only when the user
              belongs to more than one organisation. Self-hosted is single-
              tenant by design, so showing it there would be misleading. */}
          {features.billingPage && tenantsCount > 1 && (
            <MenuItem onClick={() => go('change-org')}>{t('avatar.changeOrg', 'Change organisation')}</MenuItem>
          )}
          {features.billingPage && (
            <MenuItem onClick={() => go('billing')}>{t('avatar.planBilling', 'Plan & billing')}</MenuItem>
          )}

          {/* Get help — opens the support page (tickets + their history).
              Always shown on hosted; off self-hosted (no SaaS staff to escalate to).
              FAQs live on the public marketing site at /faq for quick lookup
              without needing to be signed in. */}
          {features.supportTickets && (
            <>
              <div className="avatar-dropdown-divider" />
              <MenuItem onClick={() => go('support')}>
                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%'}}>
                  <span>{t('avatar.getHelp', 'Get help')}</span>
                  {supportUnread > 0 && (
                    <span className="notif-badge"
                          style={{background: notifColour}}
                          aria-label={`${supportUnread} unread support ${supportUnread === 1 ? 'reply' : 'replies'}`}>
                      {supportUnread}
                    </span>
                  )}
                </span>
              </MenuItem>
              <MenuItem onClick={() => { setOpen(false); window.open('/faq', '_blank', 'noopener'); }}>
                {t('avatar.browseFaq', 'Browse FAQ')} <span className="hint">↗</span>
              </MenuItem>
            </>
          )}
          <MenuItem onClick={() => { setOpen(false); window.open('/docs', '_blank', 'noopener'); }}>
            {t('avatar.apiDocs', 'API docs')} <span className="hint">↗</span>
          </MenuItem>

          {features.licensePage && (
            <>
              <div className="avatar-dropdown-divider" />
              <MenuItem onClick={() => go('license')}>{t('nav.license')}</MenuItem>
            </>
          )}

          {features.adminPanel && isSuperAdmin && (
            <>
              <div className="avatar-dropdown-divider" />
              {/* Admin console is a standalone page outside the SPA bundle —
                  jump out with a full navigation so the operator gets the
                  dedicated /admin.html shell (different layout, no tenant
                  switcher, global views). */}
              <MenuItem onClick={() => { window.location.assign('/admin'); }}>
                {t('nav.admin')} <span className="hint">{t('avatar.superadminHint')}</span>
              </MenuItem>
            </>
          )}

          <div className="avatar-dropdown-divider" />
          {/* Logout sits at the bottom in danger-red — matches the
              destructive-action convention used elsewhere in the UI
              (delete buttons in confirm modals, settings cards). */}
          <MenuItem onClick={() => go('logout')} className="avatar-dropdown-item-danger">{t('common.signOut')}</MenuItem>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Settings shell — full-width sidebar+main layout (matches the Backup-page
// screenshot). Sidebar is the canonical settings entry list. Most pages
// here are stubs in phase 1; subsequent phases fill them in.
// ============================================================================

function SettingsShell({ features, isSuperAdmin, backToEditor }) {
  const t = useT();
  // Restore the section from `?nav=settings.<id>` on refresh; otherwise
  // pick a sensible default based on what the deployment exposes. Hosted
  // lands on General; selfhosted superadmins land on Backup since that's
  // the page that's actually wired today.
  const initial = readNav().settingsSection
    || (features.backupPage && isSuperAdmin ? 'backup' : 'general');
  const [active, setActive] = useState(initial);
  // Keep `?nav` in sync so a refresh re-opens this settings section.
  useEffect(() => { writeUrlParam('nav', 'settings.' + active); }, [active]);
  // Jumps to another section while Settings is already open (command
  // palette, "Webhooks" link on the API keys page, …). The initial
  // section is only read on mount, so these used to do nothing.
  useEffect(() => {
    const onNavigate = (e) => {
      const d = e.detail || {};
      if (d.to === 'settings' && d.section && SETTINGS_SECTIONS.includes(d.section)) setActive(d.section);
    };
    window.addEventListener('pluma:navigate', onNavigate);
    return () => window.removeEventListener('pluma:navigate', onNavigate);
  }, []);
  const isMobile = useIsMobile();
  // On mobile we treat the sidebar and the main pane as two separate
  // "screens": picking a section flips to the section page; the back
  // arrow returns to the section list. Mirrors the TemplatesPage
  // mobilePanel pattern so the navigation feels native on phones.
  const [showMain, setShowMain] = useState(false);

  // Grouped so the developer-only sections (API keys, webhooks, JSON
  // schemas, registries) sit apart from everyday settings instead of
  // mixed in.
  const items = [
    { id: 'general', group: 'main', label: t('settings.nav.general'), enabled: true },
    { id: 'notifications', group: 'main', label: t('settings.nav.notifications', 'Notifications'), enabled: true },
    { id: 'users', group: 'main', label: t('settings.nav.users'), enabled: true },
    { id: 'workspaces', group: 'main', label: t('settings.nav.workspaces'), enabled: true },
    { id: 'templates', group: 'main', label: t('settings.nav.templates', 'Templates'), enabled: true },
    { id: 'signatures', group: 'main', label: t('settings.nav.signatures', 'My signatures'), enabled: true },
    { id: 'image-library', group: 'main', label: t('settings.nav.imageLibrary'), enabled: true },
    // Fonts is visible to every tier — non-enterprise sees the read-only
    // catalogue + an upgrade hint. The upload UI itself is gated server-side
    // by the customFonts feature flag (ACL fonts:write).
    { id: 'fonts', group: 'main', label: t('settings.nav.fonts', 'Fonts'), enabled: true },
    { id: 'api-keys', group: 'developer', label: t('settings.nav.apiKeys', 'API keys'), enabled: true },
    { id: 'webhooks', group: 'developer', label: t('settings.nav.webhooks'), enabled: true },
    { id: 'saved-schemas', group: 'developer', label: t('settings.nav.savedSchemas'), enabled: true },
    { id: 'docker-registries', group: 'developer', label: t('settings.nav.dockerRegistries'), enabled: !!features.dockerRegistries },
    { id: 'backup', group: 'install', label: t('settings.nav.backup'), enabled: !!features.backupPage && !!isSuperAdmin },
    // Install-wide branding (logos, accent colour, email footer).
    // Self-hosted superadmins only — hosted runs as Pluma + uses
    // per-tenant chrome elsewhere. Discoverable on every self-hosted
    // install for upsell purposes; the BrandingSettingsPage itself
    // branches on the active licence tier and either renders the
    // form (enterprise) or a "license upgrade required" prompt
    // (lower tiers). The server enforces the same gate.
    { id: 'branding', group: 'install', label: t('settings.nav.branding', 'Branding'), enabled: !!features.brandingPage && !!isSuperAdmin },
  ];
  const groupLabels = {
    main: t('settings.title'),
    developer: t('settings.group.developer', 'Developer'),
    install: t('settings.group.install', 'Installation'),
  };
  // A section this install doesn't offer (e.g. ?nav=settings.backup on
  // hosted) falls back to General instead of mounting a page whose API
  // isn't there.
  const activeEnabled = items.some((i) => i.id === active && i.enabled);
  useEffect(() => { if (!activeEnabled) setActive('general'); }, [activeEnabled]);
  const shown = activeEnabled ? active : 'general';
  const activeLabel = items.find((i) => i.id === active)?.label || '';

  const pickSection = (id) => {
    setActive(id);
    if (isMobile) setShowMain(true);
  };

  return (
    <div className={'settings-layout' + (isMobile ? ' is-mobile' : '')}>
      <div className={'settings-sidebar' + (isMobile && showMain ? ' is-hidden' : '')}>
        {['main', 'developer', 'install'].map((g) => {
          const groupItems = items.filter((i) => i.enabled && i.group === g);
          if (!groupItems.length) return null;
          return (
            <React.Fragment key={g}>
              <div className={'section-label' + (g === 'main' ? '' : ' section-label-sub')}>{groupLabels[g]}</div>
              {groupItems.map((it) => (
                <div key={it.id} role="button" tabIndex={0}
                     aria-current={active === it.id ? 'page' : undefined}
                     className={`nav-item ${active === it.id ? 'active' : ''}`}
                     onClick={() => pickSection(it.id)}
                     onKeyDown={keyActivate(() => pickSection(it.id))}>
                  {it.label}
                </div>
              ))}
            </React.Fragment>
          );
        })}
        <div className="sidebar-divider" />
        <div className="settings-back" onClick={backToEditor}>
          ← {t('settings.backToEditor')}
        </div>
      </div>
      <div className={'settings-main' + (isMobile && !showMain ? ' is-hidden' : '')}>
        {isMobile && (
          <div className="settings-main-mobile-header">
            <button className="settings-main-back" onClick={() => setShowMain(false)}
                    aria-label={t('settings.backToList', 'Back to settings')}>
              <Icons.ChevronLeft /> <span>{t('settings.title')}</span>
            </button>
            <span className="settings-main-mobile-title">{activeLabel}</span>
          </div>
        )}
        {shown === 'backup' && <BackupPage />}
        {shown === 'api-keys' && <ApiKeysPage />}
        {shown === 'webhooks' && <WebhooksPage />}
        {shown === 'fonts' && <FontsPage />}
        {shown === 'users' && <MembersPage />}
        {shown === 'saved-schemas' && <SchemasPage />}

        {shown === 'workspaces' && <WorkspacesSettingsPage />}

        {shown === 'templates' && <TemplatesSettingsPage />}

        {shown === 'signatures' && <SignaturesSettingsPage />}

        {shown === 'docker-registries' && <DockerRegistriesPage />}

        {/* Stubs — phases 5–6 fill these in. Image Library currently
            only exists inline in the template editor; phase 5 will
            promote it into a standalone settings page. */}
        {shown === 'image-library' && <ImageLibraryPage />}
        {shown === 'general' && <GeneralSettingsPage />}
        {shown === 'notifications' && <NotificationsSettingsPage />}
        {shown === 'branding' && <BrandingSettingsPage />}
      </div>
    </div>
  );
}

// Install-wide branding settings (self-hosted only).
//
// Reads /spa/install-settings to populate the form, PATCHes the
// singleton row on Save, then dispatches a `pluma:brand-updated`
// custom event so AppShell can re-fetch /spa/runtime and apply the
// new brand to the live SPA chrome without a full reload.
//
// Field validation mirrors src/install-settings/routes.js so the
// SPA fails fast before round-tripping a 400. Visual preview row at
// the top shows what the header logo + accent will look like once
// saved — operators see the change before committing.
function BrandingSettingsPage() {
  const t = useT();
  const toast = useToast();
  const runtime = useRuntime();
  const { current } = useTenant();
  // Combined enterprise-licence gate. The settings nav shows this
  // page on every self-hosted superadmin install so the upsell is
  // discoverable; the IN-PAGE branch decides whether to render the
  // form or the upsell prompt. The server enforces the same policy
  // via requireEnterpriseLicense in src/install-settings/routes.js.
  const needsEnterpriseLicense = !!runtime?.features?.brandingRequireEnterpriseLicense;
  const planUnlocksBranding    = !needsEnterpriseLicense || current?.plan === 'enterprise';

  if (!planUnlocksBranding) {
    return (
      <div className="settings-form">
        <h1 className="settings-h1">{t('branding.title', 'Branding')}</h1>
        <p className="settings-intro">
          {t('branding.intro',
            'Customise the dashboard chrome and transactional emails for this install. All fields are optional; blank fields fall back to the bundled Pluma defaults.')}
        </p>
        <div style={{
          border: '1px dashed var(--border-strong, var(--border))',
          borderRadius: 'var(--radius)',
          padding: 20,
          background: 'var(--bg-elevated)',
          marginTop: 8,
        }}>
          <div style={{fontWeight: 700, fontSize: 15, marginBottom: 8}}>
            {t('branding.upsellTitle', 'Enterprise license required')}
          </div>
          <div style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6}}>
            {t('branding.upsellBody',
              'White-labelling the dashboard chrome and transactional email layout — product name, logo, favicon, accent colour, mail footer — is part of the enterprise tier on self-hosted installs. Activate or upgrade your license to enable this page.')}
          </div>
          <a href="#/license"
             onClick={(e) => {
               e.preventDefault();
               window.dispatchEvent(new CustomEvent('pluma:navigate', {detail: {to: 'page', page: 'license'}}));
             }}
             className="btn btn-primary"
             style={{textDecoration: 'none'}}>
            {t('branding.upsellCta', 'Manage license')}
          </a>
        </div>
      </div>
    );
  }
  // From here: the operator IS on enterprise. Render the form.
  return <BrandingFormSection toast={toast} t={t} />;
}

// The form is split into its own component so the hooks below are
// only mounted (and the initial /spa/install-settings fetch only
// fires) when the enterprise gate passes. A non-enterprise operator
// who lands on this page never round-trips the API.
function BrandingFormSection({ toast, t }) {
  const confirm = useConfirm();
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [productName, setProductName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUrlDark, setLogoUrlDark] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [emailFooterText, setEmailFooterText] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.get('/spa/install-settings').then((res) => {
      const s = res.settings || {};
      setProductName(s.productName || '');
      setLogoUrl(s.logoUrl || '');
      setLogoUrlDark(s.logoUrlDark || '');
      setFaviconUrl(s.faviconUrl || '');
      setAccentColor(s.accentColor || '');
      setEmailFooterText(s.emailFooterText || '');
      setSupportEmail(s.supportEmail || '');
      setLoaded(true);
    }).catch((e) => { setLoadError(e.message); setLoaded(true); });
    // Read-once on mount; nothing else mutates the row from elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inline validators — same shape the server enforces.
  function validate() {
    const errs = {};
    if (accentColor && !/^#[0-9a-fA-F]{3,8}$/.test(accentColor.trim())) {
      errs.accentColor = t('branding.errors.accentColor', 'Must be a CSS hex colour like #6c5ce7');
    }
    const urlRe = /^(https?:\/\/|\/|data:image\/)/i;
    for (const [key, val] of [['logoUrl', logoUrl], ['logoUrlDark', logoUrlDark], ['faviconUrl', faviconUrl]]) {
      if (val && !urlRe.test(val.trim())) {
        errs[key] = t('branding.errors.url', 'Must be an absolute URL, a same-origin path, or a data:image URI');
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // `values` lets reset() save the cleared fields directly — calling
  // save() right after the setX('') calls still read the OLD state, so
  // "Reset" used to re-save the previous branding.
  async function save(values) {
    const v = values || { productName, logoUrl, logoUrlDark, faviconUrl, accentColor, emailFooterText, supportEmail };
    if (!values && !validate()) return;
    setBusy(true);
    try {
      // Trim everything; blank strings become nulls on the server.
      await api.patch('/spa/install-settings', {
        productName:     v.productName.trim() || null,
        logoUrl:         v.logoUrl.trim() || null,
        logoUrlDark:     v.logoUrlDark.trim() || null,
        faviconUrl:      v.faviconUrl.trim() || null,
        accentColor:     v.accentColor.trim() || null,
        emailFooterText: v.emailFooterText.trim() || null,
        supportEmail:    v.supportEmail.trim() || null,
      });
      toast(t('branding.saved', 'Branding saved'), 'success');
      // Tell AppShell to refetch /spa/runtime + reapply the brand
      // to the live SPA chrome. Custom event keeps the dependency
      // direction clean — Branding page doesn't have to reach into
      // AppShell's state.
      window.dispatchEvent(new CustomEvent('pluma:brand-updated'));
    } catch (e) {
      // Surface per-field server errors if they came back (errorFrom puts
      // the response body on err.detail; there is no err.response).
      if (e?.code === 'invalid_fields' && Array.isArray(e.detail?.fields)) {
        const map = {};
        for (const f of e.detail.fields) map[f.field] = f.message;
        setErrors(map);
      }
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    const ok = await confirm({
      title: t('branding.resetConfirm.title', 'Reset branding?'),
      body: t('branding.resetConfirm.body', 'All branding fields revert to the bundled Pluma defaults. This cannot be undone.'),
      confirmLabel: t('branding.resetConfirm.cta', 'Reset branding'),
      danger: true,
    });
    if (!ok) return;
    setProductName(''); setLogoUrl(''); setLogoUrlDark(''); setFaviconUrl('');
    setAccentColor(''); setEmailFooterText(''); setSupportEmail('');
    setErrors({});
    save({ productName: '', logoUrl: '', logoUrlDark: '', faviconUrl: '', accentColor: '', emailFooterText: '', supportEmail: '' });
  }

  if (!loaded) return <div style={{padding: 24, color: 'var(--text-muted)'}}>{t('common.loading', 'Loading…')}</div>;
  if (loadError) {
    return (
      <div className="settings-form">
        <div className="fill-error" role="alert">{loadError}</div>
        <button className="btn" style={{marginTop: 12}} onClick={() => window.location.reload()}>{t('crash.retry', 'Try again')}</button>
      </div>
    );
  }

  return (
    <div className="settings-form">
      <h1 className="settings-h1">{t('branding.title', 'Branding')}</h1>
      <p className="settings-intro">
        {t('branding.intro',
          'Customise the dashboard chrome and transactional emails for this install. All fields are optional; blank fields fall back to the bundled Pluma defaults. Changes take effect on the next page reload and on every email sent thereafter.')}
      </p>

      {/* Live preview — header chrome with the configured logo/colour. */}
      <div className="prop-group">
        <label className="label">{t('branding.preview', 'Preview')}</label>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: 16, borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          background: 'var(--bg-elevated)',
        }}>
          {logoUrl ? (
            <img src={logoUrl} alt="logo" style={{height: 36, maxWidth: 220}} />
          ) : (
            <div style={{
              display: 'inline-block', width: 36, height: 36,
              background: accentColor || '#6c5ce7',
              borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 20,
              textAlign: 'center', lineHeight: '36px',
            }}>{(productName || 'Pluma').charAt(0).toUpperCase()}</div>
          )}
          <span style={{fontWeight: 700, fontSize: 18}}>{productName || 'Pluma'}</span>
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.productName', 'Product name')}</label>
        <input className="input"
               placeholder="Pluma"
               value={productName}
               onChange={(e) => setProductName(e.target.value)} />
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.productNameHint', 'Replaces "Pluma" in the dashboard header, document title, and email layout.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.logoUrl', 'Logo URL')}</label>
        <div style={{display: 'flex', gap: 6}}>
          <input className="input" style={{flex: 1}}
                 placeholder="https://your-cdn.com/logo.svg"
                 value={logoUrl}
                 onChange={(e) => setLogoUrl(e.target.value)} />
          <LogoUploadButton t={t} onUploaded={(url) => setLogoUrl(url)} />
        </div>
        {errors.logoUrl && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 4}}>{errors.logoUrl}</div>}
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.logoUrlHint', 'Header logo in the dashboard and the email header. Absolute URLs, same-origin paths, or data:image URIs all work. Use Upload for a hosted-here SVG/PNG/JPEG up to 512 KB.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.logoUrlDark', 'Logo URL (dark theme — optional)')}</label>
        <div style={{display: 'flex', gap: 6}}>
          <input className="input" style={{flex: 1}}
                 placeholder="https://your-cdn.com/logo-white.svg"
                 value={logoUrlDark}
                 onChange={(e) => setLogoUrlDark(e.target.value)} />
          <LogoUploadButton t={t} onUploaded={(url) => setLogoUrlDark(url)} />
        </div>
        {errors.logoUrlDark && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 4}}>{errors.logoUrlDark}</div>}
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.logoUrlDarkHint', 'Used when the dashboard is in dark mode. Leave empty to use the main logo for both themes.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.faviconUrl', 'Favicon URL')}</label>
        <div style={{display: 'flex', gap: 6}}>
          <input className="input" style={{flex: 1}}
                 placeholder="https://your-cdn.com/favicon.svg"
                 value={faviconUrl}
                 onChange={(e) => setFaviconUrl(e.target.value)} />
          <LogoUploadButton t={t} onUploaded={(url) => setFaviconUrl(url)} />
        </div>
        {errors.faviconUrl && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 4}}>{errors.faviconUrl}</div>}
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.faviconUrlHint', 'Browser tab icon. SVG or PNG; 32×32 or larger works best.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.accentColor', 'Accent colour')}</label>
        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
          <input className="input"
                 style={{maxWidth: 160, fontFamily: 'var(--font-mono)'}}
                 placeholder="#6c5ce7"
                 value={accentColor}
                 onChange={(e) => setAccentColor(e.target.value)} />
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: /^#[0-9a-fA-F]{3,8}$/.test(accentColor) ? accentColor : 'transparent',
            border: '1px solid var(--border)',
          }} />
        </div>
        {errors.accentColor && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 4}}>{errors.accentColor}</div>}
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.accentColorHint', 'Drives the primary button + the email header avatar fill. Hex like #6c5ce7.')}
        </div>
      </div>

      <h2 className="settings-h2" style={{marginTop: 24}}>{t('branding.emailSection', 'Mail')}</h2>

      <div className="prop-group">
        <label className="label">{t('branding.supportEmail', 'Support email')}</label>
        <input className="input"
               placeholder="support@your-company.com"
               value={supportEmail}
               onChange={(e) => setSupportEmail(e.target.value)} />
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.supportEmailHint', 'Shown in the email footer and used for in-app "Contact support" links. Falls back to the SUPPORT_EMAIL env var or support@<your-domain> when blank.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('branding.emailFooterText', 'Email footer text')}</label>
        <textarea className="input"
                  rows={4}
                  placeholder={t('branding.emailFooterPlaceholder', 'Your Company GmbH — Beispielstraße 1, 50667 Köln, Germany\nRegistered: HRB 12345 · VAT: DE123456789')}
                  value={emailFooterText}
                  onChange={(e) => setEmailFooterText(e.target.value)} />
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('branding.emailFooterTextHint', 'Appended to every transactional email footer (legal notice, address, copyright). Plain text; line breaks are preserved.')}
        </div>
      </div>

      {/* Send a brand-preview email to the signed-in admin. Verifies
          SMTP + the configured brand in one round-trip — the only
          dry-run that catches "logo URL 404s for unauthenticated
          recipients" before real customers see it. */}
      <div className="prop-group" style={{borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8}}>
        <label className="label">{t('branding.sendTest', 'Test the brand end-to-end')}</label>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.5}}>
          {t('branding.sendTestHint',
            'Sends a sample transactional email to your signed-in address using the current brand. Verifies the logo renders, the accent colour is right, and SMTP is wired.')}
        </div>
        <SendTestEmailButton t={t} />
      </div>

      <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16}}>
        <button className="btn" onClick={reset} disabled={busy}>
          {t('branding.reset', 'Reset to defaults')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? t('common.saving', 'Saving…') : t('common.save')}
        </button>
      </div>
    </div>
  );
}

// Hidden-input + button pair that uploads a logo image and emits the
// resolved URL via onUploaded(url). Accepts PNG/JPEG/SVG up to 512KB
// (server enforces; the client also pre-checks so the operator
// doesn't waste a network round-trip on an obviously-oversized
// file). Server validates magic bytes + rejects SVGs containing
// <script> tags or event handlers — see install-settings/routes.js.
function LogoUploadButton({ t, onUploaded }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const toast = useToast();

  async function onChoose(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setErr(null);
    if (f.size > 512 * 1024) {
      setErr(t('branding.logoTooLarge', 'Logo must be ≤ 512 KB.'));
      e.target.value = '';
      return;
    }
    setBusy(true);
    try {
      // FileReader → base64 (browser API gives us data: URL; trim
      // the prefix so we send just the payload to the server).
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const s = String(r.result || '');
          const i = s.indexOf(',');
          resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = () => reject(r.error || new Error('read failed'));
        r.readAsDataURL(f);
      });
      const resp = await api.post('/spa/install-settings/logo', {
        contentType: f.type || 'application/octet-stream',
        base64,
      });
      onUploaded(resp.url);
      toast(t('branding.logoUploaded', 'Logo uploaded'), 'success');
    } catch (ex) {
      const body = ex?.response;
      setErr(body?.detail || body?.error || ex.message || 'upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  }

  return (
    <>
      <input ref={inputRef} type="file"
             accept="image/png,image/jpeg,image/svg+xml"
             style={{display: 'none'}}
             onChange={onChoose} />
      <button className="btn" type="button"
              onClick={() => inputRef.current && inputRef.current.click()}
              disabled={busy}
              style={{whiteSpace: 'nowrap'}}>
        {busy ? t('branding.uploading', 'Uploading…') : t('branding.upload', 'Upload')}
      </button>
      {err && (
        <div role="alert" style={{position: 'absolute', marginTop: 28, fontSize: 11, color: 'var(--danger)'}}>{err}</div>
      )}
    </>
  );
}

// Inline subcomponent so the Send-test button owns its own busy/result
// state. Surfaces server errors with their structured remediation
// (smtp_not_configured carries a docsUrl back so the operator can
// jump straight to the SMTP block in the self-host guide).
function SendTestEmailButton({ t }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function send() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post('/spa/install-settings/send-test', {});
      setResult({ kind: 'ok', message: t('branding.sendTestOk', 'Sent to {{to}}. Check your inbox.', { to: r.sentTo }) });
    } catch (e) {
      // errorFrom exposes the parsed body as err.detail (plus err.code);
      // there is no err.response, so these branches never ran before.
      const body = (e?.detail && typeof e.detail === 'object') ? { error: e.code, ...e.detail } : null;
      if (body?.error === 'smtp_not_configured') {
        setResult({
          kind: 'err',
          message: body.message + (body.remediation ? ' ' + body.remediation : ''),
          docsUrl: body.docsUrl,
        });
      } else if (body?.error === 'license_upgrade_required') {
        setResult({ kind: 'err', message: body.message });
      } else {
        setResult({ kind: 'err', message: e.message });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button className="btn" onClick={send} disabled={busy}>
        {busy ? t('branding.sending', 'Sending…') : t('branding.sendTestCta', 'Send preview to me')}
      </button>
      {result && (
        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: 'var(--radius)',
          fontSize: 12,
          lineHeight: 1.5,
          background: result.kind === 'ok' ? 'rgba(31,138,76,0.10)' : 'rgba(192,57,43,0.10)',
          color: result.kind === 'ok' ? '#1f8a4c' : '#c0392b',
        }}>
          {result.message}
          {result.docsUrl && (
            <> · <a href={result.docsUrl} target="_blank" rel="noopener noreferrer"
                   style={{color: 'inherit', textDecoration: 'underline'}}>
              {t('branding.viewDocs', 'View self-host docs')}
            </a></>
          )}
        </div>
      )}
    </div>
  );
}

// 3-dot action menu used in settings list cards. `items` is an array of
// { label, onClick, danger? } objects; passing a falsy entry skips it
// (handy for conditional rows without filter() at the call site).
// Color picker — swatch button + hex input + small preset palette. Replaces
// the browser-default <input type="color"> which is OS-styled and never
// matches the dark theme. Accepts/emits 6-digit hex strings ("#RRGGBB").
//
// We deliberately keep this small (no HSL sliders, no eyedropper) — the
// editor needs a colour for text/border/etc, not a full design system. If
// someone wants a colour we didn't preset, they type the hex.
const COLOR_PRESETS = [
  '#000000', '#333333', '#666666', '#999999', '#cccccc', '#ffffff',
  '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#27ae60', '#16a085',
  '#3498db', '#2980b9', '#6c5ce7', '#9b59b6', '#8e44ad', '#34495e',
];
function ColorPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '#000000');
  // Which edge of the trigger button the popover should anchor to.
  //   'left'  → popover's left edge sits at the trigger's left edge
  //             (grows rightward). Preferred default — matches the
  //             "menu opens below + to the right" reading direction.
  //   'right' → popover's right edge sits at the trigger's right edge
  //             (grows leftward). Used when 'left' would overflow the
  //             viewport on the right (Properties panel against the
  //             right gutter — the original bug that "right:0" tried
  //             to fix; flipping per-instance avoids the left-clip
  //             that the static right-anchor introduced when the
  //             picker sat near the panel's left edge).
  // Viewport-anchored coordinates for the popover. It used to be
  // position:absolute, which the editor's scrolling properties panel
  // clipped when a colour field sat near the panel's bottom or right
  // edge. Computing fixed coordinates from the trigger (same approach as
  // ActionMenu) escapes every overflow context — horizontally AND
  // vertically. ~280px wide / ~160px tall (documented below).
  const [coords, setCoords] = useState(null);
  const wrapRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  useEffect(() => { setDraft(value || '#000000'); }, [value]);

  const openPopover = () => {
    const r = wrapRef.current && wrapRef.current.getBoundingClientRect();
    if (r) {
      const W = 280, H = 170, pad = 8;
      // Horizontal: grow rightward from the trigger's left edge; flip to
      // right-anchored when that would overflow the viewport.
      const roomRight = window.innerWidth - r.left - pad;
      const horiz = (W > roomRight && r.right - pad >= W)
        ? { right: Math.max(pad, window.innerWidth - r.right) }
        : { left: Math.min(r.left, window.innerWidth - W - pad) };
      // Vertical: below the trigger, or flipped above when there's no room.
      const roomBelow = window.innerHeight - r.bottom;
      const vert = (roomBelow < H + pad && r.top > H + pad)
        ? { bottom: window.innerHeight - r.top + 6 }
        : { top: r.bottom + 6 };
      setCoords({ ...horiz, ...vert });
    }
    setOpen(true);
  };

  // Close on outside tap. `pointerdown` covers BOTH mouse and touch with
  // one event — `mousedown` alone misses iOS Safari taps that don't
  // synthesize a mouse event in some interaction modes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    // A fixed popover would drift from its trigger on scroll — close it.
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const commit = (raw) => {
    let v = String(raw || '').trim();
    if (v && v[0] !== '#') v = '#' + v;
    if (/^#[0-9a-f]{6}$/i.test(v)) {
      onChange(v.toLowerCase());
    } else {
      setDraft(value || '#000000');
    }
  };

  return (
    <div ref={wrapRef} style={{position: 'relative', display: 'flex', alignItems: 'center', gap: 8}}>
      <button type="button"
              onClick={() => open ? setOpen(false) : openPopover()}
              disabled={disabled}
              title="Pick colour"
              style={{
                // ≥44px tap target. The visible swatch is 28px centred
                // inside a transparent 44px hit box so the control still
                // looks small but is finger-friendly.
                width: 44, height: 44, padding: 8,
                borderRadius: 8, border: '1px solid var(--border)',
                background: 'transparent', cursor: 'pointer',
                flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                touchAction: 'manipulation',
              }}>
        <span aria-hidden="true" style={{
          display: 'block', width: '100%', height: '100%',
          borderRadius: 4, background: value || '#000000',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
        }} />
      </button>
      <input className="input" value={draft}
             disabled={disabled}
             onChange={(e) => setDraft(e.target.value)}
             onBlur={(e) => commit(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { commit(e.target.value); e.currentTarget.blur(); }
               if (e.key === 'Escape') { setDraft(value || '#000000'); e.currentTarget.blur(); }
             }}
             // `text` keyboard supports a-f for hex. `autoCapitalize=none`
             // stops iOS shifting to uppercase as you type. `autoCorrect=off`
             // stops "fff" → "FFF" suggestion popovers.
             autoCapitalize="none" autoCorrect="off" spellCheck={false}
             style={{
               fontFamily: 'var(--font-mono)', fontSize: 13,
               textTransform: 'uppercase', minHeight: 44,
             }}
             placeholder="#000000"
             maxLength={7} />
      {open && coords && (
        <div ref={popoverRef}
             style={{
          // Fixed-viewport placement computed in openPopover() from the
          // trigger rect — auto-flips left/right and up/down so it never
          // clips against the scrolling properties panel or the viewport.
          position: 'fixed', zIndex: 200,
          ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
          ...(coords.left != null ? { left: coords.left } : { right: coords.right }),
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          // 6 swatches × 36px + 5 × 8px gap + 2 × 12px pad = 280px.
          maxWidth: 'calc(100vw - 32px)',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(6, 36px)',
            gap: 8, justifyContent: 'center',
          }}>
            {COLOR_PRESETS.map((c) => (
              <button key={c} type="button"
                      onClick={() => { onChange(c); setOpen(false); }}
                      title={c}
                      style={{
                        // 36px swatch — finger-friendly, still compact.
                        width: 36, height: 36, borderRadius: 6,
                        border: c.toLowerCase() === (value || '').toLowerCase()
                          ? '2px solid var(--accent)'
                          : '1px solid var(--border)',
                        cursor: 'pointer', padding: 0,
                        background: c,
                        touchAction: 'manipulation',
                      }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  // Fixed-viewport coordinates computed from the trigger at open time.
  // The dropdown used to be position:absolute inside the trigger, so any
  // ancestor scroll container (e.g. a table's overflow-x:auto wrapper —
  // which the CSS spec promotes to clip the y-axis too) cropped it.
  // position:fixed escapes every overflow/stacking context, so this one
  // component fix covers every ActionMenu in the app.
  const [coords, setCoords] = useState(null);
  const triggerRef = React.useRef(null);
  const real = (items || []).filter(Boolean);

  const openMenu = () => {
    const r = triggerRef.current && triggerRef.current.getBoundingClientRect();
    if (r) {
      const right = Math.max(8, window.innerWidth - r.right);
      // Flip upward when there's no room below (last-row menus near the
      // viewport bottom) but there is room above.
      const estHeight = real.length * 38 + 8;
      const roomBelow = window.innerHeight - r.bottom;
      if (roomBelow < estHeight && r.top > estHeight) {
        setCoords({ bottom: window.innerHeight - r.top + 4, right });
      } else {
        setCoords({ top: r.bottom + 4, right });
      }
    }
    setOpen(true);
  };

  // Close on scroll (in any container) or resize — a fixed dropdown would
  // otherwise float detached from its trigger.
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Escape closes and hands focus back to the trigger.
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); if (triggerRef.current) triggerRef.current.focus(); } };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const t = useT();
  // Move focus into the menu when it opens so keyboard users can act.
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    if (open && coords && menuRef.current) {
      const first = menuRef.current.querySelector('button');
      if (first) first.focus();
    }
  }, [open, coords]);

  if (real.length === 0) return null;
  return (
    <div className="action-menu">
      <button ref={triggerRef} className="action-menu-trigger"
              onClick={() => open ? setOpen(false) : openMenu()}
              title={t('common.actions', 'Actions')} aria-label={t('common.actions', 'Actions')}
              aria-haspopup="menu" aria-expanded={open}>⋮</button>
      {open && coords && (
        <>
          <div className="action-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="action-menu-dropdown" role="menu" ref={menuRef}
               style={{ position: 'fixed', right: coords.right,
                        ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }) }}>
            {real.map((it, idx) => (
              <button key={idx} role="menuitem"
                      className={'action-menu-item' + (it.danger ? ' danger' : '')}
                      onClick={() => { setOpen(false); it.onClick(); }}>
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Standard settings-page shell: header + description + content slot.
// Pages that need a top-right action button pass it via `headerAction`.
// ============================================================================
// Settings → Notifications — which channel each kind of event may use, and
// browser push for this device. Categories, defaults and the locked rows
// come from the server (src/notifications/types.js); this page only shows
// the matrix and stores overrides.
// ============================================================================
function NotificationsSettingsPage() {
  const t = useT();
  const toast = useToast();
  const runtime = useRuntime();
  const [cats, setCats] = useState(null);
  const [subs, setSubs] = useState([]);
  // unsupported | blocked | notConfigured | off | on | loading
  const [pushState, setPushState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const serverPush = !!runtime?.features?.webPush;

  const load = useCallback(async () => {
    try {
      const d = await api.get('/spa/notifications/preferences');
      setCats(d.categories || []);
    } catch (e) { toast(e.message, 'error'); setCats([]); }
    try {
      const s = await api.get('/spa/notifications/push/subscriptions');
      setSubs(s.subscriptions || []);
    } catch (_) { /* devices list is optional */ }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  // Browser push on THIS device: supported? allowed? subscribed?
  useEffect(() => {
    (async () => {
      if (!serverPush) { setPushState('notConfigured'); return; }
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) { setPushState('unsupported'); return; }
      if (Notification.permission === 'denied') { setPushState('blocked'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushState(sub ? 'on' : 'off');
      } catch (_) { setPushState('off'); }
    })();
  }, [serverPush]);

  const toggle = async (key, ch, value) => {
    const next = cats.map((c) => (c.key === key ? { ...c, channels: { ...c.channels, [ch]: value } } : c));
    setCats(next);
    const body = {};
    for (const c of next) body[c.key] = c.channels;
    try {
      const d = await api.put('/spa/notifications/preferences', body);
      if (d.categories) setCats(d.categories);
      toast(t('notif.settings.saved', 'Preferences saved'));
    } catch (e) { toast(e.message, 'error'); load(); }
  };

  const enablePush = async () => {
    setBusy(true);
    try {
      // Only prompt when the browser hasn't decided yet.
      let perm = Notification.permission;
      if (perm !== 'granted') perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState(perm === 'denied' ? 'blocked' : 'off'); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(runtime.webPushPublicKey),
      });
      await api.post('/spa/notifications/push/subscriptions', { subscription: sub.toJSON(), label: navigator.userAgent.slice(0, 200) });
      setPushState('on');
      toast(t('notif.push.enabledToast', 'Browser notifications enabled on this device.'));
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const disablePush = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await api.post('/spa/notifications/push/unsubscribe', { endpoint }).catch(() => {});
      }
      setPushState('off');
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const removeDevice = async (id) => {
    try { await api.del(`/spa/notifications/push/subscriptions/${id}`); setSubs((cur) => cur.filter((s) => s.id !== id)); }
    catch (e) { toast(e.message, 'error'); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { await api.post('/spa/notifications/test', {}); toast(t('notif.push.testSent', 'Test notification sent — check the bell and this device.')); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const pushStatusText = {
    unsupported: t('notif.push.unsupported', 'This browser doesn’t support push notifications.'),
    blocked: t('notif.push.blocked', 'Notifications are blocked for this site in your browser settings.'),
    notConfigured: t('notif.push.notConfigured', 'Browser push isn’t set up on this server (no VAPID keys).'),
    on: t('notif.push.on', 'Enabled on this device'),
    off: t('notif.push.off', 'Not enabled on this device'),
    loading: '…',
  }[pushState];
  const kindLabel = (k) => t(`notif.push.kind.${k}`, k === 'webpush' ? 'Browser' : k === 'fcm' ? 'Android app' : 'iOS app');

  return (
    <>
      <SettingsHeader title={t('settings.nav.notifications', 'Notifications')}
                      subtitle={t('notif.settings.subtitle', 'Choose how you want to hear from us. Security emails (sign-in codes, password resets) are always sent.')} />

      <div className="card" style={{padding: 0, overflowX: 'auto'}}>
        {!cats ? <div className="empty">…</div> : (
          <table className="table notif-prefs">
            <thead><tr>
              <th>{t('notif.settings.col.category', 'Event')}</th>
              <th>{t('notif.settings.col.email', 'Email')}</th>
              <th>{t('notif.settings.col.inApp', 'In app')}</th>
              <th>{t('notif.settings.col.push', 'Push')}</th>
            </tr></thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.key}>
                  <td>
                    <div className="notif-cat">{t(`notif.category.${c.key}`, c.key)}</div>
                    {c.mandatory && <div className="form-hint" style={{marginTop: 2}}>{t('notif.settings.alwaysOn', 'Always on')}</div>}
                  </td>
                  {['email', 'inApp', 'push'].map((ch) => (
                    <td key={ch}>
                      <input type="checkbox"
                             checked={!!c.channels[ch]}
                             disabled={c.mandatory && ch !== 'push'}
                             aria-label={`${t(`notif.category.${c.key}`, c.key)} — ${t(`notif.settings.col.${ch}`, ch)}`}
                             onChange={(e) => toggle(c.key, ch, e.target.checked)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{marginTop: 16}}>
        <div className="card-title">{t('notif.push.title', 'Browser notifications')}</div>
        <p style={{color: 'var(--text-secondary)', fontSize: 14, margin: '6px 0 12px'}}>{t('notif.push.body', 'Get notified on this device even when Pluma isn’t open.')}</p>
        <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>
          <span className={`pill ${pushState === 'on' ? 'pill-active' : ''}`}>{pushStatusText}</span>
          {pushState === 'off' && <button className="btn btn-primary" onClick={enablePush} disabled={busy}>{t('notif.push.enable', 'Enable on this device')}</button>}
          {pushState === 'on' && <button className="btn" onClick={disablePush} disabled={busy}>{t('notif.push.disable', 'Disable on this device')}</button>}
          <button className="btn" onClick={sendTest} disabled={busy}>{t('notif.push.test', 'Send a test notification')}</button>
        </div>

        <div className="card-title" style={{marginTop: 20, fontSize: 14}}>{t('notif.push.devices', 'Devices receiving push')}</div>
        {subs.length === 0 ? (
          <p className="form-hint">{t('notif.push.noDevices', 'No devices yet.')} {t('notif.push.mobileHint', 'Mobile apps will appear here once they are registered.')}</p>
        ) : (
          <ul className="notif-devices">
            {subs.map((s) => (
              <li key={s.id}>
                <span className="pill">{kindLabel(s.kind)}</span>
                <span className="notif-device-label" title={s.label || ''}>{s.label || '—'}</span>
                <span className="form-hint">{formatDate(s.lastUsedAt || s.createdAt)}</span>
                <button className="btn" onClick={() => removeDevice(s.id)}>{t('notif.push.remove', 'Remove')}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function SettingsHeader({ title, subtitle, action }) {
  return (
    <div className="page-header">
      <div>
        <div className="page-title">{title}</div>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

// ============================================================================
// Settings → General — your own profile + interface preferences.
//
// Workspace identity (name, slug) lives in Settings → Workspaces, which
// handles rename + delete + create in one inline-edit list. Putting it
// here too would just duplicate that. Saves commit on blur so there's
// no save button to hunt for; errors revert to the server value.
// ============================================================================

// Settings → Allgemein. Interface preferences only — display name and
// email live on the Profile page (avatar dropdown → Profil bearbeiten),
// not duplicated here. Keeps this page focused on what's actually
// scoped to "the surface I'm looking at" (language, appearance).
function GeneralSettingsPage() {
  const t = useT();
  const toast = useToast();
  const { locale, setLocale, locales } = useI18n();
  const { theme, setTheme } = useTheme();

  const onLocaleChange = async (e) => {
    const next = e.target.value;
    try {
      await setLocale(next);
      toast(t('general.savedToast'));
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('settings.nav.general')}</div>
          <div className="page-subtitle">{t('general.subtitle')}</div>
        </div>
      </div>

      <div className="card">
        <div className="form-row">
          <label>{t('general.account.locale')}</label>
          <select className="input" value={locale} onChange={onLocaleChange}>
            {(locales || []).map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <div className="form-hint">{t('general.account.localeHint')}</div>
        </div>

        <div className="form-row">
          <label>{t('general.account.theme')}</label>
          <div className="theme-picker">
            {[
              { value: 'auto',  label: t('general.theme.auto') },
              { value: 'light', label: t('general.theme.light') },
              { value: 'dark',  label: t('general.theme.dark') },
            ].map((opt) => (
              <button key={opt.value} type="button"
                      className={'theme-option' + (theme === opt.value ? ' active' : '')}
                      onClick={() => setTheme(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="form-hint">{t('general.theme.hint')}</div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Settings → Image Library — tenant-scoped image asset manager. Same
// /spa/field-images endpoints as the inline picker in the template editor;
// this is the management surface (rename, batch view) that wouldn't fit in
// a modal. Drop a file anywhere on the page to upload.
// ============================================================================

function ImageLibraryPage() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { info: tenantInfo, refresh: refreshTenantInfo } = useTenantInfo();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const fileInputRef = React.useRef(null);

  // Tier caps — null on self-hosted (unlimited) and on any tenant whose
  // /me response hasn't loaded yet. The render handles "unknown yet" by
  // not showing the counter or pre-disabling, so the UX gracefully fills
  // in once tenantInfo arrives.
  const imageCap = tenantInfo?.limits?.images ?? null;
  const sizeCap  = tenantInfo?.limits?.maxImageBytes ?? null;
  const used     = images.length;
  const atCap    = imageCap != null && used >= imageCap;
  const sizeCapMB = sizeCap != null ? (sizeCap / 1024 / 1024).toFixed(0) : null;

  const load = async () => {
    setLoading(true);
    try {
      const { images } = await api.get('/spa/field-images');
      setImages(images);
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif)$/i.test(file.type)) {
      toast(t('imageLib.unsupported'), 'error');
      return;
    }
    // Client-side pre-checks against the cached tier caps. The server
    // re-validates authoritatively — these just fail fast without a
    // round-trip + presign.
    if (sizeCap != null && file.size > sizeCap) {
      window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
        detail: {
          code: 'image_too_large',
          body: { size: file.size, maxImageBytes: sizeCap, tier: tenantInfo?.plan },
        },
      }));
      return;
    }
    if (atCap) {
      window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
        detail: {
          code: 'image_limit_reached',
          body: { current: used, max: imageCap, tier: tenantInfo?.plan },
        },
      }));
      return;
    }
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    setUploading(true);
    try {
      const { url, key } = await api.post('/spa/field-images/upload-url', {
        contentType: file.type || 'image/png',
        extension: ext,
        size: file.size, // lets the server fail fast on size before presigning
      });
      const putRes = await fetch(url, {
        method: 'PUT', body: file,
        headers: { 'Content-Type': file.type || 'image/png' },
      });
      if (!putRes.ok) throw new Error(t('upload.failed', 'Upload failed. Please check your connection and try again.'));
      await api.post('/spa/field-images', {
        name: file.name.replace(/\.[^.]+$/, ''),
        s3Key: key,
        mimeType: file.type || 'image/png',
        size: file.size,
      });
      load();
      // Refresh the cached counter in tenantInfo so the "X / Y" header
      // updates immediately and a subsequent at-cap click shows the
      // modal instead of being one upload stale.
      refreshTenantInfo?.();
    } catch (err) {
      // errorFrom already dispatched the upgrade event for tier-cap codes.
      // For everything else, toast.
      if (err.code !== 'image_limit_reached' && err.code !== 'image_too_large') {
        toast(err.message, 'error');
      }
    }
    finally { setUploading(false); }
  };

  const remove = async (img) => {
    const ok = await confirm({
      title: t('imageLib.deleteConfirmTitle', 'Delete image?'),
      body: t('imageLib.confirmDelete'),
      confirmLabel: t('common.delete') || 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/field-images/${img.id}`);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  const startRename = (img) => {
    setRenamingId(img.id);
    setRenameDraft(img.name);
  };
  const commitRename = async (img) => {
    const next = renameDraft.trim();
    setRenamingId(null);
    if (!next || next === img.name) return;
    try {
      await api.patch(`/spa/field-images/${img.id}`, { name: next });
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Client-side filter — list is small, no need to plumb query params.
  const [search, setSearch] = useState('');
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter((i) => (i.name || '').toLowerCase().includes(q));
  }, [images, search]);

  return (
    <>
      <SettingsHeader
        title={
          <>
            {t('settings.nav.imageLibrary')}
            {imageCap != null && (
              <span style={{
                marginLeft: 10, fontSize: 13, fontWeight: 500,
                color: atCap ? 'var(--danger)' : 'var(--text-muted)',
              }}>
                ({used} / {imageCap})
              </span>
            )}
          </>
        }
        subtitle={
          <>
            {t('imageLib.subtitle')}
            {sizeCapMB && (
              <span style={{display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 4}}>
                {t('imageLib.sizeHint', { mb: sizeCapMB })}
              </span>
            )}
          </>
        }
        action={
          atCap ? (
            <button
              className="btn btn-primary"
              onClick={() => window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
                detail: {
                  code: 'image_limit_reached',
                  body: { current: used, max: imageCap, tier: tenantInfo?.plan },
                },
              }))}
              title={t('imageLib.atCap', 'You\'ve hit your plan\'s image limit')}
            >
              {t('upgrade.cta', 'See plans')}
            </button>
          ) : (
            <>
              {/* A real button (keyboard reachable); the hidden input is
                  opened programmatically. */}
              <button type="button" className="btn btn-primary" disabled={uploading}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                {uploading ? t('imageLib.uploading') : '+ ' + t('imageLib.upload')}
              </button>
              <input ref={fileInputRef} type="file" style={{display: 'none'}} tabIndex={-1} aria-hidden="true"
                     accept="image/png,image/jpeg,image/gif"
                     multiple={false}
                     disabled={uploading}
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            </>
          )
        }
      />

      {!loading && images.length > 0 && (
        <div style={{marginBottom: 12}}>
          <input className="input" type="search"
                 placeholder={t('imageLib.searchPlaceholder', 'Search images by name…')}
                 aria-label={t('imageLib.searchPlaceholder', 'Search images by name…')}
                 value={search}
                 onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}

      {loading ? (
        <LoadingFrame label={t('common.loading')} />
      ) : images.length === 0 ? (
        <div className="settings-empty" role="button" tabIndex={0}
             style={{cursor: 'pointer'}}
             onClick={() => fileInputRef.current && fileInputRef.current.click()}
             onKeyDown={keyActivate(() => fileInputRef.current && fileInputRef.current.click())}
             onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
             onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
             onDrop={(e) => {
               e.preventDefault();
               e.currentTarget.classList.remove('dragover');
               const f = e.dataTransfer.files?.[0];
               if (f) upload(f);
             }}>
          <p>{t('imageLib.empty')}</p>
          {/* The size limit depends on the plan — the old copy promised
              8 MB everywhere while hosted plans allow 1–5 MB. */}
          <p style={{fontSize: 12, marginTop: 8}}>
            {sizeCap
              ? t('imageLib.dropHereSized', 'Drop an image here, or click to choose a file. PNG, JPG or GIF, up to {{mb}} MB.', { mb: Math.round(sizeCap / 1024 / 1024) })
              : t('imageLib.dropHereAny', 'Drop an image here, or click to choose a file. PNG, JPG or GIF.')}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="settings-empty">{t('imageLib.noMatch', 'No images match “{{q}}”.', { q: search })}</div>
      ) : filtered.map((img) => (
        <div key={img.id} className="settings-card image-card">
          <div className="image-card-thumb">
            <img src={img.url} alt={img.name} />
          </div>
          <div className="settings-card-info">
            <div className="settings-card-title-row">
              {renamingId === img.id ? (
                <input className="input" autoFocus
                       value={renameDraft}
                       onChange={(e) => setRenameDraft(e.target.value)}
                       onBlur={() => commitRename(img)}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') commitRename(img);
                         if (e.key === 'Escape') setRenamingId(null);
                       }}
                       style={{maxWidth: 320, fontWeight: 600, fontSize: 13}} />
              ) : (
                <div className="settings-card-title" style={{cursor:'pointer'}}
                     onClick={() => startRename(img)}
                     title={t('imageLib.rename')}>{img.name}</div>
              )}
            </div>
            <div className="settings-card-meta">
              {formatSize(img.size)} · {img.mimeType} · {t('imageLib.uploaded')}: {formatDate(img.createdAt)}
            </div>
          </div>
          <div className="settings-card-actions">
            <ActionMenu items={[
              { label: t('imageLib.rename'), onClick: () => startRename(img) },
              { label: t('common.delete'), onClick: () => remove(img), danger: true },
            ]} />
          </div>
        </div>
      ))}
    </>
  );
}

// ============================================================================
// Settings → Templates — tenant-wide rendering defaults for number /
// currency fields and the render-PDF retention TTL (Phase C #12).
// Currency/number format options bake in the four
// US-vs-EU × prefix-vs-suffix combinations the spec calls out; the
// selection drives BOTH the renderer's format pass and the small
// inline preview shown next to each option.
// ============================================================================

// Retention presets for "Keep generated PDFs for" (Settings → Template
// defaults). Paid plans from Advanced up have no cap — these are limits a
// tenant may set for itself. Free keeps PDFs 30 minutes and Essentials 90
// days (the longest preset), so on Free the longer chips render locked
// with an upgrade hint instead of being hidden.
const TENANT_TTL_PRESETS = [
  { seconds: 30 * 60,           minTierIdx: 0 },
  { seconds: 60 * 60,           minTierIdx: 1 },
  { seconds: 24 * 60 * 60,      minTierIdx: 1 },
  { seconds: 7 * 24 * 60 * 60,  minTierIdx: 1 },
  { seconds: 30 * 24 * 60 * 60, minTierIdx: 1 },
  { seconds: 90 * 24 * 60 * 60, minTierIdx: 1 },
];
const TENANT_PLAN_ORDER = ['free', 'essentials', 'advanced', 'pro', 'enterprise'];
// Longest limit a tenant can set (the server's MAX_TTL_SECONDS); longer
// is "Forever".
const TENANT_TTL_MAX_SECONDS = 365 * 86400;

// LabelWithInfo — replaces long inline help paragraphs with a small
// "i" icon next to the section title. Hover or focus surfaces the
// full text as a styled tooltip so the form stays scannable but the
// detail is still one micro-interaction away.
//
// Short one-liners stay inline (a sentence is faster to read than
// hovering a tooltip). Use this only when the explanation runs to
// two-plus sentences or covers cross-references.
// Click-to-copy inline value — the enterprise affordance for IDs and
// hashes users need to paste into API calls or support tickets. Renders
// the value in a monospace chip with a copy icon; a successful copy
// flips to a check for ~1s and toasts.
// An IP address — personal data. Shown blurred; readable only while it's
// pressed (mouse / touch held down, or Space / Enter held), so it doesn't
// end up in screenshots, screen shares or over someone's shoulder.
function SensitiveIp({ value }) {
  const t = useT();
  const [shown, setShown] = useState(false);
  const show = () => setShown(true);
  const hide = () => setShown(false);
  return (
    <button type="button" className={'sensitive-ip' + (shown ? ' is-shown' : '')}
            title={t('privacy.holdToReveal', 'Click and hold to show')}
            aria-label={shown ? value : t('privacy.ipHidden', 'IP address hidden — press and hold to show')}
            onPointerDown={(e) => {
              e.preventDefault();
              // Keep receiving the release even if the pointer drifts off.
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* not supported */ }
              show();
            }}
            onPointerUp={hide} onPointerCancel={hide} onLostPointerCapture={hide}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); show(); } }}
            onKeyUp={hide} onBlur={hide}
            onContextMenu={(e) => e.preventDefault()}>
      {value}
    </button>
  );
}

function CopyValue({ value, mono = true, style }) {
  const t = useT();
  const toast = useToast();
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast(t('common.copied', 'Copied'), 'success');
      setTimeout(() => setCopied(false), 1000);
    } catch { toast(t('common.copyFailed', 'Couldn\u2019t copy to clipboard'), 'error'); }
  };
  return (
    <button type="button" onClick={copy} title={t('common.clickToCopy', 'Click to copy')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: 'inherit', font: 'inherit', textAlign: 'left', ...style }}>
      <code style={{ fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontSize: 12,
        wordBreak: 'break-all', minWidth: 0 }}>{value}</code>
      <span aria-hidden="true" style={{ flexShrink: 0, opacity: 0.55, fontSize: 12 }}>{copied ? '\u2713' : '\u29c9'}</span>
    </button>
  );
}

function LabelWithInfo({ children, info, htmlFor }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, position: 'relative'}}>
      <label className="label" htmlFor={htmlFor} style={{marginBottom: 0}}>{children}</label>
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              aria-label="More info"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, padding: 0,
                background: 'transparent', border: 'none',
                color: 'var(--text-muted)', cursor: 'help',
                borderRadius: 999,
              }}>
        <Icons.Info />
      </button>
      {open && (
        <div role="tooltip"
             style={{
               position: 'absolute', top: '100%', left: 0, marginTop: 4,
               zIndex: 50,
               background: 'var(--bg-elevated)',
               border: '1px solid var(--border)',
               borderRadius: 'var(--radius)',
               padding: '8px 10px',
               fontSize: 12, lineHeight: 1.45,
               color: 'var(--text-secondary)',
               maxWidth: 340, minWidth: 220,
               boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
             }}>
          {info}
        </div>
      )}
    </div>
  );
}

// ArtifactLifetimeChips — the tenant's "Keep generated PDFs for" picker.
//
//   - "Forever" = kept until deleted. On paid plans that's the plan
//     default, so it shows for an unset value (null) and saves null.
//   - "Plan default" = nothing saved, on plans whose default is a
//     duration (Free: 30 min; self-hosted: 72 h).
//   - Preset chips = a limit the tenant sets for itself. On Free the ones
//     above its 30-minute cap render locked with an upgrade tooltip.
//   - "Custom" opens an amount + unit input (paid plans), up to a year.
// planDefaultSeconds / planMaxSeconds (null = no cap) come from the
// server (account/me limits); until those have loaded (undefined), the
// plan stands in.
function ArtifactLifetimeChips({ value, onChange, plan, tierGated = true, canKeepForever, planDefaultSeconds, planMaxSeconds }) {
  const t = useT();
  // When tierGated is false (self-hosted), treat the operator as if
  // they were on the top tier — every preset unlocks and the upsell
  // never fires. Self-hosted operators own their infra; the SaaS-tier
  // ceilings exist to gate hosted storage cost, which doesn't apply
  // when the renders sit on their own disk.
  const rawTierIdx = Math.max(0, TENANT_PLAN_ORDER.indexOf(plan));
  const tierIdx = tierGated ? rawTierIdx : (TENANT_PLAN_ORDER.length - 1);
  const isFree = tierIdx === 0;
  const planLabel = (key) => t('plan.' + key, key);
  const tierDefaultSeconds = planDefaultSeconds ?? (!tierGated ? 72 * 60 * 60 : isFree ? 30 * 60 : 0);
  const tierMaxSeconds = planMaxSeconds !== undefined ? planMaxSeconds : (tierGated && isFree ? 30 * 60 : null);
  // 0 = kept until deleted (the "Forever" chip); the server's
  // permanentRenders flag says who may pick it (every paid plan).
  const foreverAllowed = canKeepForever ?? !(tierGated && isFree);
  const defaultIsForever = tierDefaultSeconds === 0;
  const forever = value === 0 || (value == null && defaultIsForever);
  const isPreset = (v) => TENANT_TTL_PRESETS.some((p) => p.seconds === v);
  // A saved value equal to the plan default shows as "Plan default" when
  // no preset matches it.
  const showsPlanDefault = (v) => !defaultIsForever && (v == null || (v === tierDefaultSeconds && !isPreset(v)));
  const needsCustom = (v) => v != null && v !== 0 && !isPreset(v) && !showsPlanDefault(v);

  // Custom input. The stored value is always seconds; the input shows it
  // in the largest unit that divides it evenly. Switching units converts
  // the amount, so the stored seconds never drift.
  const TTL_UNITS = [
    { id: 'minutes', label: t('settings.templates.ttl.unitMinutes', 'minutes'), secondsPerUnit: 60 },
    { id: 'hours',   label: t('settings.templates.ttl.unitHours', 'hours'),     secondsPerUnit: 60 * 60 },
    { id: 'days',    label: t('settings.templates.ttl.unitDays', 'days'),       secondsPerUnit: 24 * 60 * 60 },
  ];
  const naturalUnit = (seconds) => {
    if (!seconds) return 'minutes';
    if (seconds % 86400 === 0) return 'days';
    if (seconds % 3600 === 0) return 'hours';
    return 'minutes';
  };
  const [customOpen, setCustomOpen] = useState(() => needsCustom(value));
  const [customUnit, setCustomUnit] = useState(() => naturalUnit(value));
  // Re-derive chip and unit when the value changes from outside (org
  // switch, refetch) — but not on our own edits: typing "14" days passes
  // through "1" day, which matches a preset and used to close the input
  // mid-number.
  const emitted = useRef();
  const emit = (v) => { emitted.current = v; onChange(v); };
  useEffect(() => {
    if (value === emitted.current) return;
    setCustomOpen(needsCustom(value));
    setCustomUnit(naturalUnit(value));
  }, [value, tierDefaultSeconds]); // eslint-disable-line react-hooks/exhaustive-deps
  const unitMeta = TTL_UNITS.find((u) => u.id === customUnit) || TTL_UNITS[0];
  const customAmount = value == null || forever
    ? ''
    : String(Math.max(1, Math.round(value / unitMeta.secondsPerUnit)));
  const pick = (v) => { setCustomOpen(false); emit(v); };

  // A notice appears only when a typed Custom value can't be kept: over
  // Free's cap (with the upgrade that lifts it), or over a year, the
  // longest limit there is ("Forever" keeps PDFs longer). The picker
  // stays quiet at rest.
  const exceedsTier = value != null && value > 0 && tierMaxSeconds != null && value > tierMaxSeconds;
  const tooLong = value != null && value > TENANT_TTL_MAX_SECONDS;
  const upgradeTarget = exceedsTier && tierGated && tierIdx < TENANT_PLAN_ORDER.indexOf('advanced') ? 'advanced' : null;

  // Stronger contrast than the previous transparent-border variant.
  // The dark-mode complaint was that the chips disappeared into the
  // panel: borders read as #1c1c24 on #15151c → ~3% luminance gap.
  // bg-elevated gives the rest state a real surface; the active state
  // gets a brighter accent fill + bold text instead of just a border.
  const chipBase = {
    padding: '6px 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border-strong, var(--border))',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 500,
    transition: 'border-color 0.12s, background 0.12s',
  };
  const chipActive = {
    borderColor: 'var(--accent)',
    background: 'var(--accent)',
    color: 'white',
    fontWeight: 600,
    boxShadow: '0 0 0 1px var(--accent)',
  };
  const chipLocked = {
    color: 'var(--text-muted)',
    cursor: 'not-allowed',
    opacity: 0.55,
  };

  return (
    <div>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8}}>
        {!defaultIsForever && (
          <button type="button"
                  onClick={() => pick(null)}
                  title={t('settings.templates.ttl.planDefaultHint', 'Use your plan’s default. It changes when your plan does.')}
                  style={{...chipBase, ...(!customOpen && showsPlanDefault(value) ? chipActive : {})}}>
            {t('settings.templates.ttl.planDefault', 'Plan default ({{duration}})', { duration: formatDuration(tierDefaultSeconds, t) })}
          </button>
        )}

        {TENANT_TTL_PRESETS.map((p) => {
          const locked = p.minTierIdx > tierIdx;
          const active = !customOpen && value === p.seconds;
          const label = formatDuration(p.seconds, t);
          return (
            <button key={p.seconds}
                    type="button"
                    onClick={() => { if (!locked) pick(p.seconds); }}
                    disabled={locked}
                    title={locked
                      ? t('settings.templates.ttl.locked', 'Upgrade to {{plan}} to choose {{duration}}.',
                          { plan: planLabel(TENANT_PLAN_ORDER[p.minTierIdx]), duration: label })
                      : t('settings.templates.ttl.pick', 'Keep generated PDFs for {{duration}}.', { duration: label })}
                    style={{
                      ...chipBase,
                      ...(active ? chipActive : {}),
                      ...(locked ? chipLocked : {}),
                    }}>
              {locked && <Icon name="lock" className="icon-inline" />}
              {label}
            </button>
          );
        })}

        <button type="button"
                onClick={() => { if (foreverAllowed) pick(defaultIsForever ? null : 0); }}
                disabled={!foreverAllowed}
                title={foreverAllowed
                  ? t('settings.templates.keepForeverHint', 'Keep rendered PDFs until you delete them.')
                  : t('settings.templates.keepForeverLockedPlan', 'Upgrade to {{plan}} to keep rendered PDFs until you delete them.', { plan: planLabel('advanced') })}
                style={{
                  ...chipBase,
                  ...(forever && !customOpen ? chipActive : {}),
                  ...(!foreverAllowed ? chipLocked : {}),
                }}>
          {!foreverAllowed && <Icon name="lock" className="icon-inline" />}
          {t('settings.templates.keepForever', 'Forever')}
        </button>

        {!isFree && (
          <button type="button"
                  onClick={() => setCustomOpen((v) => !v)}
                  title={t('settings.templates.ttl.customHint', 'Pick your own duration.')}
                  style={{
                    ...chipBase,
                    ...(customOpen ? chipActive : {}),
                  }}>
            {t('settings.templates.ttl.custom', 'Custom')}
          </button>
        )}
      </div>

      {forever && !customOpen && (
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('settings.templates.keepForeverNote',
            'Rendered PDFs stay until you delete them on the Documents page or with DELETE /renders/{id}. An API call can still set its own ttlSeconds.')}
        </div>
      )}

      {customOpen && (
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap'}}>
          <input className="input"
                 style={{maxWidth: 140}}
                 type="number"
                 min={1}
                 aria-label={t('settings.templates.ttl.custom', 'Custom')}
                 placeholder={unitMeta.label}
                 value={customAmount}
                 onChange={(e) => {
                   const v = e.target.value.replace(/[^0-9]/g, '');
                   if (v === '') return emit(null);
                   const amount = Math.max(1, parseInt(v, 10) || 0);
                   emit(amount * unitMeta.secondsPerUnit);
                 }} />
          <select className="select" style={{maxWidth: 140}}
                  value={customUnit}
                  onChange={(e) => {
                    // Convert in place: keep the displayed amount, swap
                    // the unit, recompute seconds. So "2 days" → switch to
                    // hours → "48 hours" → switch to minutes → "2880
                    // minutes". The operator stays oriented; the stored
                    // value never drifts.
                    const nextUnit = e.target.value;
                    const next = TTL_UNITS.find((u) => u.id === nextUnit) || TTL_UNITS[0];
                    setCustomUnit(nextUnit);
                    if (value) {
                      const amount = Math.max(1, Math.round(value / unitMeta.secondsPerUnit));
                      emit(amount * next.secondsPerUnit);
                    }
                  }}>
            {TTL_UNITS.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </div>
      )}

      {(exceedsTier || tooLong) && (
        <div role="alert"
             style={{
               marginTop: 10,
               padding: '8px 12px',
               border: '1px solid var(--accent)',
               borderRadius: 'var(--radius)',
               background: 'var(--accent-glow, rgba(108, 92, 231, 0.10))',
               color: 'var(--text-primary)',
               fontSize: 12,
               lineHeight: 1.45,
               display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
             }}>
          <Icon name="lock" />
          <span style={{flex: 1}}>
            {upgradeTarget
              ? t('settings.templates.ttl.capNotice', 'Your {{plan}} plan keeps PDFs for at most {{max}}. Upgrade to {{target}} to keep this duration.',
                  { plan: planLabel(TENANT_PLAN_ORDER[tierIdx]), max: formatDuration(tierMaxSeconds, t), target: planLabel(upgradeTarget) })
              : exceedsTier
                ? t('settings.templates.ttl.capMax', 'PDFs are kept for at most {{max}}; a longer value is shortened to that.',
                    { max: formatDuration(tierMaxSeconds, t) })
                : t('settings.templates.ttl.maxYear', 'A limit can be at most 365 days. Choose “Forever” to keep PDFs longer.')}
          </span>
          {upgradeTarget && (
            <a href="#" onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent('pluma:navigate', {
                detail: { to: 'page', page: 'billing' },
              }));
            }} style={{
              color: 'var(--accent)', fontWeight: 600, whiteSpace: 'nowrap',
            }}>{t('billing.upgrade', 'Upgrade')} →</a>
          )}
        </div>
      )}
    </div>
  );
}

// Lookup tables shared between the settings UI and the inline preview.
// Centralised so adding a future locale only touches one place.
const CURRENCY_FORMATS = [
  { id: 'symbol-before-us', body: '1,000.00', after: false },
  { id: 'symbol-after-us',  body: '1,000.00', after: true  },
  { id: 'symbol-before-eu', body: '1.000,00', after: false },
  { id: 'symbol-after-eu',  body: '1.000,00', after: true  },
];
// ISO 4217 → display symbol for the inline preview. Falls back to the
// code itself when unmapped — that's the right behaviour for less-common
// currencies (CHF, SEK, NOK, …) where the code IS the customary symbol.
const CURRENCY_SYMBOL = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥',
  TRY: '₺', INR: '₹', RUB: '₽', KRW: '₩', BRL: 'R$',
  CAD: '$', AUD: '$', NZD: '$', MXN: '$', HKD: '$',
  SGD: '$', PHP: '₱', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  THB: '฿', ILS: '₪', UAH: '₴', VND: '₫',
};
const currencySymbolFor = (code) => CURRENCY_SYMBOL[(code || '').toUpperCase()]
  || (code || '$').toUpperCase();
const currencyFormatPreview = (opt, code) => {
  const sym = currencySymbolFor(code);
  return opt.after ? `${opt.body}${sym}` : `${sym}${opt.body}`;
};
const NUMBER_FORMATS = [
  { id: 'us-grouped', preview: '1,000.999' },
  { id: 'eu-grouped', preview: '1.000,999' },
  { id: 'us-plain',   preview: '1000.999' },
  { id: 'eu-plain',   preview: '1000,999' },
];

// ISO 4217 minor-unit count for the currencies we surface in the
// dropdown. Drives the renderer's decimal handling on currency fields —
// previously a per-field knob, now derived from the selected code so
// JPY ¥1000 and EUR €1.00 each get the right shape automatically.
// Anything not listed defaults to 2 (the common case).
const CURRENCY_DECIMALS = {
  JPY: 0, KRW: 0, VND: 0,
  // Two-decimal majors (USD, EUR, GBP, CHF, CAD, AUD, NZD, SEK, NOK,
  // DKK, PLN, CZK, RON, TRY, RUB, UAH, INR, SGD, HKD, MXN, BRL, ZAR,
  // AED, SAR, ILS, CNY, HUF) all fall through the default.
};
const currencyDecimalsFor = (code) =>
  CURRENCY_DECIMALS[(code || '').toUpperCase()] ?? 2;

// Currency dropdown — shared between Settings → Templates (default
// currency) and the per-field currency property. Same list, same
// grouping, so the operator sees one consistent palette.
function CurrencyOptions({ t }) {
  return (
    <>
      <option value="">— none —</option>
      <optgroup label={t('settings.templates.currencyCommonGroup', 'Common')}>
        <option value="USD">$ — USD (US Dollar)</option>
        <option value="EUR">€ — EUR (Euro)</option>
        <option value="GBP">£ — GBP (British Pound)</option>
      </optgroup>
      <optgroup label={t('settings.templates.currencyMoreGroup', 'More')}>
        <option value="CHF">CHF — Swiss Franc</option>
        <option value="JPY">¥ — JPY (Japanese Yen)</option>
        <option value="CNY">¥ — CNY (Chinese Yuan)</option>
        <option value="CAD">$ — CAD (Canadian Dollar)</option>
        <option value="AUD">$ — AUD (Australian Dollar)</option>
        <option value="NZD">$ — NZD (New Zealand Dollar)</option>
        <option value="SEK">SEK — Swedish Krona</option>
        <option value="NOK">NOK — Norwegian Krone</option>
        <option value="DKK">DKK — Danish Krone</option>
        <option value="PLN">PLN — Polish Zloty</option>
        <option value="CZK">CZK — Czech Koruna</option>
        <option value="HUF">HUF — Hungarian Forint</option>
        <option value="RON">RON — Romanian Leu</option>
        <option value="TRY">₺ — TRY (Turkish Lira)</option>
        <option value="RUB">₽ — RUB (Russian Ruble)</option>
        <option value="UAH">₴ — UAH (Ukrainian Hryvnia)</option>
        <option value="INR">₹ — INR (Indian Rupee)</option>
        <option value="SGD">$ — SGD (Singapore Dollar)</option>
        <option value="HKD">$ — HKD (Hong Kong Dollar)</option>
        <option value="KRW">₩ — KRW (South Korean Won)</option>
        <option value="MXN">$ — MXN (Mexican Peso)</option>
        <option value="BRL">R$ — BRL (Brazilian Real)</option>
        <option value="ZAR">R — ZAR (South African Rand)</option>
        <option value="AED">د.إ — AED (UAE Dirham)</option>
        <option value="SAR">﷼ — SAR (Saudi Riyal)</option>
        <option value="ILS">₪ — ILS (Israeli Shekel)</option>
      </optgroup>
    </>
  );
}

function TemplatesSettingsPage() {
  const t = useT();
  const { current, refresh } = useTenant();
  const { info: tenantInfo } = useTenantInfo();
  const toast = useToast();
  // Runtime feature flags — drive the deployment-mode branches below
  // (suppress S3 response when the active backend can't presign,
  // upsell signed-PDF delivery on self-hosted under non-enterprise
  // licenses, suppress tier-gating on the artifact-lifetime chips
  // on self-hosted).
  const runtime = useRuntime();
  const tierGatingEnabled = !!runtime?.features?.tierGating;
  // Combined signature gate. Wired on every deployment; on self-hosted
  // the enterprise license is the unlock — non-enterprise installs
  // see an upsell prompt instead of the delivery panel.
  const signaturesWiredAtAll = !!runtime?.features?.signatures;
  const needsEnterpriseLicense = !!runtime?.features?.signaturesRequireEnterpriseLicense;
  const planUnlocksSignatures = !needsEnterpriseLicense || current?.plan === 'enterprise';
  // Server-side requireSignatureFeature also preflights SMTP — when
  // it's missing on a self-hosted enterprise install the workflow
  // can't deliver mail, so the panel surfaces a configure-SMTP
  // notice instead of pretending it works. emailWired is always true
  // on hosted, so this only ever fires on self-hosted.
  const emailWired = !!runtime?.features?.emailWired;
  const signaturesEnabled = signaturesWiredAtAll && planUnlocksSignatures && emailWired;
  const signaturesBlockedBySmtp = signaturesWiredAtAll && planUnlocksSignatures && !emailWired;
  // Local form state — pre-filled from the tenant on first render.
  // Each setter writes locally; Save flushes the whole shape in one
  // PATCH so the operator can revert any in-progress edit with the
  // Cancel button without round-tripping the server.
  const [currency, setCurrency] = useState((current?.defaultCurrency || 'USD'));
  const [curFmt, setCurFmt] = useState(current?.currencyFormat || 'symbol-before-us');
  const [numFmt, setNumFmt] = useState(current?.numberFormat || 'us-grouped');
  // Render TTL — stored as seconds (null = "use the tier default").
  // Surfaced through the ArtifactLifetimeChips picker below so the operator
  // picks a meaningful duration, not a raw number.
  const [ttlSeconds, setTtlSeconds] = useState(
    current?.defaultRenderTtlSeconds ?? null,
  );
  // (renderResponseMode was removed — /generate picks bytes vs JSON
  // via the Accept header; no per-tenant default needed.)
  // Phase D follow-up — signed-PDF delivery routing (moved from
  // per-template). One inbox + CC/BCC for the whole tenant. CC + BCC
  // are managed as comma-separated strings — matches the operator's
  // address-book paste flow.
  const [deliveryEmail, setDeliveryEmail] = useState(current?.signedPdfDeliveryEmail || '');
  const [ccStr, setCcStr] = useState((current?.signedPdfDeliveryCcEmails || []).join(', '));
  const [bccStr, setBccStr] = useState((current?.signedPdfDeliveryBccEmails || []).join(', '));
  const [busy, setBusy] = useState(false);

  // Re-sync when the tenant context refreshes underneath us (e.g.
  // org switch). Without this the form would keep showing the prior
  // org's settings after switchTo().
  useEffect(() => {
    setCurrency(current?.defaultCurrency || 'USD');
    setCurFmt(current?.currencyFormat || 'symbol-before-us');
    setNumFmt(current?.numberFormat || 'us-grouped');
    setTtlSeconds(current?.defaultRenderTtlSeconds ?? null);
    setDeliveryEmail(current?.signedPdfDeliveryEmail || '');
    setCcStr((current?.signedPdfDeliveryCcEmails  || []).join(', '));
    setBccStr((current?.signedPdfDeliveryBccEmails || []).join(', '));
  }, [current?.id]);

  const parseList = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!current?.id) return;
    setBusy(true);
    try {
      const body = {
        defaultCurrency: (currency || '').trim() || null,
        currencyFormat: curFmt,
        numberFormat: numFmt,
        defaultRenderTtlSeconds: ttlSeconds,
        signedPdfDeliveryEmail: deliveryEmail.trim() || null,
        signedPdfDeliveryCcEmails:  parseList(ccStr),
        signedPdfDeliveryBccEmails: parseList(bccStr),
      };
      await api.patch(`/spa/tenants/${current.id}`, body);
      await refresh();
      toast(t('templates.settingsSaved', 'Settings saved'), 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SettingsHeader
        title={t('settings.templates.heading', 'Template defaults')}
        subtitle={t('settings.templates.intro',
          'Tenant-wide defaults applied at render time. Per-field overrides on number / currency fields and per-API-call overrides on render TTL still win.')}
      />

      {/* Default currency — dropdown with the three most-common
          codes pinned to the top + a longer ISO 4217 tail underneath.
          Currency fields seed from this when first created; the field
          still owns the final value and can deviate per-template. */}
      <div className="prop-group">
        <label className="label">{t('settings.templates.defaultCurrency', 'Default currency')}</label>
        <select className="select"
                value={currency.toUpperCase()}
                onChange={(e) => setCurrency(e.target.value)}>
          <CurrencyOptions t={t} />
        </select>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {t('settings.templates.defaultCurrencyHint', 'Newly created currency fields start with this value.')}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('settings.templates.currencyFormat', 'Currency format')}</label>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6}}>
          {CURRENCY_FORMATS.map((opt) => (
            <label key={opt.id}
                   style={{
                     display: 'flex', alignItems: 'center', gap: 8,
                     padding: '8px 10px',
                     border: '1px solid ' + (curFmt === opt.id ? 'var(--accent)' : 'var(--border)'),
                     borderRadius: 'var(--radius)',
                     background: curFmt === opt.id ? 'rgba(108, 92, 231, 0.08)' : 'transparent',
                     cursor: 'pointer',
                   }}>
              <input type="radio" name="curFmt" checked={curFmt === opt.id}
                     onChange={() => setCurFmt(opt.id)} />
              <span style={{fontFamily: 'var(--font-mono)', fontSize: 13}}>
                {currencyFormatPreview(opt, currency)}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="prop-group">
        <label className="label">{t('settings.templates.numberFormat', 'Number format')}</label>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6}}>
          {NUMBER_FORMATS.map((opt) => (
            <label key={opt.id}
                   style={{
                     display: 'flex', alignItems: 'center', gap: 8,
                     padding: '8px 10px',
                     border: '1px solid ' + (numFmt === opt.id ? 'var(--accent)' : 'var(--border)'),
                     borderRadius: 'var(--radius)',
                     background: numFmt === opt.id ? 'rgba(108, 92, 231, 0.08)' : 'transparent',
                     cursor: 'pointer',
                   }}>
              <input type="radio" name="numFmt" checked={numFmt === opt.id}
                     onChange={() => setNumFmt(opt.id)} />
              <span style={{fontFamily: 'var(--font-mono)', fontSize: 13}}>{opt.preview}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="prop-group">
        <LabelWithInfo info={t('settings.templates.artifactLifetimeHint',
          'How long finished PDFs stay downloadable. Paid plans keep them until you delete them unless you set a limit here; the Free plan keeps them 30 minutes. An API call can set its own value.')}>
          {t('settings.templates.artifactLifetime', 'Artifact lifetime')}
        </LabelWithInfo>
        <ArtifactLifetimeChips value={ttlSeconds}
                               onChange={setTtlSeconds}
                               plan={current?.plan || 'free'}
                               tierGated={tierGatingEnabled}
                               canKeepForever={tenantInfo?.limits?.permanentRenders}
                               planDefaultSeconds={tenantInfo?.limits?.defaultRenderTtlSeconds}
                               planMaxSeconds={tenantInfo?.limits ? tenantInfo.limits.maxRenderTtlSeconds : undefined} />
      </div>

      {/* Signed-PDF delivery routes the signed PDF over email after a
          recipient finishes the /sign-requests workflow. The workflow
          is wired on both deployment modes; on self-hosted it
          requires an enterprise license to unlock. When the install
          is self-hosted + non-enterprise we render an upsell card in
          place of the panel so the operator knows the feature exists
          and how to enable it. */}
      {signaturesWiredAtAll && !signaturesEnabled && !signaturesBlockedBySmtp && (
        <div className="prop-group">
          <LabelWithInfo info={t('settings.templates.signedPdfDeliveryHint',
            'E-signature requests deliver the signed PDF over email after the recipient completes /sign-requests. Requires an enterprise license on self-hosted installs.')}>
            {t('settings.templates.signedPdfDelivery', 'Signed-PDF delivery')}
          </LabelWithInfo>
          <div className="prop-group" style={{
            border: '1px dashed var(--border-strong, var(--border))',
            borderRadius: 'var(--radius)',
            padding: 12,
            background: 'var(--bg-elevated)',
            marginTop: 6,
          }}>
            <div style={{fontWeight: 600, fontSize: 13, marginBottom: 4}}>
              {t('settings.templates.signedPdfUpsellTitle', 'Enterprise license required')}
            </div>
            <div style={{fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5}}>
              {t('settings.templates.signedPdfUpsellBody',
                'E-signature requests and signed-PDF email delivery are part of the enterprise tier on self-hosted installs. Activate or upgrade your license to enable this feature.')}
            </div>
            <a href="#/license" onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('pluma:navigate', {detail: {to: 'page', page: 'license'}}));
              }}
               className="btn btn-sm btn-primary"
               style={{textDecoration: 'none'}}>
              {t('settings.templates.signedPdfUpsellCta', 'Manage license')}
            </a>
          </div>
        </div>
      )}
      {/* License OK but SMTP missing — the workflow would silently
          no-op, which is worse than refusing. We show a distinct
          warning card with the env-var the operator needs to set
          rather than the "upgrade license" path, since this isn't a
          tier problem. */}
      {signaturesBlockedBySmtp && (
        <div className="prop-group">
          <LabelWithInfo info={t('settings.templates.signedPdfDeliveryHint',
            'E-signature requests deliver the signed PDF over email after the recipient completes /sign-requests.')}>
            {t('settings.templates.signedPdfDelivery', 'Signed-PDF delivery')}
          </LabelWithInfo>
          <div className="prop-group" style={{
            border: '1px dashed var(--accent)',
            borderRadius: 'var(--radius)',
            padding: 12,
            background: 'var(--bg-elevated)',
            marginTop: 6,
          }}>
            <div style={{fontWeight: 600, fontSize: 13, marginBottom: 4}}>
              {t('settings.templates.smtpMissingTitle', 'SMTP is not configured')}
            </div>
            <div style={{fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5}}>
              {t('settings.templates.smtpMissingBody',
                'Your enterprise license unlocks e-signature requests, but the workflow needs transactional email to deliver invites and signed PDFs. Set SMTP_URL in the container environment and restart, then come back to this page.')}
            </div>
            <pre style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: 8,
              fontSize: 11,
              color: 'var(--text-primary)',
              margin: 0,
              overflowX: 'auto',
            }}>{`SMTP_URL=smtp://user:pass@smtp.example.com:587
EMAIL_FROM=Pluma <no-reply@your-company.com>`}</pre>
          </div>
        </div>
      )}
      {signaturesEnabled && (
        <div className="prop-group">
          <LabelWithInfo info={(
            <>
              When a recipient signs via the <code>/sign-requests</code> workflow, the re-rendered PDF is
              mailed to <strong>To</strong> (defaulting to the tenant owner's email when blank), with CC and BCC
              included. Up to 10 entries per list.
            </>
          )}>
            {t('settings.templates.signedPdfDelivery', 'Signed-PDF delivery')}
          </LabelWithInfo>

          <label className="label" style={{fontSize: 11, marginTop: 4}}>
            {t('settings.templates.signedPdfDeliveryTo', 'To')}
          </label>
          <input className="input"
                 placeholder={t('settings.templates.signedPdfDeliveryToPlaceholder', "(empty = tenant owner's email)")}
                 value={deliveryEmail}
                 onChange={(e) => setDeliveryEmail(e.target.value)} />

          <label className="label" style={{fontSize: 11, marginTop: 10}}>
            {t('settings.templates.signedPdfDeliveryCc', 'CC (comma-separated)')}
          </label>
          <input className="input"
                 placeholder="legal@acme.com, hr@acme.com"
                 value={ccStr}
                 onChange={(e) => setCcStr(e.target.value)} />

          <label className="label" style={{fontSize: 11, marginTop: 10}}>
            {t('settings.templates.signedPdfDeliveryBcc', 'BCC (comma-separated)')}
          </label>
          <input className="input"
                 placeholder="archive@acme.com"
                 value={bccStr}
                 onChange={(e) => setBccStr(e.target.value)} />
        </div>
      )}

      <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16}}>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? t('common.saving', 'Saving…') : t('common.save')}
        </button>
      </div>
    </>
  );
}

// ============================================================================
// Settings → My signatures — Phase D #4. Operator-personal signature
// library (per-user, shared across orgs). Two creation paths:
//
//   "Type your name" → choose a cursive font + your name → SPA
//      renders the name to an offscreen canvas and POSTs the PNG.
//   "Draw your signature" → freehand on a canvas, SPA exports the
//      result as PNG and POSTs.
//
// Both POST to /spa/account/signatures with the same shape
// ({name, kind, pngBase64, width, height, fontName?, text?}).
// ============================================================================

// Cursive font choices for typed signatures. Each is widely-installed
// (Mac + iOS) or has a sensible fallback chain. Used both in the
// preview row and in the canvas font string at render time. When the
// system can't find the primary face, browsers fall back through the
// chain; the last resort is the CSS `cursive` generic family.
const SIGNATURE_FONTS = [
  { id: 'snell',        label: 'Snell Roundhand', stack: '"Snell Roundhand", "Apple Chancery", "Brush Script MT", cursive' },
  { id: 'brush',        label: 'Brush Script',     stack: '"Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive' },
  { id: 'chancery',     label: 'Apple Chancery',   stack: '"Apple Chancery", "Bradley Hand", "Brush Script MT", cursive' },
  { id: 'bradley',      label: 'Bradley Hand',     stack: '"Bradley Hand", "Apple Chancery", "Brush Script MT", cursive' },
];

// Render a typed signature to a PNG via offscreen canvas. The output
// is sized to fit the text at 64px tall + ~32px padding so the
// signature reads cleanly when embedded in the rendered PDF (the
// renderer's contain-fit handles the actual scaling).
function renderTypedSignaturePng(text, fontStack) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  // First pass: measure on a temp canvas at the target font size.
  const measureCanvas = document.createElement('canvas');
  const mctx = measureCanvas.getContext('2d');
  const fontPx = 64;
  mctx.font = `400 ${fontPx}px ${fontStack}`;
  const metrics = mctx.measureText(trimmed);
  const padX = 32, padY = 24;
  const w = Math.ceil(metrics.width + padX * 2);
  const h = Math.ceil(fontPx * 1.4 + padY * 2);
  // Render canvas, transparent background, dark-grey ink so the
  // signature reads as ink rather than pure black.
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.font = `400 ${fontPx}px ${fontStack}`;
  ctx.fillStyle = '#181818';
  ctx.textBaseline = 'middle';
  ctx.fillText(trimmed, padX, h / 2);
  return { dataUrl: canvas.toDataURL('image/png'), width: w, height: h };
}

// SignatureDrawCanvas — freehand signing surface. Pointer events
// (mouse / touch / pen all share the same code path). Exposes the
// underlying <canvas> through canvasRef so the parent can grab the
// PNG when the user clicks Save.
//
// Sizing: the canvas's INTERNAL coordinate system is W×H pixels;
// the CSS layer scales it to whatever container width is available
// via width:100%. Pointer events arrive in CSS-pixel space so the
// stroke handler MUST scale them by canvas.width/rect.width
// (and same for height) before drawing — otherwise the stroke
// lands at a wrong position whenever the canvas is rendered at
// anything other than 1:1 (which it usually is, since the modal
// max-width is far below 1080).
function SignatureDrawCanvas({ canvasRef, width = 1080, height = 360 }) {
  const drawing = React.useRef(false);
  const lastPt = React.useRef(null);

  // Translate a pointer event into CANVAS-internal coordinates.
  // Without this, drawing on a CSS-scaled canvas puts every stroke
  // up against the top-left because rect.width / rect.height shrink
  // every pointer delta to a fraction of its real intended distance.
  const toCanvasCoords = (e) => {
    const c = canvasRef.current;
    if (!c) return [0, 0];
    const rect = c.getBoundingClientRect();
    const sx = c.width  / rect.width;
    const sy = c.height / rect.height;
    return [
      (e.clientX - rect.left) * sx,
      (e.clientY - rect.top)  * sy,
    ];
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    lastPt.current = toCanvasCoords(e);
  };
  const onPointerMove = (e) => {
    if (!drawing.current) return;
    const [x, y] = toCanvasCoords(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#181818';
    // Stroke width scales with the canvas's internal width so the
    // line looks the same thickness whether the canvas is 600 or
    // 1200 wide internally.
    ctx.lineWidth = Math.max(2.5, canvasRef.current.width / 360);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPt.current[0], lastPt.current[1]);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPt.current = [x, y];
  };
  const onPointerUp = () => { drawing.current = false; lastPt.current = null; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  return (
    <div>
      <canvas ref={canvasRef}
              width={width} height={height}
              style={{
                display: 'block',
                width: '100%',
                aspectRatio: `${width} / ${height}`,
                border: '1px dashed var(--border)',
                borderRadius: 'var(--radius)',
                background: '#fff',
                touchAction: 'none',
                cursor: 'crosshair',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp} />
      <div style={{marginTop: 6}}>
        <button className="btn btn-sm" onClick={clear}>Clear</button>
      </div>
    </div>
  );
}

function SignaturesSettingsPage() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [signatures, setSignatures] = useState(null);
  const [creating, setCreating] = useState(null); // null | 'typed' | 'drawn'

  // Typed-mode form state.
  const [typedName, setTypedName] = useState('');
  const [typedText, setTypedText] = useState('');
  const [typedFontId, setTypedFontId] = useState(SIGNATURE_FONTS[0].id);

  // Drawn-mode form state.
  const [drawnName, setDrawnName] = useState('');
  const drawCanvasRef = React.useRef(null);
  const canvasHasInk = () => {
    const c = drawCanvasRef.current;
    if (!c) return false;
    try {
      const { data } = c.getContext('2d').getImageData(0, 0, c.width, c.height);
      for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return true;
    } catch { /* tainted canvas */ }
    return false;
  };
  const closeDraw = async () => {
    if (canvasHasInk()) {
      const ok = await confirm({
        title: t('settings.signatures.discardTitle', 'Discard signature?'),
        body: t('settings.signatures.discardBody', 'Your drawn signature will be lost.'),
        confirmLabel: t('common.discard', 'Discard'),
        danger: true,
      });
      if (!ok) return;
    }
    setCreating(null);
  };

  const refresh = React.useCallback(() => {
    api.get('/spa/account/signatures')
      .then((r) => setSignatures(r.signatures || []))
      .catch((e) => toast(e.message, 'error'));
  }, [toast]);
  useEffect(() => { refresh(); }, [refresh]);

  const saveTyped = async () => {
    const font = SIGNATURE_FONTS.find((f) => f.id === typedFontId) || SIGNATURE_FONTS[0];
    const rendered = renderTypedSignaturePng(typedText, font.stack);
    if (!rendered) { toast(t('settings.signatures.typeFirst', 'Type your name first.'), 'warn'); return; }
    const pngBase64 = rendered.dataUrl.replace(/^data:image\/png;base64,/, '');
    try {
      await api.post('/spa/account/signatures', {
        name: typedName || typedText,
        kind: 'typed',
        pngBase64,
        width: rendered.width,
        height: rendered.height,
        fontName: font.id,
        text: typedText,
      });
      setCreating(null);
      setTypedName(''); setTypedText(''); setTypedFontId(SIGNATURE_FONTS[0].id);
      refresh();
    } catch (e) { toast(e.message, 'error'); }
  };

  const saveDrawn = async () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    // A blank canvas still encodes to several hundred bytes of PNG, so the
    // old length check never fired and empty signatures were saved.
    if (!canvasHasInk()) { toast(t('settings.signatures.drawFirst', 'Draw your signature first.'), 'warn'); return; }
    // Crop to the inked area so the signature fills its field when
    // stamped, instead of sitting small inside a mostly empty box.
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const px = ctx.getImageData(0, 0, W, H).data;
    let minX = W, minY = H, maxX = -1, maxY = -1;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (px[(y * W + x) * 4 + 3] > 8) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    const pad = 12;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(W - 1, maxX + pad); maxY = Math.min(H - 1, maxY + pad);
    const out = document.createElement('canvas');
    out.width = maxX - minX + 1; out.height = maxY - minY + 1;
    out.getContext('2d').drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    const pngBase64 = out.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
    try {
      await api.post('/spa/account/signatures', {
        name: drawnName || t('settings.signatures.drawnDefaultName', 'Drawn signature'),
        kind: 'drawn',
        pngBase64,
        width:  out.width,
        height: out.height,
      });
      setCreating(null);
      setDrawnName('');
      refresh();
    } catch (e) { toast(e.message, 'error'); }
  };

  const remove = async (sig) => {
    // Name the templates that still carry the signature before asking —
    // deleting pulls it out of every one of them.
    let refs = [];
    try { refs = (await api.get(`/spa/account/signatures/${sig.id}/references`)).templates || []; }
    catch (_) { /* unknown — fall back to the generic warning */ }
    const shown = refs.slice(0, 8).map((r) => r.name).join(', ');
    const more = refs.length > 8 ? ` …+${refs.length - 8}` : '';
    const ok = await confirm({
      title: t('settings.signatures.confirmDeleteTitle', 'Delete signature?'),
      body: refs.length > 0
        ? t('settings.signatures.confirmDeleteReferenced',
          '"{{name}}" is placed on {{count}} template(s): {{names}}. They will render without it. Delete anyway?',
          { name: sig.name, count: refs.length, names: shown + more })
        : t('settings.signatures.confirmDeleteBody',
          '"{{name}}" will be permanently deleted. Templates that reference it will no longer render with the signature embedded.',
          { name: sig.name }),
      confirmLabel: t('common.delete', 'Delete'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/spa/account/signatures/${sig.id}`);
      refresh();
    } catch (e) { toast(e.message, 'error'); }
  };
  // Sharing policy — the owner's call, per signature.
  const setSharing = async (sig, sharing) => {
    setSignatures((cur) => (cur || []).map((s) => (s.id === sig.id ? { ...s, sharing } : s)));
    try { await api.patch(`/spa/account/signatures/${sig.id}`, { sharing }); }
    catch (e) { toast(e.message, 'error'); refresh(); }
  };

  return (
    <>
      <SettingsHeader
        title={t('settings.signatures.heading', 'My signatures')}
        subtitle={t('settings.signatures.intro',
          'Your personal signatures (across every org you\'re a member of). Place one in any template by adding a Signature field and picking "User" mode.')}
      />

      {/* Create row */}
      <div style={{display: 'flex', gap: 8, marginBottom: 16}}>
        <button className="btn btn-primary" onClick={() => setCreating('typed')}>
          {t('settings.signatures.addTyped', '+ Type your name')}
        </button>
        <button className="btn" onClick={() => setCreating('drawn')}>
          {t('settings.signatures.addDrawn', '+ Draw your signature')}
        </button>
      </div>

      {creating === 'typed' && (
        <div className="settings-card is-block" style={{padding: 16, marginBottom: 16}}>
          <div className="prop-group">
            <label className="label">{t('settings.signatures.nameLabel', 'Name (label)')}</label>
            <input className="input" value={typedName}
                   placeholder={t('settings.signatures.namePlaceholderTyped', 'Casual signature')}
                   onChange={(e) => setTypedName(e.target.value)} />
          </div>

          <div className="prop-group">
            <label className="label">{t('settings.signatures.yourName', 'Your name')}</label>
            <input className="input" value={typedText}
                   placeholder="Jane Smith"
                   onChange={(e) => setTypedText(e.target.value)} />
          </div>

          <div className="prop-group">
            <label className="label">{t('settings.signatures.font', 'Font')}</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 8,
            }}>
              {SIGNATURE_FONTS.map((f) => {
                const active = typedFontId === f.id;
                return (
                  <label key={f.id}
                         style={{
                           display: 'flex', alignItems: 'center', gap: 10,
                           padding: '10px 12px',
                           border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
                           borderRadius: 'var(--radius)',
                           background: active ? 'rgba(108, 92, 231, 0.08)' : 'transparent',
                           cursor: 'pointer',
                           minWidth: 0,
                         }}>
                    <input type="radio" checked={active}
                           onChange={() => setTypedFontId(f.id)}
                           style={{flexShrink: 0}} />
                    <span style={{
                      fontFamily: f.stack,
                      fontSize: 22,
                      lineHeight: 1.15,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}>
                      {typedText || f.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12}}>
            <button className="btn" onClick={() => setCreating(null)}>{t('common.cancel', 'Cancel')}</button>
            <button className="btn btn-primary" onClick={saveTyped}>{t('common.save', 'Save')}</button>
          </div>
        </div>
      )}

      {creating === 'drawn' && (
        // Modal overlay — far better UX than an inline card for
        // freehand signing. Backdrop click closes; the canvas is
        // wide (>900 px CSS at typical viewport widths) so the user
        // has room to make a recognisable signature.
        <div className="modal-overlay" onClick={closeDraw}>
          <div className="modal" onClick={(e) => e.stopPropagation()}
               style={{maxWidth: 960, width: '90vw'}}>
            <h2 style={{marginBottom: 8}}>{t('settings.signatures.drawTitle', 'Draw your signature')}</h2>
            <p style={{color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16}}>
              {t('settings.signatures.drawIntro',
                'Sign with your mouse, trackpad, finger, or stylus. Use the full canvas — we\u2019ll trim the empty space around your signature automatically.')}
            </p>

            <div className="prop-group">
              <label className="label">{t('settings.signatures.nameLabel', 'Name (label)')}</label>
              <input className="input" value={drawnName}
                     placeholder={t('settings.signatures.namePlaceholderDrawn', 'Drawn signature')}
                     onChange={(e) => setDrawnName(e.target.value)} />
            </div>

            <div className="prop-group">
              <label className="label">{t('settings.signatures.signBelow', 'Sign below')}</label>
              <SignatureDrawCanvas canvasRef={drawCanvasRef} width={1080} height={360} />
            </div>

            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16}}>
              <button className="btn" onClick={closeDraw}>{t('common.cancel', 'Cancel')}</button>
              <button className="btn btn-primary" onClick={saveDrawn}>{t('common.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {signatures == null ? (
        <div style={{fontSize: 13, color: 'var(--text-muted)'}}>{t('common.loading', 'Loading…')}</div>
      ) : signatures.length === 0 ? (
        <div style={{fontSize: 13, color: 'var(--text-muted)'}}>
          {t('settings.signatures.empty',
            'You have no saved signatures yet. Add one above to start placing it on templates.')}
        </div>
      ) : (
        signatures.map((s) => (
          <div key={s.id} className="settings-card" style={{display: 'flex', alignItems: 'center', gap: 12, padding: 12, marginBottom: 8}}>
            <img src={s.imageUrl} alt=""
                 style={{maxWidth: 200, maxHeight: 60, background: '#fff', border: '1px solid var(--border)', borderRadius: 4}} />
            <div style={{flex: 1}}>
              <div style={{fontWeight: 600}}>{s.name}</div>
              <div style={{fontSize: 12, color: 'var(--text-muted)'}}>
                {s.kind === 'typed'
                  ? t('settings.signatures.typedKindLabel', 'Typed ({{font}})', { font: s.fontName || 'cursive' })
                  : t('settings.signatures.drawnKindLabel', 'Drawn')} ·{' '}
                {s.width}×{s.height}px ·{' '}
                {t('settings.signatures.addedOn', 'added {{date}}', { date: formatDate(s.createdAt) })}
              </div>
              <div style={{marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                <label style={{fontSize: 12, color: 'var(--text-muted)'}} htmlFor={`sig-sharing-${s.id}`}>
                  {t('settings.signatures.sharing.label', 'Your team')}
                </label>
                <select id={`sig-sharing-${s.id}`} className="select" style={{width: 'auto', fontSize: 13}}
                        value={s.sharing || 'visible'} onChange={(e) => setSharing(s, e.target.value)}>
                  <option value="visible">{t('settings.signatures.sharing.visible', 'Can see it where you placed it')}</option>
                  <option value="usable">{t('settings.signatures.sharing.usable', 'Can also place it on their templates')}</option>
                </select>
              </div>
            </div>
            <button className="btn btn-danger" onClick={() => remove(s)}>{t('common.delete', 'Delete')}</button>
          </div>
        ))
      )}
    </>
  );
}

// ============================================================================
// Settings → Workspaces — list + create + rename + delete the workspaces
// in the current tenant. Each row is editable inline; deleting the last
// remaining workspace is refused server-side (a tenant must always own
// at least one).
// ============================================================================

function WorkspacesSettingsPage() {
  const t = useT();
  const { workspaces, refresh, switchTo } = useWorkspace();
  const { current: currentTenant } = useTenant();
  const canCreateWorkspace = !!currentTenant && ['owner', 'admin'].includes(currentTenant.role);
  const { features } = useRuntime();
  const toast = useToast();
  const confirm = useConfirm();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDesc, setEditDesc] = useState('');
  // Publish-flow defaults — gated on features.publish (self-hosted Pro+).
  // On hosted (distributed) installs publish doesn't exist as a flow at
  // all, so the whole section is removed from the UI rather than shown
  // locked. The state slots are still allocated so the save handler can
  // pass them through unchanged when publish IS enabled.
  const [editRegistryId, setEditRegistryId] = useState('');
  const [editRepo, setEditRepo] = useState('');
  const [editTag, setEditTag] = useState('');
  const [editPlatforms, setEditPlatforms] = useState(['linux/amd64']);
  const [registries, setRegistries] = useState([]);
  const [busy, setBusy] = useState(false);

  const startEdit = (w) => {
    setEditingId(w.id);
    setEditName(w.name);
    setEditSlug(w.slug);
    setEditDesc(w.description || '');
    setEditRegistryId(w.defaultRegistryId || '');
    setEditRepo(w.defaultRepo || '');
    setEditTag(w.defaultTag || '');
    setEditPlatforms(Array.isArray(w.defaultPlatforms) && w.defaultPlatforms.length
      ? w.defaultPlatforms : ['linux/amd64']);
    // Lazy-load the registry list the first time the operator opens an
    // edit form. Falls back silently if the API errors (e.g. user has no
    // permission) — the registry select just shows the "no registries"
    // hint in that case.
    api.get('/spa/docker-registries')
      .then(({ registries }) => setRegistries(registries || []))
      .catch(() => setRegistries([]));
  };
  const cancelEdit = () => { setEditingId(null); };

  const togglePlatform = (p) => {
    setEditPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/spa/workspaces/${editingId}`, {
        name: editName, slug: editSlug, description: editDesc,
        defaultRegistryId: editRegistryId || null,
        defaultRepo: editRepo.trim() || null,
        defaultTag: editTag.trim() || null,
        defaultPlatforms: editPlatforms,
      });
      await refresh();
      setEditingId(null);
    } catch (e) {
      toast(e.detail?.detail || e.message, 'error');
    } finally { setBusy(false); }
  };

  const remove = async (w) => {
    // Deletes every template inside — make the user type the name.
    const ok = await confirm({
      title: t('workspace.deleteTitle', 'Delete workspace?'),
      body: t('workspace.confirmDelete', { name: w.name }),
      confirmLabel: t('common.delete', 'Delete'),
      danger: true,
      typeToConfirm: w.name,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/workspaces/${w.id}`);
      await refresh();
    } catch (e) {
      toast(e.detail?.detail || e.message, 'error');
    }
  };

  return (
    <>
      <SettingsHeader
        title={t('settings.nav.workspaces')}
        subtitle={t('workspace.settingsSubtitle')}
        action={canCreateWorkspace ? (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ {t('workspace.add')}</button>
        ) : null}
      />

      {workspaces.length === 0 ? (
        <div className="settings-empty">
          <p>{t('workspace.noneYet', 'No workspaces yet.')}</p>
        </div>
      ) : workspaces.map((w) => editingId === w.id ? (
        <div key={w.id} className="settings-card" style={{flexDirection: 'column', alignItems: 'stretch'}}>
          <input className="input" value={editName}
                 placeholder={t('workspace.col.name')}
                 onChange={(e) => setEditName(e.target.value)}
                 style={{marginBottom: 8}} />
          <input className="input" value={editSlug}
                 placeholder={t('workspace.col.slug')}
                 onChange={(e) => setEditSlug(e.target.value.toLowerCase())}
                 style={{marginBottom: 8}} />
          <textarea className="input" rows={2}
                    placeholder={t('workspace.col.description')}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    style={{marginBottom: 12}} />

          {features.publish && (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04,
                color: 'var(--text-muted)', marginBottom: 8,
              }}>
                {t('workspace.publishDefaults.heading', 'Publish defaults')}
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8}}>
                <div>
                  <label className="label" style={{fontSize: 11}}>{t('publish.registry', 'Registry')}</label>
                  {registries.length === 0 ? (
                    <div style={{fontSize: 12, color: 'var(--text-muted)', padding: '6px 0'}}>
                      {t('publish.registryEmpty', 'Add one in Settings → Docker registries first.')}
                    </div>
                  ) : (
                    <select className="input" value={editRegistryId}
                            onChange={(e) => setEditRegistryId(e.target.value)}>
                      <option value="">—</option>
                      {registries.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="label" style={{fontSize: 11}}>{t('workspace.publishDefaults.repo', 'Repository name')}</label>
                  <input className="input" value={editRepo}
                         placeholder="acme/renderer"
                         onChange={(e) => setEditRepo(e.target.value)} />
                </div>
                <div>
                  <label className="label" style={{fontSize: 11}}>{t('workspace.publishDefaults.tag', 'Default tag')}</label>
                  <input className="input" value={editTag}
                         placeholder="latest"
                         onChange={(e) => setEditTag(e.target.value)} />
                </div>
                <div>
                  <label className="label" style={{fontSize: 11}}>{t('publish.platforms', 'Platforms')}</label>
                  <div style={{display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 13}}>
                    {['linux/amd64', 'linux/arm64'].map((p) => (
                      <label key={p} style={{display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer'}}>
                        <input type="checkbox"
                               checked={editPlatforms.includes(p)}
                               onChange={() => togglePlatform(p)} />
                        {p.replace('linux/', '')}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8}}>
            <button className="btn" onClick={cancelEdit}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>{t('common.save')}</button>
          </div>
        </div>
      ) : (
        <div key={w.id} className="settings-card">
          <div className="settings-card-info">
            <div className="settings-card-title-row">
              <div className="settings-card-title">{w.name}</div>
              <span className={`publish-status-dot ${publishStatusFor(w)}`} title={
                w.lastPublishedAt ? formatDateTime(w.lastPublishedAt) : t('workspace.neverPublished')
              } />
            </div>
            <div className="settings-card-meta">
              {w.slug}
              {w.description ? ` · ${w.description}` : ''}
            </div>
          </div>
          <div className="settings-card-actions">
            <ActionMenu items={[
              { label: t('workspace.open'), onClick: () => switchTo(w) },
              { label: t('common.edit'), onClick: () => startEdit(w) },
              workspaces.length > 1 && { label: t('common.delete'), onClick: () => remove(w), danger: true },
            ]} />
          </div>
        </div>
      ))}

      {showCreate && (
        <CreateWorkspaceModal
          onClose={() => setShowCreate(false)}
          onCreated={(ws) => {
            setShowCreate(false);
            refresh();
            if (ws) switchTo(ws);
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// Settings → Docker Registries — list/create/edit/delete + test
// connection. Used by the Publish flow to authenticate when pushing
// per-workspace renderer images. Tenant-scoped, password encrypted
// at rest server-side; the form shows "•••• (set)" when a password
// is on file and accepts an empty string to mean "leave it" so the
// operator can change name/url without re-pasting the secret.
// ============================================================================

function DockerRegistriesPage() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [testing, setTesting] = useState(null); // id of the row currently being tested
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();
  // Registries are useful only with Publish (Pro+ self-hosted). Show the
  // page so non-Pro operators can see what they're missing; FeatureLocked
  // wraps the Add CTA + list rows so they can't actually add/edit. The
  // backend already 403s the mutating routes for non-Pro tenants.
  const { info: tenantInfo } = useTenantInfo();
  const tierAllows = !!tenantInfo?.limits?.features?.publish;
  const openUpgrade = () => window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
    detail: { code: 'feature_dockerRegistries', requiredTier: 'pro' },
  }));

  const load = useCallback(async () => {
    try {
      const { registries } = await api.get('/spa/docker-registries');
      setItems(registries);
    } catch (e) { toast(e.message, 'error'); setItems([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    const ok = await confirm({
      title: 'Delete registry?',
      body: t('registry.confirmDelete'),
      confirmLabel: t('common.delete') || 'Delete',
      danger: true,
    });
    if (!ok) return;
    try { await api.del(`/spa/docker-registries/${id}`); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/spa/docker-registries/${id}/test`);
      toast(r.note === 'token_flow' ? t('registry.testTokenFlow') : t('registry.testOk'), 'success');
      load();
    } catch (e) {
      toast(e.detail?.error || e.message, 'error');
      load();
    } finally { setTesting(null); }
  };

  if (!items) {
    return <>
      <SettingsHeader title={t('settings.nav.dockerRegistries')} subtitle={t('registry.subtitle')} />
      <LoadingFrame label={t('common.loading')} />
    </>;
  }

  const editingRow = editing && editing !== 'new' ? items.find((x) => x.id === editing) : null;

  return <>
    <SettingsHeader
      title={<>{t('settings.nav.dockerRegistries')} {!tierAllows && <TierBadge tier="pro" />}</>}
      subtitle={t('registry.subtitle')}
      action={
        tierAllows
          ? <button className="btn btn-primary" onClick={() => setEditing('new')}>+ {t('registry.add')}</button>
          : <button className="btn btn-primary" onClick={openUpgrade}>{t('upgrade.cta', 'See plans')}</button>
      }
    />

    {!tierAllows && (
      <div className="card" style={{marginBottom: 12, padding: 14, background: 'rgba(108,92,231,0.06)', border: '1px dashed var(--border)'}}>
        <div style={{display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap'}}>
          <TierBadge tier="pro" />
          <span style={{fontSize: 13, color: 'var(--text-secondary)'}}>
            {t('upgrade.dockerRegistries.body', 'Registries store the images your Publish flow pushes to. Available on Pro and Enterprise self-hosted plans.')}
          </span>
          <button className="btn btn-primary" style={{marginLeft: 'auto'}} onClick={openUpgrade}>
            {t('upgrade.cta', 'See plans')}
          </button>
        </div>
      </div>
    )}

    {items.length === 0 ? (
      <div className="settings-empty">
        <p>{t('registry.empty')}</p>
        {tierAllows
          ? <button className="btn" onClick={() => setEditing('new')}>+ {t('registry.add')}</button>
          : <button className="btn" onClick={openUpgrade}>{t('upgrade.cta', 'See plans')}</button>}
      </div>
    ) : (
      items.map((r) => (
        <div key={r.id} className="settings-card">
          <div className="settings-card-info">
            <div className="settings-card-title-row">
              <div className="settings-card-title">{r.name}</div>
              {r.lastTestAt && (
                <span className={`pill pill-${r.lastTestStatus === 'ok' ? 'active' : 'past_due'}`}>
                  {r.lastTestStatus}
                </span>
              )}
            </div>
            <div className="settings-card-meta">
              {r.username} @ {r.url}
              {r.lastTestAt
                ? ` · ${t('registry.col.lastTest')}: ${formatDate(r.lastTestAt)}`
                : ` · ${t('registry.notTested')}`}
            </div>
          </div>
          <div className="settings-card-actions">
            <ActionMenu items={[
              { label: testing === r.id ? t('common.testing', 'Testing…') : t('registry.test'), onClick: () => test(r.id) },
              { label: t('common.edit'), onClick: () => setEditing(r.id) },
              { label: t('common.delete'), onClick: () => remove(r.id), danger: true },
            ]} />
          </div>
        </div>
      ))
    )}

    {editing && (
      <DockerRegistryModal
        existing={editingRow}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    )}
  </>;
}

function DockerRegistryModal({ existing, onClose, onSaved }) {
  const isNew = !existing;
  const [name, setName] = useState(existing?.name || '');
  const [url, setUrl] = useState(existing?.url || 'https://index.docker.io/v1/');
  const [username, setUsername] = useState(existing?.username || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const toast = useToast();
  const t = useT();

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, url, username };
      if (password) body.password = password;
      if (isNew) {
        if (!password) { toast(t('registry.passwordRequired'), 'error'); setBusy(false); return; }
        await api.post('/spa/docker-registries', body);
      } else {
        await api.patch(`/spa/docker-registries/${existing.id}`, body);
      }
      onSaved();
    } catch (e) { toast(e.detail?.detail || e.message, 'error'); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{isNew ? t('registry.create.title') : t('registry.edit.title')}</div>
        <div className="form-row" style={{marginTop: 16}}>
          <div>
            <label className="label">{t('registry.col.name')}</label>
            <input className="input" autoFocus placeholder="Docker Hub"
                   value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('registry.col.url')}</label>
            <input className="input" placeholder="https://index.docker.io/v1/"
                   value={url} onChange={(e) => setUrl(e.target.value)} />
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              {t('registry.urlHint')}
            </div>
          </div>
          <div>
            <label className="label">{t('registry.col.username')}</label>
            <input className="input"
                   value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('registry.password')}</label>
            <input className="input" type="password" autoComplete="new-password"
                   placeholder={existing?.passwordSet ? '•••• ' + t('backup.setBlank') : ''}
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
            <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>
              {busy ? t('common.saving', 'Saving…') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// First-time tenant onboarding
// ============================================================================

// Shown to a logged-in user who has zero memberships AND the install
// already has a tenant — i.e. a user that was REMOVED from the only
// self-hosted account on the box. The previous behaviour rendered
// FirstTenantPrompt for them (because !current && tenants.length===0
// is also the "fresh install" case), they'd fill in a workspace name,
// the server would 403 with `single_tenant_only`, and on reload they'd
// see the form again. This page makes the dead-end explicit and gives
// them the only useful action: sign out.
function NoAccessPage() {
  const t = useT();
  const { emails = {} } = useRuntime();
  const supportEmail = emails?.support || emails?.contact || '';
  const signOut = async () => {
    try { await api.post('/auth/logout'); } catch { /* best-effort */ }
    window.location.href = '/app';
  };
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>{t('access.denied.title', 'No access to this install')}</h1>
        <p className="sub">
          {t('access.denied.body',
            'Your account is signed in, but you are not a member of any organisation on this Pluma install. An admin may have removed your access. Sign out and ask the operator to re-invite you if you should still have access.')}
        </p>
        {supportEmail && (
          <p className="sub" style={{marginTop: 8, fontSize: 13}}>
            {t('access.denied.contact', 'Need help?')}{' '}
            <a href={`mailto:${supportEmail}`} className="link">{supportEmail}</a>
          </p>
        )}
        <button className="btn btn-primary" onClick={signOut} style={{marginTop: 16}}>
          {t('access.denied.signOut', 'Sign out')}
        </button>
      </div>
    </div>
  );
}

function FirstTenantPrompt({ onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const t = useT();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Only the name is asked for — the tenant slug is an internal,
      // globally-unique identifier and is derived server-side.
      await api.post('/spa/tenants', { name });
      onCreated();
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>{t('tenant.first.title', 'Name your organisation')}</h1>
        <p className="sub">{t('tenant.first.body', 'Your organisation holds your templates, documents and team. You can rename it later.')}</p>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label" htmlFor="first-org-name">{t('account.orgName', 'Organisation name')}</label>
            <input id="first-org-name" className="input" required autoFocus value={name} onChange={(e) => setName(e.target.value)}
                   placeholder={t('tenant.first.placeholder', 'e.g. Becker Consulting')} />
          </div>
          <button className="btn btn-primary" disabled={busy} type="submit">{busy ? t('common.creating', 'Creating…') : t('tenant.create', 'Create organisation')}</button>
        </form>
      </div>
    </div>
  );
}

function CreateTenantModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const toast = useToast();
  const t = useT();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Slug is derived server-side — see FirstTenantPrompt.
      await api.post('/spa/tenants', { name });
      onCreated();
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('account.createOrg', 'New organisation')}</h2>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label">{t('account.orgName', 'Organisation name')}</label>
            <input className="input" required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.creating', 'Creating…') : t('tenant.create', 'Create organisation')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Templates page — list + upload + opens editor
// ============================================================================

// Asks, then deletes a template. Signed documents are kept until deleted,
// and deleting their template deletes them too, so the server refuses
// (409 template_has_signed_documents) until asked again explicitly —
// a second, louder confirm naming the count. Resolves true once deleted.
async function confirmAndDeleteTemplate({ wsSlug, templateId, name, confirm, t }) {
  const ok = await confirm({
    title: t('templates.deleteConfirm.title', 'Delete this template?'),
    body: name
      ? t('templates.deleteConfirm.bodyNamed', '"{{name}}" and all its field placements will be removed. Generated PDFs aren’t affected.', { name })
      : t('templates.deleteConfirm.body', 'This template and all its field placements will be removed. Generated PDFs aren’t affected.'),
    confirmLabel: t('common.delete', 'Delete'),
    danger: true,
  });
  if (!ok) return false;
  const path = `/api/v1/${wsSlug}/templates/${templateId}`;
  try {
    await api.del(path);
  } catch (err) {
    if (err.code !== 'template_has_signed_documents') throw err;
    const count = err.body?.signedDocuments ?? 0;
    const again = await confirm({
      title: count === 1
        ? t('templates.deleteSigned.titleOne', 'Also delete the signed document?')
        : t('templates.deleteSigned.title', 'Also delete {{count}} signed documents?', { count }),
      body: count === 1
        ? t('templates.deleteSigned.bodyOne',
          'A signed document created from this template is kept until you delete it. Deleting the template deletes it and its audit trail, for good. Download it from Sign requests first if you still need it.')
        : t('templates.deleteSigned.body',
          'Signed documents created from this template are kept until you delete them. Deleting the template deletes all {{count}} of them with their audit trails, for good. Download any you still need from Sign requests first.',
          { count }),
      confirmLabel: t('templates.deleteSigned.confirm', 'Delete template and documents'),
      danger: true,
    });
    if (!again) return false;
    await api.del(`${path}?deleteSignedDocuments=true`);
  }
  return true;
}

// 3-pane editor: TemplateBrowser (left) | TemplateEditor (center+right).
// The page is no longer "list, then click edit" — selecting a template
// in the left panel just swaps which template the editor is bound to.
// Empty workspace still falls through to the FirstRunChecklist.
function TemplatesPage() {
  // Workspace context first — the draftFolders useEffect below reads
  // ws?.customFolders in its dependency array. Declaring `ws` AFTER
  // that useEffect (the previous layout) hit a temporal-dead-zone
  // closure bug: babel-standalone transpiled `const ws = …` such that
  // the dep array always saw `undefined`, the effect only ran once on
  // mount, and setDraftFolders never propagated the persisted
  // customFolders. Net effect: empty folders disappeared on every
  // page reload. Keep this declaration above the effect.
  const { current: ws, refresh: refreshWorkspaces } = useWorkspace();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  // Initial selectedId is read from the URL — see syncRouteToState
  // below. This means a refresh restores the same template the user
  // was looking at instead of jumping back to the first one.
  const [selectedId, setSelectedId] = useState(() => readUrlParam('template'));
  // Custom folders live on the Workspace row server-side so an empty
  // folder (one no template lives in yet) survives a page refresh.
  // Source of truth is ws.customFolders; we mirror it locally for
  // optimistic updates while the PATCH is in flight.
  const [draftFolders, setDraftFolders] = useState([]);
  useEffect(() => {
    setDraftFolders(Array.isArray(ws?.customFolders) ? ws.customFolders : []);
  }, [ws?.id, ws?.customFolders]);
  // Mobile state: one pane visible at a time, switched by a bottom
  // nav. Three states matching the original: 'templates' = sidebar
  // list, 'editor' = PDF canvas, 'properties' = right rail. Hooks
  // MUST sit before any early-return below — otherwise the
  // empty-state branch and the populated branch run a different
  // number of hooks and React tears the tree (error #310).
  const isMobile = useIsMobile();
  const [mobilePanel, setMobilePanel] = useState('templates');
  const toast = useToast();
  const t = useT();
  // Keep the tenant-total templates count fresh in the header badge and
  // the Billing page tile after every create/duplicate/delete. The badge
  // would otherwise be off-by-one until the user reloads.
  const { refresh: refreshTenantInfo } = useTenantInfo();

  // Mirror selectedId into the URL whenever it changes. Use replaceState
  // (not pushState) so the back-button works for navigating between
  // app *pages*, not between every clicked template. The page number
  // (PDF page within the template) is owned by TemplateEditor and
  // syncs itself the same way.
  useEffect(() => {
    writeUrlParam('template', selectedId || null);
  }, [selectedId]);

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    if (!ws) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      // v1 list envelope: { data, meta }. Fall back to the legacy
      // {templates} key during a stale-SPA window after the swap so
      // a customer with a cached bundle doesn't see an empty grid.
      const res = await fetchAllTemplates(ws.slug);
      const list = res.templates || [];
      setItems(list);
      // Restore the URL-encoded template if it still exists in this
      // workspace, otherwise fall back to the first one. This handles
      // the refresh + the "deleted that template since" cases in one
      // pass.
      setSelectedId((cur) => {
        if (cur && list.some((t) => t.id === cur)) return cur;
        return list[0]?.id || null;
      });
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };
  // Reload when the workspace switches — the editor must reflect the
  // new workspace's templates, not the old one's. Don't blow away
  // selectedId yet; load() will validate it post-fetch.
  useEffect(() => { load(); }, [ws?.slug]); // eslint-disable-line

  // Drag a template onto a folder header → reassigns its folder via PATCH.
  // Empty string sent as null so the row goes back to the (root) group.
  const moveToFolder = async (templateId, folder) => {
    try {
      await api.patch(`/api/v1/${ws.slug}/templates/${templateId}`, { folder: folder || null });
      // Optimistic update so the UI doesn't blink.
      setItems((arr) => arr.map((t) => t.id === templateId ? { ...t, folder: folder || null } : t));
    } catch (err) { toast(err.message, 'error'); }
  };

  const addFolder = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    if (items.some((t) => t.folder === trimmed)) return;
    if (draftFolders.includes(trimmed)) return;
    const next = [...draftFolders, trimmed];
    // Optimistic — show the folder immediately, roll back if the PATCH
    // fails so the UI stays in sync with the server.
    setDraftFolders(next);
    try {
      await api.patch(`/spa/workspaces/${ws.id}`, { customFolders: next });
      // Refresh the workspaces list so other consumers (sidebar
      // dropdowns, etc.) see the new ws.customFolders too.
      refreshWorkspaces?.();
    } catch (err) {
      setDraftFolders(draftFolders);
      toast(err.message || 'Could not save folder', 'error');
    }
  };
  const removeFolder = async (name) => {
    const next = draftFolders.filter((n) => n !== name);
    setDraftFolders(next);
    try {
      await api.patch(`/spa/workspaces/${ws.id}`, { customFolders: next });
      refreshWorkspaces?.();
    } catch (err) {
      setDraftFolders(draftFolders);
      toast(err.message || 'Could not remove folder', 'error');
    }
  };

  // Rename a folder. Folders are derived from `template.folder` (a
  // plain string) plus the workspace's `customFolders` list, so a
  // rename has to touch BOTH:
  //   1. PATCH every template in the folder with the new name
  //   2. PATCH the workspace's customFolders list to swap the entry
  //      (preserves position so the sidebar doesn't reorder)
  //
  // Optimistic on success — the rows flip locally so the change
  // appears instantly. On any per-template PATCH failure we abort
  // before touching customFolders so the operator can retry without
  // landing in a split state where some templates moved and others
  // didn't.
  const renameFolder = async (oldName, newNameRaw) => {
    const newName = (newNameRaw || '').trim();
    if (!newName || newName === oldName) return;
    // Block name collisions against existing folders (draft OR
    // implicit-from-templates).
    const existing = new Set([
      ...draftFolders,
      ...items.map((t) => t.folder).filter(Boolean),
    ]);
    if (existing.has(newName)) {
      toast(t('templates.folderRenameDuplicate', 'A folder with that name already exists') || 'A folder with that name already exists', 'error');
      return;
    }
    const inFolder = items.filter((t) => t.folder === oldName);
    const movePromises = inFolder.map((tpl) =>
      api.patch(`/api/v1/${ws.slug}/templates/${tpl.id}`, { folder: newName })
        .then(() => ({ ok: true, id: tpl.id }))
        .catch((e) => ({ ok: false, id: tpl.id, err: e }))
    );
    const results = await Promise.all(movePromises);
    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      toast(`${failed.length} template(s) couldn't be renamed`, 'error');
      return;
    }
    // All templates moved — flip them locally so the sidebar shows
    // the new name immediately.
    setItems((arr) => arr.map((t) => t.folder === oldName ? { ...t, folder: newName } : t));
    // Swap the entry in customFolders (preserve order).
    if (draftFolders.includes(oldName)) {
      const next = draftFolders.map((n) => n === oldName ? newName : n);
      setDraftFolders(next);
      try {
        await api.patch(`/spa/workspaces/${ws.id}`, { customFolders: next });
        refreshWorkspaces?.();
      } catch (err) {
        setDraftFolders(draftFolders);
        toast(err.message || 'Could not save folder', 'error');
      }
    }
  };

  // Delete a folder entirely. Any templates inside are moved to root
  // (folder: null) so we never orphan them — they're still in the
  // tenant's count and quota, just shown at the top level. The folder
  // name is also removed from the workspace's customFolders list so
  // an empty draft folder doesn't bounce back on the next refresh.
  const deleteFolder = async (name, folderTemplates) => {
    const templates = folderTemplates || items.filter((t) => t.folder === name);
    // Move every contained template to root, in parallel. Failed PATCHes
    // surface as a toast but don't roll the others back — leaving a
    // partial state is better than leaving the user with a folder they
    // can't get rid of.
    const movePromises = templates.map((tpl) =>
      api.patch(`/api/v1/${ws.slug}/templates/${tpl.id}`, { folder: null })
        .then(() => ({ ok: true, id: tpl.id }))
        .catch((e) => ({ ok: false, id: tpl.id, err: e }))
    );
    const results = await Promise.all(movePromises);
    const failed = results.filter((r) => !r.ok);
    if (failed.length === 0) {
      // Optimistic local update so the rows flip to root immediately;
      // the next load() will reconfirm. Skipped on failure — we don't
      // want the UI to say "moved" when the server still thinks they're
      // in the folder.
      setItems((arr) => arr.map((t) =>
        results.some((r) => r.ok && r.id === t.id) ? { ...t, folder: null } : t
      ));
    } else {
      toast(`${failed.length} template(s) couldn't be moved out of the folder`, 'error');
    }
    // Drop the (now empty) folder name from the workspace's
    // customFolders list. Skipped if it wasn't a draft folder to begin
    // with — implicit folders disappear automatically once their last
    // template moves out.
    if (draftFolders.includes(name)) {
      await removeFolder(name);
    }
  };

  if (loading) return <LoadingFrame className="loading-frame--bare" label={t('common.loading', 'Loading…')} />;
  // A failed load must not look like an empty account (the first-run
  // "upload your first PDF" screen).
  if (loadError && items.length === 0) return <LoadErrorScreen message={loadError} onRetry={load} />;
  if (items.length === 0) {
    return (
      <div style={{padding: 32}}>
        <FirstRunChecklist onUpload={() => setShowUpload(true)} />
        {showUpload && (
          <UploadTemplateModal
            onClose={() => setShowUpload(false)}
            onCreated={(id) => { setShowUpload(false); setSelectedId(id); load(); refreshTenantInfo(); }}
          />
        )}
      </div>
    );
  }

  // Helper used by the browser + duplicate button. On mobile, picking a
  // template auto-jumps to the editor pane so the user doesn't need to
  // tap the bottom-nav themselves.
  const onMobileSelectTemplate = (id) => {
    setSelectedId(id);
    if (isMobile) setMobilePanel('editor');
  };

  return (
    <>
      <div className={'templates-3pane' + (isMobile ? ' is-mobile' : '')}>
        <div className={'templates-3pane-browser-wrap'
                        + (isMobile && mobilePanel !== 'templates' ? ' is-hidden' : '')}>
          <TemplateBrowser
            items={items}
            draftFolders={draftFolders}
            selectedId={selectedId}
            onSelect={onMobileSelectTemplate}
            onUpload={() => setShowUpload(true)}
            onAddFolder={addFolder}
            onRenameFolder={renameFolder}
            onDeleteFolder={deleteFolder}
            onRefresh={load}
            isMobile={isMobile}
            onMoveToFolder={moveToFolder}
            onDuplicate={async (id) => {
              try {
                const template = await api.post(`/api/v1/${ws.slug}/templates/${id}/duplicate`);
                setItems((arr) => [...arr, template]);
                onMobileSelectTemplate(template.id);
                toast(`Duplicated as "${template.name}"`, 'success');
                refreshTenantInfo();
              } catch (err) {
                toast(err.message, 'error');
              }
            }}
          />
        </div>
        <div className={'templates-3pane-editor'
                        + (isMobile && mobilePanel === 'templates' ? ' is-hidden' : '')}>
          {selectedId ? (
            <TemplateEditor
              key={selectedId}
              templateId={selectedId}
              isMobile={isMobile}
              mobilePanel={mobilePanel}
              setMobilePanel={setMobilePanel}
              onTemplateUpdated={(t) => setItems((arr) => arr.map((x) => x.id === t.id ? { ...x, ...t } : x))}
              onTemplateDeleted={(id) => {
                setSelectedId(null);
                setItems((arr) => arr.filter((x) => x.id !== id));
                if (isMobile) setMobilePanel('templates');
                refreshTenantInfo();
              }}
            />
          ) : (
            <div className="editor-empty">Select a template on the left to start editing.</div>
          )}
        </div>
      </div>

      {isMobile && (
        <nav className="mobile-bottom-nav">
          <button className={'mobile-nav-btn' + (mobilePanel === 'templates' ? ' active' : '')}
                  onClick={() => setMobilePanel('templates')}>
            <Icons.Folder /> <span>Templates</span>
          </button>
          <button className={'mobile-nav-btn' + (mobilePanel === 'editor' ? ' active' : '')}
                  onClick={() => setMobilePanel('editor')}
                  disabled={!selectedId}>
            <Icons.File /> <span>Editor</span>
          </button>
          <button className={'mobile-nav-btn' + (mobilePanel === 'properties' ? ' active' : '')}
                  onClick={() => setMobilePanel('properties')}
                  disabled={!selectedId}>
            <Icons.Settings /> <span>Properties</span>
          </button>
        </nav>
      )}
      {showUpload && (
        <UploadTemplateModal
          onClose={() => setShowUpload(false)}
          onCreated={(id) => { setShowUpload(false); setSelectedId(id); load(); refreshTenantInfo(); }}
        />
      )}
    </>
  );
}

// ============================================================================
// TemplateBrowser — left panel with virtual folders, search, drag-and-drop.
// Folders are derived from `template.folder`; an empty / null folder puts
// the template at the root. Drag a row onto a folder header to reassign.
// ============================================================================

function TemplateBrowser({ items, draftFolders, selectedId, onSelect, onUpload, onAddFolder, onRenameFolder, onDeleteFolder, onMoveToFolder, onDuplicate, onRefresh, isMobile }) {
  const confirm = useConfirm();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({}); // folder name → bool
  const t = useT();
  const [headerEl, setHeaderEl] = useState(null);
  useFitToWidth(headerEl, 1, headingTruncated);
  // Tenant-total templates count + cap. The cap is per-tenant (counted
  // across all workspaces), so we read it from the TenantInfoCtx
  // hydrated by AppShell rather than from `items.length` (which is the
  // current workspace's slice). When the count isn't loaded yet we hide
  // the badge so the heading doesn't flicker "0 / 5".
  const { info: tenantInfo } = useTenantInfo();
  const tCount = tenantInfo?.overCap?.templatesCount;
  const tCap = tenantInfo?.limits?.templates;
  // Show "(N / cap)" when both are known; "(N)" when cap is unlimited;
  // nothing when we have no data at all.
  const countBadge = (tCount != null)
    ? (tCap != null ? `(${tCount} / ${tCap})` : `(${tCount})`)
    : null;

  // Group templates by folder. Untitled / empty / null folder → root list.
  const filtered = items.filter((it) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (it.name || '').toLowerCase().includes(q)
      || (it.originalFilename || '').toLowerCase().includes(q)
      || (it.folder || '').toLowerCase().includes(q);
  });
  const rootItems = filtered.filter((it) => !it.folder);
  const byFolder = {};
  for (const it of filtered) {
    if (!it.folder) continue;
    (byFolder[it.folder] = byFolder[it.folder] || []).push(it);
  }
  // Merge in draft (empty) folders so a fresh "Add folder" entry shows
  // even before its first template lands.
  for (const name of draftFolders) {
    if (!byFolder[name] && !items.some((it) => it.folder === name)) {
      byFolder[name] = [];
    }
  }
  const folderNames = Object.keys(byFolder).sort((a, b) => a.localeCompare(b));

  const toggleFolder = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));

  // Auto-expand the folder that contains the active selection so the user
  // doesn't have to hunt for the highlighted row.
  useEffect(() => {
    const sel = items.find((it) => it.id === selectedId);
    if (sel?.folder && !expanded[sel.folder]) {
      setExpanded((e) => ({ ...e, [sel.folder]: true }));
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragStart = (e, id) => {
    e.dataTransfer.setData('text/x-template-id', id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDropFolder = (e, folder) => {
    e.preventDefault();
    // Folder headers sit inside the root drop zone: without this the drop
    // ALSO fired the root's "move to top level", and the two racing
    // PATCHes usually left the template back at the top level.
    e.stopPropagation();
    const id = e.dataTransfer.getData('text/x-template-id');
    if (!id) return;
    const tpl = items.find((x) => x.id === id);
    if (tpl && (tpl.folder || '') === (folder || '')) return; // unchanged
    onMoveToFolder(id, folder);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // Touch-driven reorder. HTML5 drag events don't fire on mobile, so for
  // touch we use pointer events with a 350ms long-press hold to disambiguate
  // from a tap or a vertical scroll. Once armed, the finger moves a ghost
  // tile and elementFromPoint tells us which folder header (or root drop
  // zone) is currently under the finger; release commits the move.
  const touchStateRef = useRef(null);
  const onRowPointerDown = (e, id) => {
    if (e.pointerType !== 'touch') return; // mouse path keeps the HTML5 DnD flow
    const startX = e.clientX, startY = e.clientY;
    const target = e.currentTarget;
    const state = { id, startX, startY, armed: false, ghost: null, holdTimer: 0, lastDropEl: null };
    touchStateRef.current = state;

    const arm = () => {
      state.armed = true;
      // Build a translucent ghost from the source row.
      const rect = target.getBoundingClientRect();
      const ghost = target.cloneNode(true);
      ghost.classList.add('template-card-ghost');
      ghost.style.position = 'fixed';
      ghost.style.left = rect.left + 'px';
      ghost.style.top = rect.top + 'px';
      ghost.style.width = rect.width + 'px';
      ghost.style.pointerEvents = 'none';
      ghost.style.zIndex = '9999';
      ghost.style.opacity = '0.85';
      ghost.style.transform = 'scale(1.02)';
      document.body.appendChild(ghost);
      state.ghost = ghost;
      target.classList.add('template-card-dragging');
      if (navigator.vibrate) try { navigator.vibrate(8); } catch (_) {}
    };
    state.holdTimer = setTimeout(arm, 350);

    const onMove = (me) => {
      const dx = me.clientX - state.startX;
      const dy = me.clientY - state.startY;
      // Pre-arm: any meaningful movement = scroll, abort the hold.
      if (!state.armed && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        clearTimeout(state.holdTimer);
        cleanup();
        return;
      }
      if (!state.armed) return;
      me.preventDefault();
      state.ghost.style.transform = `translate(${dx}px, ${dy}px) scale(1.02)`;
      // Highlight the folder under the finger.
      const under = document.elementFromPoint(me.clientX, me.clientY);
      const drop = under && under.closest('[data-folder-drop]');
      if (drop !== state.lastDropEl) {
        if (state.lastDropEl) state.lastDropEl.classList.remove('folder-drop-hover');
        if (drop) drop.classList.add('folder-drop-hover');
        state.lastDropEl = drop;
      }
    };
    const onUp = () => {
      clearTimeout(state.holdTimer);
      if (state.armed && state.lastDropEl) {
        const folder = state.lastDropEl.dataset.folderDrop || '';
        onMoveToFolder(id, folder);
      }
      cleanup();
    };
    const cleanup = () => {
      if (state.ghost) state.ghost.remove();
      if (state.lastDropEl) state.lastDropEl.classList.remove('folder-drop-hover');
      target.classList.remove('template-card-dragging');
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      touchStateRef.current = null;
    };
    document.addEventListener('pointermove', onMove, { passive: false });
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  const newFolder = async () => {
    const name = await confirm({
      title: t('templates.newFolder') || 'New folder',
      promptLabel: t('templates.newFolderPrompt') || 'Folder name',
      confirmLabel: 'Create',
    });
    if (name && typeof name === 'string' && name.trim()) onAddFolder(name.trim());
  };

  const listRef = useRef(null);
  const ptrIndicatorRef = useRef(null);
  usePullToRefresh(listRef, ptrIndicatorRef, onRefresh, { enabled: !!isMobile && !!onRefresh });

  return (
    <div className="template-browser">
      <div className="template-browser-header" ref={setHeaderEl}>
        <h3 className="section-label" style={{margin: 0, flex: 1, display: 'flex', alignItems: 'baseline', gap: 6}}>
          <span>{t('templates.heading')}</span>
          {countBadge && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
              letterSpacing: 0, textTransform: 'none',
            }}
            title={tCap != null
              ? t('templates.countAcrossAllWorkspaces', 'Across all workspaces, against your plan cap')
              : t('templates.countTenantTotal', 'Across all workspaces')}>
              {countBadge}
            </span>
          )}
        </h3>
        <div style={{display: 'flex', gap: 4}}>
          <button className="btn btn-icon btn-sm" title={t('templates.newFolder')} aria-label={t('templates.newFolder')} onClick={newFolder}>
            <Icons.FolderPlus />
          </button>
          <button className="btn btn-sm template-browser-add" onClick={onUpload} title={t('templates.add')} aria-label={t('templates.add')}>
            <Icons.Plus /> <span className="template-browser-add-label">{t('templates.add')}</span>
          </button>
        </div>
      </div>
      <input className="input template-browser-search"
             placeholder={t('templates.searchPlaceholder')}
             value={query} onChange={(e) => setQuery(e.target.value)} />

      {/* Pull-to-refresh indicator. Sits above the list, transformY-driven by
          usePullToRefresh. data-refreshing toggles arrow vs spinner. */}
      <div className="ptr-indicator" ref={ptrIndicatorRef} aria-hidden="true">
        <span className="ptr-arrow">↓</span>
        <span className="ptr-spinner" />
      </div>

      <div className="template-browser-list" ref={listRef}
           data-folder-drop=""
           onDragOver={onDragOver}
           onDrop={(e) => onDropFolder(e, '')}>
        {rootItems.map((it) => (
          <TemplateRow key={it.id} item={it} active={it.id === selectedId}
                       onSelect={() => onSelect(it.id)}
                       onDragStart={(e) => onDragStart(e, it.id)}
                       onPointerDown={(e) => onRowPointerDown(e, it.id)}
                       onDuplicate={() => onDuplicate(it.id)} />
        ))}

        {folderNames.map((name) => {
          const open = !!expanded[name];
          const folderItems = byFolder[name];
          return (
            <div key={name} className="sidebar-folder"
                 data-folder-drop={name}
                 onDragOver={onDragOver}
                 onDrop={(e) => onDropFolder(e, name)}>
              <div className="folder-header" role="button" tabIndex={0} aria-expanded={!!open}
                   onClick={() => toggleFolder(name)}
                   onKeyDown={(e) => { if (e.target === e.currentTarget) keyActivate(() => toggleFolder(name))(e); }}>
                <div className="folder-header-left">
                  <Icons.ChevronDown style={{transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', flexShrink: 0}} />
                  <Icons.Folder />
                  <span className="folder-name">{name}</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                  <span className="folder-count">{folderItems.length}</span>
                  {onRenameFolder && (
                    <button type="button"
                            className="folder-delete"
                            title={t('templates.renameFolder', 'Rename folder')}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const newName = await confirm({
                                title: t('templates.renameFolderTitle', 'Rename folder'),
                                promptLabel: t('templates.renameFolderLabel', 'New name'),
                                promptDefault: name,
                                confirmLabel: t('common.rename', 'Rename'),
                              });
                              if (newName && typeof newName === 'string') {
                                onRenameFolder(name, newName);
                              }
                            }}>
                      <Icons.Pencil />
                    </button>
                  )}
                  {onDeleteFolder && (
                    <button type="button"
                            className="folder-delete"
                            title={t('templates.deleteFolder', 'Delete folder')}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={async (e) => {
                              // stopPropagation: don't fire the folder
                              // header's toggle when the user just wants
                              // to delete it.
                              e.stopPropagation();
                              const count = folderItems.length;
                              // Three plural forms: zero (empty draft
                              // folder), one (singular template), many
                              // (plural). Catalog handles the {{name}}
                              // and {{count}} interpolation.
                              const bodyKey = count === 0
                                ? 'templates.deleteFolderConfirmEmpty'
                                : count === 1
                                  ? 'templates.deleteFolderConfirmOne'
                                  : 'templates.deleteFolderConfirmMany';
                              const ok = await confirm({
                                title: t('templates.deleteFolderConfirmTitle'),
                                body: t(bodyKey, { name, count }),
                                confirmLabel: t('common.delete') || 'Delete',
                                danger: true,
                              });
                              if (ok) onDeleteFolder(name, folderItems);
                            }}>
                      <Icons.Trash />
                    </button>
                  )}
                </div>
              </div>
              {open && (
                <div className="folder-body">
                  {folderItems.length === 0 ? (
                    <div className="folder-empty">{t('templates.dropHere', 'Drop a template here')}</div>
                  ) : folderItems.map((it) => (
                    <TemplateRow key={it.id} item={it} active={it.id === selectedId} indented
                                 onSelect={() => onSelect(it.id)}
                                 onDragStart={(e) => onDragStart(e, it.id)}
                                 onPointerDown={(e) => onRowPointerDown(e, it.id)}
                                 onDuplicate={() => onDuplicate(it.id)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TemplateRow({ item, active, indented, onSelect, onDragStart, onPointerDown, onDuplicate }) {
  const t = useT();
  const fieldCount = item.fields?.length || 0;
  const enabled = item.active !== false;
  // Over-cap detection. Two reasons a template can be "locked" for the
  // current plan:
  //   (1) the template was auto-disabled by tier enforcement on a
  //       downgrade — Template.disabledAt is set with disabledReason set,
  //       and item.active is false. The 14-day prune worker will hard-
  //       delete it unless the user upgrades or re-activates.
  //   (2) the template's pageCount exceeds the plan's maxPdfPages —
  //       trying to render would 413, so we surface that up-front.
  // Either path opens the upgrade modal via requestUpgrade() instead of
  // navigating into the template editor.
  const { info: tenantInfo } = useTenantInfo();
  const maxPages = tenantInfo?.limits?.maxPdfPages;
  const overPages = maxPages != null && item.pageCount != null && item.pageCount > maxPages;
  const autoDisabled = !enabled && !!item.disabledAt;
  const locked = overPages || autoDisabled;
  const onCardClick = locked
    ? (e) => {
        e.stopPropagation();
        const code = overPages ? 'pdf_too_many_pages' : 'template_limit_reached';
        const detail = overPages
          ? `This template has ${item.pageCount} pages; your current plan allows up to ${maxPages}.`
          : 'This template was disabled when your plan changed. Upgrade to re-enable it (it will be permanently deleted 14 days after it was disabled).';
        requestUpgrade({
          code,
          detail,
          body: {
            error: code, detail,
            tier: tenantInfo?.plan,
            maxPdfPages: maxPages,
            templateId: item.id,
          },
        });
      }
    : onSelect;
  return (
    <div className={`template-card ${active ? 'active' : ''} ${locked ? 'is-locked' : ''}`}
         role="button" tabIndex={0} aria-current={active ? 'true' : undefined}
         onClick={onCardClick}
         onKeyDown={(e) => { if (e.target === e.currentTarget) keyActivate(onCardClick)(e); }}
         draggable={!locked}
         onDragStart={onDragStart}
         onPointerDown={onPointerDown}
         style={locked ? { opacity: 0.65, cursor: 'pointer' } : null}
         title={locked
           ? (overPages
               ? `Locked: this template has ${item.pageCount} pages, your plan allows ${maxPages}. Click to upgrade.`
               : 'Disabled by your current plan. Click to upgrade — will be permanently deleted 14 days after disable.')
           : undefined}>
      <div className="template-card-name">
        <span>{item.name}</span>
        {locked
          ? <span className="template-card-badge" style={{background: 'rgba(245,180,0,0.15)', color: '#f5b400'}}>{t('templates.upgradeRequired', 'Upgrade required')}</span>
          : <span className="template-card-badge">{fieldCount === 1 ? t('templates.oneField', '1 field') : t('templates.nFields', '{{n}} fields', { n: fieldCount })}</span>}
      </div>
      <div className="template-card-meta">
        <span>{item.originalFilename || '—'}</span>
        <div className="template-card-actions">
          <span className={'template-card-active' + (enabled ? '' : ' disabled')}
                title={enabled ? t('templates.activeHint', 'Active — can be used to create PDFs') : t('templates.pausedHint', 'Paused — can’t be used to create PDFs')}>
            <Icons.Check />
          </span>
          {onDuplicate && (
            <button className="template-card-copy" type="button"
                    title="Duplicate"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
              <Icons.Copy />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// First-run onboarding checklist — shown on the empty Templates page in
// place of the bare "no templates yet" hint. The four steps map 1:1 to
// the actual hello-world flow; the first one ("upload your first PDF")
// is wired to the upload modal so a user can complete it in one click
// without hunting for the "+ New template" button.
// ============================================================================

function FirstRunChecklist({ onUpload }) {
  const t = useT();
  // Shown when a workspace has no templates. Leads with the no-code flow
  // (upload → add fields → fill out); the API is an optional last step.
  // The previous copy promised "rendering filled PDFs from your code",
  // described a click-and-drag gesture the editor doesn't have, and made
  // an API key step 3 of 4.
  const steps = [
    { title: t('firstRun.s1.title', 'Upload a PDF'),
      body: t('firstRun.s1.body', 'Any PDF you fill out regularly — a contract, an invoice, an application. Existing form fields are detected automatically.'),
      action: <button className="btn btn-primary" onClick={onUpload}>{t('templates.uploadPdf', 'Upload PDF')}</button> },
    { title: t('firstRun.s2.title', 'Add fields'),
      body: t('firstRun.s2.body', 'Press “Add field”, then click on the PDF wherever text, a checkbox, a date or a signature should go.') },
    { title: t('firstRun.s3.title', 'Fill it out'),
      body: t('firstRun.s3.body', 'Press “Fill out”, type your values and download the finished PDF — or send it for signature.') },
    { title: t('firstRun.s4.title', 'Automate it (optional)'),
      body: (
        <>
          {t('firstRun.s4.body', 'Create many PDFs from a spreadsheet on the Documents page, or connect your own software with an API key.')}{' '}
          <a href="/docs" target="_blank" rel="noopener">{t('developers.docs', 'API docs')}</a>
        </>
      ) },
  ];
  return (
    <div className="first-run">
      <div className="first-run-title">
        {t('firstRun.title', 'Welcome to Pluma')} <Icon name="wave" className="icon-inline" />
      </div>
      <div className="first-run-sub">{t('firstRun.subtitle', 'Turn any PDF into a form you can fill out in seconds.')}</div>
      <ol className="first-run-steps">
        {steps.map((st, i) => (
          <li key={i}>
            <span className="first-run-num" aria-hidden="true">{i + 1}</span>
            <div className="first-run-text">
              <div className="first-run-step-title">{st.title}</div>
              <div className="first-run-step-body">{st.body}</div>
            </div>
            {st.action}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ============================================================================
// Upload template modal — file picker → presigned S3 PUT → create record
// ============================================================================

function UploadTemplateModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [progress, setProgress] = useState('');
  const { current: ws } = useWorkspace();
  const toast = useToast();
  const t = useT();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast('Pick a PDF file', 'error');
    if (!name.trim()) return toast('Name required', 'error');
    if (!ws) return toast('No workspace selected', 'error');
    setBusy(true);
    try {
      setProgress(t('upload.preparing', 'Preparing upload…'));
      const { url, key } = await api.post(`/api/v1/${ws.slug}/templates/upload-url`, { contentType: file.type || 'application/pdf' });
      setProgress(t('upload.uploading', 'Uploading PDF…'));
      const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/pdf' } });
      if (!putRes.ok) throw new Error(t('upload.failed', 'Upload failed. Please check your connection and try again.'));
      setProgress(t('upload.detecting', 'Looking for form fields…'));
      // pageCount + AcroForm field detection. We read the PDF locally
      // through pdf.js to:
      //   - capture the original page count for the SETTINGS metadata,
      //   - extract any embedded AcroForm fields (Text/Checkbox/Radio)
      //     and pre-populate template.fields with their positions so the
      //     user doesn't have to manually re-place every field on a
      //     fillable PDF.
      // Failures here are non-fatal — we still create the template, just
      // empty.
      let pageCount = null;
      let detectedFields = [];
      let detectionError = null;
      try {
        const buf = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf, isEvalSupported: false }).promise;
        pageCount = doc.numPages;
        detectedFields = await extractAcroFormFields(doc);
      } catch (e) {
        // Detection failure shouldn't block the upload, but we tell
        // the user instead of swallowing the error silently — that
        // was the cause of "it doesn't work at all" reports earlier.
        detectionError = e?.message || String(e);
        console.warn('field auto-detection skipped:', detectionError);
      }
      // Inline JSON schema derived from the detected fields' normalized
      // paths. "Inline" = it rides on the template (template.jsonSchema),
      // not a reusable Schema record. Documents the data contract and
      // pre-fills the schema view; validation only kicks in if the
      // operator later turns on validateData. Null when the PDF had no
      // AcroForm fields.
      const inlineSchema = buildInlineSchemaFromFields(detectedFields);
      const template = await api.post(`/api/v1/${ws.slug}/templates`, {
        name, pdfS3Key: key, fields: detectedFields,
        jsonSchema: inlineSchema,
        originalFilename: file.name || null,
        pageCount,
      });
      if (detectionError) {
        toast(`Detection failed: ${detectionError}. You can still place fields manually.`, 'error');
      } else if (detectedFields.length > 0) {
        toast(t('upload.detectedFields', { n: detectedFields.length }), 'success');
      } else {
        toast(t('upload.noFields', 'No fillable form fields detected in this PDF. Use “Place field” to add fields manually.'), 'info');
      }
      onCreated(template.id);
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
      setProgress('');
    }
  };

  const onFile = (f) => {
    setFile(f);
    if (!name && f.name) setName(f.name.replace(/\.pdf$/i, ''));
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Upload PDF template</h2>
        <form className="form-row" onSubmit={submit}>
          <label className="upload-dropzone"
                 onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                 onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                 onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('dragover'); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}>
            <input type="file" accept="application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            {file ? <div>{file.name} <span style={{color:'var(--text-muted)'}}>({Math.round(file.size/1024)} KB)</span></div> : <div>{t('upload.dropHere', 'Click or drop a PDF here')}</div>}
          </label>
          <div>
            <label className="label" htmlFor="upload-template-name">{t('upload.templateName', 'Template name')}</label>
            <input id="upload-template-name" className="input" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {progress && <div style={{fontSize:12, color:'var(--text-secondary)'}}>{progress}</div>}
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? (progress || t('common.uploading', 'Uploading…')) : t('upload.submit', 'Upload')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Fits a one-row bar to its width by stepping through compaction levels
// until `overflows(el)` is false. Level N sets data-fit-1 … data-fit-N
// (cumulative, so CSS can key on one attribute) plus data-fit="N";
// spa.css decides what each level hides. Measured rather than
// breakpoint-based because the needed width depends on the language and
// on transient content like the save status. Re-fits on resize and
// whenever the bar's text changes. Takes the element (from a callback
// ref) rather than a ref object: the editor toolbar mounts only after
// the template loads. `overflows` must be a stable (module-level) fn.
const rowOverflows = (el) => el.scrollWidth > el.clientWidth + 1;
function useFitToWidth(el, maxLevel, overflows = rowOverflows) {
  React.useLayoutEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const apply = (level) => {
      el.dataset.fit = String(level);
      for (let i = 1; i <= maxLevel; i++) el.toggleAttribute(`data-fit-${i}`, i <= level);
    };
    const fit = () => {
      let level = 0;
      apply(0);
      while (level < maxLevel && overflows(el)) apply(++level);
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(fit); };
    fit();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    // Attributes are deliberately not observed: fit() writes data-fit.
    const mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => { ro.disconnect(); mo.disconnect(); cancelAnimationFrame(raf); };
  }, [el, maxLevel, overflows]);
}
// The template sidebar heading truncates (instead of overflowing) when
// space runs out — that's the signal to make "Add" icon-only.
const headingTruncated = (el) => {
  const label = el.querySelector('.section-label > span');
  return !!label && label.scrollWidth > label.clientWidth + 1;
};

// ============================================================================
// Template editor — pdf.js canvas + field overlays + properties panel
// ============================================================================

function TemplateEditor({ templateId, onTemplateUpdated, onTemplateDeleted, isMobile, mobilePanel, setMobilePanel }) {
  const t = useT();
  const { current: ws } = useWorkspace();
  const wsSlug = ws?.slug;
  const [template, setTemplate] = useState(null);
  // templateRef mirrors `template` for handlers that need to read the
  // latest state on event-fire without re-creating their closure every
  // render. Without this, startDrag / startResize / deleteField would have
  // to depend on `template` in useCallback and their identity would change
  // on every edit — defeating React.memo on FieldOverlay and causing a
  // full re-render storm across all field overlays for every keystroke.
  const templateRef = React.useRef(template);
  React.useEffect(() => { templateRef.current = template; }, [template]);

  // Undo/redo history. Stored on a ref so pushing entries doesn't
  // cause a re-render — only the eventual setTemplate inside undo/redo
  // does. The past stack is capped so a long editing session doesn't
  // grow unbounded; 50 deep template snapshots is ~250KB and matches
  // operator expectations for Cmd+Z range.
  //
  // historyTick is a tiny re-render trigger so the toolbar buttons
  // can disable correctly when past/future empty. Bumped after every
  // mutation; the toolbar reads past.length / future.length each render.
  const historyRef = React.useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);
  const HISTORY_LIMIT = 50;
  const bumpHistory = React.useCallback(() => setHistoryTick((n) => n + 1), []);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  // Initial PDF page from the URL — refresh restores the last viewed
  // page rather than always snapping back to 1.
  const [currentPage, setCurrentPage] = useState(() => {
    // Only when ?template= is THIS template: on a switch the URL still
    // names the previous one, and its ?page=N used to carry over.
    if (readUrlParam('template') !== templateId) return 1;
    const n = parseInt(readUrlParam('page'), 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  // Mirror currentPage to the URL (?page=N). Drop the param when on
  // page 1 to keep URLs clean for the common case.
  useEffect(() => {
    writeUrlParam('page', currentPage > 1 ? currentPage : null);
  }, [currentPage]);
  // When the template changes (user clicks a different one in the
  // sidebar), default back to page 1 unless the URL just told us to
  // open at a specific page. This effect runs only on `templateId`
  // changes, not on every field edit.
  const initialTemplateRef = React.useRef(templateId);
  useEffect(() => {
    if (initialTemplateRef.current !== templateId) {
      initialTemplateRef.current = templateId;
      setCurrentPage(1);
    }
  }, [templateId]);
  const [activeFieldId, setActiveFieldId] = useState(null);
  // Autosave indicator: 'idle' | 'saving' | 'saved' | 'error'. Driven by
  // scheduleSave so the toolbar can reassure the user their silent
  // debounced edits actually persisted.
  const [saveState, setSaveState] = useState('idle');
  const savedTimerRef = React.useRef(null);
  const [placing, setPlacing] = useState(false);
  const [showFill, setShowFill] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState('properties');
  const [canvasReady, setCanvasReady] = useState(false);
  // Per-editor cache mapping `fieldimage:<id>` → resolved presigned URL.
  // Populated lazily by resolveImageRef() when an image field is rendered.
  // Short-lived by design: URLs expire in ~10 min, the editor refetches
  // as needed on next mount.
  const [imageRefCache, setImageRefCache] = useState({});
  const [showImagePicker, setShowImagePicker] = useState(false);
  // Signature library for in-canvas preview of placed user-kind signatures.
  // Fetched once per editor mount and shared across every FieldOverlay so
  // each overlay doesn't re-hit /spa/account/signatures. Keyed { id → {imageUrl, width, height} }.
  // The properties panel uses its own copy to populate the picker dropdown;
  // hoisting it here keeps the overlay preview in sync without props gymnastics.
  const [signatureRefCache, setSignatureRefCache] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get('/spa/account/signatures')
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const s of (r.signatures || [])) {
          map[s.id] = { imageUrl: s.imageUrl, width: s.width, height: s.height };
        }
        setSignatureRefCache((cur) => ({ ...cur, ...map }));
      })
      .catch(() => { /* no library yet — overlay falls back to placeholder */ });
    return () => { cancelled = true; };
  }, []);
  // Signatures placed by OTHER members aren't in my library. Resolve every
  // id the template references through the tenant-scoped endpoint (same
  // rule as the renderer), so a colleague's signature previews like it
  // prints. Re-runs when the set of referenced ids changes.
  const referencedSignatureIds = useMemo(() => {
    const ids = new Set();
    for (const f of (template?.fields || [])) {
      if (f && f.type === 'signature' && f.signatureKind === 'user' && f.signatureId) ids.add(f.signatureId);
    }
    return [...ids].sort().join(',');
  }, [template?.fields]);
  useEffect(() => {
    if (!referencedSignatureIds) return undefined;
    let cancelled = false;
    api.get(`/spa/signature-refs?ids=${encodeURIComponent(referencedSignatureIds)}`)
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const s of Object.values(r.signatures || {})) {
          map[s.id] = { imageUrl: s.imageUrl, width: s.width, height: s.height, owner: s.owner, mine: s.mine };
        }
        setSignatureRefCache((cur) => ({ ...cur, ...map }));
      })
      .catch(() => { /* placeholder stays */ });
    return () => { cancelled = true; };
  }, [referencedSignatureIds]);
  // Linked schema's jsonSchema (or null). Used by PropertiesPanel to
  // power the JSON-path autocomplete dropdown. Refetches whenever the
  // template's schemaId changes; null when the template has no schema.
  const [linkedSchema, setLinkedSchema] = useState(null);
  // PDF zoom level. 1.0 = the editor's previous default (1.5× the PDF's
  // native page width). Steps below match common editor zoom presets.
  const [zoom, setZoom] = useState(1.0);
  // Field overlay visibility. Default ON — overlays are the editor's
  // primary UI. The toggle lets the user briefly preview the PDF clean
  // (e.g. to check how the underlying form looks without our coloured
  // rectangles on top, or to take a screenshot of just the source PDF).
  // Selecting fields still works via the FieldsList tab; clicking on
  // the canvas during placement mode also still works. We don't persist
  // this across sessions — re-opening the editor brings overlays back.
  const [showOverlays, setShowOverlays] = useState(true);
  // Debounced zoom drives the actual pdfjs canvas rerender. Rapid clicks
  // on +/+/+/- coalesce into a single rasterize once the user pauses;
  // a 120 ms idle is short enough to feel instant on a single click yet
  // long enough to swallow a 3-click sequence. Without this, every
  // intermediate zoom triggers a full page.render() — easily 200–400 ms
  // wasted per zoom run on dense PDFs.
  const [debouncedZoom, setDebouncedZoom] = useState(1.0);
  useEffect(() => {
    if (debouncedZoom === zoom) return;
    const id = setTimeout(() => setDebouncedZoom(zoom), 120);
    return () => clearTimeout(id);
  }, [zoom, debouncedZoom]);
  const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0];

  // Pinch-to-zoom on the PDF canvas.
  //
  // Naive setZoom-on-every-move was choppy: the PDF canvas re-renders at
  // `debouncedZoom` (120ms after the last change) so nothing visually
  // moves during the pinch itself — the gesture only commits at release.
  //
  // Two-track approach: apply an instant CSS transform on the wrapper
  // for live visual feedback, then commit the final ratio into zoom
  // state on release so the canvas re-rasters at the proper resolution.
  // The transform is written directly to the DOM via wrapperRef rather
  // than through state to keep the gesture at 60fps — no React reconcile
  // loop in the hot path. transformOrigin pins the scale to the pinch
  // midpoint so the document anchors around the user's fingers.
  //
  // The PDF wrapper has `touch-action: pan-x pan-y` (see spa.css) so
  // the browser still scrolls one-finger drags but yields pinch gestures
  // to us instead of triggering its own page zoom.
  const pinchRef = React.useRef({ pointers: new Map(), startDist: 0, startZoom: 1, liveRatio: 1, midpoint: null });
  // The floor drops to the fit-to-width zoom on narrow screens (0.3–0.5),
  // otherwise pinch/zoom-out clamped to 0.5 and enlarged the page instead.
  const [fitZoom, setFitZoom] = useState(null);
  const ZOOM_MIN = Math.min(ZOOM_STEPS[0], fitZoom || ZOOM_STEPS[0]);
  const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];

  // Fit the page to the available width the first time the canvas is
  // visible, if it doesn't fit at 100%. On a phone the fixed 1.5× scale
  // showed only the middle of the page. Runs again when the mobile pane
  // switches, because a hidden pane has no width to measure.
  const canvasScrollRef = React.useRef(null);
  const [toolbarEl, setToolbarEl] = useState(null);
  useFitToWidth(toolbarEl, 6);
  const fittedRef = React.useRef(false);
  useEffect(() => {
    if (!pdfDoc || fittedRef.current) return;
    const el = canvasScrollRef.current;
    if (!el || !el.clientWidth) return;
    let cancelled = false;
    pdfDoc.getPage(1).then((pg) => {
      if (cancelled) return;
      fittedRef.current = true;
      const pageW = pg.getViewport({ scale: 1.5 }).width;
      const avail = el.clientWidth - 32;
      if (avail > 120 && pageW > avail) {
        const z = Math.max(0.3, Math.floor((avail / pageW) * 100) / 100);
        setFitZoom(z);
        setZoom(z);
        setDebouncedZoom(z);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pdfDoc, mobilePanel]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearWrapperTransform = () => {
    const w = wrapperRef.current;
    if (!w) return;
    w.style.transform = '';
    w.style.transformOrigin = '';
    w.style.willChange = '';
  };
  const onPinchDown = (e) => {
    if (e.pointerType !== 'touch') return;
    const p = pinchRef.current;
    p.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (p.pointers.size === 2) {
      const pts = Array.from(p.pointers.values());
      p.startDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      p.startZoom = zoom;
      p.liveRatio = 1;
      // Anchor the scale around the midpoint of the two fingers in
      // wrapper-local coordinates. Without this the document jumps
      // toward the top-left as it grows.
      const w = wrapperRef.current;
      if (w) {
        const rect = w.getBoundingClientRect();
        const cx = (pts[0].x + pts[1].x) / 2 - rect.left;
        const cy = (pts[0].y + pts[1].y) / 2 - rect.top;
        p.midpoint = { x: cx, y: cy };
        w.style.transformOrigin = `${cx}px ${cy}px`;
        w.style.willChange = 'transform';
      }
    }
  };
  const onPinchMove = (e) => {
    if (e.pointerType !== 'touch') return;
    const p = pinchRef.current;
    if (!p.pointers.has(e.pointerId)) return;
    p.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (p.pointers.size !== 2 || p.startDist === 0) return;
    e.preventDefault();
    const pts = Array.from(p.pointers.values());
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    const targetZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, p.startZoom * (dist / p.startDist)));
    // Live ratio relative to the current rendered canvas resolution
    // (`zoom`). The wrapper CSS transform shows the pinch instantly;
    // the canvas re-rasters only on release.
    p.liveRatio = targetZoom / zoom;
    const w = wrapperRef.current;
    if (w) w.style.transform = `scale(${p.liveRatio})`;
  };
  const commitPinch = () => {
    const p = pinchRef.current;
    if (p.startDist === 0) return;
    // Commit the live ratio into the discrete zoom state. The canvas
    // re-renders at the new resolution; clear the live transform on
    // the same frame to avoid a double-scaled flash.
    const finalZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom * p.liveRatio));
    p.startDist = 0;
    p.liveRatio = 1;
    p.midpoint = null;
    clearWrapperTransform();
    if (Math.abs(finalZoom - zoom) > 0.005) setZoom(finalZoom);
  };
  const onPinchUp = (e) => {
    const p = pinchRef.current;
    p.pointers.delete(e.pointerId);
    if (p.pointers.size < 2 && p.startDist !== 0) commitPinch();
  };

  // Wheel zoom for desktop — Cmd/Ctrl + wheel (or pinch on a multi-touch
  // trackpad, which the browser surfaces as Ctrl+wheel). Without the
  // modifier the wheel falls through to scrolling. preventDefault stops
  // the browser's own ctrl+wheel page-zoom on the same gesture.
  const onWheelZoom = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom * (1 + delta)));
    if (Math.abs(next - zoom) > 0.005) setZoom(next);
  };

  const canvasRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const saveTimerRef = React.useRef(null);
  const toast = useToast();
  const confirm = useConfirm();

  // Load template metadata + PDF.
  //
  // Perf notes:
  //   - Metadata and pdf-url are fetched in PARALLEL — they both depend
  //     only on the template id from the URL, so there's no reason to
  //     serialise them. Saves one round-trip on every editor open.
  //   - /pdf-url returns a STABLE presigned URL (server-cached, see
  //     templates/routes.js) so the browser's HTTP cache keys on the
  //     same URL across template-switches. Switching back to a
  //     template you saw recently → served from disk cache, zero
  //     network. First load of any template → one direct S3 fetch
  //     (no builder proxy in the byte path).
  //   - HTTP/2 keepalive can still stall on a stale connection.
  //     fetchPdfBlobWithRetry's 5 s soft timeout + one auto-retry
  //     forces a fresh connection on the retry — instant recovery.
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    let loadedPdf = null;
    (async () => {
      try {
        const [template, pdfUrlResp] = await Promise.all([
          api.get(`/api/v1/${wsSlug}/templates/${templateId}`),
          api.get(`/spa/${wsSlug}/templates/${templateId}/pdf-url`),
        ]);
        if (cancelled) return;
        setTemplate(template);
        lastSavedRef.current = { ...pickEditable(template), apiCanSetPassword: template.apiCanSetPassword };
        const { url: pdfUrl } = pdfUrlResp;
        const blob = await fetchPdfBlobWithRetry(pdfUrl);
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        // isEvalSupported:false closes CVE-2024-4367 (pdf.js <4.2.67 can run
        // attacker JS from a crafted font). Our CSP allows 'unsafe-eval' for
        // the admin pages, so pdf.js would otherwise take the eval path.
        const pdf = await pdfjsLib.getDocument({ url: blobUrl, isEvalSupported: false }).promise;
        loadedPdf = pdf;
        if (cancelled) { pdf.destroy(); return; }
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        // URL param can outlive the PDF (template was edited/shortened,
        // or someone pasted a stale link). If ?page=N points past the
        // last page, clamp to the last page instead of leaving the viewer
        // blank on a non-existent page. Effect on the URL: writeUrlParam
        // re-runs and either trims to the last page or drops the param.
        setCurrentPage((p) => p > pdf.numPages ? pdf.numPages : p);
      } catch (err) {
        // Shown in place of the canvas — a toast alone left "Loading PDF…"
        // spinning forever.
        if (!cancelled) setLoadError(err.message || String(err));
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      // Free the pdf.js worker's copy of the document — without this the
      // worker's memory grew with every template opened.
      if (loadedPdf) { try { loadedPdf.destroy(); } catch (_) { /* already gone */ } }
    };
  }, [templateId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render the current page to the canvas. `zoom` is the user-controlled
  // multiplier exposed via the toolbar's +/-/reset controls; the
  // baseline 1.5× was the original hardcoded scale, so zoom = 1.0
  // matches what the editor showed before zoom existed.
  // The in-flight pdf.js render. A new page/zoom must cancel it first:
  // pdf.js refuses a second render() on a canvas that's still drawing
  // ("Cannot use the same canvas during multiple render() operations").
  const renderTaskRef = React.useRef(null);
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      const page = await pdfDoc.getPage(currentPage);
      if (cancelled) return;
      const viewport = page.getViewport({ scale: 1.5 * debouncedZoom });
      const canvas = canvasRef.current;
      if (cancelled || !canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      // annotationMode: DISABLE (0) skips every annotation type during
      // canvas rendering — including the form widget appearance streams
      // that carry pre-filled default values. The editor is a template
      // designer; the source PDF's widget appearances would double-
      // render on top of our overlay layer (the original "every field
      // shown twice" report). Our detector already extracts every
      // widget's geometry + defaults into the field model, so the
      // overlay is the canonical view — pdfjs's annotation paint adds
      // nothing here.
      //
      // We intentionally lose link / highlight / stamp annotations too.
      // Form-template PDFs rarely carry them; if a tenant ever needs
      // them visible for design reference, we'd flip ENABLE here and
      // accept that form widgets paint too — there's no pdfjs mode
      // that renders "everything except form widgets".
      //
      // Constant lives on pdfjsLib.AnnotationMode in pdfjs ≥ 3.0; fall
      // back to the integer literal if a sandboxed bundle strips the
      // enum.
      const A = (window.pdfjsLib && window.pdfjsLib.AnnotationMode) || { DISABLE: 0 };
      const task = page.render({ canvasContext: ctx, viewport, annotationMode: A.DISABLE });
      renderTaskRef.current = task;
      await task.promise;
      if (renderTaskRef.current === task) renderTaskRef.current = null;
      if (!cancelled) setCanvasReady(true);
    })().catch((err) => {
      if (err && err.name === 'RenderingCancelledException') return; // superseded
      console.warn('[editor] page render failed', err);
    });
    return () => {
      cancelled = true;
      if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
    };
  }, [pdfDoc, currentPage, debouncedZoom]);

  // Refetch the linked schema whenever the template's schemaId changes.
  // We don't include this in the main template-load effect because the
  // user can swap the linked schema mid-edit without remounting.
  useEffect(() => {
    let cancelled = false;
    if (!template?.schemaId) { setLinkedSchema(null); return; }
    api.get(`/spa/schemas/${template.schemaId}`).then((res) => {
      if (cancelled) return;
      setLinkedSchema(res?.schema?.jsonSchema || null);
    }).catch(() => { if (!cancelled) setLinkedSchema(null); });
    return () => { cancelled = true; };
  }, [template?.schemaId]);

  // Debounced server save — called by both persist (forward mutations)
  // and undo/redo. Same wire shape regardless of which side made the
  // change. Extracted from persist so undo doesn't re-record history
  // through persist's own history push.
  // ── Autosave ────────────────────────────────────────────────────────
  // saveNow sends ONE PATCH with only the columns that differ from the
  // last state the server confirmed (lastSavedRef). Sending every design
  // key on every save made the server bump the version and snapshot a
  // History entry for a mere rename or click, pruning real versions;
  // re-sending a stale `folder` undid folder moves made in the list.
  // Saves run strictly one after another (saveChainRef), so a slow PATCH
  // can't land after a newer one. flushSave pushes any pending edit out
  // and REJECTS if it couldn't be saved — "Fill out" relies on that so it
  // never renders an outdated copy.
  const EDITABLE_KEYS = ['fields', 'name', 'description', 'active', 'fontMode', 'renderer', 'schemaId', 'jsonSchema', 'folder'];
  const pickEditable = (tpl) => {
    const o = {};
    for (const k of EDITABLE_KEYS) o[k] = (tpl && tpl[k] !== undefined) ? tpl[k] : null;
    return o;
  };
  const lastSavedRef = React.useRef(null);
  const pendingSaveRef = React.useRef(null);
  const inflightSaveRef = React.useRef(null);
  const saveChainRef = React.useRef(Promise.resolve(true));
  const retryRef = React.useRef({ timer: null, attempts: 0 });
  const saveNow = useCallback(async (next) => {
    if (!next) return true;
    const base = lastSavedRef.current || {};
    const patch = {};
    for (const k of EDITABLE_KEYS) {
      const v = next[k] !== undefined ? next[k] : null;
      const b = base[k] !== undefined ? base[k] : null;
      if (JSON.stringify(v) !== JSON.stringify(b)) patch[k] = v;
    }
    // Password protection (Phase C #1): the SPA never holds the decrypted
    // password — TemplateEncryptionSection commits `encryptPassword:
    // <new>` or `null`, only when the user touched it.
    if (next.encryptPassword !== undefined) patch.encryptPassword = next.encryptPassword;
    if (next.apiCanSetPassword !== undefined && next.apiCanSetPassword !== base.apiCanSetPassword) patch.apiCanSetPassword = next.apiCanSetPassword;
    const stillPending = () => !!(saveTimerRef.current || pendingSaveRef.current);
    if (Object.keys(patch).length === 0) {
      if (!stillPending()) {
        setSaveState('saved');
        savedTimerRef.current = setTimeout(() => setSaveState('idle'), 2500);
      }
      return true;
    }
    try {
      const saved = await api.patch(`/api/v1/${wsSlug}/templates/${templateId}`, patch);
      retryRef.current.attempts = 0;
      lastSavedRef.current = saved ? { ...pickEditable(saved), apiCanSetPassword: saved.apiCanSetPassword }
                                   : { ...base, ...patch };
      if (!stillPending()) {
        setSaveState('saved');
        savedTimerRef.current = setTimeout(() => setSaveState('idle'), 2500);
      }
      if (saved && onTemplateUpdated) onTemplateUpdated(saved);
      // Merge server-derived flags back into local state (e.g.
      // hasEncryptPassword — the SPA can't infer it from what it sent).
      // Functional update so an edit typed during the round-trip isn't
      // clobbered: local copy stays the base.
      if (saved) {
        setTemplate((prev) => {
          if (!prev || prev.id !== saved.id) return prev;
          return {
            ...prev,
            hasEncryptPassword: saved.hasEncryptPassword ?? prev.hasEncryptPassword,
            apiCanSetPassword: saved.apiCanSetPassword ?? prev.apiCanSetPassword,
            updatedAt: saved.updatedAt ?? prev.updatedAt,
            // The transient encryptPassword was never meant to live in
            // local state; the server has it encrypted now.
            encryptPassword: undefined,
          };
        });
      }
      return true;
    } catch (err) {
      setSaveState('error');
      if (err.code === 'name_taken') {
        // Put the last saved name back so the rest of the edits can save;
        // re-sending the clashing name failed every later save.
        const oldName = base.name;
        toast(t('editor.nameTaken', 'Another template in this workspace is already called “{{name}}”. The name was not changed.', { name: patch.name }), 'error');
        setTemplate((prev) => (prev ? { ...prev, name: oldName } : prev));
        const reverted = { ...(templateRef.current || next), name: oldName };
        setTimeout(() => scheduleSaveRef.current && scheduleSaveRef.current(reverted), 0);
        return false;
      }
      toast(err.message
        ? t('editor.saveFailedWhy', 'Couldn’t save your changes: {{reason}}', { reason: err.message })
        : t('editor.saveFailed', 'Couldn’t save your changes. Your edits are kept in the editor and will retry on the next change.'), 'error');
      // Transient problems (network, 5xx) retry with backoff; plan/permission
      // rejections (4xx) wait for the user's next change.
      const transient = !err.status || err.status >= 500 || err.code === 'network_error';
      const r = retryRef.current;
      if (transient && r.attempts < 5) {
        r.attempts += 1;
        clearTimeout(r.timer);
        r.timer = setTimeout(() => {
          if (!saveTimerRef.current && templateRef.current && scheduleSaveRef.current) scheduleSaveRef.current(templateRef.current);
        }, 2000 * 2 ** (r.attempts - 1));
      }
      return false;
    }
  }, [templateId, toast, onTemplateUpdated, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Queue a save behind any in-flight one.
  const enqueueSave = useCallback((snapshot) => {
    const p = saveChainRef.current.then(() => saveNow(snapshot));
    saveChainRef.current = p.catch(() => false);
    inflightSaveRef.current = p;
    return p;
  }, [saveNow]);

  const scheduleSave = useCallback((next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState('saving');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    pendingSaveRef.current = next;
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      enqueueSave(pending);
    }, 500);
  }, [enqueueSave]);
  // saveNow's error path re-schedules through this ref (it's declared
  // before scheduleSave exists).
  const scheduleSaveRef = React.useRef(null);
  scheduleSaveRef.current = scheduleSave;

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      enqueueSave(pending);
    }
    const ok = await saveChainRef.current;
    if (!ok) {
      throw new Error(t('editor.unsavedForFill', 'Your latest changes couldn’t be saved yet, so the PDF would be out of date. Please try again in a moment.'));
    }
  }, [enqueueSave, t]);

  // Leaving the page with an unsaved or failed edit loses it — ask first.
  useEffect(() => {
    if (saveState !== 'saving' && saveState !== 'error') return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveState]);
  useEffect(() => () => clearTimeout(retryRef.current.timer), []);

  // Forward mutation: snapshot the CURRENT template into history before
  // replacing it. Any new edit also clears the redo stack (standard
  // undo-tree behaviour — branching at the cursor invalidates the
  // future). The save is scheduled the same way as before.
  // `opts.prev`: the state to put on the undo stack. Drag/resize pass the
  // template from BEFORE the gesture — by pointer-up, templateRef already
  // holds the last intermediate move, so Cmd+Z appeared to do nothing.
  const persist = useCallback((next, opts) => {
    const prev = (opts && opts.prev) || templateRef.current;
    if (prev) {
      const h = historyRef.current;
      h.past.push(prev);
      if (h.past.length > HISTORY_LIMIT) h.past.shift();
      h.future = [];
      bumpHistory();
    }
    setTemplate(next);
    scheduleSave(next);
  }, [scheduleSave, bumpHistory]);

  // Cmd+Z: pop a snapshot off `past`, push the current state onto
  // `future`, apply the snapshot, schedule the matching save.
  // No history recording here — that would push the post-undo state
  // onto `past` and Cmd+Z would oscillate between two states.
  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const prev = h.past.pop();
    const current = templateRef.current;
    if (current) h.future.push(current);
    setTemplate(prev);
    scheduleSave(prev);
    bumpHistory();
  }, [scheduleSave, bumpHistory]);

  // Cmd+Shift+Z / Cmd+Y: pop a snapshot off `future`, push the current
  // state onto `past`, apply.
  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const next = h.future.pop();
    const current = templateRef.current;
    if (current) h.past.push(current);
    setTemplate(next);
    scheduleSave(next);
    bumpHistory();
  }, [scheduleSave, bumpHistory]);

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  // historyTick is read here only to make the dependency loop honest —
  // the linter (and the eye) want to see why canUndo/canRedo refresh.
  void historyTick;

  // Click on the canvas to place a new field.
  const onCanvasClick = (e) => {
    if (!placing || !template || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const id = `field_${Date.now()}`;
    // New floating fields default to text. The user retypes them via the
    // Type select at the top of the Properties panel after placement —
    // mirrors the original PDF Microservice Builder's single-button
    // place flow, removing the always-visible type dropdown from the
    // toolbar.
    const existing = template.fields || [];
    const name = nextFieldName(existing, t('editor.newFieldName', 'Field'));
    const field = {
      id, name,
      type: 'text',
      // Bind to data straight away: the key is derived from the name and
      // follows renames while keyAuto is set (see updateField). An empty
      // jsonPath meant a freshly placed field could never be filled, and
      // nothing told non-developers to find the "JSON path" box first.
      jsonPath: uniqueDataKey(existing, fieldKeyFromName(name)), keyAuto: true, page: currentPage,
      x: clamp(x, 0, 95), y: clamp(y, 0, 95),
      width: 0, height: 0,
      fontSize: 12, color: '#000000', bold: false, default: '',
    };
    persist({ ...template, fields: [...(template.fields || []), field] });
    setActiveFieldId(id);
    setPlacing(false);
    setTab('properties');
  };

  // Mouse-driven resize via the bottom-right handle. Works for every
  // field type — text/multiline/checkbox/radio/image. Per-type minimums
  // keep checkboxes from collapsing into a 0×0 box and text fields
  // from going below the minimum readable size.
  // Resize and drag both use Pointer Events so one code path handles
  // mouse, touch, and pen. `setPointerCapture` keeps the move/up
  // events flowing to the original element even if the finger drifts
  // past the field's bounding box. `e.preventDefault()` on touch
  // prevents the browser from interpreting the gesture as a page scroll
  // (paired with `touch-action: none` on the field overlay).
  const startResize = React.useCallback((fieldId, e) => {
    e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    // preventDefault() keeps focus where it was — typically the Properties
    // name box — so Cmd+Z after a drag ran the browser's text undo there
    // (reverting the rename) instead of undoing the move. Release it.
    if (document.activeElement && document.activeElement !== document.body && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const tpl = templateRef.current;
    if (!tpl) return;
    const f0 = tpl.fields.find((f) => f.id === fieldId);
    if (!f0) return;
    if (f0.locked) return;
    const isPoint = f0.type === 'checkbox' || f0.type === 'radio';
    // QR codes are inherently square — the encoder produces a square
    // bitmap and any non-square crop would clip or stretch the
    // modules. Force a 1:1 aspect ratio during resize. Default
    // barcodeKind is 'qrcode' (see badgeLabel/typeOf fallback in
    // FieldOverlay) so an unset kind also counts as QR here.
    const isSquare = f0.type === 'barcode' && (!f0.barcodeKind || f0.barcodeKind === 'qrcode');
    // Shapes / barcodes are pure visual artefacts — operators
    // legitimately want tiny squares (separator dots, bullet marks,
    // thin dividers) or small QR badges, so we drop the resize floor
    // close to zero for both. Text/image/checkbox keep their existing
    // floors because they're glyph-bound and become unreadable below
    // a certain size.
    const isShape = f0.type === 'shape';
    const isBarcode = f0.type === 'barcode';
    const tiny = isShape || isBarcode;
    const startW = f0.width || (f0.type === 'image' ? 15 : isPoint ? 2.5 : isSquare ? 10 : isShape ? 8 : 12);
    const startH = f0.height || (f0.type === 'image' ? 10 : isPoint ? 2.5 : isSquare ? 10 : isShape ? 4 : 4);
    const minW = isPoint ? 1 : tiny ? 0.3 : 2;
    const minH = isPoint ? 1 : tiny ? 0.3 : 1.5;
    let lastW = startW, lastH = startH;
    let moved = false;

    const target = e.currentTarget;
    const pointerId = e.pointerId;
    if (target && target.setPointerCapture && pointerId != null) {
      try { target.setPointerCapture(pointerId); } catch (_) {}
    }

    const onMove = (me) => {
      if (me.pointerId != null && me.pointerId !== pointerId) return;
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      if (isSquare) {
        // QR codes need to render as actual squares. Width/height are
        // stored as PERCENTAGES of the page, but a real PDF page (e.g.
        // A4 595×842 pt) isn't square — equal width% and height% would
        // produce a stretched rectangle. Do the math in pixel space
        // (rect.* already reflects the page's true aspect ratio at
        // render scale) and convert back to percentages per axis.
        const startWPx = (startW / 100) * rect.width;
        const startHPx = (startH / 100) * rect.height;
        const dxPx = (dx / 100) * rect.width;
        const dyPx = (dy / 100) * rect.height;
        const minSidePx = Math.max((minW / 100) * rect.width, (minH / 100) * rect.height);
        const sidePx = Math.max(minSidePx, Math.max(startWPx + dxPx, startHPx + dyPx));
        lastW = (sidePx / rect.width) * 100;
        lastH = (sidePx / rect.height) * 100;
      } else {
        lastW = Math.max(minW, startW + dx);
        lastH = Math.max(minH, startH + dy);
      }
      moved = true;
      setTemplate((prev) => ({
        ...prev,
        fields: prev.fields.map((f) => f.id === fieldId ? { ...f, width: lastW, height: lastH } : f),
      }));
    };
    const onUp = (ue) => {
      if (ue && ue.pointerId != null && ue.pointerId !== pointerId) return;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      if (target && target.releasePointerCapture && pointerId != null) {
        try { target.releasePointerCapture(pointerId); } catch (_) {}
      }
      // A click on the handle isn't an edit (no save, no History entry).
      if (!moved) return;
      const cur = templateRef.current || tpl;
      const next = { ...cur, fields: cur.fields.map((f) => f.id === fieldId ? { ...f, width: lastW, height: lastH } : f) };
      // Undo should return to the size from BEFORE the gesture.
      persist(next, { prev: tpl });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, [persist]);

  const startDrag = React.useCallback((fieldId, e) => {
    e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    // preventDefault() keeps focus where it was — typically the Properties
    // name box — so Cmd+Z after a drag ran the browser's text undo there
    // (reverting the rename) instead of undoing the move. Release it.
    if (document.activeElement && document.activeElement !== document.body && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const tpl = templateRef.current;
    if (!tpl) return;
    const f0 = tpl.fields.find((f) => f.id === fieldId);
    if (!f0) return;
    if (f0.locked) return;
    let lastX = f0.x, lastY = f0.y;

    const target = e.currentTarget;
    const pointerId = e.pointerId;
    if (target && target.setPointerCapture && pointerId != null) {
      try { target.setPointerCapture(pointerId); } catch (_) {}
    }

    const onMove = (me) => {
      if (me.pointerId != null && me.pointerId !== pointerId) return;
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      lastX = clamp(f0.x + dx, 0, 95);
      lastY = clamp(f0.y + dy, 0, 95);
      setTemplate((prev) => ({
        ...prev,
        fields: prev.fields.map((f) => f.id === fieldId ? { ...f, x: lastX, y: lastY } : f),
      }));
    };
    const onUp = (ue) => {
      if (ue && ue.pointerId != null && ue.pointerId !== pointerId) return;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      if (target && target.releasePointerCapture && pointerId != null) {
        try { target.releasePointerCapture(pointerId); } catch (_) {}
      }
      // Selecting a field is a click with no movement — not an edit, so no
      // save and no History version.
      if (lastX === f0.x && lastY === f0.y) return;
      const cur = templateRef.current || tpl;
      const next = { ...cur, fields: cur.fields.map((f) => f.id === fieldId ? { ...f, x: lastX, y: lastY } : f) };
      // Undo should return to where the field was BEFORE the drag.
      persist(next, { prev: tpl });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, [persist]);

  const NON_DATA_TYPES = ['shape', 'signature'];
  const updateField = (id, patchIn) => {
    if (!template) return;
    const field = template.fields.find((f) => f.id === id);
    if (!field) return;
    const patch = { ...patchIn };

    // ── Data key rules ──
    // A key typed (or picked) by hand is never rewritten again. An
    // auto key (keyAuto, set on newly placed fields) follows the name,
    // made unique among the other fields. Legacy fields without the flag
    // are left alone unless they have no key at all — existing API/CSV
    // integrations may depend on them.
    const hasKey = (k) => Object.prototype.hasOwnProperty.call(patch, k);
    const nextType = hasKey('type') ? patch.type : field.type;
    if (hasKey('jsonPath') && !hasKey('name')) {
      patch.keyAuto = patch.jsonPath === '';
    }
    const autoKey = (patch.keyAuto !== undefined ? patch.keyAuto : field.keyAuto) || !field.jsonPath;
    if (NON_DATA_TYPES.includes(nextType)) {
      // Shapes/signatures never read data: drop an auto key so it doesn't
      // leak into the fill form, samples and schemas.
      if (field.keyAuto && hasKey('type')) { patch.jsonPath = ''; patch.keyAuto = true; }
    } else if (hasKey('name') && autoKey && String(patch.name).trim()) {
      patch.jsonPath = uniqueDataKey(template.fields, fieldKeyFromName(patch.name), id);
      patch.keyAuto = true;
    } else if (hasKey('type') && autoKey && !field.jsonPath && (field.name || '').trim()) {
      // Back from shape/signature to a data type: re-derive.
      patch.jsonPath = uniqueDataKey(template.fields, fieldKeyFromName(field.name), id);
      patch.keyAuto = true;
    }
    // A library image on an auto-keyed image field makes it static: the
    // fill form shouldn't then ask for an upload.
    if (hasKey('imageUrl') && patch.imageUrl && field.keyAuto && field.type === 'image') {
      patch.jsonPath = '';
    }
    // Required/default make no sense on fields that never read data.
    if (NON_DATA_TYPES.includes(nextType)) { delete patch.required; }

    // AcroForm radio widgets of one group share a single value: data key,
    // default and required must stay identical across the group, or the
    // PDF draws two dots / the fill form splits the group in two.
    const isFormRadio = field.type === 'radio' && field.fieldType === 'form' && !!field.formFieldName;
    const groupKeys = ['jsonPath', 'default', 'required', 'keyAuto'].filter(hasKey);

    // Form-detected radio buttons that belong to the same AcroForm
    // group (same formFieldName, fieldType === 'form') share ONE
    // transform-code value across the whole group. The renderer reads
    // transformCode per-widget, so the SPA keeps every sibling in
    // lockstep — editing the code on any widget in the group writes
    // it to all of them in a single persist. Floating (manually
    // placed) radios have no formFieldName and remain independent.
    const isLinkedRadioCodeEdit =
      field.type === 'radio' &&
      field.fieldType === 'form' &&
      !!field.formFieldName &&
      Object.prototype.hasOwnProperty.call(patch, 'transformCode');

    persist({
      ...template,
      fields: template.fields.map((f) => {
        if (f.id === id) return { ...f, ...patch };
        const sameGroup = f.type === 'radio' && f.fieldType === 'form' && f.formFieldName === field.formFieldName;
        if (isLinkedRadioCodeEdit && sameGroup) {
          return { ...f, transformCode: patch.transformCode };
        }
        if (isFormRadio && sameGroup && groupKeys.length) {
          const shared = {};
          for (const k of groupKeys) shared[k] = patch[k];
          return { ...f, ...shared };
        }
        return f;
      }),
    });
  };

  // Stable across renders so React.memo on FieldOverlay isn't busted.
  // Reads the latest template via the ref so a delete after unrelated
  // edits sees the current fields array.
  const deleteField = React.useCallback((id) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    persist({ ...tpl, fields: tpl.fields.filter((f) => f.id !== id) });
    setActiveFieldId((current) => (current === id ? null : current));
  }, [persist]);

  // Confirm-then-delete wrapper. Every delete entry point (canvas × on
  // the field overlay, sidebar × on the FieldsList, Delete button in
  // the properties panel toolbar) routes through here so the prompt
  // copy stays in one place and a misclick on any of them surfaces the
  // same dialog. Locked fields can't be deleted at all — the PP
  // toolbar disables its button, the canvas × is hidden, and this
  // belt+suspenders check rejects programmatic calls too.
  const confirmDeleteField = React.useCallback(async (id) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    const f = tpl.fields.find((ff) => ff.id === id);
    if (!f || f.locked) return;
    const ok = await confirm({
      title: 'Delete field?',
      body: `"${f.name || f.formFieldName || 'this field'}" will be removed from the template.`,
      confirmLabel: 'Delete', danger: true,
    });
    if (ok) deleteField(id);
  }, [confirm, deleteField]);

  // Arrow-key nudge for the active field. Moves in % of page dimension:
  // 0.2% per press, 1% with Shift. Clamped so a field can't be nudged
  // off the page. Locked (form-detected) fields don't move.
  const nudgeField = React.useCallback((id, dx, dy) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    const f = tpl.fields.find((ff) => ff.id === id);
    if (!f || f.locked) return;
    const clamp = (v) => Math.max(0, Math.min(100, v));
    const nx = clamp((f.x || 0) + dx);
    const ny = clamp((f.y || 0) + dy);
    if (nx === f.x && ny === f.y) return;
    persist({ ...tpl, fields: tpl.fields.map((ff) => ff.id === id ? { ...ff, x: nx, y: ny } : ff) });
  }, [persist]);

  // Global keyboard shortcuts. Skipped when focus is in a form input
  // so the browser's native input-level undo still works for text
  // editing in field-name / json-path / etc. fields.
  React.useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (e.target && e.target.isContentEditable);
      // Field-level shortcuts (no modifier): nudge with arrows, remove
      // with Delete/Backspace — only when a field is selected and the
      // user isn't typing in a form control.
      if (!typing && activeFieldId) {
        if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); confirmDeleteField(activeFieldId); return; }
        const step = e.shiftKey ? 1 : 0.2;
        let dx = 0, dy = 0;
        if (e.key === 'ArrowLeft') dx = -step;
        else if (e.key === 'ArrowRight') dx = step;
        else if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        if (dx || dy) { e.preventDefault(); nudgeField(activeFieldId, dx, dy); return; }
      }
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (typing) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, activeFieldId, confirmDeleteField, nudgeField]);


  // Clear/reset wrapper. Wipes the user-configured values on a field
  // (default, transform script, JSON-path binding, checkbox/radio
  // value mappings, image/signature references) but preserves the
  // field's identity, position, size, page, and field type. Useful
  // when an operator wants to undo a misconfigured field without
  // re-creating the placement.
  //
  // AcroForm-detected fields go through this same code path —
  // clearing one restores its "unconfigured" badge in the list while
  // keeping the binding to the underlying PDF widget intact. Locked
  // fields are rejected (same guard as delete + duplicate).
  const clearField = React.useCallback((id) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    persist({
      ...tpl,
      fields: tpl.fields.map((f) => {
        if (f.id !== id) return f;
        // Spread first, then overwrite the value-bearing keys to
        // their "empty" form. Anything not listed here stays — that's
        // what keeps the placement, font, type, and form bindings
        // intact across a clear.
        return {
          ...f,
          default: null,
          transformCode: '',
          jsonPath: '',
          checkboxOnValue: undefined,
          radioOptionIndex: undefined,
          imageRefId: null,
          signatureId: null,
          signatureImageUrl: null,
        };
      }),
    });
  }, [persist]);

  const confirmClearField = React.useCallback(async (id) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    const f = tpl.fields.find((ff) => ff.id === id);
    if (!f || f.locked) return;
    const ok = await confirm({
      title: 'Clear this field?',
      body: `"${f.name || f.formFieldName || 'this field'}" will be reset to an unconfigured state. Its placement on the PDF stays — only the default value, transform, JSON-path binding, and image/signature reference are wiped.`,
      confirmLabel: 'Clear', danger: true,
    });
    if (ok) clearField(id);
  }, [confirm, clearField]);

  // Duplicate a placed field. New copy gets a fresh id, the same
  // bindings and style, and a small offset position so it's visible as
  // distinct from the original instead of overlapping it pixel-perfect.
  // Form-detected fields can't meaningfully duplicate (they're tied to
  // a specific PDF widget rect) — silently refuse rather than creating
  // a copy with `fieldType: 'form'` that points at nothing.
  const duplicateField = React.useCallback((id) => {
    const tpl = templateRef.current;
    if (!tpl) return;
    const src = tpl.fields.find((f) => f.id === id);
    if (!src) return;
    if (src.fieldType === 'form') return;
    // Offset by ~2 percentage points on both axes — visually distinct
    // on the editor canvas at typical zoom without scrolling off the
    // page. Clamp so we don't push the field past the right/bottom
    // edge; if it would clip, wrap back to 1% from the page origin.
    const offsetX = src.x + 2 > 100 - src.width  ? 1 : src.x + 2;
    const offsetY = src.y + 2 > 100 - src.height ? 1 : src.y + 2;
    const copy = {
      ...src,
      id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      // " · copy" is locale-agnostic and reads cleanly when chained
      // (`Customer name · copy · copy` makes the chain obvious vs.
      // numeric suffixes that get ambiguous when the user manually
      // renames in between).
      name: (src.name || 'field') + ' · copy',
      // An auto key gets its own unique key (the copy is a separate value
      // in the fill form); a hand-picked key is kept, i.e. shared on
      // purpose.
      ...(src.keyAuto ? {
        jsonPath: uniqueDataKey(tpl.fields, fieldKeyFromName((src.name || 'field') + ' · copy')),
        keyAuto: true,
      } : {}),
      x: offsetX,
      y: offsetY,
      // Lock status doesn't transfer. The whole point of duplicating a
      // locked field is usually "I want a near-copy that I can edit" —
      // inheriting the lock would force the user to immediately
      // unlock the new field just to make it useful. Stay opt-in.
      locked: false,
    };
    persist({ ...tpl, fields: [...tpl.fields, copy] });
    setActiveFieldId(copy.id);
  }, [persist]);

  // Stable handler for FieldOverlay's onSelect. Wrapping the
  // setMobilePanel call in a check up here keeps the per-overlay JSX
  // free of inline closures so memoization isn't defeated.
  const selectField = React.useCallback((id) => {
    setActiveFieldId(id);
    setTab('properties');
    if (isMobile && setMobilePanel) setMobilePanel('properties');
  }, [isMobile, setMobilePanel]);

  // Cmd/Ctrl+D — duplicate the active field. We swallow the browser's
  // native "add bookmark" only when an actual field is selected, so the
  // shortcut still works as expected on other pages. Skip when the user
  // is typing into a form control (input/textarea/select/contentEditable)
  // — otherwise the shortcut hijacks the user mid-rename.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.key === 'd' || e.key === 'D')) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (!activeFieldId) return;
      const tgt = e.target;
      if (tgt) {
        const tag = (tgt.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || tgt.isContentEditable) return;
      }
      // Respect the field-level lock: silently ignore Cmd/Ctrl+D so
      // the browser's native bookmark shortcut still fires through if
      // the user is on a locked field — surfacing a toast every press
      // would be too noisy for a binding many users hit reflexively.
      const tpl = templateRef.current;
      const active = tpl?.fields?.find((f) => f.id === activeFieldId);
      if (active?.locked) return;
      e.preventDefault();
      duplicateField(activeFieldId);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeFieldId, duplicateField]);

  const activeField = template?.fields?.find((f) => f.id === activeFieldId);
  const pageFields = (template?.fields || []).filter((f) => (f.page || 1) === currentPage);

  // Resolve fieldimage:<id> references to presigned URLs for editor preview.
  useEffect(() => {
    const ids = new Set();
    for (const f of pageFields) {
      if (f.type === 'image' && typeof f.imageUrl === 'string' && f.imageUrl.startsWith('fieldimage:')) {
        const id = f.imageUrl.slice('fieldimage:'.length);
        if (!imageRefCache[id]) ids.add(id);
      }
    }
    if (ids.size === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all([...ids].map(async (id) => {
        try { const { url } = await api.get(`/spa/field-images/${id}/url`); return [id, url]; }
        catch { return [id, null]; }
      }));
      if (cancelled) return;
      setImageRefCache((prev) => {
        const next = { ...prev };
        for (const [id, url] of entries) if (url) next[id] = url;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [currentPage, template?.fields]);

  // Resolve an imageUrl to a displayable URL (handles fieldimage: prefix).
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('fieldimage:')) return imageRefCache[url.slice('fieldimage:'.length)] || '';
    return url;
  };

  if (loadError && (!template || !pdfDoc)) {
    return <LoadErrorScreen message={loadError} onRetry={() => { setLoadError(null); setReloadKey((k) => k + 1); }} />;
  }
  if (!template) return <LoadingFrame label={t('editor.loadingTemplate', 'Loading template…')} />;

  const deleteTemplate = async () => {
    try {
      if (!(await confirmAndDeleteTemplate({ wsSlug, templateId, name: template.name, confirm, t }))) return;
      if (onTemplateDeleted) onTemplateDeleted(templateId);
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
    <div className="editor-layout">
        <div className={'editor-canvas-area'
                        + (isMobile && mobilePanel === 'properties' ? ' is-hidden' : '')}>
          <div className="editor-toolbar" ref={setToolbarEl}>
            {/* All four control groups share the same outlined-pill
                .page-nav style — Place / Hide on the left, Zoom + Pages
                on the right. Previously Place + Hide rendered as the
                chunky .btn rectangles which read as a different
                control class from zoom/pagination; users found the
                mix visually noisy. The .is-active modifier flips a
                toggle into its engaged state (accent-tinted) without
                changing rectangle size, so the row of buttons reads
                like one unified toolbar. */}
            <div className="page-nav">
              {/* Undo / redo. Disabled when no history exists on that
                  side. Cmd+Z (undo), Cmd+Shift+Z or Cmd+Y (redo). */}
              <button onClick={undo} disabled={!canUndo} title={t('editor.undo', 'Undo (⌘Z)')}
                      aria-label={t('editor.undo', 'Undo (⌘Z)')}>
                <Icons.Undo />
              </button>
              <button className="editor-redo-btn" onClick={redo} disabled={!canRedo} title={t('editor.redo', 'Redo (⌘⇧Z)')}
                      aria-label={t('editor.redo', 'Redo (⌘⇧Z)')}>
                <Icons.Redo />
              </button>
              {/* Single Place Field button (no type dropdown). Field
                  type is chosen from the Properties panel after
                  placement — matches the original PDF Microservice
                  Builder UX so the user has one toolbar entry for
                  "add a thing" instead of two coupled controls. New
                  fields default to text; the user retypes them in
                  Properties when needed. */}
              <button className={'editor-place-btn' + (placing ? ' is-active' : '')}
                      onClick={() => {
                        // Force overlays back on when entering placing
                        // mode — otherwise the user clicks the canvas,
                        // places a field, and sees nothing because the
                        // overlay layer is hidden. Defensive: doesn't
                        // toggle overlays off when LEAVING placing mode.
                        if (!placing) setShowOverlays(true);
                        setPlacing(!placing);
                      }}
                      title={placing ? t('editor.placeHint', 'Click on the PDF to place the field') : t('editor.placeField', 'Add field')}
                      aria-label={placing ? t('editor.placeHint', 'Click on the PDF to place the field') : t('editor.placeField', 'Add field')}>
                <Icons.Plus />
                <span className="btn-text-mobile">
                  {placing ? t('editor.placeHintShort', 'Click on the PDF…') : t('editor.placeField', 'Add field')}
                </span>
              </button>
              {/* Field overlay visibility. is-active kicks in when
                  overlays are HIDDEN (the non-default state) so a user
                  who accidentally hid them notices the button looks
                  unusual and knows where to click to bring them back. */}
              <button className={'editor-overlay-btn' + (showOverlays ? '' : ' is-active')}
                      onClick={() => setShowOverlays((v) => !v)}
                      title={showOverlays ? t('editor.hideFields', 'Hide fields to see the plain PDF') : t('editor.showFields', 'Show fields')}
                      aria-label={showOverlays ? t('editor.hideFields', 'Hide fields to see the plain PDF') : t('editor.showFields', 'Show fields')}
                      aria-pressed={!showOverlays}>
                {showOverlays ? <Icons.Layers /> : <Icons.LayersOff />}
                <span className="btn-text-mobile">
                  {showOverlays ? t('editor.fieldsShown', 'Hide fields') : t('editor.fieldsHidden', 'Show fields')}
                </span>
              </button>
            </div>
            <div className="spacer" />
            {saveState !== 'idle' && (
              <span className={'editor-save-status is-' + saveState} role="status" aria-live="polite" style={{
                fontSize: 12, marginRight: 10, whiteSpace: 'nowrap',
                color: saveState === 'error' ? 'var(--danger)' : 'var(--text-muted)',
              }}>
                {saveState === 'saving' ? t('editor.saving', 'Saving…')
                  : saveState === 'saved' ? t('editor.savedAll', 'All changes saved')
                  : t('editor.saveError', 'Save failed — will retry')}
              </span>
            )}
            {/* Zoom controls. Desktop users without a touch screen
                or a Cmd/Ctrl+wheel reflex need a button to fall back
                on, so we keep −/100%/+ visible on desktop. The
                whole group hides on mobile (.zoom-controls is
                display:none in the mobile media query) where
                pinch-to-zoom + the freed toolbar width matter more. */}
            <div className="page-nav zoom-controls" style={{marginRight: 12}}>
              <button onClick={() => setZoom((z) => Math.max(ZOOM_MIN, ZOOM_STEPS.slice().reverse().find((s) => s < z - 0.001) || ZOOM_MIN))}
                      disabled={zoom <= ZOOM_MIN + 0.001}
                      title={t('editor.zoomOut', 'Zoom out')} aria-label={t('editor.zoomOut', 'Zoom out')}>−</button>
              <button onClick={() => setZoom(1.0)}
                      title={t('editor.zoomReset', 'Reset to 100%')}
                      style={{minWidth: 56, fontVariantNumeric: 'tabular-nums'}}>
                {Math.round(zoom * 100)}%
              </button>
              <button onClick={() => setZoom((z) => Math.min(ZOOM_MAX, ZOOM_STEPS.find((s) => s > z + 0.001) || ZOOM_MAX))}
                      disabled={zoom >= ZOOM_MAX - 0.001}
                      title={t('editor.zoomIn', 'Zoom in')} aria-label={t('editor.zoomIn', 'Zoom in')}>+</button>
            </div>
            <div className="page-nav page-nav-pages">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      title={t('editor.prevPage', 'Previous page')} aria-label={t('editor.prevPage', 'Previous page')}>‹</button>
              <span className="page-label"><span className="page-prefix">{t('editor.pagePrefix', 'Page')} </span>{currentPage}<span className="page-sep"> / </span>{totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      title={t('editor.nextPage', 'Next page')} aria-label={t('editor.nextPage', 'Next page')}>›</button>
            </div>
            {/* The main thing people want to do with a template: fill it
                in and get a PDF — no JSON, no API key. */}
            <button type="button" className="btn btn-primary btn-sm editor-fill-btn"
                    disabled={!template}
                    onClick={() => setShowFill(true)}
                    title={t('editor.fillHint', 'Fill in this template and download the PDF')}
                    aria-label={t('editor.fill', 'Fill out')}>
              <Icon name="pen" />
              <span className="btn-text-mobile">{t('editor.fill', 'Fill out')}</span>
            </button>
          </div>
          <div className="canvas-scroll" ref={canvasScrollRef}
               onPointerDown={onPinchDown}
               onPointerMove={onPinchMove}
               onPointerUp={onPinchUp}
               onPointerCancel={onPinchUp}
               onWheel={onWheelZoom}>
            {pdfDoc ? (
              <div className={`pdf-wrapper ${placing ? 'placing' : ''}`} ref={wrapperRef} onClick={onCanvasClick}>
                <canvas ref={canvasRef} />
                {canvasReady && showOverlays && pageFields.map((f) => (
                  <FieldOverlay key={f.id}
                                field={f}
                                templateFont={template?.fontMode}
                                templateRenderer={template?.renderer}
                                resolvedImageUrl={f.type === 'image' ? resolveImageUrl(f.imageUrl) : ''}
                                resolvedSignatureUrl={f.type === 'signature' && f.signatureKind === 'user' && f.signatureId
                                  ? signatureRefCache[f.signatureId]?.imageUrl || ''
                                  : ''}
                                selected={activeFieldId === f.id}
                                onSelect={selectField}
                                onDragStart={startDrag}
                                onResizeStart={startResize}
                                onDelete={confirmDeleteField} />
                ))}
              </div>
            ) : (
              <LoadingFrame label={t('editor.loadingPdf', 'Loading PDF…')} />
            )}
          </div>
        </div>

        <div className={'editor-side'
                        + (isMobile && mobilePanel !== 'properties' ? ' is-hidden' : '')
                        + (isMobile ? ' is-mobile' : '')}>
          <div className="editor-tabs" role="tablist">
            {[
              ['fields', t('editor.tab.fields', 'Fields')],
              ['properties', t('editor.tab.properties', 'Properties')],
              // "Schema" meant nothing to non-developers: this tab shows
              // the data a template expects (and the JSON for the API).
              ['schema', t('editor.tab.data', 'Data')],
              ['versions', t('editor.tab.history', 'History')],
              ['settings', t('editor.tab.settings', 'Settings')],
            ].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id}
                      className={`editor-tab ${tab === id ? 'active' : ''}`}
                      title={label}
                      onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </div>
          <div className="editor-tab-body">
            {tab === 'fields' && (
              <FieldsList fields={template.fields || []} activeId={activeFieldId}
                          currentPage={currentPage}
                          onSelect={(f) => { setActiveFieldId(f.id); setCurrentPage(f.page || 1); setTab('properties'); }}
                          onDelete={confirmDeleteField} />
            )}
            {tab === 'properties' && (
              activeField
                ? <PropertiesPanel field={activeField}
                    onChange={(patch) => {
                      updateField(activeField.id, patch);
                      // Auto-follow page changes — when the user moves a
                      // field to another page via the Page input, jump
                      // the canvas there too. Without this the field
                      // appears to vanish (it's on a different page now)
                      // and the user thinks the input didn't work.
                      if (patch.page && patch.page !== currentPage) {
                        setCurrentPage(patch.page);
                      }
                    }}
                    onPickImage={activeField.type === 'image' ? () => setShowImagePicker(true) : undefined}
                    onDuplicate={duplicateField}
                    onClear={confirmClearField}
                    onDelete={confirmDeleteField}
                    schema={linkedSchema || template?.jsonSchema || null}
                    templateFont={template?.fontMode}
                    templateRenderer={template?.renderer}
                    totalPages={totalPages} />
                : (
                  // No field selected: a short quick-start instead of a
                  // blank panel — new users land here first.
                  <div className="quick-start">
                    <div className="quick-start-title">{t('editor.quickStart.title', 'Quick start')}</div>
                    <ol>
                      <li>
                        <strong>{t('editor.quickStart.step1Title', 'Place fields')}</strong>
                        <span>{t('editor.quickStart.step1', 'Click “Add field”, then click on the PDF where text should go. Click any field to rename or restyle it.')}</span>
                      </li>
                      <li>
                        <strong>{t('editor.quickStart.step2Title', 'Fill it out')}</strong>
                        <span>{t('editor.quickStart.step2', 'Press “Fill out”, type your values and download the finished PDF.')}</span>
                        <button type="button" className="btn btn-sm btn-primary" style={{marginTop: 8}} onClick={() => setShowFill(true)}>
                          {t('editor.fill', 'Fill out')}
                        </button>
                      </li>
                      <li>
                        <strong>{t('editor.quickStart.step3Title', 'Use your own PDF')}</strong>
                        <span>{t('editor.quickStart.step3', 'Upload a PDF with “Add” on the left. Existing form fields are detected automatically.')}</span>
                      </li>
                    </ol>
                    <div className="quick-start-foot">
                      {t('editor.quickStart.more', 'Need many PDFs at once? Use “Create many” on the Documents page with a spreadsheet, or automate it with the API.')}
                    </div>
                  </div>
                )
            )}
            {/* Linked and inline schemas are mutually exclusive on the
                server (linking clears jsonSchema, saving inline clears
                schemaId). Mirror that locally, or the next autosave
                re-sent the stale side and silently undid the change. */}
            {tab === 'schema' && (
              <SchemaTab template={template}
                         onLink={(schemaId) => persist({ ...template, schemaId, ...(schemaId ? { jsonSchema: null } : {}) })}
                         onUpdateTemplate={(patch) => persist({ ...template, ...patch, ...(patch && patch.jsonSchema ? { schemaId: null } : {}) })} />
            )}
            {tab === 'versions' && (
              <VersionsPanel
                template={template}
                onRestored={(updated) => {
                  // The server already applied the restore as a new
                  // version — adopt its row wholesale so fields/schema
                  // and the version counter all match what's persisted.
                  setTemplate(updated);
                  setActiveFieldId(null);
                }}
              />
            )}
            {tab === 'settings' && (
              <TemplateSettingsTab
                template={template}
                onChange={(patch) => persist({ ...template, ...patch })}
                onDelete={deleteTemplate}
              />
            )}
          </div>
        </div>
      </div>

      {showFill && (
        <FillTemplateView
          template={template}
          pdfDoc={pdfDoc}
          beforeGenerate={flushSave}
          onClose={() => setShowFill(false)}
        />
      )}
      {showImagePicker && (
        <ImagePickerModal
          onClose={() => setShowImagePicker(false)}
          onSelect={(ref) => {
            if (activeFieldId) updateField(activeFieldId, { imageUrl: ref });
            setShowImagePicker(false);
          }}
        />
      )}
    </>
  );
}

// Settings tab inside the editor's right panel. Matches screenshot 3:
// name, description, font/character set, status panel, read-only metadata
// (id, file key, original filename, page count, created/updated dates),
// download original PDF, delete template.
// TemplateEncryptionSection — operator-facing controls for Phase C #1.
// Local-only text state: typing into the password input doesn't fire
// onChange until the operator commits via Save (otherwise every
// keystroke would PATCH the template with a fresh AES envelope and
// flood the audit log). A `hasEncryptPassword` boolean from the server
// stands in for the stored value so the SPA can show "Password is set"
// without the password ever shipping to the client.
function TemplateEncryptionSection({ template, onChange }) {
  const t = useT();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const has = !!template.hasEncryptPassword;
  const apiCan = !!template.apiCanSetPassword;
  const hasRequestSignature = Array.isArray(template.fields)
    && template.fields.some((f) => f && f.type === 'signature' && (f.signatureKind || 'request') === 'request');

  const save = () => {
    if (!pw || pw !== pw2) return;
    onChange({ encryptPassword: pw });
    setEditing(false);
    setPw('');
    setPw2('');
  };
  // Removing protection takes effect for every PDF created afterwards and
  // can't be undone (the password isn't stored in readable form) — ask.
  const clear = async () => {
    const ok = await confirm({
      title: t('encryption.clearTitle', 'Remove password protection?'),
      body: t('encryption.clearBody', 'PDFs created from this template will no longer require a password to open. To protect them again you will need to set a new password.'),
      confirmLabel: t('encryption.clearCta', 'Remove protection'),
      danger: true,
    });
    if (ok) onChange({ encryptPassword: null });
  };

  return (
    <div>
      <LabelWithInfo info={t('encryption.info', 'When set, every PDF created from this template needs this password to open. The password is stored encrypted and never shown again. Password-protected templates can’t be sent for signature, because the signer couldn’t open the document.')}>
        {t('encryption.title', 'PDF password')}
      </LabelWithInfo>

      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap'}}>
        {has && !editing && (
          <>
            <span style={{fontSize: 13}}>
              <span style={{color: 'var(--success)', marginRight: 6}} aria-hidden="true">●</span>
              {t('encryption.isSet', 'PDFs from this template are password-protected.')}
            </span>
            <button className="btn btn-sm" onClick={() => setEditing(true)}>{t('encryption.change', 'Change')}</button>
            <button className="btn btn-sm btn-danger" onClick={clear}>{t('encryption.remove', 'Remove')}</button>
          </>
        )}
        {!has && !editing && (
          <>
            <span style={{fontSize: 13, color: 'var(--text-muted)'}}>
              {t('encryption.notSet', 'No password — anyone with the PDF can open it.')}
            </span>
            <button className="btn btn-sm" onClick={() => setEditing(true)}>{t('encryption.set', 'Set password')}</button>
          </>
        )}
      </div>

      {editing && (
        // Entered twice: a typo here would lock every future PDF behind a
        // password nobody knows (it can't be displayed again).
        <div style={{display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8}}>
          <input className="input" type="password" autoComplete="new-password"
                 aria-label={t('encryption.newPassword', 'New password')}
                 placeholder={t('encryption.newPassword', 'New password')}
                 value={pw} onChange={(e) => setPw(e.target.value)} />
          <input className="input" type="password" autoComplete="new-password"
                 aria-label={t('encryption.repeatPassword', 'Repeat password')}
                 placeholder={t('encryption.repeatPassword', 'Repeat password')}
                 value={pw2} onChange={(e) => setPw2(e.target.value)} />
          {pw2 && pw !== pw2 && (
            <div style={{fontSize: 12, color: 'var(--danger)'}}>{t('encryption.mismatch', 'The passwords don’t match.')}</div>
          )}
          <div style={{display: 'flex', gap: 6}}>
            <button className="btn btn-sm btn-primary" onClick={save} disabled={!pw || pw !== pw2}>{t('common.save', 'Save')}</button>
            <button className="btn btn-sm" onClick={() => { setEditing(false); setPw(''); setPw2(''); }}>{t('common.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, marginTop: 6}}>
        <input type="checkbox"
               checked={apiCan}
               onChange={(e) => onChange({ apiCanSetPassword: e.target.checked })} />
        {t('encryption.apiOverride', 'Let integrations set a different password for each PDF (API)')}
      </label>

      {has && hasRequestSignature && (
        <div style={{
          marginTop: 10,
          padding: '8px 10px',
          borderRadius: 6,
          background: 'color-mix(in srgb, var(--warning) 14%, transparent)',
          border: '1px solid color-mix(in srgb, var(--warning) 40%, transparent)',
          fontSize: 12,
          lineHeight: 1.45,
        }}>
          <strong>{t('encryption.noSignTitle', 'Signature requests are unavailable for this template.')}</strong>{' '}
          {t('encryption.noSignBody', 'It has both a password and a signature field. Signers couldn’t open a password-protected PDF, so remove the password to send it for signature (or duplicate the template without one).')}
        </div>
      )}
    </div>
  );
}

const PLAN_ORDER = ['free', 'essentials', 'advanced', 'pro', 'enterprise'];

// (TemplateResponseSection removed — /generate picks bytes vs JSON
// via the Accept header now, no per-template knob needed.)

function TemplateSettingsTab({ template, onChange, onDelete }) {
  const t = useT();
  const { current: ws } = useWorkspace();
  const [downloading, setDownloading] = useState(false);
  // Font catalogue for the template-level default — same source as the
  // field-level picker (PropertiesPanel), so the two stay consistent.
  const [fonts, setFonts] = useState(null);
  useEffect(() => { loadFontsCatalogue().then(setFonts); }, []);
  // Legacy `fontMode` values (standard / unicode) predate the catalogue;
  // surface them as their nearest catalogue id so the dropdown can show
  // the correct selection while the stored value catches up on the next
  // save. The renderer keeps recognising the legacy values either way.
  const LEGACY_FONT_MODE = { standard: 'helvetica', extended: 'dejavu-sans', unicode: 'dejavu-sans' };
  const storedFont = template.fontMode || 'helvetica';
  const selectedFont = LEGACY_FONT_MODE[storedFont] || storedFont;

  const downloadOriginal = async () => {
    setDownloading(true);
    try {
      const { url: pdfUrl } = await api.get(`/spa/${ws.slug}/templates/${template.id}/pdf-url`);
      // Open in a new tab so the browser handles the download dialog.
      window.open(pdfUrl, '_blank', 'noopener');
    } catch (err) {
      // useToast is available via context inside the editor; pass-through.
      console.error('download failed', err);
    } finally { setDownloading(false); }
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap: 18}}>
      <div>
        <label className="label">{t('templateSettings.name')}</label>
        <input className="input"
               value={template.name || ''}
               onChange={(e) => onChange({ name: e.target.value })} />
      </div>

      <div>
        <label className="label">{t('templateSettings.description')}</label>
        <textarea className="input"
                  rows={3}
                  placeholder={t('templateSettings.descriptionPlaceholder')}
                  value={template.description || ''}
                  onChange={(e) => onChange({ description: e.target.value })} />
      </div>

      <div>
        <LabelWithInfo info={t('templateSettings.fontHelp', 'Default font for every field on this template. Fields can override it from the properties panel.')}>
          {t('templateSettings.fontMode')}
        </LabelWithInfo>
        <select className="input"
                value={selectedFont}
                onChange={(e) => onChange({ fontMode: e.target.value })}>
          {!fonts && <option value={selectedFont}>{selectedFont}</option>}
          {fonts && (fonts.builtin || []).length > 0 && (
            <optgroup label={t('templateSettings.fontBuiltin', 'Built-in')}>
              {fonts.builtin.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </optgroup>
          )}
          {fonts && (fonts.custom || []).length > 0 && (
            <optgroup label={t('templateSettings.fontCustom', 'Custom')}>
              {customFamilyEntries(fonts.custom).map((entry) => (
                <option key={entry.family} value={entry.family}
                        title={entry.variants.map(variantLabel).join(', ')}>
                  {entry.displayName}
                  {entry.variants.length > 1 ? ` (${entry.variants.length} variants)` : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      <div>
        <LabelWithInfo info={(
          <>
            <div style={{marginBottom: 6}}>
              <strong>{t('templateSettings.rendererOptionPlumav1')}</strong>{' '}
              — {t('templateSettings.rendererHelpPlumav1')}
            </div>
            <div>
              <strong>{t('templateSettings.rendererOptionStandard')}</strong>{' '}
              — {t('templateSettings.rendererHelpStandard')}
            </div>
          </>
        )}>
          {t('templateSettings.renderer')}
        </LabelWithInfo>
        <select className="input"
                value={template.renderer || 'plumav1'}
                onChange={(e) => onChange({ renderer: e.target.value })}>
          <option value="plumav1">{t('templateSettings.rendererOptionPlumav1')}</option>
          <option value="standard">{t('templateSettings.rendererOptionStandard')}</option>
        </select>
      </div>

      <div>
        <label className="label">{t('templateSettings.status')}</label>
        <div className={'template-status-card card ' + (template.active ? 'card-success' : 'card-warn')}
             onClick={() => onChange({ active: !template.active })}
             role="button"
             tabIndex={0}
             onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange({ active: !template.active }); } }}>
          <strong>{template.active ? t('templateSettings.active') : t('templateSettings.inactive')}</strong>
          <span className="template-status-hint">
            {template.active ? t('templateSettings.activeHint') : t('templateSettings.inactiveHint')}
          </span>
        </div>
      </div>

      <TemplateEncryptionSection template={template} onChange={onChange} />

      <div className="template-settings-meta">
        <div><span className="meta-label">ID:</span> <CopyValue value={template.id} /></div>
        {template.originalFilename && <div><span className="meta-label">Original:</span> {template.originalFilename}</div>}
        {template.pageCount != null && <div><span className="meta-label">Pages:</span> {template.pageCount}</div>}
        {template.createdAt && <div><span className="meta-label">Created:</span> {formatDateTime(template.createdAt)}</div>}
        {template.updatedAt && <div><span className="meta-label">Updated:</span> {formatDateTime(template.updatedAt)}</div>}
      </div>

      <button className="btn" onClick={downloadOriginal} disabled={downloading}>
        <Icon name="download" className="icon-inline" />{t('templateSettings.downloadOriginal')}
      </button>

      <button className="btn btn-danger" onClick={onDelete}>
        <Icon name="trash" className="icon-inline" />{t('templateSettings.delete')}
      </button>
    </div>
  );
}

// Whether a field's "binding" is wired up — drives the configured /
// unconfigured visual states the original PDF Microservice Builder uses.
function isFieldConfigured(f) {
  if (!f) return false;
  if (f.type === 'image') return !!f.imageUrl;
  if (f.jsonPath || f.transformCode) return true;
  const d = f.default;
  if (d === undefined || d === null || d === '' || d === false) return false;
  return true;
}

// Type-icon character for the field overlay. Rendered in serif so 'T'
// reads as Times-New-Roman shorthand for "this is a text field" rather
// than a generic letter. Number/currency icons are forward-declared
// here so the renderer is ready before those field types land in Phase B.
function fieldTypeIcon(f) {
  if (!f) return '';
  if (f.type === 'number')   return '#';
  if (f.type === 'currency') return '$';
  // These four return an ICON NAME (see ICON_PATHS), not a character: they
  // used to be the dingbats ✱ ▦ ◇ ✎, which rendered differently per platform
  // and couldn't take the theme colour. '#', '$' and 'T' stay as characters —
  // they're real typography, not pictographs.
  if (f.type === 'date')     return { icon: 'asterisk' };
  if (f.type === 'barcode')  return { icon: 'grid' };
  if (f.type === 'shape')    return { icon: 'diamond' };
  if (f.type === 'signature') return { icon: 'pen' };
  // Text and multiline both surface as 'T' — they're the same
  // information conceptually, weight + paragraph style live in the
  // properties panel.
  return 'T';
}

// Secondary hint shown next to the field name when there's room: the
// jsonPath the renderer will read, or "custom script" / the default
// value when no path is bound. Returns '' when there's nothing useful
// to display (image / point-style fields, or fully-unbound text).
function fieldSecondaryHint(f) {
  if (!f) return '';
  if (f.transformCode) return 'custom script';
  if (f.jsonPath) return f.jsonPath;
  if (f.type === 'checkbox' || f.type === 'radio' || f.type === 'image') return '';
  if (f.type === 'shape') return f.shapeKind || 'rectangle';
  if (f.type === 'barcode') return f.barcodeKind || 'qrcode';
  if (f.type === 'signature') return f.signatureKind === 'user' ? 'user signature' : 'request';
  if (f.default !== undefined && f.default !== null && f.default !== '' && f.default !== false) {
    return String(f.default);
  }
  return '';
}

// The field's display name. Falls back through formFieldName (AcroForm
// widget name) → 'field' so something always shows.
function fieldDisplayName(f) {
  if (!f) return 'field';
  return f.name || f.formFieldName || 'field';
}

// DejaVu Sans is one of the renderer's built-in font modes (the
// "extended" / "unicode" catalogue option) and is the only built-in
// face that ISN'T guaranteed to be installed on the user's machine —
// Helvetica/Times/Courier are PDF Base 14, present everywhere; DejaVu
// is shipped with the Go renderer via go:embed and copied into
// builder/public/static/fonts at deploy time. Without this @font-face
// rule the editor canvas falls back to Arial/sans-serif for any field
// set to DejaVu, which doesn't match what the PDF actually renders.
//
// Runs ONCE on script load — no catalogue fetch required. The two
// .ttf files are served by the builder's /static/fonts mount with a
// 1-year immutable cache, so the browser only ever downloads them on
// the first visit per device. font-display: swap shows the fallback
// while the bytes stream in (single-digit milliseconds on cache hit;
// ~50 ms on cold load) instead of blocking text paint.
(function injectBuiltinFontFaces() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('style[data-pluma-builtin-font-faces]')) return;
  const el = document.createElement('style');
  el.setAttribute('data-pluma-builtin-font-faces', '');
  el.textContent = [
    '@font-face {',
    '  font-family: "DejaVu Sans";',
    '  font-weight: 400;',
    '  font-style: normal;',
    '  font-display: swap;',
    '  src: url("/static/fonts/DejaVuSans.ttf") format("truetype");',
    '}',
    '@font-face {',
    '  font-family: "DejaVu Sans";',
    '  font-weight: 700;',
    '  font-style: normal;',
    '  font-display: swap;',
    '  src: url("/static/fonts/DejaVuSans-Bold.ttf") format("truetype");',
    '}',
  ].join('\n');
  document.head.appendChild(el);
})();

// Module-scope set of tenant custom-font family slugs the SPA has
// injected @font-face rules for. Populated by registerCustomFonts() when
// the catalogue arrives; read by cssFontStackFor below so an editor
// canvas preview can use the actual custom face instead of falling back
// to Helvetica.
const customFontFamilies = new Set();

// CSS font stacks that approximate each catalogue id in the browser. Used
// by FieldOverlay so the on-canvas preview reads like the rendered PDF
// (the renderer's pdf.SetFont maps these same ids to fpdf families). The
// stacks fall back through similar local faces because DejaVu and the
// PDF Standard 14 aren't guaranteed installed on every user's machine.
function cssFontStackFor(id) {
  switch (id) {
    case 'times':        return 'Times, "Times New Roman", serif';
    case 'courier':      return 'Courier, "Courier New", ui-monospace, monospace';
    case 'symbol':       return 'Symbol, "Apple Symbols", serif';
    case 'zapfdingbats': return 'ZapfDingbats, "Zapf Dingbats", serif';
    case 'dejavu-sans':
    case 'extended':
    case 'unicode':      return '"DejaVu Sans", DejaVu, Arial, sans-serif';
    case 'helvetica':
    case 'standard':
    case '':
    case undefined:
    case null:           return 'Helvetica, Arial, sans-serif';
    default:
      // If we've registered an @font-face for this family, surface it as
      // the primary preference with sensible fallbacks. JSON.stringify
      // escapes the slug safely for use inside a CSS font-family list.
      if (id && customFontFamilies.has(id)) {
        return JSON.stringify(id) + ', Helvetica, Arial, sans-serif';
      }
      return 'Helvetica, Arial, sans-serif';
  }
}

// textDecorationStyle — translate a field's textDecoration enum into
// a CSS-property partial that the editor canvas overlay merges into its
// inline style. The renderer (renderer.go drawTextDecoration) draws the
// exact same line position + dash pattern in the output PDF, so the
// preview and the print match.
//
// Returns an empty object for "none" / unknown values so the spread in
// the caller is a no-op.
function textDecorationStyle(decoration) {
  switch (decoration) {
    case 'underline':         return { textDecoration: 'underline solid' };
    case 'underline-dotted':  return { textDecoration: 'underline dotted' };
    case 'underline-dashed':  return { textDecoration: 'underline dashed' };
    case 'line-through':      return { textDecoration: 'line-through solid' };
    case 'overline':          return { textDecoration: 'overline solid' };
    default:                  return {};
  }
}

// Text alignment inside the field box — the text-like types the renderer
// draws through drawText. Values mirror the renderer: textAlign left
// (default) | center | right; verticalAlign top | middle | bottom, where
// "unset" keeps each engine's historical placement — PLUMAv1 starts
// multiline text at the top, everything else sits in the middle.
const ALIGNABLE_TYPES = new Set(['text', 'multiline', 'number', 'currency']);
function isAlignableField(field) {
  return ALIGNABLE_TYPES.has(field.type || 'text');
}
function fieldTextAlign(field) {
  return field.textAlign === 'center' || field.textAlign === 'right' ? field.textAlign : 'left';
}
function fieldVerticalAlign(field, renderer) {
  if (['top', 'middle', 'bottom'].includes(field.verticalAlign)) return field.verticalAlign;
  return field.type === 'multiline' && renderer !== 'standard' ? 'top' : 'middle';
}

// customFamilyEntries groups uploaded Font rows by family slug, sorts the
// variants inside each family by (weight, style) for deterministic
// display, and picks a stable display name (the shortest variant name,
// usually the "Regular" cut). Used by the field font pickers so a
// customer who uploaded 4 weights of "Acme Sans" sees ONE row, not 4.
function customFamilyEntries(customs) {
  if (!Array.isArray(customs) || customs.length === 0) return [];
  const byFamily = new Map();
  for (const f of customs) {
    if (!byFamily.has(f.family)) byFamily.set(f.family, []);
    byFamily.get(f.family).push(f);
  }
  const out = [];
  for (const [family, variants] of byFamily) {
    variants.sort((a, b) => (a.weight - b.weight) || a.style.localeCompare(b.style));
    // Pick the "Regular" cut if present, otherwise the shortest name.
    const regular = variants.find((v) => v.weight === 400 && v.style === 'normal');
    const displayName = regular?.name
      || [...variants].sort((a, b) => a.name.length - b.name.length)[0].name;
    out.push({ family, displayName, variants });
  }
  out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return out;
}

function variantLabel(v) {
  return v.weight + (v.style === 'italic' ? ' italic' : '');
}

// lookupFontEntry — resolves a field's `font` id (or template fontMode)
// into the catalogue entry that describes which variants exist. Returns
// null when the catalogue hasn't loaded yet or the id is unknown; callers
// should treat null as "permissive" — better to allow a checkbox we can't
// validate than to lock the user out while data is in flight.
function lookupFontEntry(id, fonts) {
  if (!id || !fonts) return null;
  const builtin = (fonts.builtin || []).find((f) => f.id === id);
  if (builtin) return builtin;
  // For custom fonts, `id` is the family slug — aggregate every uploaded
  // variant under the same family so variantSupport sees the full set.
  const variants = (fonts.custom || []).filter((f) => f.family === id);
  if (variants.length === 0) return null;
  return { kind: 'custom', family: id, variants };
}

// variantSupport — given a font catalogue entry, return which (bold,
// italic) combinations this font actually has glyphs for. Drives the
// SPA's bold/italic checkbox enablement so a user can't tick a combo
// the renderer would silently downgrade.
//
// Built-ins declare `weights` + `styles` arrays; the supported combos
// are the cross-product (every built-in either has all four cuts or
// none-of-italic). Customs declare an explicit list of {weight, style}
// variants — bold means any weight ≥ 600, italic means style==='italic'.
// Threshold matches fpdfStyleFor on the renderer (custom_fonts.go:99).
function variantSupport(entry) {
  if (!entry) return { regular: true, bold: true, italic: true, boldItalic: true };
  if (entry.kind === 'custom') {
    const has = (boldWanted, italicWanted) => entry.variants.some((v) =>
      ((v.weight >= 600) === boldWanted) &&
      ((v.style === 'italic') === italicWanted));
    return {
      regular:    has(false, false),
      bold:       has(true,  false),
      italic:     has(false, true ),
      boldItalic: has(true,  true ),
    };
  }
  // Built-in: cross-product of declared weights × styles.
  const hasReg  = (entry.weights || []).includes(400);
  const hasBold = (entry.weights || []).includes(700);
  const hasNorm = (entry.styles || []).includes('normal');
  const hasItal = (entry.styles || []).includes('italic');
  return {
    regular:    hasReg  && hasNorm,
    bold:       hasBold && hasNorm,
    italic:     hasReg  && hasItal,
    boldItalic: hasBold && hasItal,
  };
}

// canCheckBold / canCheckItalic — applies the rule the user asked for:
//   "checking either bold or italic makes the other not updateable and
//    unchecked" when the font doesn't have the boldItalic cut.
// boldEnabled means: the user is allowed to enable bold given current italic state.
function canCheckBold(support, italicCurrentlyChecked) {
  return italicCurrentlyChecked ? support.boldItalic : support.bold;
}
function canCheckItalic(support, boldCurrentlyChecked) {
  return boldCurrentlyChecked ? support.boldItalic : support.italic;
}

// registerCustomFonts injects one @font-face rule per uploaded font
// variant so the editor canvas can render in the real glyphs. Called
// every time the fonts catalogue is loaded; idempotent — re-registering
// the same (family, weight, style) is a no-op.
//
// Each variant gets the correct font-weight + font-style descriptor so
// the browser does its own weight/italic resolution: writing
// `font-family: "acme-sans"; font-weight: 700; font-style: italic`
// in cssFontStackFor + the field's bold/italic flags is enough — we
// don't have to manually pick variants in code.
//
// We use a single <style> element keyed by `data-pluma-font-faces` and
// rewrite its textContent each time the catalogue loads. That keeps the
// DOM tidy and avoids accumulating stale rules across catalogue reloads
// (which we expect — see the 5-minute refresh policy in FontsPage).
function registerCustomFonts(customs) {
  if (!Array.isArray(customs)) return;
  let styleEl = document.querySelector('style[data-pluma-font-faces]');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.setAttribute('data-pluma-font-faces', '');
    document.head.appendChild(styleEl);
  }
  const rules = [];
  customFontFamilies.clear();
  for (const f of customs) {
    if (!f || !f.family || !f.url) continue;
    customFontFamilies.add(f.family);
    // font-display: swap shows the fallback while loading; without it the
    // browser blocks text rendering for up to 3 s which feels broken in
    // the editor. The trade-off (brief font-swap flash) is fine here.
    const fmt = f.format === 'otf' ? 'opentype' : 'truetype';
    rules.push(
      '@font-face {' +
      ' font-family: ' + JSON.stringify(f.family) + ';' +
      ' font-weight: ' + (f.weight || 400) + ';' +
      ' font-style: ' + JSON.stringify(f.style || 'normal') + ';' +
      ' font-display: swap;' +
      ' src: url(' + JSON.stringify(f.url) + ') format(' + JSON.stringify(fmt) + ');' +
      ' }'
    );
  }
  // Only write textContent when the rules actually changed. A
  // CSSOM mutation triggers a stylesheet recalculation across the
  // whole document; in the editor where the catalogue load can fire
  // multiple times in quick succession (mount + idempotent refresh +
  // panel re-open) this used to cost ~50–100 ms per call for nothing.
  const next = rules.join('\n');
  if (styleEl.textContent !== next) {
    styleEl.textContent = next;
  }
}

// React.memo with the default shallow comparator. All four callback
// props (onSelect, onDragStart, onResizeStart, onDelete) are stable
// across the parent's renders thanks to React.useCallback in
// TemplateEditor; `field` keeps the same reference for unchanged fields
// (the parent's setTemplate updater maps and only the mutated field gets
// a new object). So when the user drags one field, only THAT FieldOverlay
// re-renders — not all 50 of them. This is the difference between a
// snappy editor and a 500-ms-per-mousemove freeze on field-heavy templates.
const FieldOverlay = React.memo(function FieldOverlay({ field, resolvedImageUrl, resolvedSignatureUrl, templateFont, templateRenderer, selected, onSelect, onDragStart, onResizeStart, onDelete }) {
  const t = field.type;
  const isCheckbox = t === 'checkbox';
  const isRadio = t === 'radio';
  const isImage = t === 'image';
  const isShape = t === 'shape';
  const isSignature = t === 'signature';
  const isPoint = isCheckbox || isRadio;
  // Form-detected fields are anchored to the PDF widget rect — moving or
  // resizing them would break the mapping. The original handles them as
  // click-to-select-only overlays with a dynamic label inside; we
  // mirror that. Floating (manually-placed) fields keep drag/resize.
  const isForm = field.fieldType === 'form';
  const configured = isFieldConfigured(field);

  // Type rendering — surface the same font / weight / color the renderer
  // will use, so the on-canvas overlay reads close to the final PDF.
  // Per-field `font` overrides the template default; the renderer applies
  // exactly this fallback chain (resolveFieldFont → ctx.family).
  const fontId = field.font || templateFont || 'helvetica';
  const typeStyle = isImage ? {} : {
    fontSize: `${(field.fontSize || 12) * 1.2}px`,
    fontFamily: cssFontStackFor(fontId),
    fontWeight: field.bold || isCheckbox ? 700 : 400,
    fontStyle: field.italic ? 'italic' : 'normal',
    color: field.color || undefined,
    // Map the field's textDecoration enum to CSS text-decoration props
    // so the editor preview looks the same as the rendered PDF. The CSS
    // here mirrors the renderer's manual line draw at drawTextDecoration
    // (decoration position, dash style); a tenant tweaking either side
    // sees consistent results.
    ...textDecorationStyle(field.textDecoration),
  };

  // Inline size when a width/height exists. Form-detected fields always
  // have one (from the PDF widget rect); floating fields may not, in
  // which case the CSS rule's defaults take over.
  const hasExplicitSize = field.width && field.height;
  // Shape overlays merge in actual fill / border CSS so the editor
  // preview reads close to what the renderer's drawShape will paint.
  // borderRadius:50% turns the rectangle into an ellipse (or a circle
  // when width==height) to mirror fpdf.Ellipse.
  const shapeStyle = isShape ? {
    background: field.shapeFillColor || 'transparent',
    border: (field.shapeBorderWidth > 0 && field.shapeBorderColor)
      ? `${field.shapeBorderWidth}px solid ${field.shapeBorderColor}`
      : '1px dashed rgba(108, 92, 231, 0.4)',
    borderRadius: field.shapeKind === 'ellipse' || field.shapeKind === 'circle' ? '50%' : 0,
  } : {};
  const style = (isPoint && !hasExplicitSize)
    ? { left: `${field.x}%`, top: `${field.y}%`, ...typeStyle }
    : {
        left: `${field.x}%`, top: `${field.y}%`,
        width: field.width ? `${field.width}%` : 'auto',
        height: field.height ? `${field.height}%` : 'auto',
        ...typeStyle,
        ...shapeStyle,
      };

  // Pre-checked state for form-detected checkbox / radio. The detector
  // writes the AcroForm's pre-set value into `field.default`:
  //   - checkbox: 'true' when the source PDF widget was pre-checked
  //   - radio:    a string-formatted radioIndex pointing at the
  //               pre-selected option (every widget in the group
  //               carries the same default value; the renderer's
  //               drawRadio only stamps the widget whose own
  //               radioIndex matches).
  // The overlay surfaces this as both a class (for the heavier
  // background tint) and a small glyph badge so the operator can see
  // at a glance which boxes/radios already carry default values from
  // the source PDF — the prior version just showed the label and the
  // pre-checked state was effectively invisible.
  const isPrechecked =
    (isCheckbox && (field.default === true || field.default === 'true')) ||
    (isRadio && field.default !== '' && field.default != null && Number(field.default) === field.radioIndex);

  // Track the overlay's rendered pixel height so we can hide the X
  // delete button when there isn't room for it (anything below 16px
  // means a 14px button + 1px breathing room on each side would
  // overlap the resize handle / field content). Pure CSS would need a
  // container query on every overlay, and that interacts poorly with
  // the position:absolute layout — the ResizeObserver is simpler and
  // only does work on overlays that actually change size.
  const overlayRef = React.useRef(null);
  const [isTiny, setIsTiny] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = overlayRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height ?? 0;
      setIsTiny(h < 16);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cls = ['field-overlay'];
  if (isCheckbox) cls.push('checkbox', `check-${field.checkStyle || 'x'}`);
  if (isRadio) cls.push('radio');
  if (isImage) cls.push('image');
  if (isShape) cls.push('shape');
  if (isSignature) cls.push('signature');
  if (selected) cls.push('selected');
  if (isForm) cls.push('form-detected', configured ? 'configured' : 'unconfigured');
  if (isPrechecked) cls.push('is-prechecked');
  if (field.locked) cls.push('is-locked');
  if (isTiny) cls.push('is-tiny');
  // Place the label where the renderer will put the value.
  if (isAlignableField(field)) {
    cls.push(`h-${fieldTextAlign(field)}`, `v-${fieldVerticalAlign(field, templateRenderer)}`);
  }

  // Pre-compute the three label pieces so the JSX below is uniform. The
  // hint is rendered unconditionally — flexbox shrinks it to 0 width
  // (CSS rule `.field-label-hint`) when the overlay isn't wide enough,
  // satisfying "show jsonPath / default only if there's space".
  const labelName = fieldDisplayName(field);
  const labelIcon = fieldTypeIcon(field);
  const labelHint = fieldSecondaryHint(field);

  return (
    <div className={cls.join(' ')}
         ref={overlayRef}
         style={style}
         role="button" tabIndex={0} aria-label={labelName}
         aria-pressed={selected ? 'true' : 'false'}
         onClick={(e) => { e.stopPropagation(); onSelect(field.id); }}
         onKeyDown={(e) => {
           if (e.target !== e.currentTarget) return;
           if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(field.id); }
         }}
         onPointerDown={isForm ? undefined : (e) => onDragStart(field.id, e)}>
      {isShape ? (
        // Shape overlay paints its appearance entirely through `style`
        // (background, border, border-radius). No inner content — a
        // shape is decorative, the operator identifies it from the
        // canvas and the properties panel.
        null
      ) : isSignature && field.signatureKind === 'user' && resolvedSignatureUrl ? (
        // Placed user-kind signature with a selection — preview the
        // actual signature in the overlay so the operator sees what
        // the renderer will embed. `contain` keeps the aspect ratio
        // intact (signatures are typically wide-and-short). Request-
        // kind signatures fall through to the label below: they're
        // placeholders, not data, and the recipient's signature only
        // exists post-sign.
        <img className="field-img-preview" src={resolvedSignatureUrl} alt="" draggable={false}
             style={{objectFit: 'contain'}} />
      ) : isImage ? (
        resolvedImageUrl
          ? <img className="field-img-preview" src={resolvedImageUrl} alt="" draggable={false}
                 style={{
                   objectFit: field.objectFit === 'contain' ? 'contain'
                            : field.objectFit === 'cover'   ? 'cover'
                            : 'fill',
                 }} />
          : <div className="field-img-empty">{field.name || '[image]'}</div>
      ) : isCheckbox ? (
        // Form-detected checkbox: just the precheck indicator when the
        // source PDF had this widget marked. We deliberately DON'T
        // render the widget name inside the overlay — the rect is the
        // PDF widget's own size (typically 10–14 px on screen), and a
        // squeezed-in `undefined_2` reads as a glitch. The name still
        // lives in the properties panel and the fields list.
        // Floating checkbox: just the check glyph when the user set
        // default=true (preview of the rendered output).
        isForm ? (
          isPrechecked
            ? <span className="field-overlay-precheck" aria-label="Pre-checked in source PDF">✓</span>
            : null
        ) : (
          <span className="field-check">
            {field.default === true ? (field.checkStyle === 'square' ? '■' : field.checkStyle === 'circle' ? '●' : '✕') : ''}
          </span>
        )
      ) : isRadio ? (
        // Form-detected radio: precheck dot only — same rationale as
        // checkbox above. Floating radios stay as bare dots (CSS
        // ::before draws the inner circle).
        isForm ? (
          isPrechecked
            ? <span className="field-overlay-precheck" aria-label="Pre-selected in source PDF">●</span>
            : null
        ) : null
      ) : (
        <span className="field-label">
          <span className="field-label-icon">{labelIcon && labelIcon.icon ? <Icon name={labelIcon.icon} /> : labelIcon}</span>
          <span className="field-label-name">{labelName}</span>
          {labelHint && <span className="field-label-hint">{labelHint}</span>}
        </span>
      )}

      {/* Resize handle on floating fields only. Form-detected fields are
          tied to the PDF widget rect and aren't resizable here. Locked
          fields also hide the handle entirely — keeping a non-functional
          square pinned to the corner would read as "broken" rather than
          "intentionally locked". */}
      {selected && !isForm && !field.locked && (
        <div className="field-resize"
             onPointerDown={(e) => { e.stopPropagation(); onResizeStart(field.id, e); }}
             title="Drag to resize" />
      )}
      {/* Delete button: only on floating, unlocked fields. To remove a
          form-detected field, the operator clears the PDF widget
          itself; to remove a locked one, unlock first via the
          properties panel toolbar. */}
      {!isForm && !field.locked && (
        <button className="field-delete"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
                title="Delete">×</button>
      )}
    </div>
  );
});

// FieldsList — porting the original's `field-list-item` shape:
//   • Form fields grouped at the top (read-only, can't be deleted here)
//   • Floating fields on the current page in the middle
//   • Floating fields on other pages at the bottom (dimmed, with page badge)
// Each item shows a coloured type badge, the field name, a configured
// dot, and an inline delete (floating fields only). The path subtext
// surfaces the live binding (jsonPath / "custom script" / default).
function FieldsList({ fields, activeId, onSelect, onDelete, currentPage }) {
  const t = useT();
  if (!fields || fields.length === 0) {
    return <div style={{color:'var(--text-muted)', fontSize:13}}>{t('fields.empty', 'No fields yet. Use “Add field” to place one on the PDF.')}</div>;
  }

  const formFields = fields.filter((f) => f.fieldType === 'form')
    .sort((a, b) => (a.page || 0) - (b.page || 0));
  const floating = fields.filter((f) => f.fieldType !== 'form');
  const onPage = floating.filter((f) => (f.page || 1) === currentPage);
  const otherPages = floating.filter((f) => (f.page || 1) !== currentPage)
    .sort((a, b) => (a.page || 0) - (b.page || 0));

  const badgeColor = (type, isForm) => {
    if (type === 'radio')    return '#e17055';
    if (type === 'checkbox') return '#00b894';
    if (type === 'image')    return '#e84393';
    return 'var(--accent)';
  };
  const badgeLabel = (type) => {
    if (type === 'radio')     return 'Radio';
    if (type === 'checkbox')  return 'Checkbox';
    if (type === 'multiline') return 'Multi';
    if (type === 'image')     return 'Image';
    if (type === 'number')    return 'Number';
    if (type === 'currency')  return 'Currency';
    if (type === 'barcode')   return 'Barcode';
    if (type === 'shape')     return 'Shape';
    if (type === 'signature') return 'Signature';
    return 'Text';
  };

  const renderItem = (f, opts = {}) => {
    const configured = isFieldConfigured(f);
    const isForm = f.fieldType === 'form';
    const path = f.type === 'image'
      ? (f.imageUrl ? f.imageUrl.replace(/^fieldimage:/, '') : 'Not configured')
      : f.jsonPath ? f.jsonPath
      : f.transformCode ? 'custom script'
      : (f.default !== undefined && f.default !== null && f.default !== '' && f.default !== false)
        ? 'has default' : 'Not configured';

    return (
      <div key={f.id}
           className={`field-list-item ${f.id === activeId ? 'active' : ''} ${configured ? 'configured' : 'unconfigured'}`}
           role="button" tabIndex={0} aria-current={f.id === activeId ? 'true' : undefined}
           onClick={() => onSelect(f)}
           onKeyDown={(e) => { if (e.target === e.currentTarget) keyActivate(() => onSelect(f))(e); }}
           style={opts.style}>
        <div className="field-list-item-header">
          <span className="field-list-item-name">
            <span className="field-list-badge"
                  style={{background: badgeColor(f.type, isForm)}}>
              {badgeLabel(f.type)}
            </span>
            {/* Truncate long field names with ellipsis. min-width:0 is
                load-bearing — without it the flex item refuses to
                shrink below its intrinsic text width and the row
                wraps instead of cropping. The full name is on the
                title attribute so a hover surfaces it. */}
            <span title={f.name || '(unnamed)'}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}>
              {f.name || '(unnamed)'}
            </span>
            {/* Lock indicator. Shown only when the field is locked.
                Unlocked fields render nothing here — operators wanted
                the locked state to be the only signal, not a paired
                lock/unlock pair that looks like noise. */}
            {f.locked && (
              <span aria-label="Locked"
                    title="This field is locked. Unlock it from the properties panel to edit or delete it."
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'var(--text-muted)',
                      marginLeft: 2,
                    }}>
                <Icons.Lock />
              </span>
            )}
            <span className={'configured-dot ' + (configured ? 'yes' : 'no')}
                  title={configured ? 'Configured' : 'Not configured'} />
          </span>
          {opts.badge}
          {!isForm && onDelete && (
            // Locked fields can't be deleted from here — confirmDeleteField
            // already rejects programmatic calls on locked fields, but the
            // button was previously clickable and silently no-op'd. Mark it
            // disabled so the affordance matches the behaviour, and short-
            // circuit the click handler defensively too.
            <button className="btn btn-sm btn-icon btn-danger"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (f.locked) return;
                      onDelete(f.id);
                    }}
                    disabled={!!f.locked}
                    title={f.locked ? 'Locked — unlock to delete' : 'Delete'}
                    style={f.locked ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}>
              <Icons.Trash />
            </button>
          )}
        </div>
        <div className="field-list-item-path">
          <span style={{color: configured ? '#2ed573' : undefined}}>{path}</span>
          {isForm && f.page && <span style={{marginLeft: 6, opacity: 0.7}}>· Page {f.page}</span>}
        </div>
      </div>
    );
  };

  return (
    <div>
      {formFields.length > 0 && (
        <>
          <div className="field-list-section-label">
            Form fields ({formFields.length})
          </div>
          {formFields.map((f) => renderItem(f))}
          {(onPage.length > 0 || otherPages.length > 0) && <div className="field-list-separator" />}
        </>
      )}
      <div className="field-list-section-label">
        Page {currentPage} — {onPage.length} {onPage.length === 1 ? 'field' : 'fields'}
      </div>
      {onPage.length === 0 && formFields.length === 0 && (
        <p style={{fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6}}>
          No floating fields on this page yet.
        </p>
      )}
      {onPage.map((f) => renderItem(f))}
      {otherPages.length > 0 && (
        <>
          <div className="field-list-separator" />
          <div className="field-list-section-label">
            Other pages ({otherPages.length})
          </div>
          {otherPages.map((f) => renderItem(f, {
            style: { opacity: 0.5 },
            badge: <span className="template-card-badge">Page {f.page}</span>,
          }))}
        </>
      )}
    </div>
  );
}

// SegmentedControl — a row of icon buttons where exactly one is on
// (alignment pickers). Each button carries its own label for tooltips
// and screen readers; the group is labelled as a whole.
function SegmentedControl({ label, value, options, onChange, disabled }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button"
                className={'segmented-btn' + (value === o.value ? ' is-active' : '')}
                aria-pressed={value === o.value}
                aria-label={o.label} title={o.label}
                disabled={disabled}
                onClick={() => { if (value !== o.value) onChange(o.value); }}>
          <Icon name={o.icon} />
        </button>
      ))}
    </div>
  );
}

// Controlled <input type="number"> with a local string buffer.
//
// The naive pattern — `value={field.x || 0}` + an onChange that runs
// `parseFloat(e.target.value) || 0` — makes the input un-editable.
// Clearing it to retype produces an empty string, parseFloat returns
// NaN, the `|| 0` snaps the model back to 0, and the next render
// stuffs "0" into the input before the user's next keystroke lands.
//
// We keep the typed text as the source of truth during an edit (so
// "", "1", "1.", "-" are all valid in-flight states) and only commit
// to the parent when the buffer parses to a finite number. On blur we
// normalize: clamp to [min,max] and fall back to `fallback` for
// unparseable input, so the parent never holds garbage. External
// updates (e.g. dragging the field on the canvas changes X/Y) are
// detected by ref comparison and override the local buffer; our own
// commits don't retrigger that sync.
function NumberField({ value, fallback, min, max, step, onCommit, className, style, disabled }) {
  const [raw, setRaw] = React.useState(() => formatNumber(value, fallback));
  const lastValueRef = React.useRef(value);
  React.useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setRaw(formatNumber(value, fallback));
    }
  }, [value, fallback]);
  function commit(n) {
    lastValueRef.current = n;
    onCommit(n);
  }
  return (
    <input className={className || 'input'} type="number"
           min={min} max={max} step={step} style={style} disabled={disabled}
           value={raw}
           onChange={(e) => {
             const next = e.target.value;
             setRaw(next);
             const n = parseFloat(next);
             if (Number.isFinite(n)) commit(n);
           }}
           onBlur={() => {
             // Untouched: keep the stored value (the box shows it rounded).
             if (raw === formatNumber(value, fallback)) return;
             let n = parseFloat(raw);
             if (!Number.isFinite(n)) n = fallback;
             if (typeof min === 'number') n = Math.max(min, n);
             if (typeof max === 'number') n = Math.min(max, n);
             commit(n);
             setRaw(formatNumber(n, fallback));
           }} />
  );
}

// Shown with at most 2 decimals — a click-placed or dragged field's
// position is a raw percentage like 49.937733499377.
function formatNumber(v, fallback) {
  const n = (typeof v === 'number' && Number.isFinite(v)) ? v : fallback;
  return typeof n === 'number' ? String(Math.round(n * 100) / 100) : String(n);
}

// SignatureFieldProperties — Phase D #5. Two modes:
//
//   request — placeholder filled by the signature-request workflow.
//             Operator sets nothing else; the recipient's signature
//             lands here at re-render time.
//   user    — the operator places one of THEIR own pre-stored
//             signatures. We fetch the caller's library on mount;
//             selection writes signatureId onto the field. The list
//             is short (operators typically have 1–3 signatures),
//             so we don't bother paginating.
function SignatureFieldProperties({ field, onChange, isLocked, lockedHint }) {
  const i18n = useT();
  const [mySignatures, setMySignatures] = useState(null);
  // Colleagues' signatures whose owners allow placing them ("usable").
  const [teamSignatures, setTeamSignatures] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get('/spa/account/signatures')
      .then((r) => { if (!cancelled) setMySignatures(r.signatures || []); })
      .catch(() => { if (!cancelled) setMySignatures([]); });
    api.get('/spa/signature-refs/usable')
      .then((r) => { if (!cancelled) setTeamSignatures(r.signatures || []); })
      .catch(() => { /* none shared, or not in an org */ });
    return () => { cancelled = true; };
  }, []);
  // The field may hold a colleague's signature that is only *visible* —
  // not in my library nor in the usable list, so the picker would show
  // nothing. Resolve it to name it (read-only entry).
  const [placedByOther, setPlacedByOther] = useState(null);
  const placedId = field.signatureKind === 'user' ? (field.signatureId || '') : '';
  const isMine = !!(mySignatures && placedId && mySignatures.some((s) => s.id === placedId));
  const isTeam = !!(placedId && teamSignatures.some((s) => s.id === placedId));
  useEffect(() => {
    if (!placedId || mySignatures == null || isMine || isTeam) { setPlacedByOther(null); return undefined; }
    let cancelled = false;
    api.get(`/spa/signature-refs?ids=${encodeURIComponent(placedId)}`)
      .then((r) => { if (!cancelled) setPlacedByOther((r.signatures || {})[placedId] || null); })
      .catch(() => { if (!cancelled) setPlacedByOther(null); });
    return () => { cancelled = true; };
  }, [placedId, isMine, isTeam, mySignatures]);

  const kind = field.signatureKind || 'request';
  return (
    <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
      <LabelWithInfo info={kind === 'request'
        ? i18n('properties.signature.infoRequest',
            'Renders as a dashed placeholder until signed. Create a signature request to email a recipient a signing link; their signature is stamped onto this spot. Password-protected templates can\u2019t be sent for signature.')
        : i18n('properties.signature.infoUser',
            'The selected signature is embedded in the rendered PDF at this position. Manage your signatures in Settings → Signatures.')
      }>
        {i18n('properties.signature.kind', 'Signature kind')}
      </LabelWithInfo>
      <select className="select" value={kind} disabled={isLocked}
              onChange={(e) => onChange({ signatureKind: e.target.value })}>
        <option value="request">{i18n('properties.signature.request', 'Signature request — a recipient signs here')}</option>
        <option value="user">{i18n('properties.signature.user', 'My signature — embed one of my saved signatures')}</option>
      </select>

      {kind === 'request' && (
        <>
          <LabelWithInfo info={i18n('properties.signature.signerInfo',
            'Multi-signer requests: which signer fills this box. Signer 1 is the first email in the request’s signers list. All boxes default to signer 1 for classic single-signer flows.')}>
            {i18n('properties.signature.signer', 'Signer')}
          </LabelWithInfo>
          <select className="select" disabled={isLocked}
                  value={String(field.signerIndex >= 1 ? field.signerIndex : 1)}
                  onChange={(e) => onChange({ signerIndex: parseInt(e.target.value, 10) || 1 })}>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={String(n)}>
                {i18n('properties.signature.signerN', 'Signer {{n}}', { n })}
              </option>
            ))}
          </select>
        </>
      )}

      {kind === 'user' && (
        <>
          <label className="label" style={{fontSize: 11, marginTop: 10}}>{i18n('properties.signature.label', 'Signature')}</label>
          {mySignatures == null ? (
            <div style={{fontSize: 12, color: 'var(--text-muted)'}}>{i18n('properties.signature.loading', 'Loading…')}</div>
          ) : mySignatures.length === 0 && teamSignatures.length === 0 && placedByOther ? (
            <>
              <select className="select" disabled={isLocked} value={placedId}
                      onChange={(e) => onChange({ signatureId: e.target.value || null })}>
                <option value="">{i18n('properties.signature.pick', '— Pick a signature —')}</option>
                <option value={placedId}>{placedByOther.owner.name}: {placedByOther.name}</option>
              </select>
              <div className="form-hint">{i18n('properties.signature.placedBy', 'Placed by {{name}}', { name: placedByOther.owner.name })}</div>
            </>
          ) : mySignatures.length === 0 && teamSignatures.length === 0 ? (
            <div style={{fontSize: 12, color: 'var(--text-muted)'}}>
              {i18n('properties.signature.emptyPre', 'You have no saved signatures yet. Add one in')}{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('pluma:navigate', {
                  detail: { to: 'settings', section: 'signatures' },
                }));
              }}>{i18n('properties.signature.settingsLink', 'Settings → Signatures')}</a>.
            </div>
          ) : (
            <select className="select" disabled={isLocked}
                    value={field.signatureId || ''}
                    onChange={(e) => onChange({ signatureId: e.target.value || null })}>
              <option value="">{i18n('properties.signature.pick', '— Pick a signature —')}</option>
              {placedByOther && (
                <option value={placedId}>{placedByOther.owner.name}: {placedByOther.name}</option>
              )}
              {mySignatures.length > 0 && (
                <optgroup label={i18n('properties.signature.groupMine', 'My signatures')}>
                  {mySignatures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.kind})</option>
                  ))}
                </optgroup>
              )}
              {teamSignatures.length > 0 && (
                <optgroup label={i18n('properties.signature.groupTeam', 'Shared by your team')}>
                  {teamSignatures.map((s) => (
                    <option key={s.id} value={s.id}>{s.owner.name}: {s.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          )}
          {placedByOther && (
            <div className="form-hint">{i18n('properties.signature.placedBy', 'Placed by {{name}}', { name: placedByOther.owner.name })}</div>
          )}
        </>
      )}

    </div>
  );
}

function PropertiesPanel({ field, onChange, onPickImage, onDuplicate, onClear, onDelete, schema, templateFont, templateRenderer, totalPages }) {
  const i18n = useT();
  // Tenant-wide default currency seeds newly-created currency fields
  // (Settings → Templates → Default currency). Falls back to USD so a
  // fresh tenant that hasn't visited Settings yet still gets a sensible
  // first value.
  const tenantCtx = useTenant();
  const defaultCurrencyCode = ((tenantCtx?.current?.defaultCurrency) || 'USD').toUpperCase();
  const t = field.type;
  // Text / number / currency all render as single-line strings in the
  // PDF — the only difference is the value-formatting layer in front
  // of the renderer. Keeping them under the same `isText` umbrella
  // means font/size/colour/JSON-path properties stay shared. The
  // Format dropdown is the one branch that's NOT shared with currency
  // (currency has its own dedicated property block below); it's
  // gated separately on `!isCurrency` inline.
  const isNumber = t === 'number';
  const isCurrency = t === 'currency';
  const isBarcode = t === 'barcode';
  const isShape = t === 'shape';
  const isSignature = t === 'signature';
  const isText = t === 'text' || t === 'multiline' || t === 'number' || t === 'currency' || t === '' || t === undefined;
  const isMultiline = t === 'multiline';
  const isCheckbox = t === 'checkbox';
  const isRadio = t === 'radio';
  const isImage = t === 'image';
  const isLocked = !!field.locked;
  // Tooltip shown on every grayed-out control so the user knows the
  // reason it's not interactive without having to hunt for the lock
  // toggle. Single string, used as both `title=` and the disabled
  // hover hint.
  const lockedHint = isLocked ? i18n('properties.lockedHint', 'Unlock the field to edit this') : undefined;

  // Alignment is relative to the field's box. A field placed with a single
  // click has none (0 × 0), so choosing an alignment gives it a default
  // box the user can then resize — otherwise there'd be nothing to align in.
  const setAlignment = (patch) => {
    if (!field.width) patch.width = 25;
    if (!field.height) patch.height = 3;
    onChange(patch);
  };

  // Transform-code client-side syntax check. Runs on blur (after the
  // user finishes typing) and any time the active field changes —
  // switching fields resets to "unchecked", since the new field's
  // code might be different. We use `new AsyncFunction(...)` rather
  // than esprima/acorn because the same JS parser that ships with
  // every browser is "good enough" — catches missing braces, stray
  // commas, unterminated strings, etc. The renderer (goja) might
  // still reject some ES2023+ edge cases the browser accepts, but
  // those will surface at render time with a clear error; for the
  // editor's purpose, fast feedback on typos is what matters.
  //
  // Why `new AsyncFunction` and not `new Function`:
  // the renderer wraps user code in an async IIFE so `await` is
  // legal in the body. Parsing under the same async context lets us
  // accept `await Promise.resolve(...)` as valid input, matching the
  // contract documented in the placeholder + helper text.
  const [transformSyntaxError, setTransformSyntaxError] = React.useState(null);
  const validateTransformSyntax = React.useCallback((src) => {
    if (!src || !src.trim()) {
      setTransformSyntaxError(null);
      return;
    }
    try {
      // AsyncFunction isn't a named global — grab it via the prototype
      // chain of any async function expression. Lazy-cached on first
      // call so repeated blurs don't pay the lookup.
      const AsyncFunction = (validateTransformSyntax.AF
        || (validateTransformSyntax.AF = Object.getPrototypeOf(async function () {}).constructor));
      // `new AsyncFunction` parses + compiles the body to a function
      // object but DOES NOT execute it (we never call the result).
      // Throws SyntaxError on a bad parse, which is exactly what we
      // want to surface to the user.
      // eslint-disable-next-line no-new
      new AsyncFunction('value', 'data', src);
      setTransformSyntaxError(null);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setTransformSyntaxError(e.message || 'Syntax error');
      } else {
        // Shouldn't reach here — parse failures are SyntaxError. If we
        // somehow hit another error, treat as no-error rather than
        // false-flagging valid code.
        setTransformSyntaxError(null);
      }
    }
  }, []);
  // Re-validate whenever the selected field changes. A field stored
  // with a broken transform (script the operator left half-finished
  // before navigating away) should warn on first click without the
  // user having to focus + blur to discover it. eslint-disable for
  // the deps array: we deliberately watch the field swap, not the
  // transformCode mutations — those go through onChange + onBlur.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { validateTransformSyntax(field.transformCode || ''); }, [field.id]);

  // Font catalogue — built-ins + tenant uploads — for the field font picker.
  // Cached at module level; mounting the panel doesn't re-fetch unless the
  // FontsPage invalidated the cache after an upload / delete.
  const [fonts, setFonts] = React.useState(null);
  React.useEffect(() => { loadFontsCatalogue().then(setFonts); }, []);

  // Variant support for the field's currently selected font. Used to
  // enable / disable the Bold and Italic checkboxes so the user can't
  // request a cut that doesn't exist (the renderer would silently
  // downgrade — better to make the limitation visible up front).
  //
  // Falls back to the template default font when the field has no per-
  // field override. While the catalogue is still loading, lookupFontEntry
  // returns null and variantSupport returns "permissive" (all four cuts
  // available) so the user isn't locked out by in-flight data.
  const fieldFontId = field.font || templateFont || 'helvetica';
  const fontEntry = React.useMemo(() => lookupFontEntry(fieldFontId, fonts), [fieldFontId, fonts]);
  const support = React.useMemo(() => variantSupport(fontEntry), [fontEntry]);
  const boldEnabled = canCheckBold(support, !!field.italic);
  const italicEnabled = canCheckItalic(support, !!field.bold);

  // Auto-correct: when the font changes to one that no longer supports
  // the current bold/italic combo, drop the offending flag(s) so the
  // field stays in a renderable state. We do this in an effect rather
  // than during render so the patch goes through onChange (which writes
  // through to the template's persisted state). Runs whenever the
  // resolved support set changes — usually because the user switched
  // fonts, occasionally because the catalogue finished loading.
  React.useEffect(() => {
    if (!fontEntry) return; // catalogue still loading — don't touch state
    const patch = {};
    if (field.bold && field.italic && !support.boldItalic) {
      // Drop whichever still gives us a valid cut, preferring bold.
      if (support.bold) patch.italic = false;
      else if (support.italic) patch.bold = false;
      else { patch.bold = false; patch.italic = false; }
    } else if (field.bold && !field.italic && !support.bold) {
      patch.bold = false;
    } else if (!field.bold && field.italic && !support.italic) {
      patch.italic = false;
    }
    if (Object.keys(patch).length > 0) onChange(patch);
    // onChange is stable per active field; field.bold/italic are read
    // for the comparison; support is the actual driver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [support.bold, support.italic, support.boldItalic, fontEntry]);
  // Form-field detected fields (from the PDF's AcroForm) keep a fixed
  // formFieldName tied to the PDF widget — renaming it would break the
  // mapping. Show the metadata at the top and lock the Name input.
  const isFormField = field.fieldType === 'form';

  // JSON-path autocomplete state. Suggestions come from the linked
  // schema (extracted on every render — schema is stable per template
  // and recomputing is cheap). Substring match, arrow-key navigation,
  // mouse hover preview. Closes on outside click via the ref + listener.
  const [showSuggest, setShowSuggest] = React.useState(false);
  const [suggestIdx, setSuggestIdx] = React.useState(-1);
  const acRef = React.useRef(null);
  const allPaths = React.useMemo(() => schema ? extractPathsFromSchema(schema) : [], [schema]);
  const currentPath = field.jsonPath || '';
  const filteredPaths = React.useMemo(() => {
    if (!currentPath.trim()) return allPaths;
    const lower = currentPath.toLowerCase();
    return allPaths.filter((p) => p.toLowerCase().includes(lower));
  }, [allPaths, currentPath]);

  React.useEffect(() => {
    const handler = (e) => {
      if (acRef.current && !acRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const renderHighlighted = (path) => {
    const q = currentPath.toLowerCase();
    const idx = path.toLowerCase().indexOf(q);
    if (q && idx >= 0) {
      return <>{path.slice(0, idx)}<strong>{path.slice(idx, idx + q.length)}</strong>{path.slice(idx + q.length)}</>;
    }
    return path;
  };

  // Top action bar: duplicate / lock / delete. Single row of icon
  // buttons that always sits at the top of the panel regardless of
  // field type. Form-detected fields keep Duplicate disabled (a copy
  // would have nothing to bind to in the source PDF); locked fields
  // disable Duplicate + Delete but leave the lock toggle itself live
  // so the user can always get back out. The confirm prompt lives in
  // TemplateEditor.confirmDeleteField — every entry point (canvas ×,
  // sidebar ×, this button) routes through it so the dialog copy
  // stays in one place.
  const onClickDelete = () => { if (!isLocked && onDelete) onDelete(field.id); };
  const onClickDuplicate = () => { if (onDuplicate) onDuplicate(field.id); };
  const onClickClear = () => { if (!isLocked && onClear) onClear(field.id); };
  const onClickLock = () => onChange({ locked: !isLocked });

  return (
    <>
      {/* Toolbar.
          - AcroForm-detected fields:  Clear · Lock                 (no Duplicate / Delete — the
                                                                     widget binding is structural)
          - Placed fields:             Duplicate · Clear · Lock · Delete
          Clear and Delete both prompt for confirmation upstream
          (confirmClearField + confirmDeleteField in TemplateEditor)
          so a misclick on either doesn't lose work irreversibly.
          Lock sits between the wipe actions and the destructive
          Delete so it reads as the guard that controls them. */}
      <div className="prop-toolbar">
        {!isFormField && (
          <button type="button"
                  className="btn btn-sm btn-icon"
                  onClick={onClickDuplicate}
                  disabled={!onDuplicate}
                  title={i18n('properties.toolbar.duplicate', 'Duplicate field (⌘D)')}>
            <Icons.Copy />
          </button>
        )}
        <button type="button"
                className="btn btn-sm btn-icon"
                onClick={onClickClear}
                disabled={isLocked || !onClear}
                title={isLocked
                  ? i18n('properties.toolbar.clearLocked', 'Unlock the field to clear it')
                  : i18n('properties.toolbar.clear', 'Clear field (resets default, transform, JSON-path binding, image/signature reference; placement stays)')}>
          <Icons.Eraser />
        </button>
        <button type="button"
                className={'btn btn-sm btn-icon' + (isLocked ? ' is-locked-on' : '')}
                onClick={onClickLock}
                title={isLocked
                  ? i18n('properties.toolbar.lockUnlock', 'Field is locked — click to unlock')
                  : i18n('properties.toolbar.lock', 'Lock field (prevents move, resize, delete, page/type/font-size change)')}>
          {isLocked ? <Icons.Lock /> : <Icons.LockOpen />}
        </button>
        {!isFormField && (
          <button type="button"
                  className="btn btn-sm btn-icon"
                  onClick={onClickDelete}
                  disabled={isLocked || !onDelete}
                  title={isLocked
                    ? i18n('properties.toolbar.deleteLocked', 'Unlock the field to delete it')
                    : i18n('properties.toolbar.delete', 'Delete field')}>
            <Icons.Trash />
          </button>
        )}
      </div>

      {isFormField && (
        <div className="form-field-banner">
          <div className="form-field-banner-row">
            <span className={`pill pill-form pill-form-${isRadio ? 'radio' : isCheckbox ? 'checkbox' : 'text'}`}>
              {isRadio
                ? i18n('properties.formField.radio', 'Radio')
                : isCheckbox
                  ? i18n('properties.formField.checkbox', 'Checkbox')
                  : i18n('properties.formField.text', 'Text')}
            </span>
            <span className="form-field-banner-name">{field.formFieldName}</span>
          </div>
          <div className="form-field-banner-meta">
            {field.formFieldType?.replace('PDF', '') || i18n('properties.formField.label', 'Form field')}
            {isRadio && field.radioOptions?.length
              ? ` · ${i18n('properties.formField.optionsCount', { n: field.radioOptions.length }, '{{n}} options').replace('{{n}}', field.radioOptions.length)}`
              : ''}
          </div>
        </div>
      )}

      {/* Single fieldset disables every input/select/button/textarea below
          it natively when isLocked — a uniform gate so we don't have to
          remember `disabled={isLocked}` on each control individually, and
          newly added properties inherit the behaviour for free. The
          toolbar buttons (Duplicate, Lock) sit ABOVE this fieldset so
          they keep working on locked fields. style={{border: 0}} strips
          the browser default chrome — purely a focus container. */}
      <fieldset disabled={isLocked} style={{border: 0, padding: 0, margin: 0, minInlineSize: 'auto'}}
                data-locked-tooltip={isLocked ? lockedHint : undefined}>
      {!isFormField && (
        <>
          <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <label className="label">{i18n('properties.fieldName', 'Field name')}</label>
            <input className="input" value={field.name || ''}
                   disabled={isLocked}
                   onChange={(e) => onChange({ name: e.target.value })}
                   placeholder={i18n('properties.fieldNamePlaceholder', 'e.g. Customer name')} />
          </div>
          {/* Type at the top, like the original. Floating fields can be
              text / multiline / checkbox / image — radio is form-only
              (it requires a PDF AcroForm group, can't be placed
              manually). Changing the type clears type-specific props
              that no longer apply (imageUrl, checkStyle, etc.) so the
              UI doesn't carry stale state across types. */}
          <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <label className="label">{i18n('properties.type', 'Type')}</label>
            <select className="select" value={field.type || 'text'}
                    disabled={isLocked}
                    onChange={(e) => {
                      const next = e.target.value;
                      const patch = { type: next };
                      // Reset type-specific defaults so a checkbox
                      // doesn't carry the previous text field's font
                      // size or a switched-out image's URL.
                      if (next === 'image') {
                        patch.imageUrl = field.imageUrl || '';
                        patch.objectFit = field.objectFit || 'stretch';
                        if (!field.width)  patch.width  = 20;
                        if (!field.height) patch.height = 12;
                      }
                      if (next === 'checkbox' && !field.checkStyle) patch.checkStyle = 'x';
                      // Number defaults to 2 decimal places. The
                      // showDecimals toggle keeps the operator's
                      // override around so flipping back doesn't lose
                      // the value they typed.
                      if (next === 'number') {
                        if (field.showDecimals === undefined) patch.showDecimals = true;
                        if (field.decimalPlaces === undefined) patch.decimalPlaces = 2;
                      }
                      // Currency defaults: seed currencyCode from the
                      // tenant's "Default currency" so a freshly added
                      // currency field renders correctly without the
                      // operator having to pick again. decimalPlaces
                      // tracks the ISO 4217 minor-unit count for that
                      // code (JPY → 0, EUR → 2, etc.) — no manual knob.
                      if (next === 'currency') {
                        if (field.currencyCode === undefined || field.currencyCode === '') {
                          patch.currencyCode = defaultCurrencyCode;
                        }
                        const codeForDecimals = patch.currencyCode || field.currencyCode || defaultCurrencyCode;
                        patch.decimalPlaces = currencyDecimalsFor(codeForDecimals);
                      }
                      // Barcode defaults to qrcode + a reasonable
                      // square size so a freshly-typed field has a
                      // visible bitmap instead of a 0×0 dot.
                      if (next === 'barcode') {
                        if (!field.barcodeKind) patch.barcodeKind = 'qrcode';
                        if (!field.width)  patch.width  = 15;
                        if (!field.height) patch.height = 10;
                      }
                      // Shape defaults: rectangle, light accent fill,
                      // 1pt accent border. Operator can tune both
                      // colours + width to "no fill" / "no border"
                      // via the colour picker's empty state.
                      if (next === 'shape') {
                        if (!field.shapeKind) patch.shapeKind = 'rectangle';
                        if (field.shapeFillColor === undefined)   patch.shapeFillColor = '';
                        if (field.shapeBorderColor === undefined) patch.shapeBorderColor = '#000000';
                        if (field.shapeBorderWidth === undefined) patch.shapeBorderWidth = 1;
                        if (!field.width)  patch.width  = 20;
                        if (!field.height) patch.height = 10;
                      }
                      // Signature defaults: request mode (placeholder)
                      // gets filled in by the signature-request flow.
                      // 'user' mode wants the operator to pick a stored
                      // signature; left blank until they do.
                      if (next === 'signature') {
                        if (!field.signatureKind) patch.signatureKind = 'request';
                        if (!field.width)  patch.width  = 30;
                        if (!field.height) patch.height = 8;
                      }
                      onChange(patch);
                    }}>
              <option value="text">{i18n('properties.type.text', 'Text')}</option>
              <option value="multiline">{i18n('properties.type.multiline', 'Multiline')}</option>
              <option value="number">{i18n('properties.type.number', 'Number')}</option>
              <option value="currency">{i18n('properties.type.currency', 'Currency')}</option>
              <option value="checkbox">{i18n('properties.type.checkbox', 'Checkbox')}</option>
              <option value="image">{i18n('properties.type.image', 'Image')}</option>
              <option value="barcode">{i18n('properties.type.barcode', 'Barcode / QR')}</option>
              <option value="shape">{i18n('properties.type.shape', 'Shape')}</option>
              <option value="signature">{i18n('properties.type.signature', 'Signature')}</option>
            </select>
          </div>
        </>
      )}
      {/* Shape + signature fields don't bind to a JSON path: shapes are
          pure visual decoration; signatures resolve via the recipient
          flow (request) or a saved/uploaded image (own) — never via a
          dot path on the render payload. Skip the JSON-path UI for
          both so the editor doesn't suggest a meaningless control. */}
      {!isShape && !isSignature && (
      <div className="prop-group">
        <label className="label">{i18n('properties.dataKey', 'Data key')}</label>
        <div className="json-path-autocomplete" ref={acRef}>
          {/* autoComplete="off" + the three siblings + a random
              name attribute is what it takes to defeat Chrome's
              address/name-field heuristic. Without this its native
              dropdown floats on top of our schema suggestions and
              hijacks Enter / arrow keys. Spellcheck off keeps
              `user.first_name` from being underlined. */}
          <input className="input" placeholder={isImage ? 'user.avatar (URL)' : 'user.name'}
                 value={field.jsonPath || ''}
                 name={`json-path-${field.id}`}
                 autoComplete="off"
                 autoCorrect="off"
                 autoCapitalize="off"
                 spellCheck={false}
                 onChange={(e) => { onChange({ jsonPath: e.target.value }); setShowSuggest(true); setSuggestIdx(-1); }}
                 onFocus={() => setShowSuggest(true)}
                 onKeyDown={(e) => {
                   if (!showSuggest || filteredPaths.length === 0) return;
                   if (e.key === 'ArrowDown') {
                     e.preventDefault();
                     setSuggestIdx((p) => p < filteredPaths.length - 1 ? p + 1 : 0);
                   } else if (e.key === 'ArrowUp') {
                     e.preventDefault();
                     setSuggestIdx((p) => p > 0 ? p - 1 : filteredPaths.length - 1);
                   } else if (e.key === 'Enter' && suggestIdx >= 0) {
                     e.preventDefault();
                     onChange({ jsonPath: filteredPaths[suggestIdx] });
                     setShowSuggest(false); setSuggestIdx(-1);
                   } else if (e.key === 'Escape') {
                     setShowSuggest(false);
                   }
                 }} />
          {showSuggest && filteredPaths.length > 0 && (
            <div className="json-path-suggestions">
              {filteredPaths.map((p, idx) => (
                <div key={p}
                     className={'json-path-suggestion-item' + (idx === suggestIdx ? ' highlighted' : '')}
                     onMouseDown={(e) => {
                       e.preventDefault();
                       onChange({ jsonPath: p });
                       setShowSuggest(false); setSuggestIdx(-1);
                     }}
                     onMouseEnter={() => setSuggestIdx(idx)}>
                  {renderHighlighted(p)}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
          {schema
            ? i18n('properties.dataKey.schemaHint', 'Where this field’s value comes from. Pick one from the template’s data, or type a key (use dots to nest, e.g. customer.name).')
            : i18n('properties.dataKey.hint', 'Where this field’s value comes from in the fill-in form and the API. It follows the field name unless you change it.')}
        </div>
      </div>
      )}

      {/* Currency selector sits directly under JSON path so the
          render-time formatting decision lives next to the value
          binding. Decimal places are no longer a manual knob — the
          renderer derives them from the currency (JPY → 0, EUR → 2,
          etc.). Newly-created currency fields seed from the tenant's
          default in Settings → Templates (see the type-change patch
          above). */}
      {isCurrency && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <LabelWithInfo info={i18n('properties.currency.info',
            'The selected currency drives the symbol and decimal places shown on the rendered PDF. Newly-created currency fields start with the workspace default (Settings → Templates).')}>
            {i18n('properties.currency', 'Currency')}
          </LabelWithInfo>
          <select className="select"
                  value={(field.currencyCode || '').toUpperCase()}
                  disabled={isLocked}
                  onChange={(e) => {
                    const code = e.target.value;
                    // Keep decimalPlaces in sync so the renderer (which
                    // still reads the field-level decimalPlaces) lands
                    // on the right minor-unit count without the
                    // operator having to track ISO 4217 trivia.
                    onChange({
                      currencyCode: code,
                      decimalPlaces: currencyDecimalsFor(code),
                    });
                  }}>
            <CurrencyOptions t={i18n} />
          </select>
        </div>
      )}

      {isImage && (
        <>
          <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <label className="label">{i18n('properties.image.source', 'Image source')}</label>
            <div style={{display:'flex', gap:6}}>
              <input className="input" placeholder={i18n('properties.image.sourcePlaceholder', 'fieldimage:id or https://…')}
                     value={field.imageUrl || ''}
                     disabled={isLocked}
                     onChange={(e) => onChange({ imageUrl: e.target.value })} />
              <button className="btn" type="button"
                      disabled={isLocked}
                      onClick={() => onPickImage && onPickImage()}>{i18n('properties.image.browse', 'Browse')}</button>
            </div>
          </div>
          <div className="prop-group prop-row-2" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <div>
              <label className="label">{i18n('properties.image.width', 'Width %')}</label>
              <NumberField value={field.width} fallback={20} min={3} max={100} step={0.5}
                           disabled={isLocked}
                           onCommit={(v) => onChange({ width: v })} />
            </div>
            <div>
              <label className="label">{i18n('properties.image.height', 'Height %')}</label>
              <NumberField value={field.height} fallback={12} min={2} max={100} step={0.5}
                           disabled={isLocked}
                           onCommit={(v) => onChange({ height: v })} />
            </div>
          </div>
          <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <label className="label">{i18n('properties.image.objectFit', 'Object fit')}</label>
            <select className="select" value={field.objectFit || 'stretch'}
                    disabled={isLocked}
                    onChange={(e) => onChange({ objectFit: e.target.value })}>
              <option value="stretch">{i18n('properties.image.objectFit.stretch', 'Stretch — fill the box, distort if needed')}</option>
              <option value="contain">{i18n('properties.image.objectFit.contain', 'Contain — fit inside, preserve aspect ratio')}</option>
              <option value="cover">{i18n('properties.image.objectFit.cover', 'Cover — fill the box, crop if needed')}</option>
            </select>
          </div>
          <div style={{fontSize:11, color:'var(--text-muted)', marginTop:-8, marginBottom:16}}>
            {i18n('properties.image.resizeTip', 'Tip: drag the green corner handle to resize visually.')}
          </div>
        </>
      )}

      {/* "Radio option index" property was removed — Group options below
          renders the same indices alongside their labels, so a separate
          numeric input was redundant. The active option's index is
          highlighted in that list. */}

      {isRadio && Array.isArray(field.radioOptions) && field.radioOptions.length > 0 && (
        <div className="prop-group">
          <label className="label">{i18n('properties.radio.groupOptions', 'Group options')}</label>
          <div className="radio-options-list">
            {field.radioOptions.map((opt) => (
              <div key={opt.index}
                   className={'radio-option-row' + (opt.index === field.radioIndex ? ' active' : '')}>
                <span className="radio-option-index">{opt.index}</span>
                <span className="radio-option-label">{opt.label}</span>
                {opt.page && <span className="radio-option-page">p.{opt.page}</span>}
              </div>
            ))}
          </div>
          <div style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>
            {i18n('properties.radio.hintPre', 'Set')} <code>{field.formFieldName}</code> {i18n('properties.radio.hintPost', 'in your render payload to one of these indices to mark that option.')}
          </div>
        </div>
      )}

      {isText && !isCurrency && (
        <>
          <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
            <label className="label">{i18n('properties.format', 'Format')}</label>
            <select className="select" value={field.format || ''}
                    disabled={isLocked}
                    onChange={(e) => onChange({ format: e.target.value })}>
              <option value="">{i18n('properties.format.none', 'None')}</option>
              <option value="uppercase">{i18n('properties.format.uppercase', 'UPPERCASE')}</option>
              <option value="lowercase">{i18n('properties.format.lowercase', 'lowercase')}</option>
              <option value="currency">{i18n('properties.format.currency', 'Currency (0.00)')}</option>
              <option value="date-dmy">{i18n('properties.format.dateDmy', 'dd.mm.yyyy')}</option>
              <option value="date-ymd">{i18n('properties.format.dateYmd', 'yyyy-mm-dd')}</option>
              <option value="custom">{i18n('properties.format.custom', 'Custom')}</option>
            </select>
          </div>

          {field.format === 'custom' && (
            <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
              <label className="label" style={{display: 'flex', alignItems: 'center'}}>
                <span>{i18n('properties.transformCode', 'Transform code')}</span>
                {transformSyntaxError && (
                  <span className="transform-syntax-warning" title={transformSyntaxError}>
                    <Icons.AlertTriangle /> {i18n('properties.transformCode.syntaxError', 'syntax error')}
                  </span>
                )}
              </label>
              <textarea className={'input' + (transformSyntaxError ? ' has-syntax-error' : '')}
                        rows={6}
                        spellCheck={false}
                        disabled={isLocked}
                        style={{
                          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                          fontSize: 12,
                          resize: 'vertical',
                        }}
                        placeholder={'if (value === "Einzelnachhilfe") {\n  return "Einzel";\n}\nreturn value;'}
                        value={field.transformCode || ''}
                        onChange={(e) => {
                          if (transformSyntaxError) setTransformSyntaxError(null);
                          onChange({ transformCode: e.target.value });
                        }}
                        onBlur={(e) => validateTransformSyntax(e.target.value)} />
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 6}}>
                {i18n('properties.transformCode.textBody',
                  'JS function body. Use value (resolved field) and data (full payload). Must return a string.')}
              </div>
            </div>
          )}
        </>
      )}

      {isNumber && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <LabelWithInfo info={i18n('properties.numberFormat.info',
            "Thousands / decimal separators follow the workspace's number-format default (Settings → Templates).")}>
            {i18n('properties.numberFormat', 'Number format')}
          </LabelWithInfo>
          <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6}}>
            <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13}}>
              <input type="checkbox"
                     checked={field.showDecimals !== false}
                     disabled={isLocked}
                     onChange={(e) => onChange({ showDecimals: e.target.checked })} />
              {i18n('properties.showDecimals', 'Show decimals')}
            </label>
          </div>
          {field.showDecimals !== false && (
            <>
              <label className="label" style={{fontSize: 11}}>{i18n('properties.decimalPlaces', 'Decimal places')}</label>
              <NumberField value={field.decimalPlaces} fallback={2} min={0} max={10} step={1}
                           onCommit={(v) => onChange({ decimalPlaces: Math.max(0, Math.min(10, Math.trunc(v))) })}
                           disabled={isLocked} />
            </>
          )}
        </div>
      )}

      {isBarcode && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <LabelWithInfo info={i18n('properties.barcodeType.info',
            'The resolved value is encoded with the selected symbology and stamped onto the PDF. Numeric symbologies (EAN, UPC, ITF) require digits only — non-numeric input renders as blank.')}>
            {i18n('properties.barcodeType', 'Barcode type')}
          </LabelWithInfo>
          <select className="select"
                  disabled={isLocked}
                  value={field.barcodeKind || 'qrcode'}
                  onChange={(e) => onChange({ barcodeKind: e.target.value })}>
            <optgroup label={i18n('properties.barcodeType.group2d', '2D')}>
              <option value="qrcode">{i18n('properties.barcodeType.qrcode', 'QR code')}</option>
              <option value="aztec">Aztec</option>
              <option value="datamatrix">Data Matrix</option>
              <option value="pdf417">PDF417</option>
            </optgroup>
            <optgroup label={i18n('properties.barcodeType.group1d', '1D')}>
              <option value="code128">Code 128</option>
              <option value="code39">Code 39</option>
              <option value="ean13">EAN-13</option>
              <option value="ean8">EAN-8</option>
              <option value="upca">UPC-A</option>
              <option value="itf">{i18n('properties.barcodeType.itf', 'ITF (Interleaved 2 of 5)')}</option>
            </optgroup>
          </select>
        </div>
      )}

      {isShape && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <LabelWithInfo info={i18n('properties.shape.info',
            'Resize the box to make squares vs rectangles or circles vs ellipses. Clear the fill to make the shape transparent (border only); set border width to 0 for fill-only.')}>
            {i18n('properties.shape', 'Shape')}
          </LabelWithInfo>
          <select className="select"
                  disabled={isLocked}
                  value={field.shapeKind || 'rectangle'}
                  onChange={(e) => onChange({ shapeKind: e.target.value })}>
            <option value="rectangle">{i18n('properties.shape.rectangle', 'Rectangle / Square')}</option>
            <option value="ellipse">{i18n('properties.shape.ellipse', 'Ellipse / Circle')}</option>
          </select>

          <label className="label" style={{fontSize: 11, marginTop: 10}}>{i18n('properties.shape.fillColour', 'Fill colour')}</label>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <ColorPicker value={field.shapeFillColor || ''}
                         onChange={(v) => onChange({ shapeFillColor: v })}
                         disabled={isLocked} />
            {field.shapeFillColor && (
              <button className="btn btn-sm"
                      onClick={() => onChange({ shapeFillColor: '' })}
                      disabled={isLocked}
                      title={i18n('properties.shape.noFill', 'No fill')}>
                ×
              </button>
            )}
          </div>

          <label className="label" style={{fontSize: 11, marginTop: 10}}>{i18n('properties.shape.borderColour', 'Border colour')}</label>
          <ColorPicker value={field.shapeBorderColor || '#000000'}
                       onChange={(v) => onChange({ shapeBorderColor: v })}
                       disabled={isLocked} />

          <label className="label" style={{fontSize: 11, marginTop: 10}}>{i18n('properties.shape.borderWidth', 'Border width (pt)')}</label>
          <NumberField value={field.shapeBorderWidth} fallback={1} min={0} max={20} step={0.5}
                       onCommit={(v) => onChange({ shapeBorderWidth: Math.max(0, Math.min(20, v)) })}
                       disabled={isLocked} />
        </div>
      )}

      {isSignature && (
        <SignatureFieldProperties field={field} onChange={onChange} isLocked={isLocked} lockedHint={lockedHint} />
      )}

      {isCheckbox && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className="label">{i18n('properties.checkStyle', 'Check style')}</label>
          <select className="select" value={field.checkStyle || 'x'}
                  disabled={isLocked}
                  onChange={(e) => onChange({ checkStyle: e.target.value })}>
            <option value="x">{i18n('properties.checkStyle.cross', '✕ Cross')}</option>
            <option value="square">{i18n('properties.checkStyle.square', '■ Square')}</option>
            <option value="circle">{i18n('properties.checkStyle.circle', '● Circle')}</option>
          </select>
        </div>
      )}

      {/* Custom transform code for radio + checkbox + currency. No
          Format dropdown here (unlike text fields above) — the
          transform code IS the only formatting option, so the
          textarea is always shown. The renderer treats an empty
          value as "no transform" (see drawField in
          render-service/internal/renderer/renderer.go), so leaving
          the box blank is the off-state and writing anything is the
          on-state. Placeholder copy carries a per-type example so
          users don't have to guess the return shape. */}
      {/* Developer feature: a JS snippet that turns the incoming value into
          the checkbox state / radio option. Collapsed unless already used,
          so it doesn't greet every non-developer. (Not offered on currency
          fields: the renderer formats currency before transforms run, so
          code there was silently ignored.) */}
      {(isCheckbox || isRadio) && (
        <details className="prop-group prop-advanced" open={!!field.transformCode}
                 data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <summary className="prop-advanced-summary">
            {i18n('properties.advancedDev', 'Advanced (for developers)')}
          </summary>
          <label className="label" style={{display: 'flex', alignItems: 'center', marginTop: 10}}>
            <span>{i18n('properties.transformCode', 'Transform code')}</span>
            {transformSyntaxError && (
              <span className="transform-syntax-warning" title={transformSyntaxError}>
                <Icons.AlertTriangle /> {i18n('properties.transformCode.syntaxError', 'syntax error')}
              </span>
            )}
          </label>
          <textarea className={'input' + (transformSyntaxError ? ' has-syntax-error' : '')}
                    rows={6}
                    spellCheck={false}
                    disabled={isLocked}
                    style={{
                      fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                      fontSize: 12,
                      resize: 'vertical',
                    }}
                    placeholder={isRadio
                      ? '// Return the option index (0-based).\nif (value === "Pro") return 1;\nreturn 0;'
                      : isCurrency
                        ? '// Return the number to format with the selected currency.\nreturn Number(value) / 100;'
                        : '// Return true to check the box.\nreturn !!value;'}
                    value={field.transformCode || ''}
                    onChange={(e) => {
                      if (transformSyntaxError) setTransformSyntaxError(null);
                      onChange({ transformCode: e.target.value });
                    }}
                    onBlur={(e) => validateTransformSyntax(e.target.value)} />
          <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 6}}>
            {i18n('properties.transformCode.intro',
              'JS function body. Use value (resolved field) and data (full payload).')}
            {' '}
            {isRadio
              ? i18n('properties.transformCode.bodyRadio', 'Must return an integer option index (starts at 0).')
              : isCurrency
                ? i18n('properties.transformCode.bodyCurrency', "Must return a number — the renderer applies the selected currency's symbol and decimal places afterwards.")
                : i18n('properties.transformCode.bodyCheckbox', 'Must return a boolean (true = checked).')}
            {isRadio && field.fieldType === 'form' && field.formFieldName && (
              <div style={{marginTop: 6, fontStyle: 'italic'}}>
                {i18n('properties.transformCode.radioSharedPre', 'Shared across the whole')}{' '}<code>{field.formFieldName}</code>{' '}{i18n('properties.transformCode.radioSharedPost', 'group — editing here updates every linked radio.')}
              </div>
            )}
            {' '}{i18n('properties.transformCode.leaveEmpty', 'Leave empty to skip the transform.')}
          </div>
        </details>
      )}

      {/* Font / style controls are hidden on form-detected fields.
          The source PDF's AcroForm widget carries its own font, size,
          color, and alignment; the renderer fills the value into the
          widget and the PDF reader uses the widget's appearance.
          Exposing our own font settings here would mislead the user
          into thinking they take effect (they don't for these fields). */}
      {isText && fonts && !isFormField && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className="label">{i18n('properties.font', 'Font')}</label>
          <select className="input"
                  value={field.font || ''}
                  disabled={isLocked}
                  onChange={(e) => onChange({ font: e.target.value || null })}>
            <option value="">{i18n('properties.font.templateDefault', 'Template default')}</option>
            {fonts.builtin && fonts.builtin.length > 0 && (
              <optgroup label={i18n('properties.font.builtin', 'Built-in')}>
                {fonts.builtin.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </optgroup>
            )}
            {fonts.custom && fonts.custom.length > 0 && (
              <optgroup label={i18n('properties.font.custom', 'Custom')}>
                {customFamilyEntries(fonts.custom).map((entry) => (
                  <option key={entry.family} value={entry.family}
                          title={entry.variants.map(variantLabel).join(', ')}>
                    {entry.displayName}
                    {entry.variants.length > 1
                      ? ` (${entry.variants.length} ${i18n('properties.font.variants', 'variants')})`
                      : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      )}

      {(isText || isCheckbox) && !isFormField && (
        <div className="prop-group prop-row-2" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <div>
            <label className="label">{i18n('properties.fontSize', 'Font size')}</label>
            <NumberField value={field.fontSize} fallback={12} min={6} max={72} step={0.5}
                         disabled={isLocked}
                         onCommit={(v) => onChange({ fontSize: v })} />
          </div>
          <div>
            <label className="label">{i18n('properties.color', 'Color')}</label>
            <ColorPicker value={field.color || '#000000'}
                         disabled={isLocked}
                         onChange={(c) => onChange({ color: c })} />
          </div>
        </div>
      )}

      {/* Numeric position inputs — supplement to the drag handle on the
          canvas for floating fields. Form-detected fields are anchored
          to the PDF widget rect so we hide the inputs entirely; the
          canvas drag is already disabled for them and surfacing
          editable X/Y here would let the user desync the overlay
          from the underlying widget. */}
      {!isFormField && (
        <div className="prop-group prop-row-2" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <div>
            <label className="label">{i18n('properties.xPosition', 'X position (%)')}</label>
            <NumberField value={field.x} fallback={0} min={0} max={100} step={0.1}
                         disabled={isLocked}
                         onCommit={(v) => onChange({ x: v })} />
          </div>
          <div>
            <label className="label">{i18n('properties.yPosition', 'Y position (%)')}</label>
            <NumberField value={field.y} fallback={0} min={0} max={100} step={0.1}
                         disabled={isLocked}
                         onCommit={(v) => onChange({ y: v })} />
          </div>
        </div>
      )}

      {isText && !isFormField && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className={`checkbox-row ${boldEnabled && !isLocked ? '' : 'is-disabled'}`}
                 title={boldEnabled || isLocked ? '' : i18n('properties.boldUnavailable', 'This font has no bold variant for the current italic state')}>
            <input type="checkbox" checked={!!field.bold}
                   disabled={!boldEnabled || isLocked}
                   onChange={(e) => onChange({ bold: e.target.checked })} />
            {i18n('properties.bold', 'Bold text')}
          </label>
          <label className={`checkbox-row ${italicEnabled && !isLocked ? '' : 'is-disabled'}`}
                 title={italicEnabled || isLocked ? '' : i18n('properties.italicUnavailable', 'This font has no italic variant for the current bold state')}>
            <input type="checkbox" checked={!!field.italic}
                   disabled={!italicEnabled || isLocked}
                   onChange={(e) => onChange({ italic: e.target.checked })} />
            {i18n('properties.italic', 'Italic text')}
          </label>
        </div>
      )}

      {isText && !isFormField && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <span className="label" id="prop-align-label">{i18n('properties.alignment', 'Alignment')}</span>
          <div className="prop-align-row" role="group" aria-labelledby="prop-align-label">
            <SegmentedControl
              label={i18n('properties.alignment.horizontal', 'Horizontal alignment')}
              value={fieldTextAlign(field)}
              disabled={isLocked}
              onChange={(v) => setAlignment({ textAlign: v })}
              options={[
                { value: 'left', icon: 'alignLeft', label: i18n('properties.alignment.left', 'Left') },
                { value: 'center', icon: 'alignCenter', label: i18n('properties.alignment.center', 'Center') },
                { value: 'right', icon: 'alignRight', label: i18n('properties.alignment.right', 'Right') },
              ]} />
            <SegmentedControl
              label={i18n('properties.alignment.vertical', 'Vertical alignment')}
              value={fieldVerticalAlign(field, templateRenderer)}
              disabled={isLocked}
              onChange={(v) => setAlignment({ verticalAlign: v })}
              options={[
                { value: 'top', icon: 'valignTop', label: i18n('properties.alignment.top', 'Top') },
                { value: 'middle', icon: 'valignMiddle', label: i18n('properties.alignment.middle', 'Middle') },
                { value: 'bottom', icon: 'valignBottom', label: i18n('properties.alignment.bottom', 'Bottom') },
              ]} />
          </div>
        </div>
      )}

      {isText && !isFormField && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className="label">{i18n('properties.textDecoration', 'Text decoration')}</label>
          <select className="select" value={field.textDecoration || 'none'}
                  disabled={isLocked}
                  onChange={(e) => onChange({ textDecoration: e.target.value })}>
            <option value="none">{i18n('properties.textDecoration.none', 'None')}</option>
            <option value="underline">{i18n('properties.textDecoration.underline', 'Underline')}</option>
            <option value="underline-dotted">{i18n('properties.textDecoration.underlineDotted', 'Underline (dotted)')}</option>
            <option value="underline-dashed">{i18n('properties.textDecoration.underlineDashed', 'Underline (dashed)')}</option>
            <option value="line-through">{i18n('properties.textDecoration.lineThrough', 'Line through')}</option>
            <option value="overline">{i18n('properties.textDecoration.overline', 'Overline')}</option>
          </select>
          {field.type === 'multiline' && field.textDecoration && field.textDecoration !== 'none' && (
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              {i18n('properties.textDecoration.multilineNote',
                'Decoration applies per single-line text. Multiline rendering draws the decoration along each visible line — long wrapped lines may not match the editor preview exactly.')}
            </div>
          )}
        </div>
      )}

      {/* Shapes and signatures never take data, so "required" can't be
          satisfied — it used to make every render of the template fail. */}
      {!isShape && !isSignature && (
      <div className="prop-group">
        <label className="checkbox-row">
          <input type="checkbox" checked={!!field.required}
                 onChange={(e) => onChange({ required: e.target.checked })} />
          {i18n('properties.required', 'Required field')}
        </label>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4, marginLeft: 22}}>
          {i18n('properties.required.hintPlain', 'A PDF can only be created when this field has a value.')}
        </div>
      </div>
      )}

      {/* Default value lives near the bottom of the panel because it's
          the property users tweak last (after they've finalized name,
          position, and styling). For placed fields it sits just above
          the Page picker; for form-detected fields it's the last
          editable control before the metadata footer. */}
      {!isImage && !isShape && !isSignature && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className="label">{i18n('properties.default', 'Default value')}</label>
          {isRadio && Array.isArray(field.radioOptions) && field.radioOptions.length > 0 ? (
            // Radio group: pick the pre-selected option from the
            // group's known options instead of typing a free-form
            // value. Stored as the option index (matches what the
            // renderer compares `radioIndex` against) so a number, a
            // label, or no default all round-trip correctly.
            <select className="select"
                    value={field.default === undefined || field.default === null || field.default === '' ? '' : String(field.default)}
                    disabled={isLocked}
                    onChange={(e) => {
                      const v = e.target.value;
                      onChange({ default: v === '' ? '' : (Number.isFinite(Number(v)) ? Number(v) : v) });
                    }}>
              <option value="">{i18n('properties.default.none', '— none —')}</option>
              {field.radioOptions.map((opt) => (
                <option key={opt.index} value={String(opt.index)}>
                  {opt.index} — {opt.label}
                </option>
              ))}
            </select>
          ) : isCheckbox ? (
            // Checkbox: a Boolean — pre-checked or pre-unchecked.
            <select className="select"
                    value={field.default === true ? 'true' : 'false'}
                    disabled={isLocked}
                    onChange={(e) => onChange({ default: e.target.value === 'true' })}>
              <option value="false">{i18n('properties.default.unchecked', 'Unchecked')}</option>
              <option value="true">{i18n('properties.default.checked', 'Checked')}</option>
            </select>
          ) : isMultiline ? (
            // Multiline default: real textarea so newlines in the
            // fallback round-trip exactly as the renderer will lay
            // them out. Single-line `<input>` swallowed Enter and
            // forced users to paste pre-broken text.
            <textarea className="input" placeholder={i18n('properties.default.fallback', 'Fallback if data is empty')}
                      rows={4}
                      style={{resize: 'vertical', minHeight: 72, fontFamily: 'inherit'}}
                      value={field.default || ''}
                      disabled={isLocked}
                      onChange={(e) => onChange({ default: e.target.value })} />
          ) : (
            <input className="input" placeholder={i18n('properties.default.fallback', 'Fallback if data is empty')}
                   value={field.default || ''}
                   disabled={isLocked}
                   onChange={(e) => onChange({ default: e.target.value })} />
          )}
        </div>
      )}

      {/* Page picker is floating-only. Form-detected fields are
          anchored to the PDF widget rect — editing the page would
          desync the overlay from the render. A select (vs free
          numeric input) constrains the choice to pages that
          actually exist in the PDF. */}
      {!isFormField && (
        <div className="prop-group" data-locked-tooltip={isLocked ? lockedHint : undefined}>
          <label className="label">{i18n('properties.page', 'Page')}</label>
          <select className="select" value={field.page || 1}
                  disabled={isLocked}
                  onChange={(e) => onChange({ page: parseInt(e.target.value, 10) || 1 })}>
            {Array.from({ length: Math.max(1, totalPages || 1) }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {totalPages > 1
                  ? i18n('properties.page.labelOf', { n, total: totalPages }, 'Page {{n}} of {{total}}')
                      .replace('{{n}}', n).replace('{{total}}', totalPages)
                  : i18n('properties.page.label', { n }, 'Page {{n}}').replace('{{n}}', n)}
              </option>
            ))}
          </select>
          {totalPages > 1 && (
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              {i18n('properties.page.jumpHint', 'Switching pages jumps the canvas to follow.')}
            </div>
          )}
        </div>
      )}

      </fieldset>

      <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)'}}>
        {i18n('properties.page', 'Page')} {field.page || 1} · ID: <code style={{fontSize: 10}}>{field.id}</code>
      </div>
    </>
  );
}

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

// Walk a JSON Schema and emit every dot-notation path that resolves
// to a leaf or container. Drives the JSON-path autocomplete in the
// PropertiesPanel — the user's linked schema is the source of truth
// for "what data fields exist", so suggestions stay in lockstep with
// the schema they edit on the Schema tab. For arrays we emit `[0]`
// suffixes so a `customers[].name` path autocompletes via
// `customers[0].name`, matching the JSON-path resolver server-side.
function extractPathsFromSchema(schema, prefix = '') {
  const paths = [];
  if (!schema || typeof schema !== 'object') return paths;
  // Accept `properties` / `items` regardless of an explicit `type`
  // field. JSON Schema's `type` is *optional*; hand-written or
  // imported schemas (Swagger, OpenAPI fragments, Zod-emitted) often
  // omit it. Without this leniency a perfectly valid schema produces
  // zero paths and the autocomplete silently shows nothing.
  if (schema.properties && typeof schema.properties === 'object') {
    if (prefix) paths.push(prefix);
    for (const [key, sub] of Object.entries(schema.properties)) {
      const full = prefix ? prefix + '.' + key : key;
      paths.push(...extractPathsFromSchema(sub, full));
    }
  } else if (schema.items) {
    if (prefix) paths.push(prefix);
    paths.push(...extractPathsFromSchema(schema.items, (prefix || '') + '[0]'));
  } else if (prefix) {
    paths.push(prefix);
  }
  return paths;
}

// Infer a JSON Schema from a sample JSON value. Used by the Schema
// tab's "Generate from example" affordance: paste real payload, get a
// scaffolded schema. Conservative — every present key becomes
// `required`, so users can review and trim what's actually mandatory.
function exampleToSchema(value) {
  if (value === null || value === undefined) return { type: 'string' };
  if (Array.isArray(value)) {
    return { type: 'array', items: value.length > 0 ? exampleToSchema(value[0]) : { type: 'string' } };
  }
  if (typeof value === 'object') {
    const properties = {};
    const required = [];
    for (const [k, v] of Object.entries(value)) {
      properties[k] = exampleToSchema(v);
      required.push(k);
    }
    const out = { type: 'object', properties };
    if (required.length > 0) out.required = required;
    return out;
  }
  if (typeof value === 'number') return Number.isInteger(value) ? { type: 'integer' } : { type: 'number' };
  if (typeof value === 'boolean') return { type: 'boolean' };
  return { type: 'string' };
}

// Auto-generate a JSON Schema from a template's field jsonPaths.
// Walks each field's dot-path, builds nested objects on the way down,
// and stamps a leaf type derived from the field's UI type (checkbox →
// boolean, everything else → string). Top-level `required` collects
// fields the editor marked as required. Mirrors the original builder's
// "Auto-generate from fields" behaviour so users get the same
// scaffolding they'd expect from a copy/paste workflow.
function fieldsToSchema(fields = []) {
  const schema = { type: 'object', properties: {}, required: [] };
  for (const field of fields) {
    if (!field?.jsonPath) continue;
    const parts = field.jsonPath.split('.');
    let cursor = schema.properties;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        cursor[part] = {
          type: field.type === 'checkbox' ? 'boolean' : 'string',
          description: field.name || undefined,
        };
      } else {
        if (!cursor[part]) cursor[part] = { type: 'object', properties: {} };
        cursor = cursor[part].properties;
      }
    }
    if (field.required && parts.length === 1) schema.required.push(parts[0]);
  }
  if (schema.required.length === 0) delete schema.required;
  return schema;
}

// Extract AcroForm fields from a pdf.js document and convert them into
// our template-field shape. Returns [] if the PDF has no fillable form
// fields (a flattened scan, an export, etc.) — the user just won't see
// auto-detected fields and falls back to placing them manually.
//
// PDF coordinates are bottom-left origin in PDF user-space; we convert
// to top-left percent of the page, matching the editor's storage shape.
// Extract AcroForm fields from a pdf.js document. Each detected field is
// tagged with `fieldType: 'form'` so the editor distinguishes them from
// manually-placed (`'floating'`) fields:
//
//   • formFieldName (the AcroForm field name) is locked — renaming would
//     break the rendered overlay's mapping to the PDF widget rect.
//   • formFieldType (`PDFTextField` / `PDFCheckBox` / `PDFRadioGroup`) is
//     surfaced in the properties panel so the user knows what they're
//     looking at, mirroring Adobe Acrobat / pdf-lib semantics.
//   • Radio groups become N fields — one per widget — sharing the same
//     formFieldName but with different `radioIndex`. radioOptions on each
//     entry holds the full {index, label, page, x/y/width/height} list so
//     the properties panel can show the group at a glance.
//   • Default values + required flag are pulled straight from the PDF.
//   • Width/height are preserved for every type (the previous version
//     zeroed them on text/checkbox/radio, which made resizing impossible).
//
// Returns an array of editor field objects ready to write to template.fields.
// Fallback when getFieldObjects() returns null or empty. Walks every
// page, collects annotations whose `subtype === 'Widget'`, and groups
// them into the same { fieldName: [widget, ...] } shape getFieldObjects
// would have produced. Used when a PDF's AcroForm catalog entry is
// missing or malformed but the form widgets themselves are intact.
// PDF Field Flag bits (PDF 32000-1:2008 §12.7.4.3). Used as a fallback
// when pdf.js's typed flags (radioButton / pushButton / checkBox) aren't
// populated — in some pdf.js versions they're only set on the highest-
// level field, not every kid widget.
const PDF_FIELD_FLAG_PUSHBUTTON = 1 << 15; // bit 16
const PDF_FIELD_FLAG_RADIO      = 1 << 16; // bit 17

// Classify a Btn-typed widget annotation as radio / checkbox / pushbutton.
// Checks pdf.js's typed flags first, then falls back to PDF spec bits in
// `fieldFlags`. Returns null for pushbuttons (we don't render them).
function classifyButtonWidget(a) {
  if (a.radioButton) return 'radiobutton';
  if (a.pushButton)  return null;
  if (a.checkBox)    return 'checkbox';
  // Fall through: typed flags missing, derive from raw spec bits.
  const flags = typeof a.fieldFlags === 'number' ? a.fieldFlags : 0;
  if (flags & PDF_FIELD_FLAG_RADIO)      return 'radiobutton';
  if (flags & PDF_FIELD_FLAG_PUSHBUTTON) return null;
  return 'checkbox';
}

async function sweepAnnotationsForWidgets(doc) {
  const grouped = {};
  const pageCount = doc.numPages;
  for (let p = 1; p <= pageCount; p++) {
    let annotations;
    try { annotations = await (await doc.getPage(p)).getAnnotations(); }
    catch (e) {
      console.warn('[sweepAnnotationsForWidgets] page', p, 'failed:', e?.message || e);
      continue;
    }
    for (const a of annotations || []) {
      if (a.subtype !== 'Widget') continue;
      // Group key: prefer the AcroForm field name (siblings of a radio
      // group share it), fall back to the parent kid id, then to the
      // widget's own id (degenerate single-widget group). Without this,
      // radio kids whose parent's name doesn't propagate end up split
      // into one-widget "groups" and get classified as checkboxes via
      // the widgets.length > 1 heuristic upstream.
      const name = a.fieldName || a.parentId || a.id || `field_${p}_${Object.keys(grouped).length}`;

      let type = 'text';
      const ft = (a.fieldType || '').toLowerCase();
      // Detect Btn widgets even when `fieldType` is missing — kid
      // widgets in a radio or checkbox group sometimes inherit /FT
      // from the parent dict and pdf.js doesn't propagate it onto the
      // per-widget annotation. Without this fallback the kid lands in
      // the `type = 'text'` default at the top of the function, the
      // operator sees a text overlay where a radio button should be,
      // and the original radio widget keeps showing through because
      // (a) the overlay has the wrong rect and (b) the editor never
      // emits a `radioIndex` to mark which option is selected — so
      // every render of the page leaves the prefilled value visible.
      const isBtn = ft === 'btn'
        || a.radioButton === true || a.checkBox === true || a.pushButton === true;
      if (isBtn) {
        const cls = classifyButtonWidget(a);
        if (cls === null) continue;        // pushbutton — not renderable
        type = cls;
      } else if (ft === 'tx') {
        type = a.multiLine ? 'multiline' : 'text';
      } else if (ft === 'ch') {
        type = 'choice';
      } else if (ft === 'sig') {
        continue;
      } else if (ft === '') {
        // No fieldType from pdf.js. The Btn fast-path above already
        // caught typed-flag Btn kids, so reaching this branch means
        // we genuinely don't know what kind of widget it is. We let
        // it fall through as text — better than dropping — but log it
        // so a reproducer's dev console identifies which AcroForm
        // shape we're missing.
        console.warn('[sweepAnnotationsForWidgets] widget has no fieldType, defaulting to text',
          { id: a.id, fieldName: a.fieldName, rect: a.rect,
            fieldValue: a.fieldValue, defaultFieldValue: a.defaultFieldValue,
            fieldFlags: a.fieldFlags });
      }

      const widget = {
        id: a.id, type,
        rect: a.rect,
        page: p - 1,
        defaultValue: a.defaultFieldValue,
        fieldValue: a.fieldValue,
        buttonValue: a.buttonValue,
        exportValues: a.exportValue ? [a.exportValue] : undefined,
        multiLine: a.multiLine,
        required: a.required,
        fieldFlags: a.fieldFlags,
      };
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(widget);
    }
  }

  // Post-pass: if any widget in a group declares itself a radio (via the
  // typed flag or spec bit), promote the whole group to radio. Catches
  // PDFs where some kids report radioButton and some don't, or where
  // sibling grouping picked up a stray single-widget radio that the
  // upstream `widgets.length > 1` rule would otherwise mis-classify
  // back to checkbox.
  for (const widgets of Object.values(grouped)) {
    const anyRadio = widgets.some((w) => w.type === 'radiobutton');
    if (anyRadio) {
      for (const w of widgets) {
        if (w.type === 'checkbox') w.type = 'radiobutton';
      }
    }
  }

  // Classification diagnostic — one summary line per group with type +
  // widget count. Helps spot "should be radio but classified as
  // checkbox" cases without dumping every annotation.
  try {
    const summary = Object.entries(grouped).map(([n, ws]) => {
      const t = ws[0]?.type;
      return `${n}=${t}×${ws.length}`;
    });
    console.info('[sweepAnnotationsForWidgets] classified groups:', summary);
  } catch (_) { /* logging never throws into the user's flow */ }
  return grouped;
}

async function extractAcroFormFields(doc) {
  // The per-page annotation sweep is the source of truth for widget
  // positions. pdf.js's `getFieldObjects()` API returns aggregated form
  // metadata but several versions only populate the `page` property on
  // widgets whose page has been rendered, which silently truncates
  // multi-page docs. The sweep walks every page explicitly so widgets
  // on pages 2..N are always picked up.
  //
  // We still call getFieldObjects() AFTER the sweep to harvest radio
  // group export values that aren't always in the per-page annotation
  // payload, then merge the two — the sweep wins on geometry, the
  // metadata fills in radio labels.
  const swept = await sweepAnnotationsForWidgets(doc);
  let metadata = null;
  try { metadata = await doc.getFieldObjects(); }
  catch (e) {
    console.warn('[extractAcroFormFields] getFieldObjects threw:', e?.message || e);
  }
  if (metadata) {
    for (const [name, metaWidgets] of Object.entries(metadata)) {
      if (!Array.isArray(metaWidgets) || metaWidgets.length === 0) continue;
      const sweptWidgets = swept[name];
      if (!sweptWidgets || sweptWidgets.length === 0) {
        // Sweep missed this field — keep the metadata view so the
        // operator at least sees something. Position will be {0,0}
        // until they fix it manually, but better than dropping.
        swept[name] = metaWidgets;
        continue;
      }
      // Pair-wise merge. Both arrays are widget-ordered for stable
      // pairing across the two APIs in practice; if the lengths
      // diverge, fall back to the sweep entry alone.
      for (let i = 0; i < sweptWidgets.length; i++) {
        const meta = metaWidgets[i] || metaWidgets[0];
        const w = sweptWidgets[i];
        // buttonValue / exportValues land here.
        w.buttonValue   = w.buttonValue   ?? meta.buttonValue;
        w.exportValues  = w.exportValues  ?? meta.exportValues;
        // ?? falls through on null AND undefined, so if both sources
        // produce `null` the merged value stays `null` rather than
        // becoming the safer `undefined`. Downstream code (extraction
        // at line ~8133) treats `null` as a defined value and used to
        // stringify it to the literal "null". Coercing to undefined
        // here means a single nullish check downstream catches both
        // shapes, and matches the `w.defaultValue == null` semantics
        // we actually want.
        w.defaultValue  = w.defaultValue  ?? meta.defaultValue ?? undefined;
        w.fieldValue    = w.fieldValue    ?? meta.fieldValue   ?? undefined;
        w.required      = w.required      ?? meta.required;
        if (meta.multiLine || meta.multiline) w.multiLine = true;
      }
    }
  }
  if (Object.keys(swept).length === 0) {
    console.info('[extractAcroFormFields] no AcroForm widgets found in PDF');
    return [];
  }
  const fieldObjects = swept;
  // Diagnostic: how many widgets per page after merge — surfaces the
  // "all fields on page 1" symptom early.
  const pageHist = {};
  for (const widgets of Object.values(fieldObjects)) {
    for (const w of (widgets || [])) {
      const k = (w?.page ?? '?');
      pageHist[k] = (pageHist[k] || 0) + 1;
    }
  }
  console.info('[extractAcroFormFields] detected', Object.keys(fieldObjects).length,
    'field name(s); widgets per page:', pageHist);

  // Page distribution of the OUTPUT array, computed at the end of the
  // function. If this shows the same shape as `pageHist` above, the
  // bug is in display (the editor's pageFields filter / FieldOverlay
  // render); if it collapses to all-page-1, the bug is in extraction.
  // Logged below with the final out array.

  // Resolve a pdf.js widget's page reference to the 1-indexed page
  // number doc.getPage() expects. pdf.js *usually* reports 0-indexed
  // page numbers in getFieldObjects(), but some versions (and older
  // PDFs with malformed annotation-to-page indirect refs) report
  // 1-indexed or undefined. We accept both: prefer +1, fall back to
  // the raw value if that's out of range, and finally clamp to a
  // valid page index. Failure to resolve still throws — but only
  // for genuinely bogus data, not the off-by-one rendering we're
  // seeing in the wild.
  const resolvePageNum = (raw) => {
    const n = doc.numPages;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    const candidates = [raw + 1, raw];
    for (const c of candidates) {
      if (c >= 1 && c <= n) return c;
    }
    // Out of range either way — clamp to valid bounds so the user gets
    // something rather than a hard error. Loses precision for the
    // pathological PDF, but the field stays editable.
    return Math.min(Math.max(Math.round(raw + 1), 1), n);
  };

  // Cache per-page viewport so we don't re-fetch the same page repeatedly.
  // Keyed by 1-indexed page number to match doc.getPage().
  const pageDims = {};
  const ensurePage = async (rawPage) => {
    const num = resolvePageNum(rawPage);
    if (num == null) return null;
    if (pageDims[num]) return { num, dim: pageDims[num] };
    const page = await doc.getPage(num);
    const vp = page.getViewport({ scale: 1 });
    pageDims[num] = { width: vp.width, height: vp.height };
    return { num, dim: pageDims[num] };
  };

  // Convert a pdf.js widget rect (PDF user-space, bottom-left origin) to
  // the editor's percent-of-page coords (top-left origin).
  //
  // Failure paths — and what we do about each:
  //
  //   1. w.rect missing or malformed
  //        Some pdf.js versions return widgets whose /Rect inherited
  //        from a parent or was an indirect ref that did not resolve.
  //        We default to a small placeholder rect in the top-left so
  //        the operator at least sees a clickable field on page 1 and
  //        knows the widget existed; they can drag it to the right
  //        spot manually.
  //
  //   2. w.page null or out-of-range
  //        Same recovery: clamp to page 1 with a console warning. The
  //        old behaviour was a silent drop, which manifested as "the
  //        widget is not cleared and there is no field" — the widget
  //        keeps showing on the rendered PDF because pdfcpu never gets
  //        an instruction to overwrite it, and the operator has no
  //        overlay to interact with.
  //
  // Both fallbacks emit a console.warn so a customer reporting the
  // bug can copy the diagnostic lines and we can identify which
  // widget shapes are slipping through.
  const widgetRect = async (w) => {
    const resolved = await ensurePage(w.page);
    if (!resolved) {
      console.warn('[extractAcroFormFields] widget page unresolved; falling back to page 1',
        { id: w.id, page: w.page, rect: w.rect, type: w.type });
      return { page: 1, x: 5, y: 5, width: 20, height: 4 };
    }
    const { num, dim } = resolved;
    if (!Array.isArray(w.rect) || w.rect.length < 4) {
      console.warn('[extractAcroFormFields] widget rect malformed; placing at top-left of page',
        { id: w.id, page: num, rect: w.rect, type: w.type });
      return { page: num, x: 5, y: 5, width: 20, height: 4 };
    }
    const [llx, lly, urx, ury] = w.rect;
    return {
      page: num,
      x: clamp((llx / dim.width) * 100, 0, 100),
      y: clamp(((dim.height - ury) / dim.height) * 100, 0, 100),
      width: clamp(((urx - llx) / dim.width) * 100, 0.5, 100),
      height: clamp(((ury - lly) / dim.height) * 100, 0.5, 100),
    };
  };

  // pdf.js types → editor types. Anything we can't render (signature,
  // file, etc.) is dropped silently.
  const editorTypeFor = (w) => {
    const t = (w.type || '').toLowerCase();
    if (t === 'text' || t === 'tx') return w.multiLine || w.multiline ? 'multiline' : 'text';
    if (t === 'multiline') return 'multiline';
    if (t === 'checkbox') return 'checkbox';
    if (t === 'radiobutton' || t === 'radio') return 'radio';
    if (t === 'btn') return 'checkbox';  // some pdf.js versions tag all buttons as 'btn'
    if (t === 'choice') return 'text';
    return null;
  };

  // PDF widget type → friendly pdf-lib-style class name. Cosmetic, used
  // only for the badge in the properties panel.
  const formFieldTypeFor = (w, editorType) => {
    if (editorType === 'multiline' || editorType === 'text') return 'PDFTextField';
    if (editorType === 'radio') return 'PDFRadioGroup';
    if (editorType === 'checkbox') return 'PDFCheckBox';
    return 'PDFField';
  };

  // AcroForm convention: a checkbox/radio widget is "on" when its
  // current value (or default) is set, not "Off" (the canonical OFF
  // state pdf.js produces), and ideally matches its buttonValue /
  // exportValue. We accept either case because some PDFs simply omit
  // /V on an unchecked widget rather than writing "Off" explicitly,
  // and others use empty string. Returns true only when the widget is
  // definitively checked — so a missing /V never produces a false
  // positive.
  const isCheckedWidget = (w) => {
    const v = w.fieldValue ?? w.defaultValue;
    if (v === undefined || v === null || v === '' || v === false) return false;
    const s = String(v);
    if (s === 'Off' || s === '/Off') return false;
    if (w.buttonValue) return s === String(w.buttonValue);
    if (Array.isArray(w.exportValues) && w.exportValues.length) {
      return w.exportValues.some((ev) => String(ev) === s);
    }
    // No declared ON-state name: any non-Off value counts as checked.
    return true;
  };

  // Pick the widget's effective default-as-string for text/multiline
  // fields. Tries /DV first (the canonical AcroForm default), then
  // /V (the current value — useful when a "prefilled" form has only
  // /V set), then empty string. The single source of truth for the
  // "what counts as a meaningful value" question:
  //
  //   undefined, null, '' → no value (returns '')
  //   any other shape    → stringified verbatim
  //
  // Pre-fix this logic was inlined and let `null` past its check,
  // producing the literal string "null" as the field's default and
  // confusing every downstream consumer (Properties panel, renderer,
  // operators). See the regression test in field_default_test.go for
  // the analogous Go-side bug.
  const isMeaningful = (v) => v !== undefined && v !== null && v !== '';
  const pickWidgetDefault = (w) => {
    if (isMeaningful(w.defaultValue)) return String(w.defaultValue);
    if (isMeaningful(w.fieldValue))   return String(w.fieldValue);
    return '';
  };

  const out = [];
  let counter = 0;
  const ts = Date.now();

  // Pre-bind each AcroForm field to a normalized JSON path. The PDF field
  // name becomes a valid, predictable key (see acroNameToPath) so an
  // uploaded fillable PDF arrives with its fields already wired to JSON —
  // the operator no longer hand-types a path for every field. Built once,
  // keyed by the acro name, so all widgets of a radio group share the
  // same path. Collisions (two distinct names normalizing to the same
  // key) are disambiguated with a numeric suffix so each field keeps a
  // unique binding.
  const pathByName = {};
  const usedPaths = new Set();
  for (const name of Object.keys(fieldObjects)) {
    let base = acroNameToPath(name);
    let path = base, n = 2;
    while (usedPaths.has(path)) { path = `${base}_${n++}`; }
    usedPaths.add(path);
    pathByName[name] = path;
  }

  for (const [name, widgets] of Object.entries(fieldObjects)) {
    if (!Array.isArray(widgets) || widgets.length === 0) continue;
    const first = widgets[0];
    const baseType = editorTypeFor(first);
    if (!baseType) continue;

    // Multi-widget fields. Two cases:
    //   1. pdf.js correctly labelled it `radiobutton` → real radio group.
    //   2. pdf.js labelled it `checkbox` but there are >1 widgets → in
    //      practice this is also a radio group (Adobe sometimes models
    //      mutually-exclusive checkboxes that way). Match the original's
    //      heuristic: ctor==CheckBox || RadioGroup AND widgets > 1 = radio.
    const isRadioGroup = baseType === 'radio'
      || (baseType === 'checkbox' && widgets.length > 1);

    if (isRadioGroup) {
      // Build the shared options list once (label per widget, index by order).
      // pdf.js exposes the ON-state name via buttonValue or exportValues.
      const optionLabels = widgets.map((w, i) => {
        return w.buttonValue
          || (Array.isArray(w.exportValues) ? w.exportValues[0] : null)
          || `Option ${i + 1}`;
      });

      // Resolve each widget's page+rect ahead of time so we have the
      // resolved page numbers for the radioOptions metadata.
      // widgetRect() now has its own fallback paths for null page /
      // missing rect — the previous early-continue here would have
      // bypassed those, dropping radio options the user expected.
      const resolved = [];
      for (let i = 0; i < widgets.length; i++) {
        const w = widgets[i];
        if (!w) { resolved.push(null); continue; }
        resolved.push(await widgetRect(w));
      }
      const optionsList = resolved.map((r, j) => ({
        index: j, label: optionLabels[j], page: r ? r.page : null,
      }));

      // Pre-checked radio option: pdf.js shares the group's /V across
      // every widget, so isCheckedWidget(widgets[i]) is true on the
      // pre-selected option. We propagate that as the radio group's
      // default — every widget in the group gets the same default value
      // (the matched radioIndex as a string). The renderer's drawRadio
      // only stamps the widget whose radioIndex matches the resolved
      // value, so this correctly produces "the pre-selected option is
      // drawn by default" without affecting the unselected widgets.
      const checkedIdx = widgets.findIndex(isCheckedWidget);
      const groupDefault = checkedIdx >= 0 ? String(checkedIdx) : '';

      for (let i = 0; i < widgets.length; i++) {
        const rect = resolved[i];
        if (!rect) continue;
        out.push({
          id: `field_${ts}_${counter++}`,
          // Editor label includes the option for disambiguation in lists.
          name: `${name} · ${optionLabels[i]}`,
          // Auto-bound to the normalized field name; the whole radio
          // group shares one path (the group's selected value).
          jsonPath: pathByName[name] || '',
          type: 'radio',
          page: rect.page,
          x: rect.x, y: rect.y, width: rect.width, height: rect.height,
          fontSize: 12, color: '#000000', bold: false,
          default: groupDefault,
          radioIndex: i,
          // Form-field metadata. Same `formFieldName` shared across the
          // group — the renderer fills any one of them iff the resolved
          // value matches that radio's `radioIndex`.
          fieldType: 'form',
          formFieldName: name,
          formFieldType: 'PDFRadioGroup',
          radioOptions: optionsList,
          required: !!first.required,
        });
      }
      continue;
    }

    // Single-widget: text / multiline / checkbox. widgetRect() now
    // provides fallback rects for missing /Rect or unresolved page,
    // so we no longer short-circuit on those — only on widgets where
    // the entry itself is missing or the type is unrenderable.
    for (const w of widgets) {
      if (!w) continue;
      const editorType = editorTypeFor(w);
      if (!editorType) continue;
      const rect = await widgetRect(w);
      if (!rect) continue;
      // Checkboxes: emit a real boolean so the Properties panel's
      // strict `field.default === true` check matches without any
      // string-coercion gymnastics. The renderer's truthy() check
      // accepts both true and "true" so storing the boolean form
      // here doesn't break the legacy fpdf path either. Text fields
      // keep the raw string default value as before.
      let defaultValue;
      if (editorType === 'checkbox') {
        defaultValue = isCheckedWidget(w);
      } else {
        // Pick the first defined-and-non-empty value among /DV (default)
        // and /V (current), then stringify. The previous check used
        // `!== undefined && !== ''` which let `null` through and
        // produced the literal "null" string in field.default for
        // every widget pdf.js returned with a null /DV (common for
        // widgets that only have /V, or those whose default-value
        // indirect-ref didn't resolve). pickDefault() collapses every
        // empty/nullish shape to '' uniformly.
        defaultValue = pickWidgetDefault(w);
      }
      out.push({
        id: `field_${ts}_${counter++}`,
        // PDF field names are usually descriptive ("user.name",
        // "Antragsteller", …) so they make a reasonable starting label.
        name: name || `field_${counter}`,
        // Auto-bound to the normalized AcroForm field name.
        jsonPath: pathByName[name] || '',
        type: editorType,
        page: rect.page,
        // Preserve the widget's actual size for every type — text,
        // checkbox, multiline. The previous version zeroed it on
        // checkboxes which made resizing impossible.
        x: rect.x, y: rect.y, width: rect.width, height: rect.height,
        fontSize: 12, color: '#000000', bold: false,
        default: defaultValue,
        fieldType: 'form',
        formFieldName: name,
        formFieldType: formFieldTypeFor(w, editorType),
        required: !!w.required,
      });
    }
  }
  // Final output-side page histogram — compare with the input one
  // logged earlier. Identical distributions = display issue;
  // collapsed-to-page-1 = extraction issue.
  const outHist = {};
  for (const f of out) outHist[f.page] = (outHist[f.page] || 0) + 1;
  console.info('[extractAcroFormFields] emitted', out.length, 'editor field(s); per-page:', outHist);

  // Per-widget table for reproducer collection. When a customer reports
  // "this PDF is unreliable on upload", asking them to paste this table
  // from the dev console identifies which specific widget hit which
  // failure mode without us needing the PDF itself. Includes the
  // captured default — empty string vs the literal "null" vs a real
  // value lets us see which extraction path the widget went through.
  try {
    console.table(out.map((f) => ({
      page: f.page, type: f.type, name: f.formFieldName,
      default: f.default === '' ? '(empty)' : f.default,
      x: f.x.toFixed(1), y: f.y.toFixed(1),
      w: f.width.toFixed(1), h: f.height.toFixed(1),
    })));
  } catch (_) { /* console.table not available — non-fatal */ }
  return out;
}

// ============================================================================
// Image picker modal — browse, upload, and select images from the library
// ============================================================================

function ImagePickerModal({ onClose, onSelect }) {
  useModalDismiss(onClose);
  const t = useT();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = React.useRef(null);
  const toast = useToast();
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { images } = await api.get('/spa/field-images');
      setImages(images);
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif)$/i.test(file.type)) {
      toast(t('imageLib.unsupported', 'Unsupported file type. Use PNG, JPG or GIF.'), 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast('Image too large (8 MB max). Compress and retry.', 'error');
      return;
    }
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    setUploading(true);
    try {
      const { url, key } = await api.post('/spa/field-images/upload-url', {
        contentType: file.type || 'image/png',
        extension: ext,
      });
      const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'image/png' } });
      if (!putRes.ok) throw new Error(t('upload.failed', 'Upload failed. Please check your connection and try again.'));
      await api.post('/spa/field-images', {
        name: file.name.replace(/\.[^.]+$/, ''),
        s3Key: key,
        mimeType: file.type || 'image/png',
        size: file.size,
      });
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: t('imageLib.deleteConfirmTitle', 'Delete image?'),
      body: 'Templates that reference this image will show a broken image at render time.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/field-images/${id}`);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return images;
    return images.filter((i) => (i.name || '').toLowerCase().includes(q));
  }, [images, search]);

  // Drop-anywhere on the modal body, not just on a tiny dropzone strip.
  const onModalDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal image-picker-modal"
           style={{maxWidth: 720, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column'}}
           onClick={(e) => e.stopPropagation()}
           onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
           onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.classList.remove('dragover'); }}
           onDrop={onModalDrop}>

        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12}}>
          <h2 style={{margin: 0}}>Image library</h2>
          <label className="btn btn-primary" style={{cursor: 'pointer', margin: 0}}>
            {uploading ? 'Uploading…' : '+ Upload'}
            <input ref={fileInputRef} type="file" style={{display: 'none'}}
                   accept="image/png,image/jpeg,image/gif"
                   disabled={uploading}
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </label>
        </div>

        {(images.length > 0 || search) && (
          <input className="input" type="search"
                 placeholder="Search images…"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 style={{marginBottom: 12}} />
        )}

        <div style={{flex: 1, overflowY: 'auto', minHeight: 0}}>
          {loading ? (
            <div style={{textAlign: 'center', padding: 32, color: 'var(--text-muted)'}}>Loading…</div>
          ) : images.length === 0 ? (
            <div style={{textAlign: 'center', padding: 40, color: 'var(--text-muted)', border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)'}}>
              <div style={{fontSize: 13, marginBottom: 4}}>No images yet.</div>
              <div style={{fontSize: 12}}>Drop a file here or click Upload.</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{textAlign: 'center', padding: 32, color: 'var(--text-muted)'}}>No matches for "{search}".</div>
          ) : (
            <div className="image-picker-grid">
              {filtered.map((img) => (
                <div key={img.id} className="image-tile"
                     onClick={() => onSelect('fieldimage:' + img.id)}
                     title={img.name}>
                  <img src={img.url} alt={img.name} />
                  <div className="name">{img.name}</div>
                  <button className="del"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); remove(img.id); }}
                          title="Delete">×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-actions" style={{marginTop: 16, flexShrink: 0}}>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Schema tab — link template to a reusable Schema record, edit its JSON
// ============================================================================


// Versions tab — template design history + one-click restore. Every
// field/schema-touching save snapshots server-side; this panel lists the
// snapshots (metadata only) and restores an old one AS A NEW VERSION.
// Renders always use the current version — restore is the only way an
// old state comes back, and it never rewrites history.
function VersionsPanel({ template, onRestored }) {
  const { current: ws } = useWorkspace();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // version number mid-restore

  const load = useCallback(async () => {
    if (!ws || !template?.id) return;
    try {
      // /spa, not /api/v1: version history is a dashboard-only capability
      // and is deliberately absent from the public API + docs.
      setData(await api.get(`/spa/${ws.slug}/templates/${template.id}/versions`));
    } catch (e) {
      toast(e.message, 'error');
      setData({ currentVersion: template?.version, versions: [] });
    }
    // Reload whenever the version counter moves (saves + restores).
  }, [ws?.slug, template?.id, template?.version]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const restore = async (v) => {
    const ok = await confirm({
      title: t('versions.restoreConfirm.title', 'Restore version {{v}}?', { v: v.version }),
      body: t('versions.restoreConfirm.body', 'Fields and schema are set back to version {{v}} — applied as a new version, so nothing is lost.', { v: v.version }),
      confirmLabel: t('versions.restore', 'Restore'),
    });
    if (!ok) return;
    setBusy(v.version);
    try {
      const updated = await api.post(`/spa/${ws.slug}/templates/${template.id}/versions/${v.version}/restore`);
      toast(t('versions.restored', 'Version {{v}} restored', { v: v.version }), 'success');
      onRestored(updated);
    } catch (e) {
      toast(e.detail?.message || e.message, 'error');
    } finally { setBusy(null); }
  };

  if (!data) return <div style={{color: 'var(--text-muted)', fontSize: 13}}>{t('common.loading', 'Loading…')}</div>;

  return (
    <div className="form-row">
      <div style={{fontSize: 12, color: 'var(--text-secondary)'}}>
        {t('versions.help', 'Every save of fields or schema creates a version. Restoring applies an old version as a new one.')}
      </div>
      {data.versions.length === 0 ? (
        <div style={{color: 'var(--text-muted)', fontSize: 13}}>
          {t('versions.empty', 'No versions yet — save a change to start the history.')}
        </div>
      ) : data.versions.map((v) => (
        <div key={v.version} className="card" style={{padding: '10px 12px', display: 'flex',
             alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
          <div style={{minWidth: 0}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
              <strong style={{fontSize: 13}}>v{v.version}</strong>
              {v.current && <span className="pill pill-active" style={{fontSize: 10}}>{t('versions.current', 'current')}</span>}
            </div>
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 2}}>
              {formatDateTime(v.createdAt)} · {t('versions.fieldCount', '{{n}} field(s)', { n: v.fieldCount })}
            </div>
          </div>
          {!v.current && (
            <button className="btn btn-sm" disabled={busy != null} onClick={() => restore(v)}>
              {busy === v.version ? '…' : t('versions.restore', 'Restore')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Schema tab on the editor's right rail. Two modes:
//
//   inline — schema lives on the template itself (template.jsonSchema).
//            Auto-saved on valid edits the same way every other
//            template setting persists. This is the default for new
//            templates; most users don't reuse a schema across
//            templates and don't need to think about a Schemas page.
//   saved  — schema lives in /spa/schemas and the template references
//            it by schemaId. Linking is a one-click "attach"; edits
//            to the schema body itself happen in Settings → Schemas.
//            The tab shows a read-only preview + a Detach button.
//
// Switching modes is non-destructive on the row: linking a saved
// schema clears the inline copy on the server (see PATCH /:id) and
// detaching clears schemaId — the row never carries data from a no-
// longer-active mode. The "Promote inline → saved" affordance turns
// the current inline draft into a new reusable Schema row and links
// it, so users can start inline and graduate when they need reuse.
//
// In either mode the bottom of the panel renders the inferred sample
// request body so the operator can verify the wire format their
// render endpoint expects.
function SchemaTab({ template, onLink, onUpdateTemplate }) {
  const [schemas, setSchemas] = useState([]);
  // Mode is derived from template state on load: if schemaId is set →
  // saved view; otherwise inline editor on the template.jsonSchema.
  const [mode, setMode] = useState(() => template.schemaId ? 'saved' : 'inline');
  const [linked, setLinked] = useState(null);
  const [draft, setDraft] = useState('');
  // Snapshot of the last persisted draft text so we can detect a dirty
  // editor and enable/disable Save accordingly. Generators that
  // overwrite the draft (From fields / From example) also reset this
  // to themselves so the Save button only highlights genuine edits.
  const lastSavedRef = React.useRef('');
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState(null);
  const [showExample, setShowExample] = useState(false);
  const [exampleJson, setExampleJson] = useState('');
  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const { schemas } = await api.get('/spa/schemas');
      setSchemas(schemas);
      const current = template.schemaId ? schemas.find((s) => s.id === template.schemaId) : null;
      setLinked(current || null);
      // Draft is the inline editor's working copy. Pre-fill from the
      // linked schema (so toggling Inline → Saved → Inline doesn't
      // wipe what the user had) or from template.jsonSchema, falling
      // back to an empty object skeleton.
      const initial = current?.jsonSchema || template.jsonSchema || { type: 'object', properties: {} };
      const text = JSON.stringify(initial, null, 2);
      setDraft(text);
      lastSavedRef.current = text;
      setDirty(false);
      setMode(current ? 'saved' : 'inline');
    } catch (e) { toast(e.message, 'error'); }
    // Schema state only needs to be loaded when the template's schema
    // pointer changes — refetching on jsonSchema changes would clobber
    // the user's in-progress draft mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.schemaId, toast]);
  useEffect(() => { load(); }, [load]);

  const validate = (text) => {
    try { JSON.parse(text); setErr(null); return true; }
    catch (e) { setErr(e.message); return false; }
  };

  const onDraftChange = (text) => {
    setDraft(text);
    validate(text);
    setDirty(text !== lastSavedRef.current);
  };

  // Save the inline draft to template.jsonSchema. Explicit click —
  // the rest of the editor auto-persists but schemas are large and
  // structural enough that an explicit commit reads better here: it
  // lets the user iterate freely (paste, reformat, undo) without
  // every keystroke writing through to the database.
  const saveInline = () => {
    if (!validate(draft)) return;
    try {
      const parsed = JSON.parse(draft);
      onUpdateTemplate({ jsonSchema: parsed });
      lastSavedRef.current = draft;
      setDirty(false);
      toast('Schema saved');
    } catch (e) { toast(e.message, 'error'); }
  };

  // Save current draft as a new reusable schema and link it.
  const saveAsReusable = async () => {
    if (!saveAsName.trim()) { toast('Name required', 'error'); return; }
    if (!validate(draft)) return;
    setBusy(true);
    try {
      const parsed = JSON.parse(draft);
      const { schema } = await api.post('/spa/schemas', { name: saveAsName.trim(), jsonSchema: parsed });
      setLinked(schema);
      onLink(schema.id);
      setSchemas((arr) => [...arr, schema]);
      setShowSaveAs(false);
      setSaveAsName('');
      setMode('saved');
      lastSavedRef.current = draft;
      setDirty(false);
      toast('Schema created and linked');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const detach = () => {
    onLink(null);
    setLinked(null);
    setMode('inline');
  };

  const autoFromFields = () => {
    const schema = fieldsToSchema(template.fields || []);
    const text = JSON.stringify(schema, null, 2);
    setDraft(text);
    setErr(null);
    setDirty(text !== lastSavedRef.current);
    toast('Generated schema from fields');
  };
  const generateFromExample = () => {
    try {
      const schema = exampleToSchema(JSON.parse(exampleJson));
      const text = JSON.stringify(schema, null, 2);
      setDraft(text);
      setErr(null);
      setShowExample(false);
      setExampleJson('');
      setDirty(text !== lastSavedRef.current);
      toast('Generated schema from example');
    } catch (e) {
      toast('Example must be valid JSON: ' + e.message, 'error');
    }
  };

  // Whichever schema is "live" (linked saved one, or the inline draft)
  // feeds the sample-body preview. Stays in sync as the user types.
  const liveSchema = (() => {
    if (mode === 'saved' && linked) return linked.jsonSchema;
    try { return JSON.parse(draft); } catch { return null; }
  })();

  return (
    <>
      {/* Mode toggle + (inline) dirty indicator on a single row. The
          previous layout wrapped the toggle in a .prop-group which
          stacked the indicator awkwardly; here they share a flex row
          so the indicator anchors to the right edge of the panel. */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
        <div className="schema-mode-toggle">
          <button type="button"
                  className={'schema-mode-btn' + (mode === 'inline' ? ' active' : '')}
                  onClick={() => setMode('inline')}>
            Inline
          </button>
          <button type="button"
                  className={'schema-mode-btn' + (mode === 'saved' ? ' active' : '')}
                  onClick={() => setMode('saved')}>
            Saved
          </button>
        </div>
        {mode === 'inline' && dirty && !err && (
          <span style={{fontSize: 11, color: 'var(--text-muted)'}}>Unsaved changes</span>
        )}
      </div>

      {/* Validation switches. Live on the SchemaTab because they only
          matter when a schema is attached — they're meaningless on a
          template with no schema. Both default to OFF so flipping
          Validate data on is a deliberate per-template tightening. */}
      <div className="prop-group" style={{borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8}}>
        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600}}>
          <input type="checkbox"
                 checked={!!template.validateData}
                 onChange={(e) => onUpdateTemplate({ validateData: e.target.checked })} />
          Validate render data against this schema
        </label>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5}}>
          When on, <code>/generate</code> and <code>/bulk</code> reject payloads that don't match this schema with <code>400 schema_validation_failed</code>. Renders never start; quota isn't burned.
        </div>

        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: template.validateData ? 'pointer' : 'not-allowed', fontSize: 13, marginTop: 10, opacity: template.validateData ? 1 : 0.55}}>
          <input type="checkbox"
                 disabled={!template.validateData}
                 checked={!!template.allowAdditionalData}
                 onChange={(e) => onUpdateTemplate({ allowAdditionalData: e.target.checked })} />
          Allow additional data beyond the schema
        </label>
        <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5, opacity: template.validateData ? 1 : 0.55}}>
          Off: unknown top-level keys are rejected (catches typos). On: extra fields pass through to the renderer — defined fields still have to match their declared types.
        </div>
      </div>

      {mode === 'saved' ? (
        <>
          <div className="prop-group">
            <label className="label">Linked schema</label>
            <select className="select" value={template.schemaId || ''}
                    onChange={(e) => onLink(e.target.value || null)}>
              <option value="">— none —</option>
              {schemas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              Edit reusable schemas in Settings → Saved Schemas.
            </div>
          </div>
          {linked && (
            <div className="prop-group">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6}}>
                <span style={{fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)'}}>{linked.name}</span>
                <button className="btn btn-sm" onClick={detach}>Detach</button>
              </div>
              <pre className="schema-preview">{JSON.stringify(linked.jsonSchema, null, 2)}</pre>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Generator actions: compact buttons aligned right of the
              textarea header. While the From-example panel is open we
              hide the From-fields trigger — two parallel "regenerate
              the draft" affordances side by side just clutter the
              header, and the user has already committed to a path
              by opening the example pane. */}
          <div className="prop-group">
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8}}>
              <label className="label" style={{margin: 0}}>JSON schema</label>
              <div style={{display: 'flex', gap: 6}}>
                {!showExample && (
                  <button type="button" className="btn btn-sm" onClick={autoFromFields}
                          title="Build a schema from the template's field jsonPaths">
                    From fields
                  </button>
                )}
                <button type="button" className="btn btn-sm" onClick={() => setShowExample((v) => !v)}>
                  {showExample ? 'Cancel' : 'From example'}
                </button>
              </div>
            </div>
            {showExample && (
              <div style={{marginBottom: 8, padding: 8, background: 'var(--bg-elevated)', borderRadius: 'var(--radius)'}}>
                <textarea className="input"
                          style={{minHeight: 100, fontFamily: 'var(--font-mono)', fontSize: 12}}
                          placeholder='Paste sample JSON, e.g. {"customer": {"name": "Acme"}}'
                          value={exampleJson}
                          onChange={(e) => setExampleJson(e.target.value)}
                          spellCheck={false} />
                <button type="button" className="btn btn-sm btn-primary" style={{marginTop: 6}}
                        disabled={!exampleJson.trim()}
                        onClick={generateFromExample}>
                  Generate
                </button>
              </div>
            )}
            <textarea className="input"
                      style={{minHeight: 240, fontFamily: 'var(--font-mono)', fontSize: 12}}
                      value={draft}
                      onChange={(e) => onDraftChange(e.target.value)}
                      spellCheck={false} />
            {err && <div style={{color: 'var(--danger)', fontSize: 11, marginTop: 4}}>{err}</div>}
          </div>

          {/* Two explicit save targets. "Save" commits the current
              draft to the template (template.jsonSchema). "Save as
              reusable…" promotes it to a Schema row and links this
              template to it — the workflow for a schema you intend
              to share across multiple templates. Both disabled while
              the JSON is invalid; Save additionally requires a dirty
              draft so re-clicking it on a clean editor is a no-op. */}
          {!showSaveAs ? (
            <div style={{display: 'flex', gap: 6, marginBottom: 12}}>
              <button type="button" className="btn btn-sm btn-primary" onClick={saveInline}
                      disabled={busy || !!err || !dirty}>
                Save
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setShowSaveAs(true)}
                      disabled={busy || !!err}
                      title="Turn this schema into a reusable Schema row, then link this template to it">
                Save as reusable…
              </button>
            </div>
          ) : (
            <div className="prop-group">
              <label className="label">Save as reusable</label>
              <div style={{display: 'flex', gap: 6}}>
                <input className="input" autoFocus
                       value={saveAsName}
                       onChange={(e) => setSaveAsName(e.target.value)}
                       placeholder="e.g. Customer payload" />
                <button type="button" className="btn btn-sm btn-primary" onClick={saveAsReusable}
                        disabled={busy || !saveAsName.trim()}>
                  Create
                </button>
                <button type="button" className="btn btn-sm" onClick={() => { setShowSaveAs(false); setSaveAsName(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Sample request body — auto-derived from the live schema so the
          operator sees the exact wire format the render endpoint
          expects. */}
      <div className="prop-group">
        <label className="label">Sample request body</label>
        <pre className="schema-preview">
          {liveSchema ? JSON.stringify(sampleFromSchema(liveSchema), null, 2) : '(invalid schema)'}
        </pre>
      </div>
    </>
  );
}

// ============================================================================
// Schemas page
// ============================================================================

// Schemas settings page. Two states:
//   list  — flat-card list of saved schemas with action menu
//   form  — name + JSON editor + auto-from-example, with back arrow to list
// Mirrors the original PDF Microservice Builder's pattern: editing a
// schema replaces the whole panel rather than opening a modal — schemas
// are big enough that a side-by-side modal feels cramped.
function SchemasPage() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list');         // 'list' | 'form'
  const [editingId, setEditingId] = useState(null); // schema id when editing
  const [formName, setFormName] = useState('');
  const [formDraft, setFormDraft] = useState('');
  const [formErr, setFormErr] = useState(null);
  const [showExample, setShowExample] = useState(false);
  const [exampleJson, setExampleJson] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { schemas } = await api.get('/spa/schemas');
      setItems(schemas);
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  // ---- list view helpers ----
  const summarize = (jsonSchema) => {
    if (!jsonSchema || typeof jsonSchema !== 'object') return 'empty';
    const keys = Object.keys(jsonSchema.properties || {});
    if (keys.length === 0) return 'no top-level properties';
    if (keys.length <= 4) return keys.join(', ');
    return `${keys.slice(0, 4).join(', ')}, +${keys.length - 4} more`;
  };
  const remove = async (s) => {
    const ok = await confirm({
      title: 'Delete schema?',
      body: `"${s.name}" will be removed. Templates that reference it will lose the link.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try { await api.del(`/spa/schemas/${s.id}`); load(); }
    catch (err) { toast(err.message, 'error'); }
  };

  // ---- form view ----
  const startAdd = () => {
    setEditingId(null);
    setFormName('');
    setFormDraft('{\n  "type": "object",\n  "properties": {}\n}');
    setFormErr(null);
    setShowExample(false);
    setExampleJson('');
    setMode('form');
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setFormName(s.name);
    setFormDraft(JSON.stringify(s.jsonSchema, null, 2));
    setFormErr(null);
    setShowExample(false);
    setExampleJson('');
    setMode('form');
  };
  const validate = (text) => {
    try { JSON.parse(text); setFormErr(null); return true; }
    catch (e) { setFormErr(e.message); return false; }
  };
  const onDraftChange = (text) => {
    setFormDraft(text);
    validate(text);
  };
  const generateFromExample = () => {
    try {
      const parsed = JSON.parse(exampleJson);
      setFormDraft(JSON.stringify(exampleToSchema(parsed), null, 2));
      setFormErr(null);
      setShowExample(false);
      setExampleJson('');
      toast('Generated schema from example');
    } catch (e) { toast('Example must be valid JSON: ' + e.message, 'error'); }
  };
  const save = async () => {
    if (!formName.trim()) { toast('Name is required', 'error'); return; }
    if (!validate(formDraft)) return;
    setBusy(true);
    try {
      const body = { name: formName.trim(), jsonSchema: JSON.parse(formDraft) };
      if (editingId) await api.patch(`/spa/schemas/${editingId}`, body);
      else await api.post('/spa/schemas', body);
      await load();
      setMode('list');
      toast(editingId ? 'Schema updated' : 'Schema created');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (mode === 'form') {
    return (
      <>
        <SettingsHeader
          title={
            <span style={{display:'inline-flex', alignItems:'center', gap:8}}>
              <span style={{cursor:'pointer', color:'var(--text-muted)'}} onClick={() => setMode('list')}>←</span>
              {editingId ? 'Edit schema' : 'New schema'}
            </span>
          }
        />
        <div className="card">
          <div className="form-row">
            <label>Name</label>
            <input className="input" value={formName}
                   onChange={(e) => setFormName(e.target.value)}
                   placeholder="e.g. Customer payload" />
          </div>

          <div className="form-row">
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4}}>
              <label style={{margin:0}}>JSON schema</label>
              <button type="button" className="btn btn-sm" onClick={() => setShowExample((v) => !v)}>
                {showExample ? 'Cancel' : 'From example'}
              </button>
            </div>
            {showExample && (
              <div style={{marginBottom: 8, padding: 10, background: 'var(--bg-elevated)', borderRadius: 'var(--radius)'}}>
                <textarea className="input"
                          style={{minHeight: 100, fontFamily:'var(--font-mono)', fontSize:12}}
                          placeholder='Paste sample JSON, e.g. {"customer": {"name": "Acme"}}'
                          value={exampleJson}
                          onChange={(e) => setExampleJson(e.target.value)}
                          spellCheck={false} />
                <button type="button" className="btn btn-sm btn-primary" style={{marginTop: 6}}
                        disabled={!exampleJson.trim()}
                        onClick={generateFromExample}>
                  Generate
                </button>
              </div>
            )}
            <textarea className="input"
                      style={{minHeight: 320, fontFamily:'var(--font-mono)', fontSize:12}}
                      value={formDraft}
                      onChange={(e) => onDraftChange(e.target.value)}
                      spellCheck={false} />
            {formErr && <div style={{color:'var(--danger)', fontSize:11, marginTop:4}}>{formErr}</div>}
          </div>

          <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
            <button className="btn" onClick={() => setMode('list')}>{t('common.cancel') || 'Cancel'}</button>
            <button className="btn btn-primary" onClick={save} disabled={busy || !!formErr}>
              {editingId ? 'Save changes' : 'Create schema'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SettingsHeader
        title="Schemas"
        subtitle="Reusable JSON schemas your templates can reference."
        action={<button className="btn btn-primary" onClick={startAdd}>+ New schema</button>}
      />
      {loading ? (
        <div className="settings-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="settings-empty">
          <p>No schemas yet.</p>
          <button className="btn" onClick={startAdd}>+ New schema</button>
        </div>
      ) : items.map((s) => (
        <div key={s.id} className="settings-card">
          <div className="settings-card-info">
            <div className="settings-card-title-row">
              <div className="settings-card-title">{s.name}</div>
            </div>
            <div className="settings-card-meta">
              {summarize(s.jsonSchema)} · updated {formatDate(s.updatedAt)}
            </div>
          </div>
          <div className="settings-card-actions">
            <ActionMenu items={[
              { label: t('common.edit') || 'Edit', onClick: () => startEdit(s) },
              { label: t('common.delete') || 'Delete', onClick: () => remove(s), danger: true },
            ]} />
          </div>
        </div>
      ))}
    </>
  );
}

// ============================================================================
// API keys page
// ============================================================================

function ApiKeysPage() {
  const t = useT();
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  // Lifetime preset for new keys. Stored as a string so React can match
  // the <select> value attribute; converted to an ISO date at submit
  // time. "never" sends no expiresAt; the named windows convert via
  // computeExpiresAt below.
  const [newKeyLifetime, setNewKeyLifetime] = useState('never');
  // 'live' | 'test'. Test keys (pk_test_) are the sandbox: watermarked
  // output, no quota, no webhooks/emails.
  const [newKeyMode, setNewKeyMode] = useState('live');
  // Workspace grants for the new key. An API key reaches only the
  // workspaces in this list; we require at least one before allowing
  // submit so a fresh key isn't immediately useless. Auto-seeded
  // below with a sensible default so the user never has to remember
  // to pick one before submit.
  const [newKeyWorkspaceIds, setNewKeyWorkspaceIds] = useState([]);
  const [createdKey, setCreatedKey] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();
  const { workspaces, current: currentWs } = useWorkspace();

  const load = async () => {
    setLoading(true);
    try {
      const { keys } = await api.get('/spa/api-keys');
      setItems(keys);
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Pre-fill the workspace selection so the form always lands with at
  // least one chip already toggled on. Two fallbacks because the
  // current-workspace context loads asynchronously:
  //   1. currentWs.id if available — matches the workspace the user
  //      is sitting in, which is what they almost always want.
  //   2. workspaces[0].id otherwise — covers admins/owners who don't
  //      have a current workspace pinned but still have access.
  // Only runs while the list is empty so a user who explicitly
  // deselected can leave it empty (the submit button stays disabled
  // until they pick again; we don't want to fight their input).
  useEffect(() => {
    if (newKeyWorkspaceIds.length > 0) return;
    const seed = currentWs?.id || workspaces[0]?.id;
    if (seed) setNewKeyWorkspaceIds([seed]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWs?.id, workspaces.length]);

  const toggleNewKeyWorkspace = (id) => {
    setNewKeyWorkspaceIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };
  const wsName = (id) => workspaces.find((w) => w.id === id)?.name || id;

  // computeExpiresAt converts the lifetime preset into an ISO 8601
  // string the REST API accepts. "never" → null = no expiry. The named
  // windows offset from now and produce an absolute timestamp the
  // server stores verbatim — picking the date at submit time keeps the
  // displayed countdown honest if the user lingers on the form.
  const computeExpiresAt = (preset) => {
    if (preset === 'never') return null;
    const ms = {
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
      '180d': 180 * 24 * 60 * 60 * 1000,
      '365d': 365 * 24 * 60 * 60 * 1000,
    }[preset];
    if (!ms) return null;
    return new Date(Date.now() + ms).toISOString();
  };

  const create = async () => {
    if (!newKeyName.trim()) return;
    if (newKeyWorkspaceIds.length === 0) {
      toast(t('apiKeys.workspacesRequired', 'Pick at least one workspace this key can reach.'), 'error');
      return;
    }
    try {
      const { key } = await api.post('/spa/api-keys', {
        name: newKeyName,
        expiresAt: computeExpiresAt(newKeyLifetime),
        workspaceIds: newKeyWorkspaceIds,
        mode: newKeyMode,
      });
      setCreatedKey(key.plaintext);
      setNewKeyName('');
      setNewKeyLifetime('never');
      setNewKeyMode('live');
      // Reset to "current workspace only" so the next creation isn't
      // pre-filled with whatever the previous one ended at.
      setNewKeyWorkspaceIds(currentWs?.id ? [currentWs.id] : []);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  // Workspace grants are write-once: set at creation, never changed
  // after. If the operator needs a key with different access, the
  // flow is "revoke + re-issue" — that keeps the audit trail clean
  // (a key's blast radius matches what it was issued for, not what
  // it was last edited to) and avoids the "we changed the key's
  // grants but the cached integrations didn't notice" class of bug.
  // The server still exposes a PATCH /:id endpoint for tooling that
  // genuinely needs it; the SPA just doesn't call it.

  const revoke = async (id) => {
    const ok = await confirm({
      title: t('apiKeys.revokeConfirm.title'),
      body: 'Existing clients using this key will stop working immediately.',
      confirmLabel: t('apiKeys.revoke'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/api-keys/${id}`);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  // Hard delete — only offered once a key is revoked. The confirm copy
  // calls out that it's removing the row from the list, NOT changing
  // its auth status (already revoked = already useless to anyone
  // holding the plaintext).
  const hardDelete = async (k) => {
    const ok = await confirm({
      title: t('apiKeys.deleteConfirm.title'),
      body: t('apiKeys.deleteConfirm.body'),
      confirmLabel: t('apiKeys.delete'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/api-keys/${k.id}/permanently`);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  // expiresAtCell formats the expiry column. Three visible states:
  //   never  → em-dash
  //   future → localised date (the column is daily granular, fine for a
  //            lifetime tracker)
  //   past   → "expired" badge, same styling as revoked
  // The server enforces the same expiry; the UI is showing what's
  // already true.
  const expiresAtCell = (k) => {
    if (!k.expiresAt) return <span style={{color:'var(--text-muted)'}}>—</span>;
    const d = new Date(k.expiresAt);
    if (d.getTime() <= Date.now()) {
      return <span className="badge badge-free">{t('apiKeys.expired')}</span>;
    }
    return formatDate(d);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('apiKeys.title')}</div>
          <div className="page-subtitle">{t('apiKeys.subtitle')}</div>
        </div>
      </div>

      {/* Settings → API keys. Say who it is for, so non-developers know
          they can ignore it, and point developers at the docs and
          webhooks. */}
      <div className="dev-intro">
        <div>
          <div className="dev-intro-title">{t('developers.introTitle', 'Automate PDFs from your own software')}</div>
          <div className="dev-intro-body">
            {t('developers.introBody', 'An API key lets your own software or an automation tool create PDFs and send signature requests without opening Pluma. Not a developer? You don’t need this — use “Fill out” on any template.')}
          </div>
        </div>
        <div className="dev-intro-links">
          <a className="btn btn-sm" href="/docs" target="_blank" rel="noopener">{t('developers.docs', 'API docs')} ↗</a>
          <button type="button" className="btn btn-sm"
                  onClick={() => window.dispatchEvent(new CustomEvent('pluma:navigate', { detail: { to: 'settings', section: 'webhooks' } }))}>
            {t('settings.nav.webhooks', 'Webhooks')}
          </button>
        </div>
      </div>

      <div className="card" style={{marginBottom: 24}}>
        <div className="card-title">{t('apiKeys.createTitle')}</div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom: 12}}>
          <input className="input" style={{flex:'1 1 240px'}} placeholder={t('apiKeys.namePlaceholder', 'Key name, e.g. Production server')} aria-label={t('apiKeys.namePlaceholder', 'Key name, e.g. Production server')} value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
          <select className="input" style={{flex:'0 0 200px'}} value={newKeyLifetime} onChange={(e) => setNewKeyLifetime(e.target.value)}>
            <option value="never">{t('apiKeys.lifetime.never')}</option>
            <option value="30d">{t('apiKeys.lifetime.30d')}</option>
            <option value="90d">{t('apiKeys.lifetime.90d')}</option>
            <option value="180d">{t('apiKeys.lifetime.180d')}</option>
            <option value="365d">{t('apiKeys.lifetime.365d')}</option>
          </select>
          {/* Sandbox toggle — pk_test_ keys render watermarked, unbilled
              output and never send emails/webhooks. */}
          <select className="input" style={{flex:'0 0 150px'}} value={newKeyMode} onChange={(e) => setNewKeyMode(e.target.value)}>
            <option value="live">{t('apiKeys.mode.live', 'Live')}</option>
            <option value="test">{t('apiKeys.mode.test', 'Test (sandbox)')}</option>
          </select>
          <button className="btn btn-primary" onClick={create}
                  disabled={!newKeyName.trim() || newKeyWorkspaceIds.length === 0}>
            {t('apiKeys.generate')}
          </button>
        </div>

        {/* Workspace multi-select. Per-workspace ACL is mandatory — a
            key must reach at least one workspace before submit is
            enabled. Rendered as a wrapped row of toggle chips so the
            UI scales to dozens of workspaces without growing tall. */}
        <div>
          <label className="label" style={{marginBottom: 6}}>
            {t('apiKeys.workspacesLabel', 'Allowed workspaces')}
            <span style={{color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, textTransform: 'none', letterSpacing: 0}}>
              {t('apiKeys.workspacesHint', 'The key can only reach the workspaces selected here.')}
            </span>
          </label>
          {workspaces.length === 0 ? (
            <div style={{fontSize: 12, color: 'var(--text-muted)'}}>
              {t('apiKeys.noWorkspaces', 'No workspaces yet. Create one first.')}
            </div>
          ) : (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
              {workspaces.map((w) => {
                const on = newKeyWorkspaceIds.includes(w.id);
                return (
                  <button key={w.id} type="button"
                          className={'btn btn-sm' + (on ? ' btn-primary' : '')}
                          onClick={() => toggleNewKeyWorkspace(w.id)}>
                    {on && <Icons.Check />} {w.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {createdKey && (
          <>
            <div style={{marginTop:16, fontSize:13, color:'var(--warning)'}}>{t('apiKeys.copySecret')}</div>
            <div className="key-display">{createdKey}</div>
            <button className="btn" onClick={() => { navigator.clipboard.writeText(createdKey); toast('Copied'); }}>Copy</button>
            <button className="btn" style={{marginLeft:8}} onClick={() => setCreatedKey(null)}>{t('apiKeys.dismiss')}</button>
          </>
        )}
      </div>

      {loading ? <div className="empty">Loading…</div> : items.length === 0 ? (
        <div className="empty">{t('apiKeys.empty')}</div>
      ) : isMobile ? (
        // The desktop table has 7 columns — hopeless inside a 320-px
        // viewport. On mobile we render one card per key with the
        // name + revoke action on the head, the prefix and workspace
        // chips below, and a wrap-friendly meta row for last-used /
        // created / expires. Action remains a single primary tap
        // target at the bottom.
        <div className="api-key-card-list">
          {items.map((k) => {
            const grants = k.workspaceIds || [];
            return (
              <div key={k.id} className="api-key-card">
                <div className="api-key-card-head">
                  <div className="api-key-card-name">{k.name}</div>
                  {k.revokedAt
                    ? <span className="badge badge-free">{t('apiKeys.revoked')}</span>
                    : <code className="api-key-card-prefix">{k.prefix}…</code>}
                </div>
                {grants.length === 0 ? (
                  <div style={{fontSize: 12, color: 'var(--danger)'}}>{t('apiKeys.noAccess', 'No access')}</div>
                ) : (
                  <div className="api-key-card-grants">
                    {grants.map((id) => (
                      <span key={id} className="badge badge-free" style={{fontSize: 11}}>{wsName(id)}</span>
                    ))}
                  </div>
                )}
                <div className="api-key-card-meta">
                  <span>{t('apiKeys.colLastUsed')}: {k.lastUsedAt ? formatDate(k.lastUsedAt) : t('apiKeys.never')}</span>
                  <span>{t('apiKeys.colCreated')}: {formatDate(k.createdAt)}</span>
                  <span>{t('apiKeys.colExpires')}: {expiresAtCell(k)}</span>
                </div>
                {k.revokedAt
                  ? <button className="btn btn-danger" onClick={() => hardDelete(k)}>{t('apiKeys.delete')}</button>
                  : <button className="btn btn-danger" onClick={() => revoke(k.id)}>{t('apiKeys.revoke')}</button>}
              </div>
            );
          })}
        </div>
      ) : (
        <table className="table">
          <thead><tr>
            <th>{t('apiKeys.colName')}</th>
            <th>{t('apiKeys.colPrefix')}</th>
            <th>{t('apiKeys.colWorkspaces', 'Workspaces')}</th>
            <th>{t('apiKeys.colLastUsed')}</th>
            <th>{t('apiKeys.colCreated')}</th>
            <th>{t('apiKeys.colExpires')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {items.map((k) => {
              const grants = k.workspaceIds || [];
              return (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td><code style={{fontSize:11}}>{k.prefix}…</code></td>
                  <td style={{maxWidth: 280}}>
                    {grants.length === 0 ? (
                      <span style={{color: 'var(--danger)', fontSize: 12}}>
                        {t('apiKeys.noAccess', 'No access')}
                      </span>
                    ) : (
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                        {grants.map((id) => (
                          <span key={id} className="badge badge-free" style={{fontSize: 11}}>{wsName(id)}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : t('apiKeys.never')}</td>
                  <td>{formatDate(k.createdAt)}</td>
                  <td>{expiresAtCell(k)}</td>
                  <td>
                    {k.revokedAt ? (
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        <span className="badge badge-free">{t('apiKeys.revoked')}</span>
                        <button className="btn btn-danger" onClick={() => hardDelete(k)}>{t('apiKeys.delete')}</button>
                      </div>
                    ) : (
                      <button className="btn btn-danger" onClick={() => revoke(k.id)}>{t('apiKeys.revoke')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

// ============================================================================
// Jobs page — bulk render job dashboard with auto-refresh + manual submit
// ============================================================================

function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const { current: ws } = useWorkspace();
  const toast = useToast();

  const load = useCallback(async () => {
    if (!ws) return;
    try {
      // v1 list envelope: { data, meta }. The page only needs the rows.
      const res = await api.get(`/api/v1/${ws.slug}/jobs`);
      setJobs(res.jobs || []);
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [toast, ws]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any job is still working. Stops when everything is
  // terminal (completed/failed) so we don't hammer the API on idle dashboards.
  useEffect(() => {
    const active = jobs.some((j) => j.status === 'pending' || j.status === 'running');
    if (!active) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [jobs, load]);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Bulk jobs</div>
          <div className="page-subtitle">Async render jobs — submit a batch and download the resulting zip when done.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New job</button>
      </div>

      {loading ? <div className="empty">Loading…</div> : jobs.length === 0 ? (
        <div className="empty">No jobs yet. Submit a batch to see it here.</div>
      ) : (
        <table className="table">
          <thead><tr><th>Template</th><th>Status</th><th>Progress</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {jobs.map((j) => <JobRow key={j.id} job={j} onChange={load} />)}
          </tbody>
        </table>
      )}

      {showNew && <NewJobModal onClose={() => setShowNew(false)} onSubmitted={() => { setShowNew(false); load(); }} />}
    </>
  );
}

function JobRow({ job, onChange }) {
  const t = useT();
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const { current: ws } = useWorkspace();
  const toast = useToast();

  const fetchDownload = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      const detail = await api.get(`/api/v1/${ws.slug}/jobs/${job.id}`);
      if (detail.downloadUrl) {
        setDownloadUrl(detail.downloadUrl);
        window.open(detail.downloadUrl, '_blank');
      } else {
        toast('No download available', 'error');
      }
    } catch (err) { toast(err.message, 'error'); }
    finally { setBusy(false); }
  };

  const pct = job.itemsTotal > 0 ? Math.round((job.itemsDone / job.itemsTotal) * 100) : 0;
  return (
    <tr>
      <td>{job.templateName}</td>
      <td>
        <span className={`status-dot status-${job.status}`} />
        {t(`renders.status.${job.status}`, job.status)}
        {job.error && <div style={{fontSize:11, color:'var(--danger)', marginTop:2}}>{job.error}</div>}
      </td>
      <td>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <div className="progress-bar"><div className="progress-bar-fill" style={{width: `${pct}%`}} /></div>
          <span style={{fontSize:11, color:'var(--text-muted)'}}>{job.itemsDone} / {job.itemsTotal}</span>
        </div>
      </td>
      <td style={{fontSize:12, color:'var(--text-secondary)'}}>{formatDateTime(job.createdAt)}</td>
      <td>
        {job.status === 'completed' && (
          <button className="btn btn-primary" onClick={fetchDownload} disabled={busy}>Download zip</button>
        )}
      </td>
    </tr>
  );
}


function NewJobModal({ onClose, onSubmitted }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  // 'json' = array of { data } objects; 'csv' = flat rows with
  // dot-notation headers expanded into nested data. Switching format
  // swaps the sample only while the box is still untouched/sample text.
  const [format, setFormat] = useState('json');
  const [itemsText, setItemsText] = useState(JSON_SAMPLE);
  // `pristine` = the textarea still holds an auto-generated sample the
  // user hasn't touched. While pristine, selecting a template or flipping
  // the format regenerates the sample from that template's schema (the
  // same shape as the editor's "Sample request body"). The first manual
  // edit / file load flips it false so we never stomp real input.
  const [pristine, setPristine] = useState(true);
  const [savedById, setSavedById] = useState({});
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [err, setErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const { current: ws } = useWorkspace();
  const toast = useToast();
  const t = useT();

  const currentTemplate = templates.find((tt) => tt.id === templateId) || null;

  // Parse the textarea according to the active format into the canonical
  // [{ data, filename? }] item array. Throws with a friendly message.
  const parseItems = (text, fmt) => {
    if (fmt === 'csv') {
      const items = csvToJobItems(text);
      if (!items.length) throw new Error('CSV has a header but no data rows');
      return items;
    }
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) throw new Error('JSON must be an array of items');
    return parsed;
  };

  const switchFormat = (next) => {
    if (next === format) return;
    setFormat(next);
    setErr(null);
    // Regeneration is handled by the sample effect when pristine; an
    // edited body is left as-is so format-flipping never destroys input.
  };

  // Shared file-loader for the Upload button + drop-on-textarea. Picks
  // the format from the file extension, loads the text into the box, and
  // validates so the user sees the count (or a parse error) before
  // submitting. Always populates the textarea so malformed input can be
  // fixed in place rather than re-picking the file.
  const loadFile = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr('File too large (8 MB max)'); return; }
    const isCsv = /\.csv$/i.test(file.name || '') || file.type === 'text/csv';
    const fmt = isCsv ? 'csv' : 'json';
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setPristine(false);
      setFormat(fmt);
      setItemsText(text);
      try {
        const items = parseItems(text, fmt);
        setErr(null);
        toast(`Loaded ${items.length} item${items.length === 1 ? '' : 's'} from ${file.name}`, 'success');
      } catch (parseErr) {
        setErr('File loaded but does not parse: ' + parseErr.message);
      }
    };
    reader.onerror = () => setErr('Could not read file');
    reader.readAsText(file);
  };

  useEffect(() => {
    if (!ws) return;
    // Templates carry their inline jsonSchema / schemaId / fields; saved
    // schemas are fetched alongside so a template linked by schemaId can
    // still produce a sample. Both failures are non-fatal for sample gen.
    Promise.all([
      fetchAllTemplates(ws.slug),
      api.get('/spa/schemas').catch(() => ({ schemas: [] })),
    ]).then(([res, sres]) => {
      const list = res.templates || [];
      setTemplates(list);
      const map = {};
      for (const s of (sres.schemas || [])) if (s.id && s.jsonSchema) map[s.id] = s.jsonSchema;
      setSavedById(map);
      if (list.length && !templateId) setTemplateId(list[0].id);
    }).catch((e) => toast(e.message, 'error'));
  }, [ws?.slug]); // eslint-disable-line

  // Regenerate the sample body whenever the selected template or format
  // changes — but only while the box is pristine (untouched sample).
  useEffect(() => {
    if (!pristine) return;
    const schema = schemaForTemplate(currentTemplate, savedById);
    setItemsText(jobSampleText(format, schema));
  }, [templateId, format, pristine, savedById, templates]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!templateId) return toast('Pick a template', 'error');
    let items;
    try {
      items = parseItems(itemsText, format);
    } catch (e) {
      setErr(e.message);
      return;
    }
    setBusy(true);
    try {
      // Workspace-scoped async job creation. CSV is expanded to the same
      // { data } item shape client-side, so the endpoint is unchanged.
      await api.post(`/api/v1/${ws.slug}/jobs`, { templateId, items });
      onSubmitted();
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{maxWidth: 560}} onClick={(e) => e.stopPropagation()}>
        <h2>New bulk job</h2>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label">Template</label>
            <select className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4}}>
              <label className="label" style={{margin: 0}}>
                {format === 'csv'
                  ? 'Rows (CSV — dot-notation headers nest into data)'
                  : 'Items (JSON array of { "data": {...} })'}
              </label>
              <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                {/* Format toggle. */}
                <div className="interval-toggle">
                  <button type="button" className={format === 'json' ? 'active' : ''} onClick={() => switchFormat('json')}>JSON</button>
                  <button type="button" className={format === 'csv' ? 'active' : ''} onClick={() => switchFormat('csv')}>CSV</button>
                </div>
                {/* File upload — .json or .csv. The format is inferred
                    from the extension; size capped at 8 MB. */}
                <label className="btn btn-sm" style={{cursor: 'pointer', margin: 0}}>
                  Upload…
                  <input type="file" accept="application/json,.json,text/csv,.csv,text/plain,.txt"
                         style={{display: 'none'}}
                         onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </div>
            </div>
            <textarea className={'input' + (dragOver ? ' is-dragover' : '')}
                      style={{minHeight: 200, fontFamily: 'var(--font-mono)', fontSize: 12}}
                      value={itemsText}
                      onChange={(e) => { setItemsText(e.target.value); setPristine(false); setErr(null); }}
                      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault(); setDragOver(false);
                        loadFile(e.dataTransfer.files?.[0]);
                      }}
                      spellCheck={false} />
            {err && <div style={{color:'var(--danger)', fontSize:11, marginTop:4}}>{err}</div>}
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              {format === 'csv'
                ? <>First row = headers. Use <code>obj.key</code> to nest. A <code>filename</code> column names each PDF. Drop a <code>.csv</code> file here.</>
                : <>Or drop a <code>.json</code> file onto the box.</>}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.submitting', 'Submitting…') : 'Submit'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Single-PDF generator. Same template-aware sample + JSON/CSV input as the
// bulk modal, but renders ONE PDF via /templates/:id/generate and adds it
// to the renders list. JSON is a single { data: {...} } object; CSV is a
// header + one data row (extra rows are ignored — that's what bulk is for).
// ============================================================================
// Fill-in form — the no-code way to produce a PDF from a template.
// ============================================================================
// Builds a regular form from the template's fields (one input per data
// key; see fillFormFromFields in src/shared/data-shape.js) and posts the
// values to the same /generate endpoint the API uses. Used by the
// editor's "Fill out" button and the Documents page's "Create PDF".

const FILL_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

// dd.mm.yyyy / yyyy-mm-dd → yyyy-mm-dd for <input type="date">; anything
// else is returned untouched (rendered in a text input instead).
function toIsoDate(v) {
  const s = String(v || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return s;
}

function FillFieldInput({ ff, value, onChange, onError, t }) {
  const id = 'fill-' + ff.path.replace(/[^a-zA-Z0-9_-]/g, '_');
  const label = (
    <label className="label" htmlFor={id}>
      {ff.label}{ff.required && <span className="fill-required" aria-hidden="true"> *</span>}
    </label>
  );
  switch (ff.kind) {
    case 'checkbox':
      return (
        <label className="fill-check" htmlFor={id}>
          <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          <span>{ff.label}</span>
        </label>
      );
    case 'radio':
      return (
        <fieldset className="fill-radio-group">
          <legend className="label">{ff.label}{ff.required && <span className="fill-required" aria-hidden="true"> *</span>}</legend>
          {(ff.options || []).map((o) => (
            <label key={o.value} className="fill-check">
              <input type="radio" name={id} value={o.value} required={ff.required}
                     checked={String(value) === o.value} onChange={() => onChange(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
          {!ff.required && value !== '' && value != null && (
            <button type="button" className="btn-link fill-clear" onClick={() => onChange('')}>
              {t('fill.clearChoice', 'Clear choice')}
            </button>
          )}
        </fieldset>
      );
    case 'select':
      return (
        <div>
          {label}
          <select id={id} className="select" value={value || ''} required={ff.required}
                  onChange={(e) => onChange(e.target.value)}>
            <option value="">{t('fill.choose', 'Choose…')}</option>
            {(ff.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      );
    case 'multiline':
      return (
        <div>
          {label}
          <textarea id={id} className="input" rows={3} value={value || ''} required={ff.required}
                    onChange={(e) => onChange(e.target.value)} />
        </div>
      );
    case 'number':
    case 'currency':
      return (
        <div>
          {label}
          <div className={ff.currencyCode ? 'fill-affix' : ''}>
            {ff.currencyCode && <span className="fill-affix-text">{ff.currencyCode}</span>}
            {/* A number input can only show a complete number; an amount
                still being typed on the page ("12.", "1,234") waits. */}
            <input id={id} className="input" type="number" inputMode="decimal"
                   step={ff.kind === 'currency' ? '0.01' : 'any'}
                   value={/^-?\d+(\.\d+)?$/.test(String(value == null ? '' : value)) ? value : ''} required={ff.required}
                   onChange={(e) => onChange(e.target.value)} />
          </div>
        </div>
      );
    case 'date': {
      const iso = toIsoDate(value);
      const isIso = !iso || /^\d{4}-\d{2}-\d{2}$/.test(iso);
      return (
        <div>
          {label}
          <input id={id} className="input" type={isIso ? 'date' : 'text'} value={iso || ''}
                 required={ff.required} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
    }
    case 'image': {
      const isData = typeof value === 'string' && value.startsWith('data:');
      const onFile = (file) => {
        if (!file) return;
        if (file.size > FILL_IMAGE_MAX_BYTES) {
          onError(t('fill.imageTooLarge', 'That image is larger than 5 MB.'));
          return;
        }
        const reader = new FileReader();
        reader.onload = () => onChange(String(reader.result || ''));
        reader.readAsDataURL(file);
      };
      return (
        <div>
          {label}
          {isData ? (
            <div className="fill-image">
              <img src={value} alt="" />
              <button type="button" className="btn btn-sm" onClick={() => onChange('')}>
                {t('fill.removeImage', 'Remove')}
              </button>
            </div>
          ) : (
            // The URL box stays mounted while typing (it used to be swapped
            // for a preview after the first character, stealing focus).
            <div className="fill-image-pick">
              <label className="btn btn-sm fill-file">
                {t('fill.chooseImage', 'Choose image…')}
                {/* PNG/JPEG/GIF only — the renderer can't embed WebP. */}
                <input id={id} type="file" accept="image/png,image/jpeg,image/gif"
                       onChange={(e) => { onFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
              </label>
              <span className="fill-or">{t('fill.or', 'or')}</span>
              <input className="input" type="url" placeholder={t('fill.imageUrl', 'Paste an image URL')}
                     value={value || ''} onChange={(e) => onChange(e.target.value)} />
            </div>
          )}
        </div>
      );
    }
    default: // text, barcode
      return (
        <div>
          {label}
          <input id={id} className="input" value={value || ''} required={ff.required}
                 onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Fill out — type straight onto the PDF and see the result as you go.
// ---------------------------------------------------------------------------
// Full-screen view laid out like the template builder. The page is drawn
// with pdf.js and every data field becomes an input sitting where the
// renderer will print its value, in the field's font, size, colour and
// alignment, with number / currency / date formatting applied once you
// leave the field — so what's on screen is what the PDF will contain. The
// Fields panel lists the same inputs (screen readers, very long
// templates). On phones a tap opens the field in a bottom sheet.

// Kinds edited as text (on phones: in the bottom sheet).
const FILL_TEXT_KINDS = new Set(['text', 'multiline', 'number', 'currency', 'date', 'select', 'barcode']);
// Whether a typed comma is the decimal separator for a number / currency
// field: it is in the account's EU formats (Settings → Template defaults).
function fillDecimalComma(kind, defaults) {
  const d = defaults || {};
  return kind === 'currency' ? String(d.currencyFormat || '').endsWith('-eu') : String(d.numberFormat || '').startsWith('eu');
}
// A typed character a number / currency box refuses (a letter and the
// like) is blocked before it lands, so the caret doesn't jump. Pastes and
// other multi-character insertions go through and are cleaned up by
// numberInputChars ("€ 1.234,56" → "1.234,56").
function blockNonNumeric(e) {
  if (e.data && e.data.length === 1 && /[^\d.,-]/.test(e.data)) e.preventDefault();
}
// fpdf's default cell margin (1 mm): PLUMAv1 insets text by it on both sides.
const PLUMA_CELL_MARGIN_PT = 2.835;

// The template's PDF as a pdf.js document — reused when the caller already
// has it open (the editor), loaded (and destroyed afterwards) otherwise.
function useTemplatePdf(template, wsSlug, pdfDocProp) {
  const [state, setState] = useState({ pdf: pdfDocProp || null, error: null });
  const templateId = template && template.id;
  useEffect(() => {
    if (pdfDocProp) { setState({ pdf: pdfDocProp, error: null }); return undefined; }
    if (!templateId || !wsSlug) return undefined;
    let cancelled = false;
    let doc = null;
    setState({ pdf: null, error: null });
    (async () => {
      try {
        const { url } = await api.get(`/spa/${wsSlug}/templates/${templateId}/pdf-url`);
        const blob = await fetchPdfBlobWithRetry(url);
        const data = new Uint8Array(await blob.arrayBuffer());
        // isEvalSupported:false — see the editor's loader (CVE-2024-4367).
        const loaded = await pdfjsLib.getDocument({ data, isEvalSupported: false }).promise;
        if (cancelled) { loaded.destroy(); return; }
        doc = loaded;
        setState({ pdf: loaded, error: null });
      } catch (err) {
        if (!cancelled) setState({ pdf: null, error: err.message || String(err) });
      }
    })();
    return () => {
      cancelled = true;
      if (doc) { try { doc.destroy(); } catch (_) { /* already gone */ } }
    };
  }, [templateId, wsSlug, pdfDocProp]);
  return state;
}

// Static images on the template ("fieldimage:<id>" from the image library)
// → displayable URLs, fetched once per id.
function useFieldImageResolver(fields) {
  const [urls, setUrls] = useState({});
  const ids = useMemo(() => [...new Set((fields || [])
    .filter((f) => f.type === 'image' && typeof f.imageUrl === 'string' && f.imageUrl.startsWith('fieldimage:'))
    .map((f) => f.imageUrl.slice('fieldimage:'.length)))], [fields]);
  const key = ids.join('|');
  useEffect(() => {
    if (!ids.length) return undefined;
    let cancelled = false;
    Promise.all(ids.map((id) => api.get(`/spa/field-images/${id}/url`)
      .then((r) => [id, r.url || ''])
      .catch(() => [id, ''])))
      .then((entries) => { if (!cancelled) setUrls(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return useCallback((url) => {
    if (!url) return '';
    if (url.startsWith('fieldimage:')) return urls[url.slice('fieldimage:'.length)] || '';
    return url;
  }, [urls]);
}

// The saved signatures the template's "user" signature fields point to —
// mine or a colleague's — resolved for everyone in the org (same rule as
// the renderer, see src/signatures/library.js).
function useMySignatures(fields) {
  const [byId, setById] = useState({});
  const ids = (fields || [])
    .filter((f) => f && f.type === 'signature' && f.signatureKind === 'user' && f.signatureId)
    .map((f) => f.signatureId);
  const key = [...new Set(ids)].sort().join(',');
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    api.get(`/spa/signature-refs?ids=${encodeURIComponent(key)}`).then((r) => {
      if (cancelled) return;
      setById(r.signatures || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [key]);
  return byId;
}

// Barcode / QR preview. The image is the renderer's own PNG for the value
// (POST /spa/barcode-preview → the encoder that draws the PDF), so what
// the fill view shows is what prints. Requests are debounced while
// typing, the last image stays up until the next one arrives, and results
// are cached per kind + box size + value for the session.
const BARCODE_PREVIEW_CACHE = new Map(); // key → { url } | { invalid: true }
function rememberBarcodePreview(key, entry) {
  BARCODE_PREVIEW_CACHE.set(key, entry);
  if (BARCODE_PREVIEW_CACHE.size > 300) {
    const [oldKey, old] = BARCODE_PREVIEW_CACHE.entries().next().value;
    BARCODE_PREVIEW_CACHE.delete(oldKey);
    if (old && old.url) URL.revokeObjectURL(old.url);
  }
}
function useBarcodePreview(kind, value, wPt, hPt) {
  const w = Math.max(1, Math.round(wPt));
  const h = Math.max(1, Math.round(hPt));
  const key = `${kind}|${w}x${h}|${value}`;
  const [preview, setPreview] = useState(() => (value ? BARCODE_PREVIEW_CACHE.get(key) || null : null));
  useEffect(() => {
    if (!value) { setPreview(null); return undefined; }
    const hit = BARCODE_PREVIEW_CACHE.get(key);
    if (hit) { setPreview(hit); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/spa/barcode-preview', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...tenantHeader() },
          body: JSON.stringify({ kind, value, width: w, height: h }),
        });
        if (!res.ok) return; // renderer unreachable: keep what's shown
        // A PNG, or { valid: false } when the value can't be encoded.
        const entry = /image\//.test(res.headers.get('content-type') || '')
          ? { url: URL.createObjectURL(await res.blob()) }
          : { invalid: true };
        rememberBarcodePreview(key, entry);
        if (!cancelled) setPreview(entry);
      } catch (_) { /* offline: keep what's shown */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return preview;
}

const BARCODE_KIND_NAMES = {
  aztec: 'Aztec', datamatrix: 'Data Matrix', pdf417: 'PDF417', code128: 'Code 128', code39: 'Code 39',
  ean13: 'EAN-13', ean8: 'EAN-8', upca: 'UPC-A', itf: 'ITF',
};

// A barcode field in the fill view. The box shows the code as it will
// print — stretched to the box, as the PDF does. Editing happens in a
// small input under the box (above it near the page's bottom edge) while
// the field has focus; the code redraws as you type. On phones a tap opens
// the bottom sheet instead. editable=false: a code from a default value.
function FillBarcode({ f, path, label, raw, printed, setValue, geo, cls, editable, isMobile, onTap, pageHeight, t }) {
  const inputRef = useRef(null);
  const kind = f.barcodeKind || 'qrcode';
  const s = geo.fontPt ? geo.fs / geo.fontPt : 1;
  const preview = useBarcodePreview(kind, printed, geo.w / s, geo.h / s);
  const kindName = kind === 'qrcode' ? t('properties.barcodeType.qrcode', 'QR code') : (BARCODE_KIND_NAMES[kind] || kind);
  const unencodable = !!(printed && preview && preview.invalid);
  const rule = ({
    ean13: t('fill.barcodeRule.ean13', 'EAN-13 takes 12 or 13 digits.'),
    ean8: t('fill.barcodeRule.ean8', 'EAN-8 takes 7 or 8 digits.'),
    upca: t('fill.barcodeRule.upca', 'UPC-A takes 11 or 12 digits.'),
    itf: t('fill.barcodeRule.itf', 'ITF takes an even number of digits.'),
    code39: t('fill.barcodeRule.code39', 'Code 39 takes A–Z, 0–9, spaces and - . $ / + %.'),
    code128: t('fill.barcodeRule.code128', 'Code 128 takes letters, digits and common symbols.'),
  })[kind] || t('fill.barcodeRule.long', 'The value is too long or has characters this code can’t hold.');
  const problem = unencodable
    ? `${t('fill.barcodeInvalid', 'Not a valid {{kind}} — it won’t print.', { kind: kindName })} ${rule}`
    : '';
  const box = { left: geo.x, top: geo.y, width: geo.w, height: geo.h };
  const content = !printed
    ? <span className="fv-code-hint">{label || kindName}</span>
    : unencodable
      ? <span className="fv-code-error">{problem}</span>
      : preview && preview.url
        ? <img src={preview.url} alt="" />
        // First preview still loading, or the renderer unreachable: the value.
        : <span className="fv-code-hint">{printed}</span>;
  const classes = cls.concat('fv-code', unencodable ? 'is-unencodable' : '').join(' ');

  if (!editable) {
    return <div className={'fv-static fv-code' + (unencodable ? ' is-unencodable' : '')} style={box}>{content}</div>;
  }
  if (isMobile) {
    return (
      <button type="button" className={classes} data-fill-path={path} style={box}
              aria-label={`${label}${printed ? `: ${printed}` : ''}`} title={problem || undefined}
              onClick={() => onTap(path)}>
        {content}
      </button>
    );
  }
  const above = pageHeight && geo.y + geo.h + 44 > pageHeight;
  return (
    <div className={classes} style={box} title={problem || undefined}
         onMouseDown={(e) => {
           if (e.target === inputRef.current) return;
           e.preventDefault();
           if (inputRef.current) inputRef.current.focus();
         }}>
      {content}
      <input ref={inputRef} className={'fv-code-input' + (above ? ' is-above' : '')}
             data-fill-path={path} aria-label={`${label} (${kindName})`} aria-invalid={unencodable || undefined}
             type="text" autoComplete="off" spellCheck={false} placeholder={kindName}
             value={raw} onChange={(e) => setValue(path, e.target.value)}
             onKeyDown={(e) => {
               // Enter / Esc leave the field (Esc would otherwise close the view).
               if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); e.currentTarget.blur(); }
             }} />
    </div>
  );
}

// Where a field sits on the page, in px, plus the font size in px. Mirrors
// the renderer: a field without a width / height (placed with one click)
// is a text line that runs to the page edge, a 12 pt box for a
// checkbox / radio, and alignment only applies on an axis with a size.
function fillFieldGeometry(f, s, pageW, pageH, renderer) {
  const kind = f.type || 'text';
  const fontPt = Number(f.fontSize) || (renderer === 'standard' ? 12 : 10);
  const fs = fontPt * s;
  const x = (Number(f.x) || 0) / 100 * pageW * s;
  const y = (Number(f.y) || 0) / 100 * pageH * s;
  const w = (Number(f.width) || 0) / 100 * pageW * s;
  const h = (Number(f.height) || 0) / 100 * pageH * s;
  const hasW = w > 0;
  const hasH = h > 0;
  if (kind === 'checkbox' || kind === 'radio') {
    const side = 12 * s;
    return { x, y, w: hasW && hasH ? w : side, h: hasW && hasH ? h : side, fs, fontPt, hasW: true, hasH: true };
  }
  if (kind === 'image' || kind === 'signature' || kind === 'shape' || kind === 'barcode') {
    return { x, y, w: hasW ? w : pageW * s * 0.2, h: hasH ? h : pageH * s * 0.12, fs, fontPt, hasW: true, hasH: true };
  }
  const toEdge = Math.max(fs * 4, pageW * s - x);
  if (kind === 'multiline') {
    return { x, y, w: hasW ? w : toEdge, h: hasH ? h : 0, fs, fontPt, hasW, hasH };
  }
  // Single line without a height: PLUMAv1 gives it a height of half the
  // font size (baseline 0.55 × size below y); Standard draws the baseline
  // 0.3 × size below y. A one-line input puts its baseline ~0.95 × size
  // below its top.
  const lift = renderer === 'standard' ? 0.65 : 0.4;
  return { x, y: hasH ? y : y - fs * lift, w: hasW ? w : toEdge, h: hasH ? h : fs * 1.2, fs, fontPt, hasW, hasH };
}

function FillFieldWidget({ f, ff, value, setValue, geo, renderer, templateFont, invalid, active, isMobile, onTap,
  defaults, resolveImage, signatures, onError, pageHeight, t }) {
  const [focused, setFocused] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const kind = ff ? ff.kind : null;
  const path = ff ? ff.path : null;
  const label = ff ? ff.label : (f.name || '');
  const required = !!(ff && ff.required);
  const box = { left: geo.x, top: geo.y, width: geo.w, height: geo.h || undefined };
  const fontStyle = {
    fontSize: geo.fs,
    lineHeight: `${geo.fs * 1.2}px`,
    fontFamily: cssFontStackFor(f.font || templateFont || 'helvetica'),
    fontWeight: f.bold ? 700 : 400,
    fontStyle: f.italic ? 'italic' : 'normal',
    color: f.color || '#000000',
    ...textDecorationStyle(f.textDecoration),
  };
  const hAlign = geo.hasW ? fieldTextAlign(f) : 'left';
  const vAlign = geo.hasH ? fieldVerticalAlign(f, renderer) : (f.type === 'multiline' ? 'top' : 'middle');
  const inset = renderer === 'standard' ? 0 : PLUMA_CELL_MARGIN_PT * (geo.fs / geo.fontPt);
  const cls = ['fv'];
  if (invalid) cls.push('is-invalid');
  if (required) cls.push('is-required');
  if (active) cls.push('is-active');

  // Multiline: grow the textarea with its content so top / middle /
  // bottom can place the block inside the box.
  React.useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = '0px';
    ta.style.height = `${Math.max(geo.fs * 1.2, ta.scrollHeight)}px`;
  });

  // ----- Static content (prints, but isn't filled here) -----
  if (f.type === 'shape') {
    const ellipse = f.shapeKind === 'ellipse' || f.shapeKind === 'circle';
    return (
      <div className="fv-static" aria-hidden="true" style={{
        ...box,
        background: f.shapeFillColor || 'transparent',
        border: f.shapeBorderWidth > 0 && f.shapeBorderColor ? `${Math.max(1, f.shapeBorderWidth * (geo.fs / geo.fontPt))}px solid ${f.shapeBorderColor}` : 'none',
        borderRadius: ellipse ? '50%' : 0,
      }} />
    );
  }
  if (f.type === 'signature') {
    const placed = f.signatureKind === 'user' && f.signatureId && signatures[f.signatureId];
    return (
      <div className="fv-static fv-signature" style={box} title={label}>
        {placed && placed.imageUrl
          ? <img src={placed.imageUrl} alt="" />
          : <span>{f.signatureKind === 'user' ? t('fill.signatureMine', 'Saved signature') : t('fill.signatureLater', 'Signed later')}</span>}
      </div>
    );
  }
  if (!ff) {
    // A data field nobody connected to the form, or a static image: show
    // what will print.
    if (f.type === 'image') {
      const src = resolveImage(f.imageUrl);
      return src ? <div className="fv-static" style={box}><img src={src} alt="" style={{ objectFit: f.objectFit === 'contain' ? 'contain' : f.objectFit === 'cover' ? 'cover' : 'fill' }} /></div> : null;
    }
    const printed = previewFieldValue(f, f.default == null || f.default === false ? '' : String(f.default), defaults);
    if (!printed || f.type === 'checkbox' || f.type === 'radio') return null;
    if (f.type === 'barcode' && renderer !== 'standard') {
      return <FillBarcode f={f} label={label} printed={printed} geo={geo} cls={[]} editable={false} t={t} />;
    }
    return <div className="fv-static fv-static-text" style={{ ...box, ...fontStyle, textAlign: hAlign, paddingLeft: inset, paddingRight: inset }}>{printed}</div>;
  }

  // ----- Checkbox / radio: toggled right on the page -----
  if (kind === 'checkbox') {
    const on = !!value;
    const side = Math.min(geo.w, geo.h);
    const mark = f.checkStyle === 'square'
      ? <span className="fv-mark-square" style={{ width: side * 0.55, height: side * 0.55, background: fontStyle.color }} />
      : f.checkStyle === 'circle'
        ? <span className="fv-mark-dot" style={{ width: side * 0.6, height: side * 0.6, background: fontStyle.color }} />
        : <span style={{ fontSize: (Number(f.fontSize) ? geo.fs : Math.max(geo.w, geo.h) * 0.7), fontWeight: 700, color: fontStyle.color, fontFamily: fontStyle.fontFamily, lineHeight: 1 }}>X</span>;
    return (
      <button type="button" role="checkbox" aria-checked={on} aria-label={label} data-fill-path={path}
              className={cls.concat('fv-check').join(' ')} style={box}
              onClick={() => setValue(path, !on)}>
        {on && mark}
      </button>
    );
  }
  if (kind === 'radio') {
    const mine = String(f.radioIndex);
    const on = value !== '' && value != null && String(value) === mine;
    const opt = (ff.options || []).find((o) => o.value === mine);
    const side = Math.min(geo.w, geo.h);
    return (
      <button type="button" role="radio" aria-checked={on} data-fill-path={path}
              aria-label={opt ? `${label}: ${opt.label}` : label}
              className={cls.concat('fv-check', 'fv-radio').join(' ')} style={box}
              onClick={() => setValue(path, on && !required ? '' : mine)}>
        {on && <span className="fv-mark-dot" style={{ width: side * 2 / 3, height: side * 2 / 3, background: fontStyle.color }} />}
      </button>
    );
  }

  // ----- Image: click (or drop a file) to add -----
  if (kind === 'image') {
    const src = (typeof value === 'string' && value) || resolveImage(f.imageUrl);
    const takeFile = (file) => {
      if (!file) return;
      if (!/^image\/(png|jpe?g|gif)$/.test(file.type)) { onError(t('imageLib.unsupported', 'Unsupported file type. Use PNG, JPG or GIF.')); return; }
      if (file.size > FILL_IMAGE_MAX_BYTES) { onError(t('fill.imageTooLarge', 'That image is larger than 5 MB.')); return; }
      const reader = new FileReader();
      reader.onload = () => setValue(path, String(reader.result || ''));
      reader.readAsDataURL(file);
    };
    return (
      <div className={cls.concat('fv-image').join(' ')} style={box}
           onDragOver={(e) => e.preventDefault()}
           onDrop={(e) => { e.preventDefault(); takeFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
        <button type="button" className="fv-image-pick" data-fill-path={path}
                aria-label={`${label}: ${src ? t('fill.changeImage', 'Change image') : t('fill.addImage', 'Add image')}`}
                onClick={() => fileRef.current && fileRef.current.click()}>
          {src
            ? <img src={src} alt="" style={{ objectFit: f.objectFit === 'contain' ? 'contain' : f.objectFit === 'cover' ? 'cover' : 'fill' }} />
            : <span className="fv-image-hint">{t('fill.addImage', 'Add image')}</span>}
        </button>
        {typeof value === 'string' && value && (
          <button type="button" className="fv-image-remove" aria-label={t('fill.removeImage', 'Remove')}
                  onClick={() => setValue(path, '')}><Icon name="close" /></button>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" hidden
               onChange={(e) => { takeFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
      </div>
    );
  }

  // ----- Barcode / QR: the code as it will print -----
  // (The Standard engine prints a barcode field's value as plain text, so
  // there it stays a text input.)
  if (kind === 'barcode' && renderer !== 'standard') {
    const rawCode = value == null ? '' : String(value);
    return <FillBarcode f={f} path={path} label={label} raw={rawCode} printed={previewFieldValue(f, rawCode, defaults)}
                        setValue={setValue} geo={geo} cls={cls} editable isMobile={isMobile} onTap={onTap}
                        pageHeight={pageHeight} t={t} />;
  }

  // ----- Text-like -----
  const raw = value == null ? '' : String(value);
  const shown = focused
    ? (kind === 'date' ? previewFieldValue({ ...f, format: f.format || 'date-ymd' }, raw, defaults) : raw)
    : previewFieldValue(f, raw, defaults);
  // Fields detected from the PDF's own form sit under the PDF's printed
  // labels, so a placeholder would only repeat their (often cryptic) ids.
  const placeholder = f.fieldType === 'form' ? '' : label + (required ? ' *' : '');
  const commonText = {
    'data-fill-path': path,
    'aria-label': label,
    'aria-required': required || undefined,
    'aria-invalid': invalid || undefined,
    placeholder,
  };
  const textCase = f.format === 'uppercase' ? 'uppercase' : f.format === 'lowercase' ? 'lowercase' : undefined;

  if (isMobile) {
    // Phones: the page is too small to type on — tap to open the sheet.
    return (
      <button type="button" className={cls.concat('fv-tap', raw ? '' : 'is-empty').join(' ')} data-fill-path={path}
              aria-label={`${label}${raw ? `: ${shown}` : ''}`}
              style={{ ...box, ...fontStyle, textAlign: hAlign, paddingLeft: inset, paddingRight: inset,
                alignItems: vAlign === 'top' ? 'flex-start' : vAlign === 'bottom' ? 'flex-end' : 'center',
                justifyContent: hAlign === 'center' ? 'center' : hAlign === 'right' ? 'flex-end' : 'flex-start',
                textTransform: textCase, minHeight: geo.h ? undefined : geo.fs * 1.2 }}
              onClick={() => onTap(path)}>
        <span className="fv-tap-text">{raw ? shown : placeholder || '\u00a0'}</span>
      </button>
    );
  }

  if (kind === 'multiline') {
    return (
      <div className={cls.concat('fv-multi').join(' ')}
           style={{ ...box, minHeight: geo.h ? undefined : geo.fs * 1.2 }}
           onMouseDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); taRef.current && taRef.current.focus(); } }}>
        <textarea ref={taRef} rows={1} {...commonText} value={raw}
                  style={{ ...fontStyle, textAlign: hAlign, paddingLeft: inset, paddingRight: inset, textTransform: textCase,
                    marginTop: vAlign === 'top' ? 0 : 'auto', marginBottom: vAlign === 'middle' ? 'auto' : 0 }}
                  onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                  onChange={(e) => setValue(path, e.target.value)} />
      </div>
    );
  }

  if (kind === 'select') {
    return (
      <select className={cls.concat('fv-input').join(' ')} {...commonText}
              style={{ ...box, ...fontStyle, textAlign: hAlign, textAlignLast: hAlign, paddingLeft: inset, paddingRight: inset }}
              value={raw} onChange={(e) => setValue(path, e.target.value)}>
        <option value="">{placeholder || t('fill.choose', 'Choose…')}</option>
        {(ff.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  // Single line. The input fills the box; padding moves the one-line
  // content box to the top or bottom. A box shorter than a line grows
  // around its middle so the text isn't clipped.
  const lineH = geo.fs * 1.2;
  let top = geo.y;
  let height = geo.h;
  let padTop = 0;
  let padBottom = 0;
  if (height >= lineH) {
    if (vAlign === 'top') padBottom = height - lineH;
    else if (vAlign === 'bottom') padTop = height - lineH;
  } else {
    top = vAlign === 'top' ? geo.y : vAlign === 'bottom' ? geo.y + geo.h - lineH : geo.y - (lineH - geo.h) / 2;
    height = lineH;
  }
  const numeric = kind === 'number' || kind === 'currency';
  return (
    <input className={cls.concat('fv-input', kind === 'barcode' ? 'fv-barcode' : '').join(' ')} {...commonText}
           type="text" inputMode={numeric ? 'decimal' : undefined}
           onBeforeInput={numeric ? blockNonNumeric : undefined}
           autoComplete="off" spellCheck={kind === 'text' ? undefined : false}
           style={{ ...box, top, height, ...fontStyle, textAlign: hAlign, textTransform: textCase,
             paddingLeft: inset, paddingRight: inset, paddingTop: padTop, paddingBottom: padBottom }}
           value={shown}
           onFocus={() => setFocused(true)}
           onBlur={() => {
             setFocused(false);
             // Dates are typed the way they print (dd.mm.yyyy); store ISO so
             // schema validation and every date format accept them.
             if (kind === 'date' && raw) setValue(path, toIsoDate(raw));
             // "1.234,5" / "1,234.5" / "1234,5" → "1234.5", so it formats
             // like the PDF and matches what the panel's number input sends.
             if (numeric && raw) {
               const plain = normalizeNumberInput(raw, fillDecimalComma(kind, defaults));
               if (plain !== raw) setValue(path, plain);
             }
           }}
           onChange={(e) => setValue(path, numeric ? numberInputChars(e.target.value) : e.target.value)} />
  );
}

// One template page with its field layer, drawn at `scale` CSS px per PDF
// point — the same 1.5 × zoom the template builder uses, so both views
// match. `clean` drops the field boxes (values stay) for a print preview.
const FillPdfPage = React.memo(function FillPdfPage({ pdf, pageNumber, scale, fields, byPath, values, setValue,
  invalid, activePath, renderer, templateFont, isMobile, onTap, defaults, resolveImage, signatures, onError, clean, t }) {
  const [page, setPage] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let alive = true;
    pdf.getPage(pageNumber).then((p) => { if (alive) setPage(p); }).catch(() => {});
    return () => { alive = false; };
  }, [pdf, pageNumber]);

  useEffect(() => {
    if (!page || !scale || !canvasRef.current) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const viewport = page.getViewport({ scale: scale * dpr });
    const canvas = canvasRef.current;
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    // Widget appearances off: our inputs are the field layer.
    const A = (window.pdfjsLib && window.pdfjsLib.AnnotationMode) || { DISABLE: 0 };
    const task = page.render({ canvasContext: canvas.getContext('2d'), viewport, annotationMode: A.DISABLE });
    task.promise.catch((err) => {
      if (!err || err.name !== 'RenderingCancelledException') console.warn('[fill] page render failed', err);
    });
    return () => task.cancel();
  }, [page, scale]);

  const base = page ? page.getViewport({ scale: 1 }) : null;
  // Until the page is loaded, reserve a Letter-sized sheet.
  const width = (base ? base.width : 612) * scale;
  const height = (base ? base.height : 792) * scale;
  return (
    <div className={'fill-page' + (clean ? ' is-clean' : '')} style={{ width, height }}
         role="group" aria-label={t('fill.pageN', 'Page {{n}}', { n: pageNumber })}>
      <canvas ref={canvasRef} style={{ width, height }} aria-hidden="true" />
      {base && fields.map((f) => {
        const ff = f.jsonPath && byPath.get(f.jsonPath.trim());
        return (
          <FillFieldWidget key={f.id} f={f} ff={ff || null}
                           value={ff ? values[ff.path] : undefined} setValue={setValue}
                           geo={fillFieldGeometry(f, scale, base.width, base.height, renderer)}
                           renderer={renderer} templateFont={templateFont}
                           invalid={!!(ff && invalid.has(ff.path))} active={!!(ff && ff.path === activePath)}
                           isMobile={isMobile} onTap={onTap}
                           defaults={defaults} resolveImage={resolveImage} signatures={signatures}
                           onError={onError} pageHeight={height} t={t} />
        );
      })}
    </div>
  );
});

// Phones: one field at a time in a sheet at the bottom of the screen,
// with Previous / Next. The page above updates as you type.
function FillSheet({ ff, value, onChange, onPrev, onNext, onDone, position, decimalComma, t }) {
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [ff.path]);
  const id = 'fill-sheet-input';
  const raw = value == null ? '' : String(value);
  // Number / currency: same rules as the box on the page. A typed amount
  // becomes a plain number when the field is left — on blur, and on
  // Previous / Next / Done / Enter, which keep the same input element
  // (so no blur fires) when moving between fields.
  const numeric = ff.kind === 'number' || ff.kind === 'currency';
  const settle = () => {
    if (!numeric || !raw) return;
    const plain = normalizeNumberInput(raw, decimalComma);
    if (plain !== raw) onChange(plain);
  };
  const leave = (go) => () => { settle(); go(); };
  const onKey = (e) => { if (e.key === 'Enter' && ff.kind !== 'multiline') { e.preventDefault(); settle(); onNext(); } };
  let input;
  if (ff.kind === 'multiline') {
    input = <textarea id={id} ref={inputRef} className="input" rows={4} value={raw} onChange={(e) => onChange(e.target.value)} />;
  } else if (ff.kind === 'select') {
    input = (
      <select id={id} ref={inputRef} className="select" value={raw} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('fill.choose', 'Choose…')}</option>
        {(ff.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (ff.kind === 'date') {
    input = <input id={id} ref={inputRef} className="input" type="date" value={toIsoDate(raw)} onKeyDown={onKey}
                   onChange={(e) => onChange(e.target.value)} />;
  } else {
    input = <input id={id} ref={inputRef} className="input" value={raw} onKeyDown={onKey}
                   inputMode={numeric ? 'decimal' : undefined}
                   enterKeyHint={position.index < position.total - 1 ? 'next' : 'done'}
                   onBeforeInput={numeric ? blockNonNumeric : undefined}
                   onBlur={numeric ? settle : undefined}
                   onChange={(e) => onChange(numeric ? numberInputChars(e.target.value) : e.target.value)} />;
  }
  return (
    <div className="fill-sheet" role="dialog" aria-labelledby="fill-sheet-label">
      <div className="fill-sheet-head">
        <label id="fill-sheet-label" className="fill-sheet-label" htmlFor={id}>
          {ff.label}{ff.required && <span className="fill-required" aria-hidden="true"> *</span>}
        </label>
        <span className="fill-sheet-count">{position.index + 1} / {position.total}</span>
      </div>
      {input}
      <div className="fill-sheet-actions">
        <button type="button" className="btn" onClick={leave(onPrev)} disabled={position.index === 0}>{t('fill.prev', 'Previous')}</button>
        <button type="button" className="btn" onClick={leave(onNext)} disabled={position.index >= position.total - 1}>{t('fill.next', 'Next')}</button>
        <button type="button" className="btn btn-primary" onClick={leave(onDone)}>{t('fill.done', 'Done')}</button>
      </div>
    </div>
  );
}

// Right-hand "Fields" panel of the fill view — every input in reading
// order, under a heading per page, synced with the page: the selected
// field is highlighted in both places.
function FillFieldsPanel({ fields, pageOf, values, setValue, invalid, selectedPath, onFocusField, onError, hint, note, t }) {
  let lastPage = null;
  return (
    <div className="fill-side-fields">
      {hint && <p className="fill-side-hint">{hint}</p>}
      {fields.map((ff) => {
        const page = pageOf.get(ff.path) || 1;
        const heading = page !== lastPage
          ? <div className="fill-side-page">{t('fill.pageN', 'Page {{n}}', { n: page })}</div>
          : null;
        lastPage = page;
        return (
          <React.Fragment key={ff.path}>
            {heading}
            <div data-panel-path={ff.path} onFocus={() => onFocusField(ff.path)}
                 className={'fill-side-row' + (ff.path === selectedPath ? ' is-selected' : '')
                   + (invalid.has(ff.path) ? ' fill-field-invalid' : '')}>
              <FillFieldInput ff={ff} t={t} value={values[ff.path]} onError={onError}
                              onChange={(v) => setValue(ff.path, v)} />
            </div>
          </React.Fragment>
        );
      })}
      {note}
    </div>
  );
}

// Fill out — laid out like the template builder (toolbar, page canvas,
// side panel), but only values can be entered: nothing moves, resizes or
// changes style. Type on the page or in the Fields panel; both stay in
// sync. On phones one pane shows at a time ("On the PDF" / "As a form"),
// and tapping a field on the page opens it in a bottom sheet.
function FillTemplateView({ template, pdfDoc, onClose, onGenerated, beforeGenerate, templates, onTemplateChange, onAdvanced }) {
  const t = useT();
  const confirm = useConfirm();
  const { current: ws } = useWorkspace();
  const tenantCtx = useTenant();
  const tenant = tenantCtx && tenantCtx.current;
  const isMobile = useIsMobile();
  // Phones only: which pane is showing.
  const [mode, setMode] = useState('pdf');
  const [sideTab, setSideTab] = useState('fields');
  const [savedSchema, setSavedSchema] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [filename, setFilename] = useState('');
  const [invalid, setInvalid] = useState(() => new Set());
  // Field open in the phone bottom sheet.
  const [activePath, setActivePath] = useState(null);
  // Field highlighted on the page and in the panel.
  const [selectedPath, setSelectedPath] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBoxes, setShowBoxes] = useState(true);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const [toolbarEl, setToolbarEl] = useState(null);
  useFitToWidth(toolbarEl, 6);
  // The field the user was last in — "Next field" continues from there
  // (clicking the button itself takes focus away from the field).
  const lastPathRef = useRef(null);
  const trackFocus = (e) => {
    const el = e.target && e.target.closest ? e.target.closest('[data-fill-path]') : null;
    if (!el) return;
    const path = el.getAttribute('data-fill-path');
    lastPathRef.current = path;
    setSelectedPath(path);
  };

  // A template linked to a saved schema validates against it, so its
  // leaf types decide how values are coerced.
  useEffect(() => {
    if (!template || !template.schemaId) return;
    let alive = true;
    api.get('/spa/schemas').then((r) => {
      const hit = (r.schemas || []).find((x) => x.id === template.schemaId);
      if (alive && hit && hit.jsonSchema) setSavedSchema(hit.jsonSchema);
    }).catch(() => {});
    return () => { alive = false; };
  }, [template && template.schemaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const schema = useMemo(
    () => schemaForTemplate(template, savedSchema && template ? { [template.schemaId]: savedSchema } : {}),
    [template, savedSchema],
  );
  const fields = (template && template.fields) || [];
  const formFields = useMemo(() => fillFormFromFields(fields, schema), [fields, schema]);
  const byPath = useMemo(() => new Map(formFields.map((ff) => [ff.path, ff])), [formFields]);
  // Each input's page and reading position (its first field on the PDF):
  // drives the panel order, "Next field" and page switching.
  const { pageOf, ordered } = useMemo(() => {
    const first = new Map();
    for (const f of fields) {
      const path = f.jsonPath && f.jsonPath.trim();
      if (!path || !byPath.has(path)) continue;
      const pos = [Number(f.page) || 1, Number(f.y) || 0, Number(f.x) || 0];
      const cur = first.get(path);
      if (!cur || pos[0] < cur[0] || (pos[0] === cur[0] && (pos[1] < cur[1] || (pos[1] === cur[1] && pos[2] < cur[2])))) first.set(path, pos);
    }
    const at = (ff) => first.get(ff.path) || [1, 0, 0];
    const sorted = formFields.slice().sort((a, b) => {
      const pa = at(a); const pb = at(b);
      return (pa[0] - pb[0]) || (pa[1] - pb[1]) || (pa[2] - pb[2]);
    });
    return { pageOf: new Map(formFields.map((ff) => [ff.path, at(ff)[0]])), ordered: sorted };
  }, [fields, formFields, byPath]);
  const [values, setValues] = useState(() => initialFillValues(formFields));
  // New inputs (e.g. once a linked schema loads) get their defaults;
  // anything already typed is kept.
  useEffect(() => { setValues((v) => ({ ...initialFillValues(formFields), ...v })); }, [formFields]);

  const renderer = (template && template.renderer) || 'plumav1';
  const defaults = useMemo(() => ({
    numberFormat: tenant && tenant.numberFormat,
    currencyFormat: tenant && tenant.currencyFormat,
    defaultCurrency: tenant && tenant.defaultCurrency,
  }), [tenant]);
  const { pdf, error: pdfError } = useTemplatePdf(template, ws && ws.slug, pdfDoc);
  const resolveImage = useFieldImageResolver(fields);
  const signatures = useMySignatures(fields);
  const totalPages = pdf ? pdf.numPages : 1;

  // Zoom, as in the template builder: 100% = 1.5 × the PDF's own size,
  // and a page that doesn't fit starts fitted to the width.
  const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0];
  const [zoom, setZoom] = useState(1);
  const [debouncedZoom, setDebouncedZoom] = useState(1);
  const [fitZoom, setFitZoom] = useState(null);
  const ZOOM_MIN = Math.min(ZOOM_STEPS[0], fitZoom || ZOOM_STEPS[0]);
  const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];
  useEffect(() => {
    if (debouncedZoom === zoom) return undefined;
    const id = setTimeout(() => setDebouncedZoom(zoom), 120);
    return () => clearTimeout(id);
  }, [zoom, debouncedZoom]);
  const fittedRef = useRef(false);
  useEffect(() => {
    if (!pdf || fittedRef.current) return undefined;
    const el = scrollRef.current;
    if (!el || !el.clientWidth) return undefined;
    let cancelled = false;
    pdf.getPage(1).then((pg) => {
      if (cancelled) return;
      fittedRef.current = true;
      const cs = window.getComputedStyle(el);
      const avail = el.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0) - 8;
      const pageW = pg.getViewport({ scale: 1.5 }).width;
      if (avail > 120 && pageW > avail) {
        const z = Math.max(0.3, Math.floor((avail / pageW) * 100) / 100);
        setFitZoom(z); setZoom(z); setDebouncedZoom(z);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pdf, mode, result]);

  const setValue = useCallback((path, v) => {
    setValues((cur) => ({ ...cur, [path]: v }));
    setInvalid((cur) => {
      if (!cur.has(path)) return cur;
      const next = new Set(cur);
      next.delete(path);
      return next;
    });
  }, []);

  const isFilled = useCallback((ff) => (ff.kind === 'checkbox'
    ? !!values[ff.path]
    : String(values[ff.path] == null ? '' : values[ff.path]).trim() !== ''), [values]);
  const required = formFields.filter((ff) => ff.required);
  const requiredDone = required.filter(isFilled).length;
  const sheetFields = ordered.filter((ff) => FILL_TEXT_KINDS.has(ff.kind));

  // Esc / backdrop / Close used to throw away everything typed (and any
  // uploaded image) without asking.
  const dirty = !result && (filename.trim() !== ''
    || JSON.stringify(values) !== JSON.stringify(initialFillValues(formFields)));
  const requestClose = useCallback(async () => {
    if (dirty) {
      const ok = await confirm({
        title: t('fill.discardTitle', 'Discard what you entered?'),
        body: t('fill.discardBody', 'The values you typed in this form will be lost.'),
        confirmLabel: t('common.discard', 'Discard'),
        danger: true,
      });
      if (!ok) return;
    }
    onClose();
  }, [dirty, confirm, onClose, t]);
  // Esc closes the innermost thing first: the sheet, then the view.
  const onEscape = useCallback(() => {
    if (activePath) setActivePath(null);
    else requestClose();
  }, [activePath, requestClose]);
  useModalDismiss(onEscape, { busy });

  // The app behind stays put while this covers it.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Fields that can carry data but have no data key are invisible to
  // this form — tell the user instead of silently ignoring them.
  const unbound = fields.filter((f) =>
    ['text', 'multiline', 'number', 'currency', 'checkbox', 'image', 'barcode'].includes(f.type || 'text')
    && !(f.jsonPath && f.jsonPath.trim())
    && !(f.default != null && f.default !== '' && f.default !== false)
    && !(f.type === 'image' && f.imageUrl)).length;

  const body = useMemo(() => {
    const b = { data: buildFillData(formFields, values, schema) };
    if (filename.trim()) b.filename = filename.trim();
    return b;
  }, [formFields, values, schema, filename]);

  const esc = (p) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(p) : p);
  // Run `fn` on a field's element on the page once its page is drawn
  // (switching pages loads the page first).
  const onCanvasField = (path, fn) => {
    let tries = 0;
    const seek = () => {
      const el = scrollRef.current && scrollRef.current.querySelector(`[data-fill-path="${esc(path)}"]`);
      if (el) { fn(el); return; }
      if (tries++ < 40) requestAnimationFrame(seek);
    };
    requestAnimationFrame(seek);
  };

  // Go to a field: its page, highlighted, focused on the page (phones:
  // open it in the sheet, or focus it in the list).
  const focusField = useCallback((path) => {
    const ff = byPath.get(path);
    if (!ff) return;
    setSelectedPath(path);
    lastPathRef.current = path;
    if (isMobile && mode === 'form') {
      const row = panelRef.current && panelRef.current.querySelector(`[data-panel-path="${esc(path)}"]`);
      const input = row && row.querySelector('input, textarea, select, button');
      if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (input) input.focus({ preventScroll: true });
      return;
    }
    setCurrentPage(pageOf.get(path) || 1);
    if (isMobile && FILL_TEXT_KINDS.has(ff.kind)) { setActivePath(path); return; }
    onCanvasField(path, (el) => {
      const target = el.matches('input, textarea, select, button') ? el : el.querySelector('input, textarea, select, button');
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (target) target.focus({ preventScroll: true });
    });
  }, [byPath, pageOf, isMobile, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typing in the panel: show the field on the page without taking focus.
  const revealField = useCallback((path) => {
    setSelectedPath(path);
    lastPathRef.current = path;
    setCurrentPage(pageOf.get(path) || 1);
    onCanvasField(path, (el) => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }, [pageOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting on the page scrolls the panel to the same field.
  useEffect(() => {
    if (!selectedPath || !panelRef.current) return;
    const row = panelRef.current.querySelector(`[data-panel-path="${esc(selectedPath)}"]`);
    if (row && !row.contains(document.activeElement)) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // The opened sheet's field scrolls into view above the sheet.
  useEffect(() => {
    if (!activePath) return;
    onCanvasField(activePath, (el) => el.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }, [activePath]); // eslint-disable-line react-hooks/exhaustive-deps

  const nextField = () => {
    const order = ordered.map((ff) => ff.path);
    if (!order.length) return;
    const current = activePath || lastPathRef.current;
    const start = order.indexOf(current);
    const pick = (ok) => {
      for (let i = 1; i <= order.length; i++) {
        const p = order[(start + i + order.length) % order.length];
        if (ok(byPath.get(p))) return p;
      }
      return null;
    };
    const target = pick((ff) => !isFilled(ff)) || pick(() => true);
    if (target) focusField(target);
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const missing = ordered.filter((ff) => ff.required && !isFilled(ff));
    if (missing.length) {
      setInvalid(new Set(missing.map((ff) => ff.path)));
      setError(t('fill.missingRequired', 'Please fill in: {{fields}}', {
        fields: missing.slice(0, 4).map((ff) => ff.label).join(', ') + (missing.length > 4 ? ' …' : ''),
      }));
      focusField(missing[0].path);
      return;
    }
    setActivePath(null);
    setBusy(true);
    setError(null);
    try {
      if (beforeGenerate) await beforeGenerate();
      const r = await api.post(`/api/v1/${ws.slug}/templates/${template.id}/generate`, body);
      setResult(r);
      if (onGenerated) onGenerated(r);
    } catch (err) {
      const list = err.detail && Array.isArray(err.detail.errors) ? err.detail.errors : null;
      if (list && list.length) {
        // The server reports JSON-pointer paths ("/customer/name"; for a
        // missing value: the parent + params.missingProperty) and English
        // ajv messages. Translate both into the form's own labels.
        const bad = new Set();
        const describe = (x) => {
          let path = String(x.path || '/').split('/').filter(Boolean).join('.');
          if (x.params && x.params.missingProperty) path = path ? `${path}.${x.params.missingProperty}` : x.params.missingProperty;
          const ff = byPath.get(path);
          if (ff) bad.add(ff.path);
          const lbl = ff ? ff.label : (path || t('fill.template', 'Template'));
          const why = x.keyword === 'required' ? t('fill.err.required', 'is required')
            : x.keyword === 'type' ? t('fill.err.type', 'has the wrong format')
            : x.keyword === 'enum' ? t('fill.err.enum', 'must be one of the allowed values')
            : x.keyword === 'format' || x.keyword === 'pattern' ? t('fill.err.format', 'isn’t valid')
            : (x.message || '');
          return `“${lbl}” ${why}`;
        };
        const text = list.slice(0, 3).map(describe).join('; ');
        setInvalid(bad);
        setError(t('fill.validationFailed', 'Some values don’t match what this template expects:') + ' ' + text);
      } else {
        setError(err.message || t('fill.failed', 'Couldn’t create the PDF.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const apiSnippet = useMemo(() => {
    if (!ws || !template) return '';
    const url = `${window.location.origin}/api/v1/${ws.slug}/templates/${template.id}/generate`;
    const json = JSON.stringify(body, (k, v) => (typeof v === 'string' && v.startsWith('data:') ? 'data:…' : v));
    return `curl -X POST '${url}' \\\n  -H 'Authorization: Bearer YOUR_API_KEY' \\\n  -H 'Content-Type: application/json' \\\n  -d '${json.replace(/'/g, "'\\''")}' \\\n  -o document.pdf`;
  }, [ws, template, body]);

  if (!template) return null;

  const pageFields = (n) => fields
    .filter((f) => (Number(f.page) || 1) === n)
    .slice()
    .sort((a, b) => ((Number(a.y) || 0) - (Number(b.y) || 0)) || ((Number(a.x) || 0) - (Number(b.x) || 0)));
  const activeFF = activePath ? byPath.get(activePath) : null;
  const sheetIndex = activeFF ? sheetFields.findIndex((ff) => ff.path === activePath) : -1;
  const moveSheet = (delta) => {
    const next = sheetFields[sheetIndex + delta];
    if (next) { setCurrentPage(pageOf.get(next.path) || 1); setActivePath(next.path); setSelectedPath(next.path); }
  };
  const unboundNote = unbound > 0 && (
    <div className="fill-note">
      {t('fill.unbound', '{{n}} field(s) on this template aren’t connected to the form yet. Select them in the editor and give them a name.', { n: unbound })}
    </div>
  );
  const showCanvas = !isMobile || mode === 'pdf';
  const showSide = !isMobile || mode === 'form';

  return (
    <div className={'fill-view' + (activeFF ? ' has-sheet' : '')} role="dialog" aria-modal="true" aria-labelledby="fill-title">
      <div className="fill-view-bar">
        <button type="button" className="btn btn-sm icon-btn" onClick={requestClose} disabled={busy}
                aria-label={t('common.close', 'Close')}><Icon name="close" /></button>
        <div className="fill-view-title">
          <h2 id="fill-title">{t('fill.title', 'Fill out “{{name}}”', { name: template.name })}</h2>
          {templates && templates.length > 1 && onTemplateChange && !result && (
            <select className="select fill-view-template" value={template.id}
                    aria-label={t('fill.template', 'Template')}
                    onChange={(e) => onTemplateChange(e.target.value)}>
              {templates.map((tt) => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
            </select>
          )}
        </div>
        {!result && (
          <>
            {required.length > 0 && (
              <span className={'fill-view-progress' + (requiredDone === required.length ? ' is-done' : '')}>
                {t('fill.progress', '{{done}} of {{total}} required', { done: requiredDone, total: required.length })}
              </span>
            )}
            {isMobile && formFields.length > 0 && (
              <SegmentedControl label={t('fill.viewMode', 'View')} value={mode} onChange={setMode}
                                options={[
                                  { value: 'pdf', icon: 'page', label: t('fill.viewPdf', 'On the PDF') },
                                  { value: 'form', icon: 'list', label: t('fill.viewForm', 'As a form') },
                                ]} />
            )}
            <button type="button" className="btn btn-primary btn-sm fill-view-create" onClick={submit}
                    disabled={busy || formFields.length === 0}>
              {busy ? t('common.generating', 'Generating…') : t('fill.create', 'Create PDF')}
            </button>
          </>
        )}
      </div>

      {error && !result && (
        <div className="fill-view-error" role="alert">
          <span>{error}</span>
          <button type="button" className="btn-link" onClick={() => setError(null)} aria-label={t('common.close', 'Close')}>
            <Icon name="close" />
          </button>
        </div>
      )}

      {result ? (
        <div className="fill-view-body fill-view-center">
          <div className="fill-done card">
            <div className="fill-done-icon" aria-hidden="true"><Icon name="check" /></div>
            <h3>{t('fill.readyTitle', 'Your PDF is ready')}</h3>
            <p className="fill-done-meta">
              {result.filename}
              {result.expiresAt ? (
                <> · {t('fill.availableUntil', 'download available until {{time}}', { time: formatDateTime(result.expiresAt) })}</>
              ) : result.expiresAt === null && (
                <> · {t('fill.keptUntilDeleted', 'kept on the Documents page until you delete it')}</>
              )}
            </p>
            <div className="fill-done-actions">
              <a className="btn btn-primary" href={result.url} download={result.filename || true} target="_blank" rel="noopener">
                <Icon name="download" className="icon-inline" />{t('fill.download', 'Download PDF')}
              </a>
              <button type="button" className="btn" onClick={() => setResult(null)}>
                {t('fill.again', 'Fill again')}
              </button>
            </div>
            <button type="button" className="btn-link" onClick={() => {
              onClose();
              window.dispatchEvent(new CustomEvent('pluma:navigate', { detail: { to: 'page', page: 'renders' } }));
            }}>
              {t('fill.seeDocuments', 'See all your documents →')}
            </button>
          </div>
        </div>
      ) : formFields.length === 0 ? (
        <div className="fill-view-body fill-view-center">
          <div className="fill-empty card">
            {t('fill.noFields', 'This template has no fields to fill in yet. Close this window and use “Add field” to place one on the PDF.')}
          </div>
        </div>
      ) : (
        <div className={'editor-layout fill-layout' + (isMobile ? ' is-mobile' : '')}>
          {showCanvas && (
            <div className="editor-canvas-area">
              <div className="editor-toolbar" ref={setToolbarEl}>
                <div className="page-nav">
                  <button type="button" className="fill-next-btn" onClick={nextField}
                          title={t('fill.nextField', 'Next field')} aria-label={t('fill.nextField', 'Next field')}>
                    <Icon name="arrowDown" />
                    <span className="btn-text-mobile">{t('fill.nextField', 'Next field')}</span>
                  </button>
                  <button type="button" className={'editor-overlay-btn' + (showBoxes ? '' : ' is-active')}
                          onClick={() => setShowBoxes((v) => !v)} aria-pressed={!showBoxes}
                          title={showBoxes ? t('fill.hideBoxesHint', 'Hide the field boxes to see the page as it will print') : t('fill.showBoxes', 'Show field boxes')}
                          aria-label={showBoxes ? t('fill.hideBoxes', 'Hide field boxes') : t('fill.showBoxes', 'Show field boxes')}>
                    {showBoxes ? <Icons.Layers /> : <Icons.LayersOff />}
                    <span className="btn-text-mobile">{showBoxes ? t('fill.hideBoxes', 'Hide field boxes') : t('fill.showBoxes', 'Show field boxes')}</span>
                  </button>
                </div>
                <div className="spacer" />
                <div className="page-nav zoom-controls" style={{marginRight: 12}}>
                  <button type="button" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, ZOOM_STEPS.slice().reverse().find((s) => s < z - 0.001) || ZOOM_MIN))}
                          disabled={zoom <= ZOOM_MIN + 0.001}
                          title={t('editor.zoomOut', 'Zoom out')} aria-label={t('editor.zoomOut', 'Zoom out')}>−</button>
                  <button type="button" onClick={() => setZoom(1.0)}
                          title={t('editor.zoomReset', 'Reset to 100%')}
                          style={{minWidth: 56, fontVariantNumeric: 'tabular-nums'}}>
                    {Math.round(zoom * 100)}%
                  </button>
                  <button type="button" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, ZOOM_STEPS.find((s) => s > z + 0.001) || ZOOM_MAX))}
                          disabled={zoom >= ZOOM_MAX - 0.001}
                          title={t('editor.zoomIn', 'Zoom in')} aria-label={t('editor.zoomIn', 'Zoom in')}>+</button>
                </div>
                <div className="page-nav page-nav-pages">
                  <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                          title={t('editor.prevPage', 'Previous page')} aria-label={t('editor.prevPage', 'Previous page')}>‹</button>
                  <span className="page-label"><span className="page-prefix">{t('editor.pagePrefix', 'Page')} </span>{currentPage}<span className="page-sep"> / </span>{totalPages}</span>
                  <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          title={t('editor.nextPage', 'Next page')} aria-label={t('editor.nextPage', 'Next page')}>›</button>
                </div>
              </div>
              <div className="canvas-scroll fill-canvas" ref={scrollRef} onFocus={trackFocus}>
                {pdfError ? (
                  <div className="fill-empty card">{t('fill.pdfFailed', 'Couldn’t load the PDF. Switch to “As a form” to fill it in anyway.')}</div>
                ) : !pdf ? (
                  <LoadingFrame label={t('fill.loadingPdf', 'Loading the PDF…')} />
                ) : (
                  <FillPdfPage key={currentPage} pdf={pdf} pageNumber={currentPage} scale={1.5 * debouncedZoom}
                               fields={pageFields(currentPage)}
                               byPath={byPath} values={values} setValue={setValue} invalid={invalid}
                               activePath={activePath || selectedPath} clean={!showBoxes}
                               renderer={renderer} templateFont={template.fontMode} isMobile={isMobile}
                               onTap={(path) => { setSelectedPath(path); setActivePath(path); }}
                               defaults={defaults} resolveImage={resolveImage}
                               signatures={signatures} onError={setError} t={t} />
                )}
              </div>
            </div>
          )}
          {showSide && (
            <aside className="editor-side fill-side">
              <div className="editor-tabs" role="tablist">
                {[
                  ['fields', t('editor.tab.fields', 'Fields')],
                  ['data', t('editor.tab.data', 'Data')],
                ].map(([id, label]) => (
                  <button key={id} type="button" role="tab" aria-selected={sideTab === id}
                          className={'editor-tab' + (sideTab === id ? ' active' : '')}
                          onClick={() => setSideTab(id)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="editor-tab-body" ref={panelRef}>
                {sideTab === 'fields' ? (
                  <FillFieldsPanel fields={ordered} pageOf={pageOf} values={values} setValue={setValue}
                                   invalid={invalid} selectedPath={selectedPath}
                                   onFocusField={isMobile ? setSelectedPath : revealField}
                                   onError={setError} note={unboundNote} t={t}
                                   hint={isMobile ? null : t('fill.typeHint', 'Click a field and type. The page shows exactly what the PDF will contain.')} />
                ) : (
                  <div className="fill-side-data">
                    <label className="label" htmlFor="fill-filename">{t('fill.filename', 'File name')}</label>
                    <input id="fill-filename" className="input" value={filename}
                           placeholder={`${template.name}.pdf`} onChange={(e) => setFilename(e.target.value)} />
                    <div className="label fill-side-api-label">{t('fill.apiTitle', 'The same request with the API')}</div>
                    <pre className="schema-preview fill-api">{apiSnippet}</pre>
                    {onAdvanced && (
                      <button type="button" className="btn-link" onClick={onAdvanced}>
                        {t('fill.pasteJson', 'Paste JSON or CSV data instead')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      )}

      {activeFF && showCanvas && !result && (
        <FillSheet ff={activeFF} value={values[activeFF.path]} t={t}
                   decimalComma={fillDecimalComma(activeFF.kind, defaults)}
                   onChange={(v) => setValue(activeFF.path, v)}
                   position={{ index: Math.max(0, sheetIndex), total: sheetFields.length }}
                   onPrev={() => moveSheet(-1)} onNext={() => moveSheet(1)}
                   onDone={() => setActivePath(null)} />
      )}
    </div>
  );
}

function GeneratePdfModal({ onClose, onGenerated, initialTemplateId }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [format, setFormat] = useState('json');
  const [text, setText] = useState('');
  const [pristine, setPristine] = useState(true);
  const [savedById, setSavedById] = useState({});
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [err, setErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const { current: ws } = useWorkspace();
  const toast = useToast();
  const t = useT();

  const currentTemplate = templates.find((tt) => tt.id === templateId) || null;

  useEffect(() => {
    if (!ws) return;
    Promise.all([
      fetchAllTemplates(ws.slug),
      api.get('/spa/schemas').catch(() => ({ schemas: [] })),
    ]).then(([res, sres]) => {
      const list = res.templates || [];
      setTemplates(list);
      const map = {};
      for (const s of (sres.schemas || [])) if (s.id && s.jsonSchema) map[s.id] = s.jsonSchema;
      setSavedById(map);
      if (list.length && !templateId) {
        setTemplateId(initialTemplateId && list.some((x) => x.id === initialTemplateId) ? initialTemplateId : list[0].id);
      }
    }).catch((e) => toast(e.message, 'error'));
  }, [ws?.slug]); // eslint-disable-line

  // Regenerate the sample (single { data } for JSON, one CSV row) when the
  // template/format change, while the box is still untouched.
  useEffect(() => {
    if (!pristine) return;
    const schema = schemaForTemplate(currentTemplate, savedById);
    if (format === 'csv') {
      setText(jobSampleText('csv', schema));
    } else {
      // sampleFromSchema returns { data: {...} } — the exact /generate body.
      const hasProps = schema && schema.properties && Object.keys(schema.properties).length > 0;
      setText(hasProps ? JSON.stringify(sampleFromSchema(schema), null, 2) : '{\n  "data": { "name": "Alice" }\n}');
    }
  }, [templateId, format, pristine, savedById, templates]); // eslint-disable-line

  // Build the /generate body { data, filename? } from the textarea.
  const parseBody = (raw, fmt) => {
    if (fmt === 'csv') {
      const items = csvToJobItems(raw); // [{ data, filename? }]
      if (!items.length) throw new Error('CSV has a header but no data row');
      if (items.length > 1) toast(`CSV has ${items.length} rows — only the first is used. Use a bulk job for all rows.`, 'info');
      return items[0];
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('JSON must be an object like { "data": { ... } }');
    }
    // Accept either { data: {...} } or a bare {...} (treated as data).
    const body = (parsed.data !== undefined) ? parsed : { data: parsed };
    if (!body.data || typeof body.data !== 'object') throw new Error('"data" must be an object');
    return body;
  };

  const loadFile = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr('File too large (8 MB max)'); return; }
    const isCsv = /\.csv$/i.test(file.name || '') || file.type === 'text/csv';
    const reader = new FileReader();
    reader.onload = () => {
      setPristine(false);
      setFormat(isCsv ? 'csv' : 'json');
      setText(String(reader.result || ''));
      setErr(null);
    };
    reader.onerror = () => setErr('Could not read file');
    reader.readAsText(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!templateId) return toast('Pick a template', 'error');
    let body;
    try { body = parseBody(text, format); }
    catch (e2) { setErr(e2.message); return; }
    setBusy(true);
    try {
      // Default Accept (JSON) → the render is stored and returned as a
      // presigned URL, so it lands in the renders list. Open it for the
      // operator and refresh.
      const r = await api.post(`/api/v1/${ws.slug}/templates/${templateId}/generate`, body);
      if (r?.url) window.open(r.url, '_blank', 'noopener');
      toast(t('renders.generated', 'PDF generated'), 'success');
      onGenerated();
    } catch (err) {
      toast(err.detail?.message || err.message, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{maxWidth: 560}} onClick={(e) => e.stopPropagation()}>
        <h2>{t('renders.generate.title', 'Generate a PDF')}</h2>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label">Template</label>
            <select className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.map((tt) => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4}}>
              <label className="label" style={{margin: 0}}>
                {format === 'csv'
                  ? 'Row (CSV — dot-notation headers nest into data)'
                  : 'Body (JSON — { "data": { … } })'}
              </label>
              <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                <div className="interval-toggle">
                  <button type="button" className={format === 'json' ? 'active' : ''} onClick={() => { if (format !== 'json') { setFormat('json'); setErr(null); } }}>JSON</button>
                  <button type="button" className={format === 'csv' ? 'active' : ''} onClick={() => { if (format !== 'csv') { setFormat('csv'); setErr(null); } }}>CSV</button>
                </div>
                <label className="btn btn-sm" style={{cursor: 'pointer', margin: 0}}>
                  Upload…
                  <input type="file" accept="application/json,.json,text/csv,.csv,text/plain,.txt"
                         style={{display: 'none'}}
                         onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </div>
            </div>
            <textarea className={'input' + (dragOver ? ' is-dragover' : '')}
                      style={{minHeight: 180, fontFamily: 'var(--font-mono)', fontSize: 12}}
                      value={text}
                      onChange={(e) => { setText(e.target.value); setPristine(false); setErr(null); }}
                      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => { e.preventDefault(); setDragOver(false); loadFile(e.dataTransfer.files?.[0]); }}
                      spellCheck={false} />
            {err && <div style={{color:'var(--danger)', fontSize:11, marginTop:4}}>{err}</div>}
            <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
              {format === 'csv'
                ? <>First row = headers. Use <code>obj.key</code> to nest. A <code>filename</code> column names the PDF.</>
                : <>The body is sent to <code>/generate</code> as-is.</>}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.generating', 'Generating…') : 'Generate'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Registries page (Enterprise)
// ============================================================================

// ============================================================================
// Renders page — unified view of every rendered artifact in the
// workspace. Two stacked tables:
//
//   1) "Active jobs" — only shown when any async /jobs row is still
//      pending/running/failed. Compact, auto-refreshing every 3 s.
//      Hidden entirely when the queue is empty so a quiet workspace
//      stays quiet.
//   2) "Renders" — every persisted artifact (single PDFs from
//      /generate, ZIPs from /bulk-sync AND completed /jobs, since
//      job completion writes a Render row keyed by jobId). This is
//      the main table: sortable columns, search, template + type
//      filters, multi-select with bulk download / copy URLs.
//
// Mobile: the desktop table flips to stacked cards below the 720 px
// breakpoint (cards keep the same multi-select affordances + actions).
// ============================================================================

const RENDER_CONTENT_TYPES = ['', 'application/pdf', 'application/zip'];

function RendersPage() {
  const { current: ws } = useWorkspace();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const isMobile = useIsMobile(720);

  // Main table state.
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);

  // Active jobs (in-flight async work). Loaded separately because the
  // poll cadence is different — renders are stable once written, jobs
  // change every few seconds while running.
  const [jobs, setJobs] = useState([]);

  // Filters / search.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Sort. Server supports createdAt + expiresAt; filename + bytes are
  // post-sorted client-side over whatever the server returned.
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Multi-select. Keep as a Set for O(1) toggle; size drives the
  // bottom action bar.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Template dropdown options — loaded once. Filename-template join is
  // done in-table by looking up `templateId` against this list.
  const [templates, setTemplates] = useState([]);

  // "New job" (bulk) + "Generate PDF" (single) modals.
  const [showNew, setShowNew] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  // Template whose fill-in form is open (null = closed).
  const [fillTemplateId, setFillTemplateId] = useState(null);
  const [advancedTemplateId, setAdvancedTemplateId] = useState(null);
  // "Send for signature" modal — opened from the selection bar for
  // selected renders whose template has a request-kind "sign here" field.
  const [showSign, setShowSign] = useState(false);

  // Debounce the search box so each keystroke doesn't refetch.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  // Templates dropdown is workspace-scoped; loaded once per ws.
  useEffect(() => {
    if (!ws) return;
    fetchAllTemplates(ws.slug)
      .then((r) => setTemplates(r.templates || []))
      .catch(() => setTemplates([]));
  }, [ws?.slug]); // eslint-disable-line

  const serverSortable = (f) => f === 'createdAt' || f === 'expiresAt';

  // Server only sorts on createdAt + expiresAt. For filename / size the
  // NEWEST rows are fetched and re-sorted client-side — passing the
  // client direction through (createdAt:asc) loaded the OLDEST 100.
  const serverSortParam = () => (serverSortable(sortField) ? `${sortField}:${sortDir}` : 'createdAt:desc');
  // How many rows are on screen, so a background refresh re-fetches all
  // of them instead of collapsing "Load more" pages back to the first.
  const loadedCountRef = useRef(0);
  useEffect(() => { loadedCountRef.current = renders.length; }, [renders.length]);

  // `silent`: background refresh (job polling) — no "Loading…" swap, so
  // the table doesn't flash and the scroll position survives.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!ws) return;
    if (!silent) setLoading(true);
    try {
      const qs = new URLSearchParams();
      const limit = silent ? Math.min(500, Math.max(100, loadedCountRef.current)) : 100;
      qs.set('limit', String(limit));
      qs.set('offset', '0');
      if (templateFilter) qs.set('templateId', templateFilter);
      qs.set('sortBy', serverSortParam());
      const res = await api.get(`/api/v1/${ws.slug}/renders?${qs.toString()}`);
      setRenders(res.renders || []);
      setNextOffset(res.meta?.hasMore ? (res.renders || []).length : null);
    } catch (e) { if (!silent) toast(e.message, 'error'); }
    finally { if (!silent) setLoading(false); }
  }, [ws, templateFilter, sortField, sortDir, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (!ws || nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '100');
      qs.set('offset', String(nextOffset));
      if (templateFilter) qs.set('templateId', templateFilter);
      qs.set('sortBy', serverSortParam());
      const res = await api.get(`/api/v1/${ws.slug}/renders?${qs.toString()}`);
      const newRows = res.renders || [];
      setRenders((prev) => [...prev, ...newRows]);
      setNextOffset(res.meta?.hasMore ? Number(nextOffset) + newRows.length : null);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoadingMore(false); }
  }, [ws, nextOffset, loadingMore, templateFilter, sortField, sortDir, toast]);

  const loadJobs = useCallback(async () => {
    if (!ws) return;
    try {
      // Pull recent jobs and filter to non-terminal ones client-side.
      // The list-jobs endpoint paginates server-side and a non-empty
      // active queue is small, so 100 covers the realistic worst case.
      const res = await api.get(`/api/v1/${ws.slug}/jobs?limit=100&sortBy=createdAt:desc`);
      const all = res.jobs || [];
      setJobs(all.filter((j) =>
        j.status === 'pending' || j.status === 'running' || j.status === 'failed'));
    } catch { /* best-effort; the main table is the priority */ }
  }, [ws?.slug]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Auto-refresh active jobs while ANY is still working. Stops once
  // everything is terminal (failed counts as terminal but keeps the
  // row visible so the operator sees the error inline).
  useEffect(() => {
    const stillRunning = jobs.some((j) => j.status === 'pending' || j.status === 'running');
    if (!stillRunning) return;
    const id = setInterval(() => {
      loadJobs();
      load({ silent: true });
    }, 3000);
    return () => clearInterval(id);
  }, [jobs, loadJobs, load]);

  // A selection made under one filter must not survive into another:
  // bulk Download / Delete / Send would act on rows the user can no
  // longer see.
  useEffect(() => { setSelected(new Set()); }, [templateFilter, typeFilter, debouncedSearch, ws?.slug]);

  // Client-side filter + sort. Search and type filter run on top of
  // whatever the server returned; sort by filename / bytes also runs
  // here because the server doesn't expose those axes.
  const visible = useMemo(() => {
    let arr = renders;
    if (typeFilter) arr = arr.filter((r) => r.contentType === typeFilter);
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      arr = arr.filter((r) => (r.filename || '').toLowerCase().includes(q));
    }
    if (!serverSortable(sortField)) {
      arr = [...arr].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  }, [renders, typeFilter, debouncedSearch, sortField, sortDir]);

  const templateNameById = useMemo(() => {
    const map = {};
    for (const tpl of templates) map[tpl.id] = tpl.name;
    return map;
  }, [templates]);

  // Whether a render can be sent for signature is a property of the
  // RENDER, not the live template: `render.signable` is captured at
  // render time (the template's "sign here" geometry then). The live
  // template may have gained or lost the field since, so we trust the
  // render's own flag — this is the bug fix for "template changed after
  // the render was made".
  const renderById = useMemo(() => {
    const m = {};
    for (const r of renders) m[r.id] = r;
    return m;
  }, [renders]);
  // Enabled only when at least one render is selected and EVERY selected
  // render is signable.
  const selectedSignable = selected.size > 0
    && [...selected].every((id) => renderById[id]?.signable === true);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelected(new Set(visible.map((r) => r.id)));
  };
  const clearSelected = () => setSelected(new Set());

  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  // Bulk download. Mints an ATTACHMENT URL per render (?download=1 →
  // Content-Disposition: attachment) and fires a same-tab anchor click
  // for each. window.open is the wrong tool here: popup blockers only
  // allow opens in the synchronous stack of the user's click, so every
  // open after the first await got silently swallowed — "select 5,
  // download, get 1". Anchor navigation to an attachment URL isn't a
  // popup; the browser saves each file (Chrome asks once to allow
  // multiple downloads). The stagger keeps download managers happy.
  const downloadSelected = async () => {
    if (!ws || selected.size === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      try {
        const r = await api.get(`/api/v1/${ws.slug}/renders/${id}/url?download=1`);
        if (r.url) {
          const a = document.createElement('a');
          a.href = r.url;
          a.rel = 'noopener';
          // Hint only — cross-origin ignores the attribute, the
          // attachment header on the URL is what forces the save.
          a.download = r.filename || '';
          document.body.appendChild(a);
          a.click();
          a.remove();
          ok++;
          await new Promise((res) => setTimeout(res, 300));
        } else { fail++; }
      } catch { fail++; }
    }
    setBulkBusy(false);
    if (ok) toast(t('renders.bulk.downloadOk', '{{n}} download(s) started', { n: ok }), 'success');
    if (fail) toast(t('renders.bulk.downloadFail', '{{n}} failed', { n: fail }), 'error');
    if (ok && !fail) clearSelected();
  };

  // Copy URLs to clipboard — newline-separated so a CLI / script can
  // pipe them to xargs curl. URLs are minted fresh; if any mint fails
  // we still copy the successful ones and surface the count.
  const copyUrls = async () => {
    if (!ws || selected.size === 0) return;
    setBulkBusy(true);
    const urls = [];
    let fail = 0;
    for (const id of selected) {
      try {
        const r = await api.get(`/api/v1/${ws.slug}/renders/${id}/url`);
        if (r.url) urls.push(r.url); else fail++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    if (urls.length) {
      try {
        await navigator.clipboard.writeText(urls.join('\n'));
        toast(t('renders.bulk.copyOk', '{{n}} URL(s) copied', { n: urls.length }), 'success');
      } catch {
        toast('Could not write to clipboard', 'error');
      }
    }
    if (fail) toast(t('renders.bulk.copyFail', '{{n}} failed', { n: fail }), 'error');
  };

  // Bulk delete. Removes the artifact + row for each selected render
  // after one confirm. Per-id loop mirrors the other bulk actions; a
  // failed id is counted, not fatal to the batch. Reloads on completion.
  const deleteSelected = async () => {
    if (!ws || selected.size === 0) return;
    const ok = await confirm({
      title: t('renders.bulk.deleteConfirm.title', 'Delete {{n}} render(s)?', { n: selected.size }),
      body: t('renders.bulk.deleteConfirm.body', 'The PDF/ZIP file and its record are removed permanently. This cannot be undone.'),
      confirmLabel: t('common.delete', 'Delete'),
      danger: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    let okCount = 0, fail = 0;
    for (const id of selected) {
      try { await api.del(`/api/v1/${ws.slug}/renders/${id}`); okCount++; }
      catch { fail++; }
    }
    setBulkBusy(false);
    if (okCount) toast(t('renders.bulk.deleteOk', '{{n}} deleted', { n: okCount }), 'success');
    if (fail) toast(t('renders.bulk.deleteFail', '{{n}} failed', { n: fail }), 'error');
    clearSelected();
    load();
  };

  const fmtBytes = (n) => {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };
  const fmtDate = (d) => formatDateTime(d);
  const typeLabel = (ct) => ct === 'application/pdf' ? 'PDF' : ct === 'application/zip' ? 'ZIP' : '—';

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('renders.title', 'Renders')}</div>
          <div className="page-subtitle">
            {t('renders.subtitle', 'Every rendered PDF and bulk ZIP from this workspace. In-flight async jobs surface above.')}
          </div>
        </div>
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
          <button className="btn" onClick={() => setShowNew(true)}
                  title={t('renders.newJobHint', 'Create one PDF per row of a spreadsheet (CSV) or JSON list')}>
            {t('renders.newJob', 'Create many')}
          </button>
          <button className="btn btn-primary" disabled={templates.length === 0}
                  onClick={() => setFillTemplateId((templateFilter && templates.some((x) => x.id === templateFilter)) ? templateFilter : (templates[0] && templates[0].id))}>
            {t('renders.generatePdf', 'Create PDF')}
          </button>
        </div>
      </div>

      {/* ───── Active jobs (only when something is in-flight) ───── */}
      {jobs.length > 0 && (
        <ActiveJobsSection
          jobs={jobs}
          templateNameById={templateNameById}
          isMobile={isMobile}
          t={t}
          fmtDate={fmtDate}
          wsSlug={ws.slug}
          onChanged={loadJobs}
        />
      )}

      {/* ───── Filters bar ───── */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <input className="input"
               placeholder={t('renders.searchPlaceholder', 'Search by filename…')}
               value={search} onChange={(e) => setSearch(e.target.value)}
               style={{flex: '1 1 200px', minWidth: 160}} />
        <select className="select" value={templateFilter}
                onChange={(e) => setTemplateFilter(e.target.value)}
                style={{maxWidth: 200}}>
          <option value="">{t('renders.filter.allTemplates', 'All templates')}</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
          ))}
        </select>
        <select className="select" value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{maxWidth: 140}}>
          <option value="">{t('renders.filter.allTypes', 'All types')}</option>
          <option value="application/pdf">PDF</option>
          <option value="application/zip">ZIP</option>
        </select>
      </div>

      {/* ───── Selected actions bar ───── */}
      {selected.size > 0 && (
        <div className="renders-selected-bar">
          <div style={{fontSize: 13, fontWeight: 600}}>
            {t('renders.bulk.count', '{{n}} selected', { n: selected.size })}
          </div>
          <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
            <button className="btn btn-sm btn-primary" onClick={downloadSelected} disabled={bulkBusy}>
              {bulkBusy ? t('common.working', 'Working…') : t('renders.bulk.download', 'Download')}
            </button>
            <button className="btn btn-sm" onClick={copyUrls} disabled={bulkBusy}>
              {t('renders.bulk.copyUrls', 'Copy URLs')}
            </button>
            {/* Greyed unless every selected render is a PDF whose template
                has a "sign here" field — see isSignableRender. The title
                explains the disabled state on hover. */}
            <button className="btn btn-sm" onClick={() => setShowSign(true)}
                    disabled={bulkBusy || !selectedSignable}
                    title={selectedSignable ? '' : t('renders.bulk.signHint', 'Select only PDF renders whose template has a sign-here field.')}>
              {t('renders.bulk.sign', 'Send for signature')}
            </button>
            <button className="btn btn-sm" onClick={deleteSelected} disabled={bulkBusy}
                    style={{color: 'var(--danger)', borderColor: 'var(--danger)'}}>
              {t('renders.bulk.delete', 'Delete')}
            </button>
            <button className="btn btn-sm" onClick={clearSelected} disabled={bulkBusy}>
              {t('renders.bulk.clear', 'Clear')}
            </button>
          </div>
        </div>
      )}

      {/* ───── Main table (or mobile cards) ───── */}
      {loading ? (
        <div className="empty">{t('common.loading', 'Loading…')}</div>
      ) : visible.length === 0 ? (
        <div className="empty">
          {templateFilter || typeFilter || debouncedSearch
            ? t('renders.emptyFiltered', 'No documents match these filters.')
            : (
              <>
                <div>{t('renders.empty', 'No documents yet. Fill out a template to create your first PDF — it will show up here.')}</div>
                {templates.length > 0 && (
                  <button className="btn btn-primary" style={{marginTop: 14}}
                          onClick={() => setFillTemplateId(templates[0].id)}>
                    {t('renders.generatePdf', 'Create PDF')}
                  </button>
                )}
              </>
            )}
        </div>
      ) : isMobile ? (
        <RenderCards
          rows={visible}
          selected={selected}
          onToggle={toggleSelected}
          templateNameById={templateNameById}
          fmtBytes={fmtBytes}
          fmtDate={fmtDate}
          typeLabel={typeLabel}
          t={t}
        />
      ) : (
        <RenderTable
          rows={visible}
          selected={selected}
          onToggle={toggleSelected}
          onToggleAll={allSelected ? clearSelected : selectAllVisible}
          allSelected={allSelected}
          sortField={sortField}
          sortDir={sortDir}
          onSort={toggleSort}
          templateNameById={templateNameById}
          fmtBytes={fmtBytes}
          fmtDate={fmtDate}
          typeLabel={typeLabel}
          t={t}
        />
      )}

      {nextOffset != null && !loading && (
        <div style={{textAlign: 'center', marginTop: 16}}>
          <button className="btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore
              ? t('common.loading', 'Loading…')
              : t('renders.loadMore', 'Load more')}
          </button>
        </div>
      )}

      {showNew && <NewJobModal onClose={() => setShowNew(false)} onSubmitted={() => { setShowNew(false); load(); loadJobs(); }} />}
      {showGenerate && <GeneratePdfModal initialTemplateId={advancedTemplateId} onClose={() => setShowGenerate(false)} onGenerated={() => { setShowGenerate(false); load(); }} />}
      {fillTemplateId && templates.some((x) => x.id === fillTemplateId) && (
        <FillTemplateView
          key={fillTemplateId}
          template={templates.find((x) => x.id === fillTemplateId)}
          templates={templates}
          onTemplateChange={setFillTemplateId}
          onGenerated={() => load()}
          onAdvanced={() => { setAdvancedTemplateId(fillTemplateId); setFillTemplateId(null); setShowGenerate(true); }}
          onClose={() => setFillTemplateId(null)}
        />
      )}
      {showSign && (
        <SendForSignatureModal
          ws={ws}
          renders={[...selected].map((id) => renderById[id]).filter(Boolean)}
          templateNameById={templateNameById}
          onClose={() => setShowSign(false)}
          onDone={(allOk) => { setShowSign(false); if (allOk) clearSelected(); }}
        />
      )}
    </>
  );
}

// Send selected renders for e-signature. One recipient, one sign request
// per render (the renderId source mode). The caller only opens this for a
// selection where every render is signable, so we don't re-check here —
// the server is the final authority and per-render failures surface
// individually.
// Flask: the "test request" switch on the send-for-signature form.
function FlaskMark() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 3h6" /><path d="M10 3v6.2L4.6 18.4A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.7-2.6L14 9.2V3" /><path d="M7.5 15h9" />
    </svg>
  );
}

// Globe: the per-signer language control on the send-for-signature form.
function GlobeMark() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18" /><path d="M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

// A test request's signed copy is deleted 24 h after signing (the API
// reports when): past that, there is nothing left to download.
function signedCopyGone(r) {
  return !!(r && r.signedPdfExpiresAt && new Date(r.signedPdfExpiresAt).getTime() < Date.now());
}

function SendForSignatureModal({ ws, renders, templateNameById, onClose, onDone }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  // 1..5 signers; slot i signs the fields assigned "Signer i" in the
  // editor. Classic docs (all fields default Signer 1) use one row.
  // locale = the signer's email language ('' = automatic).
  const [signers, setSigners] = useState([{ email: '', name: '', locale: '' }]);
  const { locales = [] } = useContext(I18nCtx);
  const [signingOrder, setSigningOrder] = useState('parallel');
  const [requireVerification, setRequireVerification] = useState(false);
  // Off by default: the certificate prints signer IPs/emails into the
  // signed PDF, which shouldn't circulate unless the sender chose it.
  const [includeCertificate, setIncludeCertificate] = useState(false);
  const [ttlDays, setTtlDays] = useState('7');
  // Test requests: watermarked, not legally binding, 24 h, no quota. The
  // Free plan can only send those (its sign-request quota is 0), so the
  // switch is on and locked there; paid plans choose per request.
  const { info: tenantInfo } = useTenantInfo();
  const freeOnly = tenantInfo?.limits?.signRequests === 0;
  const [testMode, setTestMode] = useState(false);
  useEffect(() => { if (freeOnly) setTestMode(true); }, [freeOnly]);
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [err, setErr] = useState('');
  // Renders that already went out in this dialog. After a partial failure
  // the modal stays open for a retry; without this, Send re-sent every
  // document — duplicate emails and duplicate quota/balance charges.
  const sentIdsRef = useRef(new Set());

  // How many DISTINCT people can sign these documents — the number of
  // "Signer N" slots captured in the render snapshot. Adding a signer beyond
  // that produces someone with nothing to sign, which the server rejects
  // (signer_has_no_field), so the UI caps here rather than letting the user
  // build an invalid request and discover it on submit. Across a multi-render
  // selection we take the MINIMUM: the same signer list is posted for every
  // render, so it has to fit the most restrictive document. Falls back to the
  // old hardcoded 5 for renders from before signerSlots existed.
  const slotCounts = (renders || []).map((r) => Number(r.signerSlots) || 0).filter((n) => n > 0);
  const maxSigners = slotCounts.length ? Math.min(...slotCounts) : 5;
  const setSigner = (i, patch) => setSigners((arr) => arr.map((s, j) => j === i ? { ...s, ...patch } : s));
  const addSigner = () => setSigners((arr) => arr.length >= maxSigners ? arr : [...arr, { email: '', name: '', locale: '' }]);
  const removeSigner = (i) => setSigners((arr) => arr.length <= 1 ? arr : arr.filter((_, j) => j !== i));
  // A known address gets the name and language this tenant used for it
  // before — only into fields that are still empty. (The server fills
  // them in the same way, so this just shows it.)
  const recall = async (i, rawEmail) => {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      const r = await api.get(`/spa/sign-recipients?email=${encodeURIComponent(email)}`);
      setSigners((arr) => arr.map((s, j) => ((j !== i || s.email.trim().toLowerCase() !== email) ? s
        : { ...s, name: s.name || r.name || '', locale: s.locale || r.locale || '' })));
    } catch (_) { /* a convenience — the request works without it */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = signers.map((s) => ({
      email: s.email.trim().toLowerCase(),
      name: s.name.trim() || undefined,
      locale: s.locale || undefined,
    }));
    if (cleaned.some((s) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email))) {
      setErr(t('renders.sign.invalidEmail', 'Enter a valid email address.'));
      return;
    }
    // Fewer signers than the document has slots is allowed (the server no
    // longer rejects it), but it silently leaves signature boxes blank in the
    // finished PDF — so confirm it's deliberate rather than a forgotten row.
    // Uses the app's ConfirmProvider, not window.confirm: the native dialog
    // can't be styled or translated, blocks the whole tab, and looks nothing
    // like the rest of the product.
    if (cleaned.length < maxSigners) {
      const blank = maxSigners - cleaned.length;
      const ok = await confirm({
        title: t('renders.sign.fewerSignersTitle', 'Send with unsigned slots?'),
        body: t('renders.sign.fewerSignersBody',
          'This document has {{slots}} signature slots but you added {{added}}. The remaining {{blank}} will stay blank in the signed PDF.',
          { slots: maxSigners, added: cleaned.length, blank }),
        confirmLabel: t('renders.sign.fewerSignersCta', 'Send anyway'),
      });
      if (!ok) return;
    }
    setBusy(true); setErr('');
    const ttlSeconds = parseInt(ttlDays, 10) * 24 * 3600;
    let ok = 0;
    const failed = [];
    // Sequential, not parallel: each call bumps the sign-request quota +
    // sends emails; a burst could trip the render limiter. The counts
    // are small (a selection), so the wall-clock cost is fine.
    for (const r of renders) {
      if (sentIdsRef.current.has(r.id)) continue;
      try {
        await api.post(`/api/v1/${ws.slug}/sign-requests`, {
          renderId: r.id,
          signers: cleaned,
          signingOrder,
          requireEmailVerification: requireVerification,
          includeCertificate,
          ttlSeconds,
          test: testMode,
        });
        sentIdsRef.current.add(r.id);
        ok++;
      } catch (e2) {
        // Keep the server's human-readable reason (errorFrom puts the
        // response `detail` on err.message) — not just the filename, so
        // the operator sees WHY it failed (e.g. an unassigned signer).
        failed.push({ name: r.filename || r.id, reason: e2.message || t('common.error', 'Something went wrong.') });
      }
    }
    setBusy(false);
    if (ok) {
      toast(testMode
        ? t('renders.sign.sentTestOk', '{{n}} sent as test request', { n: ok })
        : t('renders.sign.sentOk', '{{n}} sent for signature', { n: ok }), 'success');
    }
    if (failed.length) {
      // Validation reasons (unassigned signer, etc.) repeat identically
      // across a multi-render selection — dedupe. When every failure
      // shares one reason, show just that reason; otherwise list each
      // document with its own reason.
      const reasons = [...new Set(failed.map((f) => f.reason))];
      if (!ok && reasons.length === 1) {
        setErr(reasons[0]);
      } else {
        toast(t('renders.sign.sentFail', '{{n}} could not be sent', { n: failed.length }), 'error');
        setErr(failed.map((f) => `${f.name}: ${f.reason}`).join('\n'));
      }
      return; // keep the modal open so the operator can retry / adjust
    }
    onDone(true);
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{maxWidth: 480}} onClick={(e) => e.stopPropagation()}>
        <h2>{t('renders.sign.title', 'Send for signature')}</h2>
        {/* Single document: no subtitle. "This document will be emailed to the
            signers below" restated the modal title and the Signers label right
            under it, and pushed the capacity line — the one thing people
            actually need — further from the heading. The multi-document case
            keeps its line because the COUNT is real information the title
            doesn't carry. */}
        {renders.length > 1 && (
          <p className="sub">
            {t('renders.sign.bodyMany', '{{n}} documents will be emailed to the signers below.', { n: renders.length })}
          </p>
        )}
        {/* Capacity is the thing people get wrong here, so state it plainly
            and show progress against it — "2 of 3 slots used" answers "how
            many people can sign this?" without opening the editor. */}
        <div className="sign-capacity" role="status">
          <Icon name="users" />
          <span className="sign-capacity-text">
            {t('renders.sign.capacity', 'This document can be signed by {{max}} {{people}} — {{used}} of {{max}} slots used', {
              max: maxSigners,
              used: signers.length,
              people: maxSigners === 1
                ? t('renders.sign.person', 'person')
                : t('renders.sign.people', 'people'),
            })}
          </span>
          <span className="sign-capacity-pips" aria-hidden="true">
            {Array.from({ length: maxSigners }, (_, i) => (
              <i key={i} className={'sign-pip' + (i < signers.length ? ' is-filled' : '')} />
            ))}
          </span>
        </div>
        <form className="form-row" onSubmit={submit}>
          <div>
            {/* Label + flask on one line; the row carries the spacing below it
                so the flask never sits right on top of the first signer's globe. */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8}}>
              <label className="label" style={{marginBottom: 0}}>{t('renders.sign.signers', 'Signers')}</label>
              <button type="button" className={'btn btn-sm icon-btn sign-test-toggle' + (testMode ? ' is-on' : '')}
                      aria-pressed={testMode} disabled={freeOnly || busy}
                      onClick={() => setTestMode((v) => !v)}
                      title={t('renders.sign.testToggle', 'Test request')}
                      aria-label={t('renders.sign.testToggle', 'Test request')}>
                <FlaskMark />
              </button>
            </div>
            {testMode && (
              <div className="sign-test-note">
                {t('renders.sign.testNote', 'Test request – watermarked, not legally binding, kept 24 h, no quota used.')}
                {freeOnly ? ' ' + t('renders.sign.testLocked', 'Free plan: test requests only.') : ''}
              </div>
            )}
            {signers.map((s, i) => (
              <div key={i} style={{display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap'}}>
                <span style={{fontSize: 11, color: 'var(--text-muted)', minWidth: 18}}>#{i + 1}</span>
                <input className="input" type="email" required autoFocus={i === 0}
                       style={{flex: '2 1 180px', minWidth: 0}} placeholder="signer@example.com"
                       value={s.email} onChange={(e) => setSigner(i, { email: e.target.value })}
                       onBlur={(e) => recall(i, e.target.value)} />
                <input className="input" style={{flex: '1 1 110px', minWidth: 0}}
                       placeholder={t('renders.sign.namePlaceholder', 'Name (optional)')}
                       value={s.name} onChange={(e) => setSigner(i, { name: e.target.value })} />
                {/* Language: a globe, plus the code once one is chosen — the
                    native dropdown sits invisibly on top so a tap still opens
                    it. Compact enough to stay on the signer's line. */}
                <span className={'sign-lang' + (s.locale ? ' is-set' : '')}
                      title={t('renders.sign.languageHint', 'The language of this signer’s emails and signing page')}>
                  <GlobeMark />
                  {s.locale && <span className="sign-lang-code">{s.locale.toUpperCase()}</span>}
                  <select className="sign-lang-select"
                          aria-label={t('renders.sign.languageLabel', 'Email language')}
                          value={s.locale} onChange={(e) => setSigner(i, { locale: e.target.value })}>
                    <option value="">{t('renders.sign.languageAuto', 'Language: auto')}</option>
                    {locales.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </span>
                {signers.length > 1 && (
                  <button type="button" className="btn btn-sm icon-btn"
                          aria-label={t('renders.sign.removeSigner', 'Remove signer')}
                          onClick={() => removeSigner(i)}><Icon name="close" /></button>
                )}
              </div>
            ))}
            {/* Capped at the document's slot count, not a fixed 5: a signer
                past the last slot has nothing to sign and the server rejects
                the whole request. Show why the button is gone rather than
                silently hiding it. */}
            {signers.length < maxSigners ? (
              <button type="button" className="btn btn-sm" onClick={addSigner}>
                {t('renders.sign.addSigner', '+ Add signer')}
              </button>
            ) : (
              <div style={{fontSize: 11, color: 'var(--text-muted)'}}>
                {t('renders.sign.allSlotsUsed', 'All {{max}} signer slots assigned.', { max: maxSigners })}
              </div>
            )}
            {signers.length > 1 && (
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 6}}>
                {t('renders.sign.signerFieldsHint', 'Assign sign-here fields to each signer in the editor.')}
              </div>
            )}
          </div>
          {signers.length > 1 && (
            <div>
              <label className="label">{t('renders.sign.order', 'Signing order')}</label>
              <select className="select" value={signingOrder} onChange={(e) => setSigningOrder(e.target.value)}>
                <option value="parallel">{t('renders.sign.order.parallel', 'All at once (parallel)')}</option>
                <option value="sequential">{t('renders.sign.order.sequential', 'One after another (sequential)')}</option>
              </select>
            </div>
          )}
          <label style={{display: 'flex', gap: 8, alignItems: 'center', fontSize: 13}}>
            <input type="checkbox" checked={requireVerification}
                   onChange={(e) => setRequireVerification(e.target.checked)} />
            {t('renders.sign.requireVerification', 'Require email verification before signing (one-time code)')}
          </label>
          <label style={{display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13}}>
            <input type="checkbox" checked={includeCertificate} style={{marginTop: 2}}
                   onChange={(e) => setIncludeCertificate(e.target.checked)} />
            <span>
              {t('renders.sign.includeCertificate', 'Attach certificate of completion to the signed PDF')}
              <span style={{display: 'block', fontSize: 11, color: 'var(--text-muted)'}}>
                {t('renders.sign.includeCertificateHint', 'Audit page with timestamps, emails and partially masked IP addresses.')}
              </span>
            </span>
          </label>
          <div>
            <label className="label">{t('renders.sign.ttl', 'Signable for')}</label>
            {/* A test request is signable for 24 hours: that option is selected
                and the real windows stay listed but disabled, so a Free user
                sees what a paid plan would offer. */}
            <select className="select" value={testMode ? 'test' : ttlDays}
                    onChange={(e) => { if (!testMode) setTtlDays(e.target.value); }}>
              {testMode && <option value="test">{t('renders.sign.hours24', '24 hours')}</option>}
              <option value="7" disabled={testMode}>{t('renders.sign.days', '{{n}} days', { n: 7 })}</option>
              <option value="14" disabled={testMode}>{t('renders.sign.days', '{{n}} days', { n: 14 })}</option>
              <option value="30" disabled={testMode}>{t('renders.sign.days', '{{n}} days', { n: 30 })}</option>
            </select>
          </div>
          {/* The documents this batch will send. */}
          <div style={{fontSize: 12, color: 'var(--text-muted)', maxHeight: 120, overflow: 'auto',
                       border: '1px solid var(--border)', borderRadius: 6, padding: 8}}>
            {renders.map((r) => (
              <div key={r.id} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {r.filename}{templateNameById[r.templateId] ? ` · ${templateNameById[r.templateId]}` : ''}
              </div>
            ))}
          </div>
          {err && <div style={{color: 'var(--danger)', fontSize: 12, whiteSpace: 'pre-line', lineHeight: 1.45}}>{err}</div>}
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('common.working', 'Working…') : t('renders.sign.send', 'Send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────── Active jobs ───────────────────────────
//
// Compact header strip surfaced ONLY when at least one async job is
// still moving. No multi-select here — the queue is short-lived and
// the cancel surface lives in a separate Job Detail page (future
// follow-up). Failed jobs stay visible until the operator clears them
// by hitting the API directly — surfacing the error inline is more
// useful than auto-hiding.

function ActiveJobsSection({ jobs, templateNameById, isMobile, t, fmtDate, wsSlug, onChanged }) {
  return (
    <div className="active-jobs-section">
      <div className="active-jobs-header">
        <div className="active-jobs-title">{t('renders.activeJobs', 'Active jobs')}</div>
        <div className="active-jobs-count">{jobs.length}</div>
      </div>
      {isMobile ? (
        <div className="active-jobs-cards">
          {jobs.map((j) => (
            <ActiveJobCard key={j.id} job={j} templateNameById={templateNameById} fmtDate={fmtDate} t={t} wsSlug={wsSlug} onChanged={onChanged} />
          ))}
        </div>
      ) : (
        <table className="table active-jobs-table">
          <thead>
            <tr>
              <th>{t('renders.col.template', 'Template')}</th>
              <th>{t('renders.col.status', 'Status')}</th>
              <th>{t('renders.col.progress', 'Progress')}</th>
              <th>{t('renders.col.started', 'Started')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <ActiveJobRow key={j.id} job={j} templateNameById={templateNameById} fmtDate={fmtDate} t={t} wsSlug={wsSlug} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function useJobRowActions(job, wsSlug, onChanged) {
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    const ok = await confirm({
      title: t('renders.job.cancelConfirm.title', 'Cancel this job?'),
      body: t('renders.job.cancelConfirm.body', 'The queued job stops before it runs. This cannot be undone.'),
      confirmLabel: t('renders.job.stop', 'Stop job'),
      cancelLabel: t('renders.job.keep', 'Keep job'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api.post(`/api/v1/${wsSlug}/jobs/${job.id}/cancel`); toast(t('renders.job.cancelled', 'Job cancelled'), 'success'); onChanged && onChanged(); }
    catch (e) { toast(/job_running/.test(e.message) ? t('renders.job.tooLateToCancel', 'This job has already started.') : e.message, 'error'); }
    finally { setBusy(false); }
  };
  const dismiss = async () => {
    setBusy(true);
    try { await api.del(`/api/v1/${wsSlug}/jobs/${job.id}`); onChanged && onChanged(); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return { busy, cancel, dismiss };
}

function JobRowActions({ job, wsSlug, onChanged }) {
  const t = useT();
  const { busy, cancel, dismiss } = useJobRowActions(job, wsSlug, onChanged);
  if (job.status === 'pending') {
    return <button className="btn btn-sm" onClick={cancel} disabled={busy}>{busy ? t('common.working', 'Working…') : t('renders.job.cancel', 'Cancel')}</button>;
  }
  if (job.status === 'failed') {
    return <button className="btn btn-sm" onClick={dismiss} disabled={busy}>{busy ? t('common.working', 'Working…') : t('renders.job.dismiss', 'Dismiss')}</button>;
  }
  return null;
}

function ActiveJobRow({ job, templateNameById, fmtDate, t, wsSlug, onChanged }) {
  const pct = job.itemsTotal > 0 ? Math.round((job.itemsDone / job.itemsTotal) * 100) : 0;
  return (
    <tr>
      <td>{templateNameById[job.templateId] || job.templateName || '—'}</td>
      <td>
        <span className={`status-dot status-${job.status}`} />
        {t(`renders.status.${job.status}`, job.status)}
        {job.error && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 4}}>{job.error}</div>}
      </td>
      <td>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 140}}>
          <div className="progress-bar"><div className="progress-bar-fill" style={{width: `${pct}%`}} /></div>
          <span style={{fontSize: 11, color: 'var(--text-muted)'}}>{job.itemsDone}/{job.itemsTotal}</span>
        </div>
      </td>
      <td style={{fontSize: 12, color: 'var(--text-secondary)'}}>{fmtDate(job.startedAt || job.createdAt)}</td>
      <td style={{textAlign: 'right'}}><JobRowActions job={job} wsSlug={wsSlug} onChanged={onChanged} /></td>
    </tr>
  );
}

function ActiveJobCard({ job, templateNameById, fmtDate, t, wsSlug, onChanged }) {
  const pct = job.itemsTotal > 0 ? Math.round((job.itemsDone / job.itemsTotal) * 100) : 0;
  return (
    <div className="active-job-card">
      <div className="active-job-card-row">
        <span className={`status-dot status-${job.status}`} />
        <span style={{fontWeight: 600, fontSize: 13}}>{t(`renders.status.${job.status}`, job.status)}</span>
        <span style={{marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)'}}>{job.itemsDone}/{job.itemsTotal}</span>
      </div>
      <div className="active-job-card-row" style={{fontSize: 13}}>
        {templateNameById[job.templateId] || job.templateName || '—'}
      </div>
      <div className="progress-bar"><div className="progress-bar-fill" style={{width: `${pct}%`}} /></div>
      {job.error && <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 6}}>{job.error}</div>}
      <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>{fmtDate(job.startedAt || job.createdAt)}</div>
      {(job.status === 'pending' || job.status === 'failed') && (
        <div style={{marginTop: 8, textAlign: 'right'}}><JobRowActions job={job} wsSlug={wsSlug} onChanged={onChanged} /></div>
      )}
    </div>
  );
}

// ─────────────────────────── Render table (desktop) ───────────────────────────

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th onClick={() => onSort(field)} className="sortable-header">
      <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
        {label}
        <span className="sort-arrow" aria-hidden="true">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}

function RenderTable({
  rows, selected, onToggle, onToggleAll, allSelected,
  sortField, sortDir, onSort, templateNameById,
  fmtBytes, fmtDate, typeLabel, t,
}) {
  return (
    <div className="renders-table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th style={{width: 32}}>
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} aria-label="Select all" />
            </th>
            <SortHeader label={t('renders.col.filename', 'Filename')} field="filename" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <th>{t('renders.col.template', 'Template')}</th>
            <th>{t('renders.col.type', 'Type')}</th>
            <SortHeader label={t('renders.col.size', 'Size')} field="bytes" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortHeader label={t('renders.col.created', 'Created')} field="createdAt" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortHeader label={t('renders.col.expires', 'Expires')} field="expiresAt" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RenderRow key={r.id}
                       row={r}
                       checked={selected.has(r.id)}
                       onToggle={onToggle}
                       templateNameById={templateNameById}
                       fmtBytes={fmtBytes}
                       fmtDate={fmtDate}
                       typeLabel={typeLabel}
                       t={t} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RenderRow({ row, checked, onToggle, templateNameById, fmtBytes, fmtDate, typeLabel, t }) {
  const { current: ws } = useWorkspace();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      const r = await api.get(`/api/v1/${ws.slug}/renders/${row.id}/url`);
      if (r.url) window.open(r.url, '_blank', 'noopener');
      else toast(t('renders.urlUnavailable', 'Download link unavailable. The file may have expired.'), 'error');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <tr>
      <td onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={() => onToggle(row.id)} aria-label="Select row" />
      </td>
      <td style={{fontWeight: 500}}>
        {row.filename}
        {row.testMode && <span className="pill pill-incomplete" style={{marginLeft: 6, fontSize: 10}}>TEST</span>}
      </td>
      <td style={{fontSize: 12, color: 'var(--text-secondary)'}}>
        {templateNameById[row.templateId] || '—'}
      </td>
      <td><span className="type-badge">{typeLabel(row.contentType)}</span></td>
      <td style={{fontSize: 12, color: 'var(--text-secondary)'}}>{fmtBytes(row.bytes)}</td>
      <td style={{fontSize: 12, color: 'var(--text-secondary)'}}>{fmtDate(row.createdAt)}</td>
      <td style={{fontSize: 12, color: 'var(--text-secondary)'}}>{row.expiresAt ? fmtDate(row.expiresAt) : t('renders.keptUntilDeleted', 'Until deleted')}</td>
      <td>
        <button className="btn btn-sm" onClick={open} disabled={busy}>
          {busy ? '…' : t('renders.download', 'Download')}
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────────── Render cards (mobile) ───────────────────────────

function RenderCards({ rows, selected, onToggle, templateNameById, fmtBytes, fmtDate, typeLabel, t }) {
  return (
    <div className="renders-card-list">
      {rows.map((r) => (
        <RenderCard key={r.id}
                    row={r}
                    checked={selected.has(r.id)}
                    onToggle={onToggle}
                    templateNameById={templateNameById}
                    fmtBytes={fmtBytes}
                    fmtDate={fmtDate}
                    typeLabel={typeLabel}
                    t={t} />
      ))}
    </div>
  );
}

function RenderCard({ row, checked, onToggle, templateNameById, fmtBytes, fmtDate, typeLabel, t }) {
  const { current: ws } = useWorkspace();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      const r = await api.get(`/api/v1/${ws.slug}/renders/${row.id}/url`);
      if (r.url) window.open(r.url, '_blank', 'noopener');
      else toast(t('renders.urlUnavailable', 'Download link unavailable. The file may have expired.'), 'error');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`render-card${checked ? ' is-selected' : ''}`}>
      <div className="render-card-head">
        <input type="checkbox" checked={checked} onChange={() => onToggle(row.id)} aria-label="Select" />
        <span className="render-card-filename">{row.filename}</span>
        {row.testMode && <span className="pill pill-incomplete" style={{fontSize: 10}}>TEST</span>}
        <span className="type-badge">{typeLabel(row.contentType)}</span>
      </div>
      <div className="render-card-meta">
        <span>{templateNameById[row.templateId] || '—'}</span>
        <span>·</span>
        <span>{fmtBytes(row.bytes)}</span>
      </div>
      <div className="render-card-dates">
        <span>{t('renders.col.created', 'Created')}: {fmtDate(row.createdAt)}</span>
        <span>{t('renders.col.expires', 'Expires')}: {row.expiresAt ? fmtDate(row.expiresAt) : t('renders.keptUntilDeleted', 'Until deleted')}</span>
      </div>
      <button className="btn btn-sm" onClick={open} disabled={busy} style={{marginTop: 10, width: '100%'}}>
        {busy ? '…' : t('renders.download', 'Download')}
      </button>
    </div>
  );
}

// ============================================================================
// Sign-requests dashboard — list every sign-request fired from the
// current workspace, with status, recipient, timing metadata, and
// a one-click download for signed PDFs. Mirrors the JobsPage shape
// (cursor-paginated, auto-refresh while anything is pending).
// ============================================================================

const SIGN_REQUEST_STATUSES = ['', 'pending', 'signed', 'declined', 'expired', 'revoked'];

function SignRequestsPage() {
  const { current: ws } = useWorkspace();
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  // Sign-request quota + balance overage rate. Fetched once on mount
  // from /billing/summary so the page can show "X of Y used" without
  // a dedicated endpoint. Refreshed on every successful list load so
  // the count tracks new requests fired since the page opened.
  const [quotaTile, setQuotaTile] = useState(null);
  // Row whose detail drawer (signers + audit trail) is open, or null.
  const [detailRow, setDetailRow] = useState(null);
  const toast = useToast();
  const t = useT();

  // Debounce the search input so each keystroke doesn't fire a fetch.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const loadedCountRef = useRef(0);
  useEffect(() => { loadedCountRef.current = rows.length; }, [rows.length]);
  // `silent`: the 15 s poll — refresh in place without the "Loading…"
  // swap (which jumped the page to the top and dropped "Load more" rows).
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!ws) return;
    if (!silent) setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (debouncedQ) qs.set('q', debouncedQ);
      qs.set('limit', String(silent ? Math.min(500, Math.max(50, loadedCountRef.current)) : 50));
      qs.set('offset', '0');
      const res = await api.get(`/api/v1/${ws.slug}/sign-requests?${qs.toString()}`);
      // v1 envelope: { signRequests, meta:{count,limit,offset,hasMore} }.
      // We use meta.hasMore to drive the loadMore button — no cursor needed.
      const rows = res.signRequests || [];
      setRows(rows);
      setNextCursor(res.meta?.hasMore ? rows.length : null);
    } catch (e) {
      // Tier-gated tenants (self-hosted non-enterprise) get 402 from
      // the API — swallow silently so the lock screen renders cleanly
      // instead of toast-spamming. Other errors still surface.
      // Match the error CODE: e.message is the translated sentence.
      if (!silent && !/license_upgrade_required|paid_tier_required|not_available_on_self_hosted/.test(e.code || e.message || '')) {
        toast(e.message, 'error');
      }
      if (!silent) setRows([]);
    }
    finally { if (!silent) setLoading(false); }
  }, [ws, status, debouncedQ, toast]);

  // Fetch quota usage once per page mount + after status changes so the
  // count stays in sync even when the operator narrows the filter. The
  // /billing/summary endpoint returns the full billing snapshot — we
  // only need `usage.signRequests*` and `balance.signRequestOverageCents`
  // for the tile, so we ignore the rest.
  // Only where billing exists: self-hosted has no /billing/summary (the
  // licence sets the quota there) and the tile stays hidden.
  const runtime = useRuntime();
  const tenantCtx = useTenant();
  const billingAvailable = !!runtime?.features?.billingPage;
  const refreshQuota = useCallback(async () => {
    if (!billingAvailable) return;
    try {
      const summary = await api.get('/billing/summary');
      const u = summary?.usage || {};
      setQuotaTile({
        used:           u.signRequests       ?? 0,
        cap:            u.signRequestsCap    ?? null,    // null → unlimited
        pct:            u.signRequestsPct    ?? 0,
        period:         u.period             || '',
        deploymentMode: summary?.deploymentMode || 'hosted',
        plan:           summary?.plan || 'free',
        overageCents:   summary?.balance?.signRequestOverageCents ?? null,
        balanceCents:   summary?.balance?.cents ?? 0,
      });
    } catch { /* tile is best-effort — a failure here doesn't break the list */ }
  }, [billingAvailable]);
  useEffect(() => { refreshQuota(); }, [refreshQuota]);

  const loadMore = useCallback(async () => {
    if (!ws || nextCursor == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (debouncedQ) qs.set('q', debouncedQ);
      qs.set('offset', String(nextCursor));
      qs.set('limit', '50');
      const res = await api.get(`/api/v1/${ws.slug}/sign-requests?${qs.toString()}`);
      const newRows = res.signRequests || [];
      setRows((prev) => [...prev, ...newRows]);
      setNextCursor(res.meta?.hasMore ? (Number(nextCursor) + newRows.length) : null);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoadingMore(false); }
  }, [ws, status, debouncedQ, nextCursor, loadingMore, toast]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any row is still pending — the recipient might
  // sign while the operator is watching. Stops once everything is
  // terminal so the dashboard doesn't hammer the API while idle.
  useEffect(() => {
    const hasPending = rows.some((r) => r.status === 'pending');
    if (!hasPending) return;
    const id = setInterval(() => { load({ silent: true }); refreshQuota(); }, 15000);
    return () => clearInterval(id);
  }, [rows, load, refreshQuota]);

  // Tier gate. Free hosted tenants get signRequestsCap === 0 from the
  // billing summary — the feature is paid-only there (the API returns
  // 402 paid_tier_required). Self-hosted is license-gated instead:
  // Enterprise (+ SMTP) gets the real page, lower tiers see the
  // license-upgrade wall (the API answers 402 license_upgrade_required /
  // 503 smtp_not_configured). Wait for the quota fetch AND the
  // initial list fetch to land before deciding — a tenant who
  // downgraded from a paid tier still has historical rows we want to
  // surface in read-only mode rather than showing them the upsell
  // wall (no way to access their own history otherwise).
  const isHostedFree = quotaTile && quotaTile.cap === 0 && quotaTile.deploymentMode === 'hosted';
  // Self-hosted: e-signatures work with an Enterprise license (+ SMTP on
  // the host) — only NON-enterprise self-hosted installs see the gate
  // page. Enterprise self-hosted falls through to the real page. Decided
  // from the runtime flags + the tenant's licence tier: self-hosted has
  // no /billing/summary, so quotaTile never arrives there.
  const isSelfHostedGated = !!runtime?.features?.signaturesRequireEnterpriseLicense
    && tenantCtx?.current?.plan !== 'enterprise';
  if (isHostedFree && !loading && rows.length === 0) {
    return <SignRequestsLockedPage />;
  }
  if (isSelfHostedGated && !loading && rows.length === 0) {
    return <SignRequestsSelfHostedPage />;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('signRequests.title', 'Sign requests')}</div>
          <div className="page-subtitle">
            {t('signRequests.subtitle',
              'Every signature request sent from this workspace. Select one to see details; download the signed PDF once all signers have signed.')}
          </div>
        </div>
      </div>

      {/* View-only banner. Fires when the tenant has historical rows
          BUT no longer has entitlement to send new requests — typically
          a paid → free downgrade on hosted, or a license downgrade on
          self-hosted. The list itself stays browsable so the operator
          can pull old signed PDFs; only creation is blocked. */}
      {(isHostedFree || isSelfHostedGated) && rows.length > 0 && (
        <div className="card" style={{
          borderColor: 'var(--warning)',
          background: 'rgba(255,165,2,0.08)',
          marginBottom: 16,
          padding: '10px 14px',
          fontSize: 13,
        }}>
          <strong style={{color: 'var(--warning)'}}>
            {isHostedFree
              ? t('signRequests.freeTitle', 'Test e-signatures on the Free plan')
              : t('signRequests.viewOnly.title', 'View-only mode')}
          </strong>
          <span style={{marginLeft: 8, color: 'var(--text-secondary)'}}>
            {isHostedFree
              ? t('signRequests.freeBanner',
                  'On the Free plan, requests are test requests: watermarked, not legally binding, signable for 24 h. Upgrade for real signatures.')
              : t('signRequests.viewOnly.bodySelfHosted',
                  'You can browse your past sign requests, but sending new ones requires an enterprise license.')}
          </span>
          {isHostedFree && (
            <a href="/app?nav=billing" style={{marginLeft: 10, color: 'var(--accent)', fontWeight: 600}}
               onClick={(e) => {
                 e.preventDefault();
                 window.dispatchEvent(new CustomEvent('pluma:navigate', { detail: { to: 'page', page: 'billing' } }));
               }}>
              {t('signRequests.viewOnly.upgrade', 'See plans →')}
            </a>
          )}
        </div>
      )}

      {/* Quota tile — surfaces the operator's monthly sign-request
          allowance + the per-overage cost. Hidden when the quota
          fetch hasn't returned yet (we don't want a flash of zero)
          and on the unlimited / self-hosted paths. */}
      {quotaTile && quotaTile.cap > 0 && (
        <SignRequestsQuotaTile data={quotaTile} t={t} />
      )}

      <div style={{display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap'}}>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}
                style={{maxWidth: 180}}>
          {SIGN_REQUEST_STATUSES.map((s) => (
            <option key={s || 'all'} value={s}>
              {s ? t(`signRequests.status.${s}`, s) : t('signRequests.allStatuses', 'All statuses')}
            </option>
          ))}
        </select>
        <input className="input" placeholder={t('signRequests.searchPlaceholder', 'Search recipient email…')}
               value={q} onChange={(e) => setQ(e.target.value)}
               style={{flex: 1, minWidth: 200}} />
      </div>

      {loading ? (
        <div className="empty">{t('common.loading', 'Loading…')}</div>
      ) : rows.length === 0 ? (
        <div className="empty">
          {status || debouncedQ
            ? t('signRequests.emptyFiltered', 'No sign requests match these filters.')
            : t('signRequests.empty', 'No signature requests yet. Open a rendered document on the Renders page and choose \u201cSend for signature\u201d to send one.')}
        </div>
      ) : (
        <>
          {/* The 7-column table overflows narrow viewports (anything
              under ~720px or so). Wrap in a horizontally-scrollable
              container so the page itself never grows wider than the
              device width; users swipe sideways to see the rest of
              the row. -webkit-overflow-scrolling smooths iOS Safari. */}
          <div className="table-scroll" style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
            <table className="table" style={{minWidth: 760}}>
              <thead><tr>
                <th>{t('signRequests.col.status', 'Status')}</th>
                <th>{t('signRequests.col.recipient', 'Recipient')}</th>
                <th>{t('signRequests.col.template', 'Template')}</th>
                <th>{t('signRequests.col.sent', 'Sent')}</th>
                <th>{t('signRequests.col.completed', 'Completed')}</th>
                <th>{t('signRequests.col.requestedBy', 'Requested by')}</th>
                <th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => <SignRequestRow key={r.id} row={r} wsSlug={ws.slug} onOpen={() => setDetailRow(r)} onChanged={load} />)}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div style={{marginTop: 16, textAlign: 'center'}}>
              <button className="btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? t('common.loading') : t('common.loadMore', 'Load more')}
              </button>
            </div>
          )}
        </>
      )}
      {detailRow && (
        <SignRequestDetailModal wsSlug={ws.slug} row={detailRow} onClose={() => setDetailRow(null)} />
      )}
    </>
  );
}

// SignRequestsQuotaTile — same visual language as the BillingPage
// UsageTile so an operator who has seen one immediately understands
// the other. Shown only on hosted paid plans (Free renders the locked
// page above; self-hosted hides this surface entirely). Pulls live
// data from /billing/summary so the cap stays in sync with whatever
// the operator's current plan is, not a hardcoded constant.
function SignRequestsQuotaTile({ data, t }) {
  const { used, cap, pct, period, overageCents, balanceCents } = data;
  const color = pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : '';
  return (
    <div className="card" style={{marginBottom: 20, padding: 16}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12}}>
        <div style={{flex: 1, minWidth: 220}}>
          <div className="label">{t('signRequests.quota.usedThisPeriod', 'Used this period')}</div>
          <div style={{fontSize: 24, fontWeight: 700, lineHeight: 1.1, marginTop: 2}}>
            {formatNum(used)} / {formatNum(cap)}
          </div>
          <div className="sub" style={{marginTop: 4}}>
            {t('signRequests.quota.includedInPlan', 'Included in your plan · {{pct}}% used', {
              pct: pct.toFixed(1),
            })}
          </div>
          <div className="usage-bar" style={{marginTop: 10}}>
            <div className={`usage-bar-fill ${color}`}
                 style={{width: `${Math.min(100, pct)}%`}} />
          </div>
        </div>
        <div style={{textAlign: 'right', minWidth: 180, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6}}>
          {period && (
            <div>{t('billing.period', { period })}</div>
          )}
          {overageCents && (
            <div>
              {/* Price arrives pre-formatted via Intl ("€0.10" en /
                  "0,10 €" de) — locale strings must never hard-code a
                  currency symbol or decimal separator. */}
              {t('signRequests.quota.overage', 'Beyond the quota: {{price}} per request, from your balance', { price: formatEur(overageCents) })}
            </div>
          )}
          {balanceCents != null && (
            <div>
              {t('signRequests.quota.balance', 'Balance: {{amount}}', {
                amount: formatEur(balanceCents),
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// SignRequestsLockedPage — Free hosted plans can't fire sign-requests
// (the API returns 402 paid_tier_required and the dashboard mirrors
// that). Render the gate as a clear value-prop + an Upgrade CTA that
// drops the operator straight at the billing page so they can pick a
// plan in one click.
function SignRequestsLockedPage() {
  const t = useT();
  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('signRequests.title', 'Sign requests')}</div>
        </div>
      </div>
      <div className="card" style={{padding: 32, textAlign: 'center', maxWidth: 560, margin: '40px auto'}}>
        <div className="empty-icon" aria-hidden="true"><Icon name="lock" size={44} /></div>
        <h2 style={{margin: '0 0 8px', fontSize: 22, fontWeight: 600}}>
          {t('signRequests.freeTitle', 'Test e-signatures on the Free plan')}
        </h2>
        <p style={{margin: '0 0 10px', color: 'var(--text-secondary)', lineHeight: 1.55, fontSize: 14}}>
          {t('signRequests.freeBanner', 'On the Free plan, requests are test requests: watermarked, not legally binding, signable for 24 h. Upgrade for real signatures.')}
          {' '}{t('signRequests.freeHow', 'Select a rendered PDF under Documents and choose “Send for signature” — it goes out as a test request.')}
        </p>
        <p style={{margin: '0 0 20px', color: 'var(--text-secondary)', lineHeight: 1.55, fontSize: 14}}>
          {t('signRequests.locked.body',
            'Send PDFs out for legally-binding signatures, track who has signed, and get the signed PDF back in your inbox. Available on Essentials and up — starting at 10 requests / month with overage at €0.10 each.')}
        </p>
        <button className="btn btn-primary" style={{padding: '10px 22px'}}
                onClick={() => window.dispatchEvent(new CustomEvent('pluma:navigate', {
                  detail: { to: 'page', page: 'billing' },
                }))}>
          {t('signRequests.locked.cta', 'See plans & upgrade')}
        </button>
      </div>
    </>
  );
}

// SignRequestsSelfHostedPage — shown ONLY on self-hosted installs whose
// license tier is below Enterprise. E-signatures DO work self-hosted with
// an Enterprise license (plus SMTP configured on the host, so invite and
// completion emails can go out) — the API answers 402
// license_upgrade_required below that tier, or 503 smtp_not_configured
// when the license is right but the host has no mailer. Mirror the
// upgrade path here instead of an empty table.
function SignRequestsSelfHostedPage() {
  const t = useT();
  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('signRequests.title', 'Sign requests')}</div>
        </div>
      </div>
      <div className="card" style={{padding: 32, textAlign: 'center', maxWidth: 560, margin: '40px auto'}}>
        <div className="empty-icon" aria-hidden="true"><Icon name="tag" size={44} /></div>
        <h2 style={{margin: '0 0 8px', fontSize: 22, fontWeight: 600}}>
          {t('signRequests.selfHosted.title', 'Sign requests need an Enterprise license')}
        </h2>
        <p style={{margin: '0 0 8px', color: 'var(--text-secondary)', lineHeight: 1.55, fontSize: 14}}>
          {t('signRequests.selfHosted.body',
            'On self-hosted installs, the e-signature workflow is part of the Enterprise license. Upgrade the license on this install, configure SMTP so recipient emails can go out, and this page unlocks. Alternatively, wire your own signature collection on top of the standard /generate API.')}
        </p>
      </div>
    </>
  );
}

function SignRequestRow({ row, wsSlug, onOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();

  const revoke = async () => {
    const ok = await confirm({
      title: t('signRequests.revokeConfirm.title', 'Revoke this request?'),
      body: t('signRequests.revokeConfirm.body', 'The signing link stops working immediately and no one can sign. This cannot be undone.'),
      confirmLabel: t('signRequests.revoke', 'Revoke request'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/api/v1/${wsSlug}/sign-requests/${row.id}/revoke`);
      toast(t('signRequests.revoked', 'Request revoked'), 'success');
      onChanged && onChanged();
    } catch (e) {
      const msg = /not_pending/.test(e.message)
        ? t('signRequests.revokeUnavailable', 'This request can no longer be revoked.')
        : e.message;
      toast(msg, 'error');
    } finally { setBusy(false); }
  };

  const copyId = () => {
    navigator.clipboard.writeText(row.id)
      .then(() => toast(t('common.copied', 'Copied'), 'success'))
      .catch(() => toast(t('common.copyFailed', 'Couldn\u2019t copy to clipboard'), 'error'));
  };

  // Signed documents are kept until deleted \u2014 this is where they go.
  // Pending requests are revoked first (the server answers 409).
  const remove = async () => {
    const ok = await confirm({
      title: t('signRequests.deleteConfirm.title', 'Delete this request?'),
      body: row.status === 'signed'
        ? t('signRequests.deleteConfirm.bodySigned', 'The signed PDF, the original and the audit trail are deleted for good. Download the signed PDF first if you still need it.')
        : t('signRequests.deleteConfirm.body', 'The request, its documents and its audit trail are deleted for good.'),
      confirmLabel: t('signRequests.delete', 'Delete request'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.del(`/api/v1/${wsSlug}/sign-requests/${row.id}`);
      toast(t('signRequests.deleted', 'Request deleted'), 'success');
      onChanged && onChanged();
    } catch (e) {
      toast(e.message, 'error');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/${wsSlug}/sign-requests/${row.id}/resend`);
      toast(t('signRequests.resent', 'Reminder email sent'), 'success');
    } catch (e) {
      const msg = /nobody_to_notify|not_pending/.test(e.message)
        ? t('signRequests.resendUnavailable', 'This request has no pending signer to remind.')
        : e.message;
      toast(msg, 'error');
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/api/v1/${wsSlug}/sign-requests/${row.id}/signed-pdf-url`);
      if (res.url) {
        // open in a new tab so the browser handles the download dialog
        window.open(res.url, '_blank', 'noopener');
      } else {
        toast(t('signRequests.downloadUnavailable', 'Signed PDF not available yet.'), 'error');
      }
    } catch (e) {
      // 425 = still processing — the re-render hasn't finished yet.
      // 409 = wrong status (declined / expired) — no PDF will ever exist.
      const msg = /still_processing/.test(e.message)
        ? t('signRequests.stillProcessing', 'Still processing — try again in a few seconds.')
        : e.message;
      toast(msg, 'error');
    } finally { setBusy(false); }
  };

  const requestedBy = row.requestedByName
    || row.requestedByApiKeyName && `${row.requestedByApiKeyName} (API key)`
    || '—';

  const completedAt = row.signedAt
    ? formatDateTime(row.signedAt)
    : row.declinedAt
      ? `${formatDateTime(row.declinedAt)} ${t('signRequests.declinedSuffix', '(declined)')}`
      : '—';

  return (
    <tr onClick={onOpen} style={{cursor: 'pointer'}} tabIndex={0} role="button"
        title={row.declineReason ? `${t('signRequests.detail.declineReason', 'Reason for declining')}: ${row.declineReason}` : undefined}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen(); } }}>
      <td>
        <SignRequestStatusPill status={row.status} />
        {row.testMode && <span className="pill pill-incomplete" style={{marginLeft: 6, fontSize: 10}}>TEST</span>}
      </td>
      <td>
        {row.recipientName && (
          <div style={{fontWeight: 600, fontSize: 13}}>{row.recipientName}</div>
        )}
        <code style={{fontSize: 12, color: row.recipientName ? 'var(--text-muted)' : undefined}}>
          {row.recipientEmail}
        </code>
      </td>
      <td>{row.templateName || <span style={{color: 'var(--text-muted)'}}>—</span>}</td>
      <td>{formatDateTime(row.createdAt)}</td>
      <td>{completedAt}</td>
      <td>{requestedBy}</td>
      <td style={{whiteSpace: 'nowrap'}} onClick={(e) => e.stopPropagation()}>
        <div style={{display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end'}}>
          {row.status === 'signed' && row.hasSignedPdf && !signedCopyGone(row) && (
            <button className="btn btn-sm" onClick={download} disabled={busy}>
              {busy ? t('common.loading') : t('signRequests.downloadPdf', 'Download PDF')}
            </button>
          )}
          <ActionMenu items={[
            { label: t('signRequests.copyId', 'Copy request ID'), onClick: copyId },
            ...(row.status === 'pending' ? [{ label: t('signRequests.resend', 'Resend email'), onClick: resend }] : []),
            ...(row.status === 'pending' ? [{ label: t('signRequests.revoke', 'Revoke request'), onClick: revoke, danger: true }] : []),
            ...(row.status !== 'pending' ? [{ label: t('signRequests.delete', 'Delete request'), onClick: remove, danger: true }] : []),
          ]} />
        </div>
      </td>
    </tr>
  );
}

function SignRequestStatusPill({ status }) {
  const t = useT();
  const label = t(`signRequests.status.${status}`, status);
  const tint = {
    pending:  { bg: 'rgba(245, 158, 11, 0.16)', fg: '#b25a00' },
    signed:   { bg: 'rgba(31, 138, 76, 0.18)',  fg: '#1f8a4c' },
    declined: { bg: 'rgba(192, 57, 43, 0.16)',  fg: '#c0392b' },
    expired:  { bg: 'rgba(148, 163, 184, 0.20)', fg: '#475569' },
    revoked:  { bg: 'rgba(120, 113, 108, 0.18)', fg: '#57534e' },
  }[status] || { bg: 'rgba(148, 163, 184, 0.20)', fg: '#475569' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      background: tint.bg, color: tint.fg, fontSize: 11, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>{label}</span>
  );
}

// SignRequestDetailModal — the audit view for a single sign request.
// Fetches GET /sign-requests/:id (which carries signers + auditLog +
// hashes — the list payload stays lean) and lays out three sections:
// request facts, per-signer progress, and the append-only event
// timeline. Everything here is read-only evidence; the only action is
// the signed-PDF download for completed requests.
function SignRequestDetailModal({ wsSlug, row, onClose }) {
  useModalDismiss(onClose);
  const t = useT();
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/api/v1/${wsSlug}/sign-requests/${row.id}`)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [wsSlug, row.id]);

  const d = detail || row; // list row fills the header while the detail loads
  const ts = (v) => (v ? formatDateTime(v) : null);

  const eventLabel = (name) =>
    t(`signRequests.audit.event.${name}`, {
      created: 'Request created',
      invited: 'Signer invited',
      viewed: 'Document viewed',
      verification_sent: 'Verification code sent',
      email_verified: 'Email verified',
      signed: 'Signed',
      declined: 'Declined',
      sealed: 'Digital seal applied',
      completed: 'All signatures complete',
    }[name] || name);

  const factRow = (label, value) => value ? (
    <div style={{display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.7}}>
      <span style={{color: 'var(--text-muted)', minWidth: 130, flexShrink: 0}}>{label}</span>
      <span style={{minWidth: 0}}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{maxWidth: 640, maxHeight: '85vh', overflowY: 'auto'}}>
        <div className="modal-title" style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
          {d.templateName || t('signRequests.detail.title', 'Sign request')}
          <SignRequestStatusPill status={d.status} />
          {d.testMode && <span className="pill pill-incomplete" style={{fontSize: 10}}>TEST</span>}
        </div>
        {d.testMode && (
          <div className="sign-test-note" style={{marginBottom: 12}}>
            {d.status !== 'signed' || !d.signedPdfExpiresAt
              ? t('signRequests.test.short', 'Test request – not legally binding.')
              : signedCopyGone(d)
                ? t('signRequests.test.gone', 'Test request – the signed copy was deleted after 24 h.')
                : t('signRequests.test.note', 'Test request – not legally binding. Signed copy deleted {{date}}.', { date: ts(d.signedPdfExpiresAt) })}
          </div>
        )}

        {err && <div className="form-error" style={{marginBottom: 12}}>{err}</div>}

        <div style={{marginBottom: 16}}>
          {factRow(t('signRequests.detail.requestId', 'Request ID'), <CopyValue value={d.id} />)}
          {factRow(t('signRequests.col.sent', 'Sent'), ts(d.createdAt))}
          {factRow(t('signRequests.detail.expires', 'Signable until'), d.status === 'pending' ? ts(d.expiresAt) : null)}
          {/* Signers can give a reason when declining; it was stored and
              returned by the API but never shown. */}
          {factRow(t('signRequests.detail.declineReason', 'Reason for declining'),
            d.status === 'declined' && d.declineReason ? `“${d.declineReason}”` : null)}
          {factRow(t('signRequests.detail.order', 'Signing order'),
            detail && (detail.signers || []).length > 1
              ? t(`renders.sign.order.${d.signingOrder || 'parallel'}`, d.signingOrder || 'parallel') : null)}
          {factRow(t('signRequests.detail.verification', 'Email verification'),
            d.requireEmailVerification ? t('signRequests.detail.verificationRequired', 'required (one-time code)') : null)}
          {factRow(t('signRequests.detail.certificate', 'Certificate page'),
            d.includeCertificate ? t('signRequests.detail.certificateEmbedded', 'embedded in the signed PDF') : null)}
          {factRow(t('signRequests.detail.originalSha', 'Original SHA-256'),
            detail?.originalPdfSha256 ? <CopyValue value={detail.originalPdfSha256} style={{fontSize: 11}} /> : null)}
          {factRow(t('signRequests.detail.signedSha', 'Signed SHA-256'),
            detail?.signedPdfSha256 ? (
              <>
                <CopyValue value={detail.signedPdfSha256} style={{fontSize: 11}} />
                <a href={`/sign/verify?sha256=${detail.signedPdfSha256}`} target="_blank" rel="noopener" style={{fontSize: 12, marginLeft: 8}}>
                  {t('signRequests.detail.verifyLink', 'Verify this document')}
                </a>
              </>
            ) : null)}
        </div>

        {!detail && !err && <div className="empty">{t('common.loading', 'Loading…')}</div>}

        {detail && (detail.signers || []).length > 0 && (
          <>
            <div className="label" style={{marginBottom: 6}}>{t('renders.sign.signers', 'Signers')}</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18}}>
              {detail.signers.map((sg) => (
                <div key={sg.signerIndex} className="card" style={{padding: '10px 12px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                    <span style={{fontWeight: 600, fontSize: 13}}>
                      {t('properties.signature.signerN', 'Signer {{n}}', { n: sg.signerIndex })}
                    </span>
                    <code style={{fontSize: 12}}>{sg.name ? `${sg.name} <${sg.email}>` : sg.email}</code>
                    <SignRequestStatusPill status={sg.status} />
                  </div>
                  <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.6}}>
                    {[
                      sg.invitedAt && `${t('signRequests.detail.invited', 'Invited')}: ${ts(sg.invitedAt)}`,
                      sg.viewedAt && `${t('signRequests.detail.viewed', 'Viewed')}: ${ts(sg.viewedAt)}`,
                      sg.emailVerifiedAt && `${t('signRequests.detail.emailVerified', 'Email verified')}: ${ts(sg.emailVerifiedAt)}`,
                      sg.signedAt && `${t('signRequests.detail.signed', 'Signed')}: ${ts(sg.signedAt)}${sg.signatureMethod ? ` (${sg.signatureMethod})` : ''}`,
                      sg.declinedAt && `${t('signRequests.detail.declined', 'Declined')}: ${ts(sg.declinedAt)}`,
                    ].filter(Boolean).join(' · ') || t('signRequests.detail.notInvitedYet', 'Not invited yet (waiting for their turn)')}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {detail && (
          <>
            <div className="label" style={{marginBottom: 6}}>{t('signRequests.detail.auditTrail', 'Audit trail')}</div>
            {(detail.auditLog || []).length === 0 ? (
              <div className="empty" style={{padding: 12}}>{t('signRequests.detail.noEvents', 'No events recorded.')}</div>
            ) : (
              <div style={{borderLeft: '2px solid var(--border)', paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8}}>
                {detail.auditLog.map((ev, i) => (
                  <div key={i} style={{fontSize: 12.5, lineHeight: 1.5}}>
                    <div style={{fontWeight: 600}}>{eventLabel(ev.event)}</div>
                    <div style={{color: 'var(--text-muted)'}}>
                      {ev.at ? formatDateTime(ev.at) : ''}
                      {ev.signerIndex ? ` · ${t('properties.signature.signerN', 'Signer {{n}}', { n: ev.signerIndex })}` : ''}
                      {ev.email ? ` · ${ev.email}` : ''}
                      {ev.ip && <> · IP <SensitiveIp value={ev.ip} /></>}
                      {ev.event === 'signed' && ev.consent && ev.consent.accepted ? ` · ${t('signRequests.audit.consentRecorded', 'Consent statement accepted')}` : ''}
                      {ev.event === 'sealed' && ev.subject ? ` · ${ev.subject}${ev.fingerprint ? ' · ' + String(ev.fingerprint).slice(0, 16) + '…' : ''}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16}}>
          {d.status === 'signed' && d.hasSignedPdf && !signedCopyGone(d) && (
            <button className="btn" onClick={async () => {
              try {
                const res = await api.get(`/api/v1/${wsSlug}/sign-requests/${d.id}/signed-pdf-url`);
                if (res.url) window.open(res.url, '_blank', 'noopener');
              } catch (e) { toast(e.message, 'error'); }
            }}>
              <Icon name="download" className="icon-inline" />{t('signRequests.downloadPdf', 'Download PDF')}
            </button>
          )}
          <button className="btn" onClick={onClose}>{t('common.close', 'Close')}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Billing page — plan + usage + invoices in one view
// ============================================================================

// EUR cents → locale-formatted currency ("€12.50" en / "12,50 €" de).
// Thin alias over formatEur so older call sites keep their name.
function eur(cents) {
  return formatEur(cents || 0);
}

// Consumer right of withdrawal (§ 356 (4)/(5) BGB): before the Stripe
// redirect the buyer must EXPRESSLY request immediate performance and
// acknowledge the early expiry — an active tick, not a sentence above the
// Pay button. The server refuses a checkout without it (400
// withdrawal_consent_required) and records the consent on the session.
function WithdrawalConsentModal({ pending, onConfirm, onClose, t }) {
  const [ticked, setTicked] = useState(false);
  useEffect(() => { setTicked(false); }, [pending]);
  if (!pending) return null;
  const termsHref = '/terms#withdrawal';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="withdrawal-consent-title" onClick={(e) => e.stopPropagation()} style={{maxWidth: 560}}>
        <div className="modal-title" id="withdrawal-consent-title">{t('checkout.consent.title', 'Before you continue to payment')}</div>
        <p style={{marginTop: 10, fontSize: 13, lineHeight: 1.5}}>
          {pending && pending.startsAt
            ? t('checkout.consent.ledeFrom', { date: formatDateOnly(new Date(pending.startsAt)) })
            : t('checkout.consent.lede', 'The service starts immediately after payment. Consumers in the EU have a fourteen-day right of withdrawal; the full instruction and the model form are in the Terms.')}{' '}
          <a href={termsHref} target="_blank" rel="noopener">{t('checkout.consent.terms', 'Withdrawal instruction (Terms, section 6)')}</a>
        </p>
        <label style={{display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, fontSize: 13.5, lineHeight: 1.5, cursor: 'pointer'}}>
          <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} style={{marginTop: 3, flex: '0 0 auto'}} />
          <span>{t('checkout.consent.text', 'I expressly request that the service begins before the end of the withdrawal period. I acknowledge that I lose my right of withdrawal once the service has been fully performed or the digital content has been provided, and that if I withdraw before that, I owe a proportionate amount for the part already provided.')}</span>
        </label>
        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18}}>
          <button className="btn" type="button" onClick={onClose}>{t('checkout.consent.cancel', 'Cancel')}</button>
          <button className="btn" type="button" disabled={!ticked} onClick={onConfirm}>{t('checkout.consent.continue', 'Continue to payment')}</button>
        </div>
      </div>
    </div>
  );
}

function BillingPage() {
  const { current: currentTenant } = useTenant() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Monthly vs. annual-prepay billing interval for the plan cards.
  const [interval, setIntervalChoice] = useState('monthly');
  const toast = useToast();
  const t = useT();
  // A checkout waits behind the withdrawal-consent modal: { kind, plan | amountCents }.
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get('/billing/summary');
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message || 'failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkout = async (plan) => {
    setBusy(true);
    try {
      const { url } = await api.post('/billing/checkout', { plan, interval, withdrawalConsent: true, withdrawalConsentVersion: 1 });
      window.location.href = url;
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  // Prepaid-balance top-up — one-off Stripe payment, amount in EUR cents.
  const topUp = async (amountCents) => {
    setBusy(true);
    try {
      const { url } = await api.post('/billing/balance/topup', { amountCents, withdrawalConsent: true, withdrawalConsentVersion: 1 });
      window.location.href = url;
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  const portal = async () => {
    setBusy(true);
    try {
      const { url } = await api.post('/billing/portal');
      window.location.href = url;
    } catch (err) {
      toast(err.message, 'error');
      setBusy(false);
    }
  };

  if (error) {
    return (
      <>
        <div className="page-header"><div className="page-title">{t('billing.title')}</div></div>
        <div className="card">
          <div style={{color: 'var(--danger)'}}>{t('billing.failedToLoad', { detail: error })}</div>
          <button className="btn" style={{marginTop: 12}} onClick={load}>{t('common.retry')}</button>
        </div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <div className="page-header"><div className="page-title">{t('billing.title')}</div></div>
        <LoadingFrame label={t('common.loading')} />
      </>
    );
  }

  const { plan, limits, subscription, usage, invoices, plans, customerPortalAvailable, stripeConfigured, balance, continuation } = data;
  const hasLiveSub = !!subscription && ['active', 'trialing', 'past_due', 'unpaid'].includes(subscription.status);
  // Only the owner manages payments (server: billing:write). Older servers
  // don't send the flag — treat as allowed, the server still decides.
  const canManage = data.canManageBilling !== false;
  // The subscription is set to end: a new plan can be chosen now and starts
  // on that date (the server makes it a trial until then).
  const continuing = !!continuation && hasLiveSub;
  // A plan already booked to follow it (changed or cancelled in the portal).
  const booked = continuing && continuation.booked ? continuation.booked : null;
  // Stripe takes a billing date at most one interval ahead: a monthly plan
  // that would start more than a month out can be booked from this date.
  const bookableFrom = continuing && continuation.bookableFrom ? continuation.bookableFrom[interval] : null;
  // Annual prepay is offered only when at least one paid plan has a yearly
  // Stripe Price configured for this tenant's deployment mode.
  const mode = data.deploymentMode || 'hosted';
  // Only the plans that exist for this deployment mode — Essentials is
  // hosted-only (listPlans() gives it self_hosted: null).
  const visiblePlans = plans.filter((p) => p[mode]);
  const annualAvailable = visiblePlans.some((p) => (p[mode] || {}).yearlyPriceCents > 0);
  const renderQuotaPct = usage.quota ? usage.quotaPct : 0;
  const usageColor = renderQuotaPct >= 90 ? 'danger' : renderQuotaPct >= 75 ? 'warn' : '';

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('billing.title')}</div>
          <div className="page-subtitle">
            {t('billing.currentPlan')}: <span className={`badge badge-${plan}`}>{t('plan.' + plan)}</span>
            {/* A running quota promotion (admin → Promotions): the limits
                below already include it; this says why they are higher. */}
            {(data?.promotions || []).map((p) => (
              <span key={p.id} className="pill"
                    style={{marginLeft: 8, background: 'var(--accent-glow)', color: 'var(--accent)', textTransform: 'none', letterSpacing: 0, fontWeight: 500}}>
                {t('billing.promoTitle', 'Promotion')}: {t('pricing.promoBadge', '+{{percent}} % {{caps}} until {{date}}', {
                  percent: p.percent,
                  caps: (p.keys || []).map((k) => t('pricing.promoCap.' + k, k)).join(', '),
                  date: new Date(p.endsAt).toLocaleDateString(typeof currentLocale === 'string' ? currentLocale : undefined, { dateStyle: 'medium' }),
                })}
              </span>
            ))}
            {subscription && (
              <span className={`pill pill-${subscription.status}`} style={{marginLeft: 8}}>
                {t('subscription.' + subscription.status)}
              </span>
            )}
            {subscription && subscription.cancelAtPeriodEnd && (
              <span className="pill" style={{marginLeft: 8, background: 'rgba(255,165,2,0.18)', color: 'var(--warning)'}}>
                {t('billing.cancellingPill', { date: formatDateOnly(new Date(subscription.currentPeriodEnd)) })}
              </span>
            )}
          </div>
        </div>
        <div style={{display: 'flex', gap: 8}}>
          {/* Self-hosted licences live in their own standalone portal
              (/licenses) — separate from the hosted tenant's billing.
              Open in a new tab so the operator doesn't lose their place
              in the SPA. Hosted-only — self-hosted installs don't issue
              licences, they consume one. */}
          {mode === 'hosted' && (
            <a className="btn" href="/licenses" target="_blank" rel="noopener">
              {t('billing.manageLicences', 'Manage licences')}
            </a>
          )}
          {customerPortalAvailable && (
            <button className="btn" onClick={portal} disabled={busy}>{t('billing.manageInStripe')}</button>
          )}
        </div>
      </div>

      {/* Loud, unmissable notice when the subscription is set to cancel —
          a quiet grey "cancels at period end" sub-line wasn't obvious. */}
      {subscription && subscription.cancelAtPeriodEnd && (
        <SubscriptionEndingNotice
          tenantId={currentTenant && currentTenant.id}
          endsAt={subscription.currentPeriodEnd}
          action={customerPortalAvailable
            ? <button className="btn btn-primary" onClick={portal} disabled={busy}>{t('billing.manageInStripe')}</button>
            : null}
        >
          {/* The new owner after a transfer, and members who aren't the
              owner, can't resume it in Stripe — they get their own text. */}
          {t(data.handover ? 'billing.cancelNoticeHandover' : booked ? 'billing.cancelNoticeBooked' : !canManage ? 'billing.cancelNoticeMember' : 'billing.cancelNotice', {
            plan: t('plan.' + plan),
            next: booked ? t('plan.' + booked.plan) : '',
            date: formatDateOnly(new Date(subscription.currentPeriodEnd)),
          })}
        </SubscriptionEndingNotice>
      )}

      <div className="grid-3" style={{marginBottom: 24}}>
        <UsageTile usage={usage} limits={limits} color={usageColor} />
        <TemplatesUsageTile usage={usage} />
        <ImagesUsageTile usage={usage} limits={limits} />
        {/* Sign-request allowance — hosted only. Self-hosted sign
            requests are license-gated (Enterprise + SMTP), not metered,
            so there's no allowance to chart there. */}
        {mode === 'hosted' && <SignRequestsUsageTile usage={usage} balance={balance} />}
        <SubscriptionTile subscription={subscription} plan={plan} stripeConfigured={stripeConfigured} />
        <BillingShortcutsTile invoices={invoices} />
      </div>

      {/* Prepaid balance — paid tiers only. Free is hard-capped on
          renders and locked out of sign-requests, so there is nothing a
          top-up could ever be spent on; the API 402s the attempt and we
          don't dangle the card. (A downgraded tenant's leftover balance
          reactivates when they upgrade again.) */}
      {balance && stripeConfigured && mode === 'hosted' && plan !== 'free' && (
        <BalanceCard balance={balance} usage={usage} onTopUp={(amountCents) => setPending({ kind: 'topup', amountCents })} disabled={busy || !canManage} />
      )}

      <div className="card-title" style={{marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <span>{t('billing.choosePlan')}</span>
        {annualAvailable && (
          <div className="interval-toggle">
            <button className={interval === 'monthly' ? 'active' : ''}
                    onClick={() => setIntervalChoice('monthly')}>{t('billing.monthly', 'Monthly')}</button>
            <button className={interval === 'yearly' ? 'active' : ''}
                    onClick={() => setIntervalChoice('yearly')}>{t('billing.annual', 'Annual')}</button>
          </div>
        )}
      </div>
      {!canManage && (
        <div className="card" style={{marginBottom: 12, fontSize: 13}}>{t('billing.ownerOnly', 'Only the organisation’s owner can change the plan or payment details.')}</div>
      )}
      {canManage && continuing && (
        <div className="card" style={{marginBottom: 12, fontSize: 13, borderColor: 'var(--accent)'}}>
          {booked
            ? t('billing.bookedNote', { next: t('plan.' + booked.plan), date: formatDateOnly(new Date(booked.startsAt)) })
            : t('billing.continuationNote', { date: formatDateOnly(new Date(continuation.startsAt)) })}
        </div>
      )}
      <WithdrawalConsentModal
        pending={pending}
        t={t}
        onClose={() => setPending(null)}
        onConfirm={() => {
          const p = pending;
          setPending(null);
          if (!p) return;
          if (p.kind === 'topup') topUp(p.amountCents); else checkout(p.plan);
        }}
      />
      <div className="plan-grid" style={{marginBottom: 24}}>
        {visiblePlans.map((p) => (
          <PlanCard
            key={p.key}
            data={p}
            deploymentMode={mode}
            interval={interval}
            // Continuing (the subscription is set to end): every paid plan,
            // the current one included, can be booked to start on the end
            // date; Free simply applies then. No card is "current" for that.
            current={!(continuing && canManage) && p.key === plan}
            // With a live subscription every change goes through the
            // Stripe portal: /billing/checkout answers 409
            // already_subscribed, and the Free card's button used to do
            // nothing at all.
            onSelect={!canManage
              ? null
              : booked
              ? (p.key === booked.plan || !customerPortalAvailable ? null : portal)
              : continuing
              ? (p.key === 'free' || bookableFrom ? null : () => setPending({ kind: 'checkout', plan: p.key, startsAt: continuation.startsAt }))
              : hasLiveSub
              ? portal
              : p.key === 'free'
                ? (customerPortalAvailable ? portal : null)
                : (p.key === 'enterprise' && (p[mode] || p.hosted || {}).monthlyPriceCents == null)
                  ? null
                  : () => setPending({ kind: 'checkout', plan: p.key })}
            actionLabel={continuing && canManage && booked
              ? (p.key === booked.plan
                  ? t('billing.bookedFrom', { date: formatDateOnly(new Date(booked.startsAt)) })
                  : p.key === 'free' ? null : t('billing.switchPlan', 'Switch to this plan'))
              : continuing && canManage
              ? (p.key === 'free'
                  ? t('billing.freeAutomatic', { date: formatDateOnly(new Date(continuation.startsAt)) })
                  : bookableFrom ? t('billing.bookableFrom', { date: formatDateOnly(new Date(bookableFrom)) })
                  : p.key === plan ? t('billing.continuePlan', 'Continue with this plan') : t('billing.chooseThisPlan', 'Choose this plan'))
              : hasLiveSub
              ? (PLAN_ORDER.indexOf(p.key) < PLAN_ORDER.indexOf(plan)
                  ? t('billing.downgrade', 'Downgrade')
                  : t('billing.switchPlan', 'Switch to this plan'))
              : null}
            disabled={busy}
          />
        ))}
      </div>

      <div className="card-title" style={{marginBottom: 12}}>{t('billing.invoices')}</div>
      <div className="card" style={{padding: 0, overflowX: 'auto'}}>
        {!stripeConfigured && (
          <div className="empty">{t('billing.stripeNotConfigured')}</div>
        )}
        {stripeConfigured && invoices.length === 0 && (
          <div className="empty">{t('billing.noInvoices')}</div>
        )}
        {invoices.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>{t('billing.invoiceNumber')}</th>
                <th>{t('billing.invoiceStatus')}</th>
                <th>{t('billing.invoiceAmount')}</th>
                <th>{t('billing.invoicePeriod')}</th>
                <th>{t('billing.invoiceDate')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={{fontFamily: 'var(--font-mono)', fontSize: 12}}>{inv.number || inv.id.slice(0, 12)}</td>
                  <td><span className={`pill pill-${inv.status}`}>{t('invoice.status.' + inv.status, inv.status)}</span></td>
                  <td>{formatMoney(inv.amountDue, inv.currency)}</td>
                  <td>{formatPeriod(inv.periodStart, inv.periodEnd)}</td>
                  <td>{formatDate(inv.created)}</td>
                  <td style={{textAlign: 'right'}}>
                    {inv.hostedInvoiceUrl && <a className="btn" href={inv.hostedInvoiceUrl} target="_blank" rel="noopener">{t('billing.invoiceView')}</a>}
                    {inv.invoicePdf && <a className="btn" href={inv.invoicePdf} target="_blank" rel="noopener" style={{marginLeft: 6}}>PDF</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function UsageTile({ usage, limits, color }) {
  const t = useT();
  const isUnlimited = !usage.quota;
  return (
    <div className="stat-tile">
      <div className="label">{t('billing.rendersThisMonth')}</div>
      <div className="value">{formatNum(usage.renders)}</div>
      <div className="sub">
        {isUnlimited
          ? t('billing.unlimitedOnPlan')
          // "of 10 (0.0%)" read like noise — show the share only once
          // there is one, as a whole percent.
          : (Math.round(usage.quotaPct) >= 1
            ? t('billing.ofQuota', { quota: formatNum(usage.quota), pct: String(Math.round(usage.quotaPct)) })
            : t('billing.ofQuotaPlain', 'of {{quota}}', { quota: formatNum(usage.quota) }))}
      </div>
      {!isUnlimited && (
        <div className="usage-bar">
          <div
            className={`usage-bar-fill ${color}`}
            style={{width: `${Math.min(100, usage.quotaPct)}%`}}
          />
        </div>
      )}
      <div className="sub" style={{marginTop: 8}}>{t('billing.period', { period: formatUsagePeriod(usage.period) })}</div>
    </div>
  );
}

// Templates count vs cap — same visual language as UsageTile. The cap is
// per-TENANT total (counted across all workspaces); see plan-limits.js.
// cap = null on Enterprise → "Unlimited" with no progress bar.
function TemplatesUsageTile({ usage }) {
  const t = useT();
  const count = usage?.templatesCount ?? 0;
  const cap = usage?.templatesCap;            // null = unlimited
  const pct = usage?.templatesPct ?? 0;
  const isUnlimited = cap == null;
  const color = pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : '';
  return (
    <div className="stat-tile">
      <div className="label">{t('billing.templatesTotal', 'Templates')}</div>
      <div className="value">{formatNum(count)}</div>
      <div className="sub">
        {isUnlimited
          ? t('billing.unlimitedOnPlan')
          : t('billing.ofTemplateCap', { cap: formatNum(cap), pct: pct.toFixed(1) })}
      </div>
      {!isUnlimited && (
        <div className="usage-bar">
          <div
            className={`usage-bar-fill ${color}`}
            style={{width: `${Math.min(100, pct)}%`}}
          />
        </div>
      )}
      <div className="sub" style={{marginTop: 8}}>{t('billing.templatesTenantWide', 'Across all workspaces')}</div>
    </div>
  );
}

// Sign-request count vs cap. Hosted only — Free shows a "Paid plans
// only" state with an upgrade CTA (cap === 0); paid tiers show a
// progress bar; operator-unlimited (null) shows "Unlimited". The cents
// rate for overage is sourced from the balance payload so the copy
// stays in sync with config.balance.signRequestOverageCents.
function SignRequestsUsageTile({ usage, balance }) {
  const t = useT();
  const count = usage?.signRequests ?? 0;
  const cap = usage?.signRequestsCap;
  const pct = usage?.signRequestsPct ?? 0;
  const isUnlimited = cap == null;
  const isPaidOnly = cap === 0;
  const color = pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : '';
  const overageCents = balance?.signRequestOverageCents != null
    ? balance.signRequestOverageCents
    : null;
  return (
    <div className="stat-tile">
      <div className="label">{t('billing.signRequestsThisMonth', 'Sign-requests this month')}</div>
      <div className="value">{formatNum(count)}</div>
      <div className="sub">
        {isUnlimited
          ? t('billing.unlimitedOnPlan')
          : isPaidOnly
            ? t('billing.signRequestsPaidOnly', 'Available on Essentials and up')
            : t('billing.ofSignRequestCap', { cap: formatNum(cap), pct: pct.toFixed(1) }, '{{cap}} included · {{pct}}% used')}
      </div>
      {!isUnlimited && !isPaidOnly && (
        <div className="usage-bar">
          <div className={`usage-bar-fill ${color}`}
               style={{width: `${Math.min(100, pct)}%`}} />
        </div>
      )}
      {!isUnlimited && !isPaidOnly && overageCents && (
        <div className="sub" style={{marginTop: 8}}>
          {/* Price pre-formatted via Intl ("€0.10" / "0,10 €"). */}
          {t('billing.signRequestsOverage', { price: formatEur(overageCents) },
            'Beyond the included quota: {{price}} per request, deducted from your prepaid balance')}
        </div>
      )}
    </div>
  );
}

// Image-library count vs cap + per-upload size budget. Mirrors
// TemplatesUsageTile — tenant-total count, null cap means unlimited
// (self-hosted), progress bar hidden in that case. The size cap line
// (limits.maxImageBytes) sits underneath so the operator sees both
// dimensions of the quota at a glance.
function ImagesUsageTile({ usage, limits }) {
  const t = useT();
  const count = usage?.imagesCount ?? 0;
  const cap = usage?.imagesCap;             // null = unlimited
  const pct = usage?.imagesPct ?? 0;
  const isUnlimited = cap == null;
  const color = pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : '';
  const sizeMB = limits?.maxImageBytes != null
    ? (limits.maxImageBytes / 1024 / 1024).toFixed(0)
    : null;
  return (
    <div className="stat-tile">
      <div className="label">{t('billing.imagesTotal', 'Images')}</div>
      <div className="value">{formatNum(count)}</div>
      <div className="sub">
        {isUnlimited
          ? t('billing.unlimitedOnPlan')
          : t('billing.ofImageCap', { cap: formatNum(cap), pct: pct.toFixed(1) })}
      </div>
      {!isUnlimited && (
        <div className="usage-bar">
          <div
            className={`usage-bar-fill ${color}`}
            style={{width: `${Math.min(100, pct)}%`}}
          />
        </div>
      )}
      <div className="sub" style={{marginTop: 8}}>
        {sizeMB
          ? t('billing.imagesSizeCap', { mb: sizeMB })
          : t('billing.imagesUnlimitedSize', 'Any size per image')}
      </div>
    </div>
  );
}

function SubscriptionTile({ subscription, plan, stripeConfigured }) {
  const t = useT();
  if (!subscription && plan === 'free') {
    return (
      <div className="stat-tile">
        <div className="label">{t('billing.subscription')}</div>
        <div className="value" style={{fontSize: 18}}>{t('billing.freePlan')}</div>
        <div className="sub">{t('billing.upgradeToUnlock')}</div>
      </div>
    );
  }
  if (!subscription) {
    return (
      <div className="stat-tile">
        <div className="label">{t('billing.subscription')}</div>
        <div className="value" style={{fontSize: 18}}>—</div>
        <div className="sub">{stripeConfigured ? t('billing.noActiveSubscription') : t('billing.stripeNotConfigured')}</div>
      </div>
    );
  }
  const cancelling = subscription.cancelAtPeriodEnd;
  return (
    <div className="stat-tile" style={cancelling
      ? { borderColor: 'var(--warning)', background: 'rgba(255,165,2,0.07)' } : {}}>
      <div className="label">{cancelling ? t('billing.cancelsOn') : t('billing.renewsOn')}</div>
      <div className="value" style={{fontSize: 22, color: cancelling ? 'var(--warning)' : undefined}}>
        {formatDateOnly(new Date(subscription.currentPeriodEnd))}
      </div>
      <div className="sub" style={cancelling ? { color: 'var(--warning)', fontWeight: 600 } : {}}>
        {cancelling ? t('billing.cancelsAtPeriodEnd') : t('billing.renewsAutomatically')}
      </div>
    </div>
  );
}

function BillingShortcutsTile({ invoices }) {
  const t = useT();
  const lastInvoice = invoices[0];
  return (
    <div className="stat-tile">
      <div className="label">{t('billing.latestInvoice')}</div>
      {lastInvoice ? (
        <>
          <div className="value" style={{fontSize: 22}}>{formatMoney(lastInvoice.amountDue, lastInvoice.currency)}</div>
          <div className="sub">
            <span className={`pill pill-${lastInvoice.status}`}>{lastInvoice.status}</span>
            {' · '}{formatDate(lastInvoice.created)}
          </div>
          {lastInvoice.invoicePdf && (
            <a className="btn" href={lastInvoice.invoicePdf} target="_blank" rel="noopener" style={{marginTop: 12}}>
              {t('billing.downloadPdf')}
            </a>
          )}
        </>
      ) : (
        <>
          <div className="value" style={{fontSize: 18}}>—</div>
          <div className="sub">{t('billing.noInvoices')}</div>
        </>
      )}
    </div>
  );
}

// Prepaid render-balance panel — current balance, top-up buttons, history.
// Shown only for hosted tenants with Stripe configured.
function BalanceCard({ balance, usage, onTopUp, disabled }) {
  const t = useT();
  const [custom, setCustom] = useState('');
  // Per-render + per-sign-request overage rates, pre-formatted via Intl
  // so the symbol position and decimal separator follow the locale.
  const perRender = formatEur(balance.overageCentsPerRender ?? 2);
  const perSignRequest = formatEur(balance.signRequestOverageCents ?? 10);
  const overageActive = usage.quota != null && usage.renders >= usage.quota;
  const customEuros = parseFloat(custom);
  const submitCustom = () => {
    if (customEuros > 0) onTopUp(Math.round(customEuros * 100));
  };
  return (
    <div className="card" style={{marginBottom: 24}}>
      <div className="card-title">{t('billing.balance.title', 'Prepaid balance')}</div>
      <p className="sub" style={{marginTop: 0}}>
        {t('billing.balance.help',
          'Keeps renders and e-signature requests flowing past your monthly quota: extra renders cost {{perRender}} each, extra sign-requests {{perSignRequest}} each, deducted from this balance. On the Free plan, sign-requests can’t be covered by balance — upgrading is required.',
          { perRender, perSignRequest })}
      </p>
      <div style={{display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16, flexWrap: 'wrap'}}>
        <div style={{fontSize: 32, fontWeight: 700}}>{eur(balance.cents)}</div>
        {overageActive && balance.cents > 0 && (
          <span className="pill pill-active">{t('billing.balance.coveringPill', 'covering overage')}</span>
        )}
      </div>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4}}>
        {balance.topupPresetsCents.map((c) => (
          <button key={c} className="btn" disabled={disabled} onClick={() => onTopUp(c)}>+ {eur(c)}</button>
        ))}
        <div style={{display: 'flex', gap: 6}}>
          <input className="input" type="number" min="5" step="1"
                 placeholder={t('billing.balance.customPlaceholder', 'Custom €')}
                 value={custom} onChange={(e) => setCustom(e.target.value)} style={{width: 120}} />
          <button className="btn btn-primary" disabled={disabled || !(customEuros > 0)} onClick={submitCustom}>
            {t('billing.balance.topUpCta', 'Top up')}
          </button>
        </div>
      </div>
      {balance.transactions.length > 0 && (
        <details style={{marginTop: 14}}>
          <summary className="sub" style={{cursor: 'pointer'}}>{t('billing.balance.recentActivity', 'Recent activity')}</summary>
          <table className="table" style={{marginTop: 8}}>
            <tbody>
              {balance.transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{
                    tx.kind === 'topup' ? t('billing.balance.kind.topup', 'Top-up')
                    : tx.kind === 'sign_request_overage' ? t('billing.balance.kind.signRequestOverage', 'Sign-request overage')
                    : t('billing.balance.kind.renderOverage', 'Render overage')
                  }</td>
                  <td style={{color: tx.amountCents < 0 ? 'var(--danger)' : 'var(--accent)', fontWeight: 600}}>
                    {tx.amountCents < 0 ? '−' : '+'}{eur(Math.abs(tx.amountCents))}
                  </td>
                  <td className="sub">{eur(tx.balanceAfter)}</td>
                  <td className="sub">{formatDate(tx.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function PlanCard({ data, deploymentMode, interval = 'monthly', current, onSelect, actionLabel, disabled }) {
  const t = useT();
  // Sales contact address — operator-configured via PUBLIC_SALES_EMAIL
  // (falls back to PUBLIC_CONTACT_EMAIL). When neither is set we hide
  // the Enterprise "Contact sales" CTA rather than ship a broken mailto:.
  const runtime = useRuntime();
  const salesEmail = (runtime.emails && runtime.emails.sales) || '';
  // listPlans() returns each tier nested by deployment mode:
  //   { key, label, hosted: {monthlyPriceCents, yearlyPriceCents, renders, ...},
  //                 self_hosted: {...} }
  // Pick the block that matches this tenant's deployment, with a hosted
  // fallback so a missing/unknown mode doesn't crash the render.
  const tier = data[deploymentMode] || data.hosted || {};
  const bullets = buildPlanBullets(tier, t, deploymentMode);
  // Show the yearly price only when annual is selected AND this tier has one.
  const showYearly = interval === 'yearly' && tier.yearlyPriceCents != null && tier.yearlyPriceCents > 0;
  const priceCents = showYearly ? tier.yearlyPriceCents : tier.monthlyPriceCents;
  // Whole-percent saving of annual vs. 12× monthly.
  const savePct = (showYearly && tier.monthlyPriceCents > 0)
    ? Math.round((1 - tier.yearlyPriceCents / (tier.monthlyPriceCents * 12)) * 100)
    : 0;
  return (
    <div className={`plan-card ${current ? 'current' : ''}`}>
      <div className="plan-name">{t('plan.' + data.key)}</div>
      <div className="plan-price">{
        priceCents == null ? t('billing.planPrice.custom', 'Custom')
        : priceCents === 0 ? t('billing.planPrice.free', 'Free')
        : formatEur(priceCents, { whole: true })
      }</div>
      {priceCents != null && priceCents > 0 && (
        <div className="plan-price-sub">
          {showYearly ? t('billing.perYear', '/year') : t('billing.perMonth')}
          {savePct > 0 && <span className="pill pill-active" style={{marginLeft: 6, fontSize: 11}}>−{savePct}%</span>}
        </div>
      )}
      <ul>
        {bullets.map((f) => <li key={f}>{f}</li>)}
      </ul>
      {current ? (
        <button className="btn" disabled>{t('billing.currentPlanLabel')}</button>
      ) : (data.key === 'enterprise' && priceCents == null) ? (
        // Sales-driven enterprise (self-hosted only — per-deal license,
        // no fixed price). Hosted enterprise has a published price and
        // falls through to the normal Upgrade button below.
        salesEmail
          ? <a className="btn btn-primary" href={`mailto:${salesEmail}`} style={{justifyContent: 'center'}}>{t('billing.contactSales')}</a>
          : <button className="btn" disabled style={{justifyContent: 'center'}}>{t('billing.contactSales')}</button>
      ) : (
        <button className={'btn ' + (data.key === 'free' ? '' : 'btn-primary')} onClick={onSelect || undefined} disabled={disabled || !onSelect}>
          {actionLabel || (data.key === 'free' ? t('billing.downgradeInStripe') : t('billing.upgrade'))}
        </button>
      )}
    </div>
  );
}

// Translate the limits matrix into human-readable bullets — the same rows
// the public pricing cards list (web-marketing/src/pages/Pricing.jsx,
// featuresFor), so the in-app cards never show less than the website.
// Missing values (null) become "Unlimited"; numbers go through Intl. The
// renders bullet is omitted on plans that don't meter renders
// (self-hosted: renders=null).
function buildPlanBullets(tier, t, deploymentMode = 'hosted') {
  const out = [];
  if (tier.renders != null) {
    out.push(t('billing.bullet.rendersPerMonth', { n: formatNum(tier.renders) }));
  } else if (tier.renders === null) {
    out.push(t('billing.bullet.unmeteredRenders'));
  }
  out.push(tier.templates == null
    ? t('billing.bullet.unlimitedTemplates')
    : t('billing.bullet.templates', { n: formatNum(tier.templates) }));
  out.push(tier.users == null
    ? t('billing.bullet.unlimitedUsers')
    : tier.users === 1
      ? t('billing.bullet.userOne', '1 user')
      : t('billing.bullet.users', { n: formatNum(tier.users) }));
  out.push(tier.workspaces == null
    ? t('billing.bullet.unlimitedWorkspaces')
    : tier.workspaces === 1
      ? t('billing.bullet.workspaceOne', '1 workspace')
      : t('billing.bullet.workspaces', { n: formatNum(tier.workspaces) }));
  // Admin cap — only meaningful on self-hosted (hosted lifts it on every
  // tier, so the bullet would be noise there). Same rule as the website.
  if (deploymentMode === 'self_hosted') {
    out.push(tier.admins == null
      ? t('billing.bullet.unlimitedAdmins', 'Unlimited admins')
      : tier.admins === 1
        ? t('billing.bullet.adminOne', '1 admin')
        : t('billing.bullet.admins', { n: formatNum(tier.admins) }));
  }
  // Image library — surface both dimensions of the cap (count + per-upload
  // size) on a single bullet so customers can see what they're getting at
  // a glance. Hidden entirely when both are null (self-hosted unlimited).
  if (tier.images != null || tier.maxImageBytes != null) {
    const countPart = tier.images == null
      ? t('billing.bullet.unlimitedImages')
      : t('billing.bullet.images', { n: formatNum(tier.images) });
    if (tier.maxImageBytes != null) {
      const mb = (tier.maxImageBytes / 1024 / 1024).toFixed(0);
      out.push(t('billing.bullet.imagesWithSize', { n: countPart, mb }));
    } else {
      out.push(countPart);
    }
  }
  // Per-render hard caps — the builder answers HTTP 413 above them
  // (plan-limits.js maxPdfBytes / maxPdfPages; the page cap is Free-only).
  out.push(tier.maxPdfBytes == null
    ? t('billing.bullet.pdfSizeUnlimited', 'No PDF size cap')
    : t('billing.bullet.pdfSize', { mb: Math.round(tier.maxPdfBytes / 1024 / 1024) }));
  out.push(tier.maxPdfPages == null
    ? t('billing.bullet.pagesUnlimited', 'No page-count cap')
    : t('billing.bullet.pages', { pages: formatNum(tier.maxPdfPages) }));
  // Rendered-PDF retention: Free keeps PDFs 30 min; paid plans have no
  // cap (kept until deleted unless the account sets its own limit).
  if (tier.maxRenderTtlSeconds != null) {
    out.push(t('billing.bullet.renderRetention', { duration: formatDuration(tier.maxRenderTtlSeconds, t) }));
  } else if (tier.permanentRenders) {
    out.push(t('billing.bullet.unlimitedRetention', 'Unlimited PDF retention'));
  }
  // E-signature allowance. Hosted paid tiers carry a monthly number; Free
  // (0) lists nothing, and self-hosted has no quota at all there (the
  // feature is licence-gated and unmetered).
  if (tier.signRequests > 0) {
    out.push(t('billing.bullet.signRequests', { n: formatNum(tier.signRequests) }));
  } else if (tier.signRequests === null && deploymentMode === 'hosted') {
    out.push(t('billing.bullet.signRequestsUnlimited', 'Unlimited e-signature requests / month'));
  }
  // Feature flags. Custom fonts is what Enterprise unlocks beyond the
  // quotas; Publish and S3-backed storage are self-hosted licence features
  // (on hosted we run our own storage and backups — no per-customer perk).
  if (tier.features?.customFonts) out.push(t('billing.bullet.customFonts', 'Custom fonts (TTF/OTF uploads)'));
  if (deploymentMode === 'self_hosted') {
    if (tier.features?.publish) out.push(t('billing.bullet.publish', 'Publish as renderer image'));
    if (tier.features?.s3Backup) out.push(t('billing.bullet.s3Backup'));
  }
  return out;
}

// Small formatting helpers — kept inline because they're billing-specific.
// Pluma bills exclusively in EUR; the Stripe invoice currency is still
// forwarded so a legacy non-EUR invoice (e.g. test-mode) renders honestly,
// but everything we issue today is EUR.
// "2026-10" → "October 2026" in the UI language.
function formatUsagePeriod(period) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  if (!m) return period || '—';
  try {
    return new Intl.DateTimeFormat(currentLocale, { month: 'long', year: 'numeric' })
      .format(new Date(Number(m[1]), Number(m[2]) - 1, 1));
  } catch (_) { return period; }
}

function formatMoney(cents, currency) {
  if (cents == null) return '—';
  const cur = (currency || 'eur').toUpperCase();
  try {
    return new Intl.NumberFormat(currentLocale, { style: 'currency', currency: cur }).format(cents / 100);
  } catch (_) {
    // Unknown currency code from a legacy invoice — show it neutrally.
    return `${cur} ${(cents / 100).toFixed(2)}`;
  }
}
function formatPlanPrice(cents) {
  if (cents == null) return 'Custom';
  if (cents === 0) return 'Free';
  return formatEur(cents, { whole: true });
}
function formatNum(n) {
  return new Intl.NumberFormat(currentLocale).format(n);
}
// A retention window in words, in the UI language: "30 min", "1 hour",
// "1 week", "90 days" — the largest whole unit. Billing bullets and the
// Settings retention picker.
function formatDuration(seconds, t) {
  const n = (unit) => Math.round(seconds / unit);
  if (seconds < 3600) return t('duration.minutes', '{{n}} min', { n: n(60) });
  if (seconds < 86400) return n(3600) === 1 ? t('duration.hour', '1 hour') : t('duration.hours', '{{n}} hours', { n: n(3600) });
  if (seconds === 7 * 86400) return t('duration.week', '1 week');
  return n(86400) === 1 ? t('duration.day', '1 day') : t('duration.days', '{{n}} days', { n: n(86400) });
}
// Date/time formatting — locale-aware via the app's currentLocale (the
// same mirror formatEur/formatNum use), NOT the browser default. Uses
// Intl dateStyle so each locale gets its own convention automatically
// (German → 01.07.2026, English → Jul 1, 2026). formatDate = date only;
// formatDateTime = date + short time; both accept a Date, an ISO string,
// or a unix-seconds number.
function toDate(input) {
  if (input == null || input === '') return null;
  const d = typeof input === 'number' ? new Date(input * 1000) : new Date(input);
  return isNaN(d.getTime()) ? null : d;
}
function formatDate(input) {
  const d = toDate(input);
  if (!d) return '—';
  return new Intl.DateTimeFormat(currentLocale, { dateStyle: 'medium' }).format(d);
}
function formatDateTime(input) {
  const d = toDate(input);
  if (!d) return '—';
  return new Intl.DateTimeFormat(currentLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}
function formatDateOnly(d) {
  const dd = toDate(d);
  if (!dd) return '—';
  return new Intl.DateTimeFormat(currentLocale, { dateStyle: 'medium' }).format(dd);
}
function formatPeriod(startUnix, endUnix) {
  if (!startUnix || !endUnix) return '—';
  return `${formatDate(startUnix)} – ${formatDate(endUnix)}`;
}

// ============================================================================
// Error boundary
// ============================================================================
// Without one, any exception thrown while rendering unmounts the whole
// React tree and the user is left staring at a blank page. Class
// components can't use hooks, so copy is read straight from the module-
// scope catalog mirror (kept in sync by I18nProvider).
// `resetKey`: when it changes (AppShell passes the active page) a
// crashed section clears itself, so navigating away recovers.
function tStatic(key, fallback) {
  const s = currentCatalog && currentCatalog[key];
  return s != null ? s : fallback;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[pluma] UI error:', error, info && info.componentStack);
  }
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { fullPage } = this.props;
    return (
      <div className={fullPage ? 'auth-page' : 'crash-wrap'}>
        <div className="crash-card" role="alert">
          <div className="crash-icon" aria-hidden="true">!</div>
          <h2>{tStatic('crash.title', 'Something went wrong')}</h2>
          <p>{tStatic('crash.body', 'This screen hit an unexpected error. Changes that were already saved are safe — reloading usually fixes it.')}</p>
          <div className="crash-actions">
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              {tStatic('crash.reload', 'Reload page')}
            </button>
            {!fullPage && (
              <button className="btn" onClick={() => this.setState({ error: null })}>
                {tStatic('crash.retry', 'Try again')}
              </button>
            )}
          </div>
          <details className="crash-details">
            <summary>{tStatic('crash.details', 'Technical details')}</summary>
            <pre>{String((error && (error.stack || error.message)) || error)}</pre>
          </details>
        </div>
      </div>
    );
  }
}

// ============================================================================
// Root
// ============================================================================

function Root() {
  const { user, loading } = useAuth();
  // Wait for /spa/runtime too — AuthPage picks its initial mode (register vs
  // login, single-step vs two-step) from runtime flags, and useState only
  // reads the initial value once, so the flags must be present before it mounts.
  const runtime = useContext(RuntimeCtx);
  if (loading || !runtime) {
    return (
      <div className="auth-page">
        <LoadingFrame className="loading-frame--bare" label={tStatic('common.loading', 'Loading…')} />
      </div>
    );
  }
  if (!user) return <AuthPage />;
  return (
    <TenantProvider>
      <WorkspaceProvider>
        <AppShell />
      </WorkspaceProvider>
    </TenantProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <RuntimeProvider>
        <I18nProvider>
          <ErrorBoundary fullPage>
            <ToastProvider>
              <ConfirmProvider>
                <AuthProvider>
                  <Root />
                </AuthProvider>
              </ConfirmProvider>
            </ToastProvider>
          </ErrorBoundary>
        </I18nProvider>
      </RuntimeProvider>
    </ThemeProvider>
  );
}

// ============================================================================
// License page — self-hosted only. Read-only view of the operator's
// license: who it's for, which plan, when it expires, whether the
// signature verifies. Activation happens via the LICENSE_KEY env var,
// not through this UI; we surface the steps clearly so operators don't
// hunt for the right config knob.
// ============================================================================

function LicensePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(false);
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  // syncTenantPlan() on the server bumps tenant.plan when a licence
  // activation / heartbeat / clear changes the effective tier, but the
  // SPA's TenantProvider holds its own snapshot under `current`. Every
  // FeatureLocked / TierBadge in the app reads `current.plan`, so
  // without an explicit refetch the UI stays on the pre-activation
  // tier — tier-gated buttons keep prompting the operator to upgrade
  // a licence they just activated. Pulling refreshTenant out of the
  // context here so every server-side mutation below pairs with a
  // client-side refresh.
  const { refresh: refreshTenant } = useTenant();

  const load = useCallback(() => {
    api.get('/spa/license').then(setData).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    setBusy(true);
    try {
      const r = await api.post('/spa/license/activate', { key: keyInput.trim() });
      setData(r);
      setKeyInput('');
      await refreshTenant();
      toast(t('license.activate.success'), 'success');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post('/spa/license/heartbeat');
      setData(r.status);
      // A heartbeat can flip the plan too — e.g. when a cancelled
      // licence's grace window expires and syncTenantPlan() drops
      // tenant.plan back to free. Keep the SPA in step.
      await refreshTenant();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    const ok = await confirm({
      title: t('license.clear'),
      // (Previously reused the "licence unreachable for 14+ days" text,
      // which describes a different situation.)
      body: t('license.clearConfirmBody', 'This removes the licence key from this install. Paid features stop working until a licence is added again.'),
      confirmLabel: t('license.clear'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      setData(await api.del('/spa/license'));
      await refreshTenant();
    }
    catch (err) { toast(err.message, 'error'); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <>
        <div className="page-header"><div className="page-title">{t('license.title')}</div></div>
        <div className="card"><div style={{color:'var(--danger)'}}>{error}</div></div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <div className="page-header"><div className="page-title">{t('license.title')}</div></div>
        <LoadingFrame label={t('common.loading')} />
      </>
    );
  }

  const statusPill = !data.configured
    ? { label: t('license.status.missing'), color: 'past_due' }
    : data.expired
      ? { label: t('license.status.expired'), color: 'past_due' }
      : data.verified
        ? { label: t('license.status.active'), color: 'active' }
        : { label: t('license.status.unverified'), color: 'open' };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('license.title')}</div>
          <div className="page-subtitle">{t('license.subtitle')}</div>
        </div>
        <span className={`pill pill-${statusPill.color}`}>{statusPill.label}</span>
      </div>

      {!data.configured && (
        <div className="card" style={{marginBottom: 16}}>
          <div className="card-title">{t('license.notConfigured.title')}</div>
          <p style={{color:'var(--text-secondary)', marginTop: 8}}>{t('license.notConfigured.body')}</p>
          {/* Buy CTA is hidden when the image wasn't built with a
              PLUMA_LICENSE_DOMAIN — typically a locally-built dev
              image or an air-gapped install. Showing a broken anchor
              is worse than not showing the link; operators in that
              state already know how to obtain a licence. */}
          {data.buyUrl && (
            <a className="btn btn-primary" href={data.buyUrl} target="_blank" rel="noopener noreferrer" style={{marginTop: 12}}>
              {t('license.buyCta')}
            </a>
          )}
        </div>
      )}

      {data.configured && !data.verified && (
        <div className="card" style={{marginBottom: 16, borderColor:'var(--warn)'}}>
          <div className="card-title">{t('license.unverified.title')}</div>
          <p style={{color:'var(--text-secondary)', marginTop: 8}}>
            {t('license.unverified.body')} <code>{data.verifyReason || '—'}</code>
          </p>
        </div>
      )}

      {data.configured && (
        <div className="grid-3" style={{marginBottom: 24}}>
          <div className="stat-tile">
            <div className="label">{t('license.customer')}</div>
            <div className="value" style={{fontSize: 18}}>{data.customer || '—'}</div>
            <div className="sub">{data.deploymentMode || 'self_hosted'}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('license.plan')}</div>
            <div className="value" style={{fontSize: 18}}>{data.plan ? t('plan.' + data.plan) : '—'}</div>
            {data.features?.s3Backup && <div className="sub">{t('billing.bullet.s3Backup')}</div>}
          </div>
          <div className="stat-tile">
            <div className="label">{t('license.expires')}</div>
            <div className="value" style={{fontSize: 22}}>
              {data.expiresAt ? formatDateOnly(new Date(data.expiresAt)) : t('license.noExpiry')}
            </div>
            {data.supportUntil && (
              <div className="sub">
                {t('license.supportUntil', { date: formatDateOnly(new Date(data.supportUntil)) })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Heartbeat status — surfaces revocation / unreachability so the
          operator isn't surprised when caps drop. */}
      {data.configured && (
        <div className="card" style={{marginBottom: 16}}>
          <div className="card-title">{t('license.heartbeat.title')}</div>
          <div style={{marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center'}}>
            <div>
              {data.heartbeat?.lastSuccessAt
                ? formatDateOnly(new Date(data.heartbeat.lastSuccessAt))
                : <em>{t('license.heartbeat.never')}</em>}
              {data.heartbeat?.status && <span className="pill" style={{marginLeft: 8}}>{data.heartbeat.status}</span>}
            </div>
            <button className="btn" onClick={refresh} disabled={busy}>{busy ? t('common.refreshing', 'Refreshing…') : t('license.refresh')}</button>
          </div>
          {data.heartbeat?.revokedAt && (
            <div style={{marginTop: 10, color: 'var(--danger)'}}>{t('license.heartbeat.revoked')}</div>
          )}
          {data.graceExpired && (
            <div style={{marginTop: 10, color: 'var(--danger)'}}>{t('license.heartbeat.graceExpired', { days: data.graceDays || 14 })}</div>
          )}
          {!data.graceExpired && data.heartbeat?.status === 'unreachable' && (
            <div style={{marginTop: 10, color: 'var(--warn)'}}>{t('license.heartbeat.unreachable')}</div>
          )}
        </div>
      )}

      {/* Activate / update license. Operator pastes the envelope; we
          verify the signature locally and run an immediate heartbeat. */}
      <div className="card" style={{marginBottom: 16}}>
        <div className="card-title">{t('license.activate.title')}</div>
        <p style={{color:'var(--text-secondary)', marginTop: 8}}>{t('license.activate.body')}</p>
        <form onSubmit={activate} style={{marginTop: 12}}>
          <textarea className="input" rows={5}
                    style={{fontFamily: 'var(--font-mono)', fontSize: 12}}
                    placeholder={t('license.activate.placeholder')}
                    value={keyInput} onChange={(e) => setKeyInput(e.target.value)} />
          <div style={{display: 'flex', gap: 8, marginTop: 10}}>
            <button type="submit" className="btn btn-primary" disabled={busy || !keyInput.trim()}>
              {busy ? t('common.activating', 'Activating…') : t('license.activate.cta')}
            </button>
            {data.configured && (
              <button type="button" className="btn" onClick={clear} disabled={busy}>{t('license.clear')}</button>
            )}
            {data.buyUrl && (
              <a className="btn" href={data.buyUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft: 'auto'}}>
                {t('license.buyCta')}
              </a>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-title">{t('license.howTo.title')}</div>
        <ol style={{marginTop: 12, paddingLeft: 20, color:'var(--text-secondary)', lineHeight: 1.7}}>
          {/* Step 1 only references the buy URL when one was baked in.
              On an air-gapped or locally-built image we collapse the
              step to its plain text — the operator already knows where
              to obtain the licence. */}
          <li>
            {t('license.howTo.step1')}
            {data.buyUrl && (
              <> <a href={data.buyUrl} target="_blank" rel="noopener noreferrer">{data.buyUrl.replace(/^https?:\/\//, '')}</a></>
            )}
          </li>
          <li>{t('license.howTo.step2')} <code>LICENSE_KEY</code> {t('license.howTo.step2Tail')}</li>
          <li><code>docker compose up -d</code> — {t('license.howTo.step3')}</li>
        </ol>
      </div>
    </>
  );
}

// ============================================================================
// Backup configuration — self-hosted only, superadmin-gated.
// Operator points Pluma at an S3-compatible bucket (any provider:
// MinIO, Hetzner Object Storage, Backblaze, AWS) for periodic backups.
// Credentials are encrypted at rest server-side; the form shows
// "•••• (set)" when a value is on file and accepts an empty string
// to mean "leave it" so the operator can change endpoint or schedule
// without re-pasting the secret.
// ============================================================================

// Static catalogue of strategies the page renders cards for. Each entry
// drives the card header + which field group it exposes. Keeping it here
// rather than reading from the server gives a stable visual order and
// lets a brand-new install paint the cards before /spa/backup-config has
// returned.
const BACKUP_STRATEGIES = [
  {
    key: 'download',
    icon: 'download',
    titleKey: 'backup.strategy.download',         titleFallback: 'Download to browser',
    subtitleKey: 'backup.strategy.download.sub',  subtitleFallback: 'On-demand zip you save locally. No server config.',
  },
  {
    key: 's3',
    icon: 'cloud',
    titleKey: 'backup.strategy.s3',               titleFallback: 'S3-compatible bucket',
    subtitleKey: 'backup.strategy.s3.sub',        subtitleFallback: 'AWS S3, MinIO, Backblaze B2, Hetzner Object Storage…',
  },
  {
    key: 'sftp',
    icon: 'lock',
    titleKey: 'backup.strategy.sftp',             titleFallback: 'SFTP',
    subtitleKey: 'backup.strategy.sftp.sub',      subtitleFallback: 'Secure file transfer over SSH. Password or PEM key.',
  },
  {
    key: 'ftp',
    icon: 'download',
    titleKey: 'backup.strategy.ftp',              titleFallback: 'FTP',
    subtitleKey: 'backup.strategy.ftp.sub',       subtitleFallback: 'Plain FTP. Password only.',
  },
  {
    key: 'rsync',
    icon: 'copy',
    titleKey: 'backup.strategy.rsync',            titleFallback: 'rsync (over SSH)',
    subtitleKey: 'backup.strategy.rsync.sub',     subtitleFallback: 'Differential sync via rsync. PEM key required.',
  },
];

function BackupPage() {
  // configs is keyed by strategy so each card owns its own slice of state.
  // forms is the parallel "what's in the input boxes right now" layer —
  // separated so a typing render doesn't depend on a re-fetch.
  const [configs, setConfigs] = useState(null);
  const [forms, setForms] = useState({});
  // null = tile grid. A strategy key = drilled into that strategy's
  // detail page. We keep this in component state rather than a real
  // route so back-button-to-grid stays cheap and lossless.
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const toast = useToast();
  const t = useT();
  const { info: tenantInfo } = useTenantInfo();

  // Remote-backup strategies (S3 / SFTP / FTP / rsync) gate on the
  // s3Backup feature flag — Advanced+. The "download" strategy is free
  // because it's the operator pulling a snapshot to their own machine.
  const remoteAllowed = !!tenantInfo?.limits?.features?.s3Backup;
  const openUpgrade = () => window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
    detail: { code: 'feature_s3Backup', requiredTier: 'advanced' },
  }));

  const load = useCallback(async () => {
    try {
      const r = await api.get('/spa/backup-config');
      const byStrategy = {};
      const formsNext = {};
      for (const cfg of r.configs || []) {
        byStrategy[cfg.strategy] = cfg;
        formsNext[cfg.strategy] = {
          enabled: cfg.enabled,
          schedule: cfg.schedule,
          endpoint: cfg.endpoint || '',
          region: cfg.region || 'eu-central-1',
          bucket: cfg.bucket || '',
          forcePathStyle: cfg.forcePathStyle ?? true,
          accessKey: '', secretKey: '',
          host: cfg.host || '',
          port: cfg.port || '',
          username: cfg.username || '',
          remotePath: cfg.remotePath || '',
          password: '', privateKey: '',
        };
      }
      setConfigs(byStrategy);
      setForms(formsNext);
    } catch (e) { toast(e.message, 'error'); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (!configs) {
    return <>
      <div className="page-header">
        <div className="page-title">{t('backup.title')}</div>
      </div>
      <LoadingFrame label={t('common.loading')} />
    </>;
  }

  // ---- Drilled-into detail view ------------------------------------------
  // Selecting a tile swaps the page out for the full configuration form
  // for that strategy. The grid + recent-runs table fade off-screen until
  // the operator clicks "← Back to destinations".
  if (selectedStrategy) {
    const meta = BACKUP_STRATEGIES.find((m) => m.key === selectedStrategy);
    if (!meta) { setSelectedStrategy(null); return null; }
    return (
      <BackupStrategyDetail
        meta={meta}
        config={configs[meta.key]}
        form={forms[meta.key] || {}}
        onForm={(next) => setForms((s) => ({ ...s, [meta.key]: { ...(s[meta.key] || {}), ...next } }))}
        remoteAllowed={remoteAllowed}
        onReload={load}
        onBack={() => setSelectedStrategy(null)}
      />
    );
  }

  // ---- Tile grid (default landing) ---------------------------------------
  return <>
    <div className="page-header">
      <div>
        <div className="page-title">{t('backup.title')}</div>
        <div className="page-subtitle">
          {t('backup.subtitle.tiles',
             'Pick a destination to configure. Each one can be enabled with its own schedule and runs independently.')}
        </div>
      </div>
    </div>

    {!remoteAllowed && (
      <div className="card" style={{marginBottom: 16, padding: 14, background:'rgba(45,125,240,0.06)', border:'1px dashed var(--border)'}}>
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <TierBadge tier="advanced" />
          <span style={{fontSize:13, color:'var(--text-secondary)'}}>
            {t('upgrade.s3Backup.body', 'Off-site destinations need at least Advanced. Local "Download" backups are always available.')}
          </span>
          <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={openUpgrade}>
            {t('upgrade.cta', 'See plans')}
          </button>
        </div>
      </div>
    )}

    {/* Tile grid. `minmax(min(280px, 100%), 1fr)` collapses cleanly to a
        single column on phones — the inner min() returns 100% under
        280px so the track width drops to viewport-width instead of
        forcing horizontal scroll. */}
    <div style={{
      display:'grid',
      gridTemplateColumns:'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
      gap: 16,
    }}>
      {BACKUP_STRATEGIES.map((meta) => (
        <BackupStrategyTile
          key={meta.key}
          meta={meta}
          config={configs[meta.key]}
          remoteAllowed={remoteAllowed}
          onOpen={() => setSelectedStrategy(meta.key)}
        />
      ))}
    </div>

    <BackupRecentRuns configs={configs} />
  </>;
}

// Compact summary tile. Shows the strategy name, an icon, the enabled-
// state pill, and a single-line "last run" status. The full body (form,
// secrets, Save/Test/Backup now) lives in BackupStrategyDetail behind the
// "Configure" click. Keeps the landing page scannable instead of having
// the operator hunt for the right card on a long page.
function BackupStrategyTile({ meta, config, remoteAllowed, onOpen }) {
  const t = useT();
  const tierLocked = meta.key !== 'download' && !remoteAllowed;
  const ready = meta.key === 'download'
    ? true
    : meta.key === 's3'    ? !!(config?.bucket && config?.accessKeySet && config?.secretKeySet)
    : meta.key === 'sftp'  ? !!(config?.host && config?.username && (config?.passwordSet || config?.privateKeySet))
    : meta.key === 'ftp'   ? !!(config?.host && config?.username && config?.passwordSet)
    : meta.key === 'rsync' ? !!(config?.host && config?.username && config?.privateKeySet) : false;

  const lastRunPill = config?.lastRunStatus === 'ok' ? 'active'
    : config?.lastRunStatus === 'error' ? 'past_due'
    : config?.lastRunStatus === 'running' ? 'open' : '';

  // Status hint shown below the title. Three states:
  //   • enabled + has creds  → "Active · last run ..."
  //   • has creds, disabled  → "Configured — disabled"
  //   • blank slate          → "Not configured yet"
  let statusLine;
  if (config?.enabled && ready) {
    statusLine = config?.lastRunAt
      ? <>{t('backup.tile.active', 'Active')} · {formatDate(config.lastRunAt)}</>
      : t('backup.tile.activeNoRuns', 'Active · no runs yet');
  } else if (ready && meta.key !== 'download') {
    statusLine = t('backup.tile.configuredOff', 'Configured — disabled');
  } else if (meta.key === 'download') {
    statusLine = config?.lastRunAt
      ? <>{t('backup.tile.lastDownload', 'Last download')} · {formatDate(config.lastRunAt)}</>
      : t('backup.tile.downloadReady', 'Ready to download');
  } else {
    statusLine = t('backup.tile.unconfigured', 'Not configured');
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`card ${tierLocked ? 'tier-locked-inner' : ''}`}
      style={{
        textAlign:'left', cursor:'pointer', padding: 18,
        display:'flex', flexDirection:'column', gap: 12,
        // Native <button> reset — strip the user-agent gradients so the
        // card looks like a card, not a button.
        border:'1px solid var(--border)', background:'var(--surface)',
        font:'inherit', color:'inherit',
      }}
    >
      <div style={{display:'flex', alignItems:'flex-start', gap: 12}}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background:'var(--accent)', color:'#fff',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize: 22, fontWeight: 700, flexShrink: 0,
        }}><Icon name={meta.icon} size={22} /></div>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap'}}>
            <strong style={{fontSize: 15}}>{t(meta.titleKey, meta.titleFallback)}</strong>
            {tierLocked && <TierBadge tier="advanced" />}
            {config?.enabled && meta.key !== 'download' && (
              <span className="pill pill-active" style={{fontSize: 10}}>{t('backup.enabledPill', 'on')}</span>
            )}
          </div>
          <div style={{fontSize: 12, color:'var(--text-secondary)', marginTop: 2}}>
            {t(meta.subtitleKey, meta.subtitleFallback)}
          </div>
        </div>
      </div>

      <div style={{
        borderTop:'1px solid var(--border)', paddingTop: 10,
        display:'flex', alignItems:'center', justifyContent:'space-between', gap: 8, fontSize: 12,
      }}>
        <div style={{display:'flex', alignItems:'center', gap: 6, color:'var(--text-secondary)', minWidth: 0}}>
          {config?.lastRunStatus && (
            <span className={`pill pill-${lastRunPill}`} style={{fontSize: 10}}>{config.lastRunStatus}</span>
          )}
          <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{statusLine}</span>
        </div>
        <span style={{color:'var(--accent)', fontWeight: 600, flexShrink: 0}}>
          {t('backup.tile.configure', 'Configure')} →
        </span>
      </div>
    </button>
  );
}

// Drilled-in detail view. Same form/save/test/run logic the old card
// had, with a back button at the top and a recent-runs footer scoped to
// just this strategy (since the operator drilled in specifically to
// inspect this one).
function BackupStrategyDetail({ meta, config, form, onForm, remoteAllowed, onReload, onBack }) {
  const t = useT();
  return (
    <>
      <div className="page-header">
        <div>
          <button className="btn btn-link" onClick={onBack} style={{marginBottom: 8, padding: 0, fontSize: 13}}>
            ← {t('backup.detail.back', 'Back to destinations')}
          </button>
          <div className="page-title">{t(meta.titleKey, meta.titleFallback)}</div>
          <div className="page-subtitle">{t(meta.subtitleKey, meta.subtitleFallback)}</div>
        </div>
      </div>
      <BackupStrategyCard
        meta={meta}
        config={config}
        form={form}
        onForm={onForm}
        remoteAllowed={remoteAllowed}
        onReload={onReload}
      />
    </>
  );
}

// Recent runs table — one row per strategy that has ever run, sorted by
// lastRunAt desc. The shape only carries the MOST RECENT run per
// strategy (the schema is one row per strategy, intentionally — runs
// don't accumulate). If you want a full audit log, that's a follow-up
// schema change (BackupRun model).
//
// Mobile: the table re-flows to a stacked card list under 640px so the
// columns don't run off the edge of the screen. Both layouts read from
// the same data — no duplication of which row is shown where.
function BackupRecentRuns({ configs }) {
  const t = useT();
  const rows = Object.values(configs || {})
    .filter((c) => c.lastRunAt)
    .sort((a, b) => new Date(b.lastRunAt) - new Date(a.lastRunAt));

  if (rows.length === 0) {
    return (
      <>
        <div className="card-title" style={{marginTop: 24, marginBottom: 12}}>
          {t('backup.recentRuns', 'Recent runs')}
        </div>
        <div className="card"><div className="empty">{t('backup.noRunsYet', 'No backups yet — click "Backup now" to create the first.')}</div></div>
      </>
    );
  }

  const labelFor = (s) => {
    const meta = BACKUP_STRATEGIES.find((m) => m.key === s);
    return meta ? t(meta.titleKey, meta.titleFallback) : s;
  };
  const pillFor = (s) =>
    s === 'ok' ? 'active' :
    s === 'error' ? 'past_due' :
    s === 'running' ? 'open' : '';

  return (
    <>
      <div className="card-title" style={{marginTop: 24, marginBottom: 12}}>
        {t('backup.recentRuns', 'Recent runs')}
      </div>

      {/* Desktop / tablet: real table. Hidden under 640px so the stacked
          mobile list takes over (CSS-only swap — no JS resize hook). */}
      <div className="card backup-runs-desktop" style={{padding: 0, overflow:'hidden'}}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('backup.col.when', 'When')}</th>
              <th>{t('backup.col.strategy', 'Destination')}</th>
              <th>{t('backup.col.status', 'Status')}</th>
              <th>{t('backup.col.filename', 'Filename')}</th>
              <th style={{textAlign:'right'}}>{t('backup.col.size', 'Size')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.strategy}>
                <td>{formatDate(r.lastRunAt)}</td>
                <td>{labelFor(r.strategy)}</td>
                <td><span className={`pill pill-${pillFor(r.lastRunStatus)}`}>{t('backup.status.' + r.lastRunStatus, r.lastRunStatus)}</span></td>
                <td style={{fontFamily:'var(--font-mono)', fontSize: 12, wordBreak:'break-all'}}>
                  {r.lastRunFilename || '—'}
                </td>
                <td style={{textAlign:'right'}}>
                  {r.lastRunBytes != null ? `${formatNum(r.lastRunBytes)} bytes` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card list. Same data, vertical layout, no
          horizontal scroll. The .backup-runs-desktop / .backup-runs-mobile
          classes (in public/static/spa.css) own the visibility swap
          around 640px. We deliberately do NOT set `display:'grid'` as an
          inline style here — inline styles beat class rules on
          specificity, which would force the mobile list to render on
          desktop too (the bug that produced the duplicate "Recent runs"
          card under the table). The CSS sets `display: grid` only inside
          the @media (max-width: 640px) block. */}
      <div className="backup-runs-mobile">
        {rows.map((r) => (
          <div key={r.strategy} className="card" style={{padding: 14}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap: 8}}>
              <strong style={{fontSize: 14}}>{labelFor(r.strategy)}</strong>
              <span className={`pill pill-${pillFor(r.lastRunStatus)}`} style={{fontSize: 10}}>
                {r.lastRunStatus}
              </span>
            </div>
            <div style={{fontSize: 12, color:'var(--text-secondary)', marginTop: 4}}>
              {formatDate(r.lastRunAt)}
              {r.lastRunBytes != null && <> · {formatNum(r.lastRunBytes)} bytes</>}
            </div>
            {r.lastRunFilename && (
              <div style={{fontFamily:'var(--font-mono)', fontSize: 11, color:'var(--text-muted)', marginTop: 6, wordBreak:'break-all'}}>
                {r.lastRunFilename}
              </div>
            )}
            {r.lastRunError && (
              <div style={{color:'var(--danger)', fontSize: 12, marginTop: 6}}>{r.lastRunError}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// One strategy card. Owns its own save/test/run state so a slow S3 test
// doesn't freeze the SFTP card. Reload happens once after a write so the
// "•••• (set)" placeholders + lastRunAt pill reflect the new row.
function BackupStrategyCard({ meta, config, form, onForm, remoteAllowed, onReload }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const tierLocked = meta.key !== 'download' && !remoteAllowed;
  const ready = meta.key === 'download'
    ? true
    : meta.key === 's3'    ? !!(config?.bucket && config?.accessKeySet && config?.secretKeySet)
    : meta.key === 'sftp'  ? !!(config?.host && config?.username && (config?.passwordSet || config?.privateKeySet))
    : meta.key === 'ftp'   ? !!(config?.host && config?.username && config?.passwordSet)
    : meta.key === 'rsync' ? !!(config?.host && config?.username && config?.privateKeySet) : false;

  const save = async () => {
    setBusy(true); setTestResult(null);
    try {
      const payload = { ...form };
      // Port comes in as a string from <input type="number"> — coerce so
      // the server's typeof === 'number' branch accepts it. Empty → null
      // means "clear it".
      if (payload.port === '' || payload.port == null) payload.port = null;
      else payload.port = Number(payload.port);
      await api.put(`/spa/backup-config/${meta.key}`, payload);
      // Reload from the server so all derived booleans (passwordSet, etc.)
      // come back correct. Local secret fields blank out after success.
      onForm({ accessKey: '', secretKey: '', password: '', privateKey: '' });
      onReload();
      toast(t('backup.saved', 'Saved'), 'success');
    } catch (e) {
      toast(e.detail?.message || e.message, 'error');
    } finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post(`/spa/backup-config/${meta.key}/test`, {});
      setTestResult({ ok: !!r.ok, message: r.message });
    } catch (e) {
      setTestResult({ ok: false, message: e.detail?.message || e.message });
    } finally { setTesting(false); }
  };

  const runBackup = async () => {
    setRunning(true); setTestResult(null);
    try {
      if (meta.key === 'download') {
        // Direct fetch — api.post expects JSON, but we want the zip body.
        const res = await fetch(`/spa/backup-config/${meta.key}/run`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(window.__currentTenantId ? { 'X-Tenant-Id': window.__currentTenantId } : {}) },
          body: '{}',
        });
        if (!res.ok) {
          let msg = 'backup_failed';
          try { const j = await res.json(); msg = j.message || j.error || msg; } catch (_) {}
          throw new Error(msg);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = res.headers.get('content-disposition') || '';
        const m = cd.match(/filename="([^"]+)"/);
        a.download = m ? m[1] : `pluma-backup-${new Date().toISOString().slice(0,10)}.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast(t('backup.runDownloaded', 'Backup ready — downloading'), 'success');
      } else {
        const r = await api.post(`/spa/backup-config/${meta.key}/run`, {});
        toast(t('backup.runOk', 'Backup uploaded ({{bytes}} bytes)', { bytes: r.bytes }), 'success');
      }
      onReload();
    } catch (e) {
      toast(e.detail?.message || e.message, 'error');
    } finally { setRunning(false); }
  };

  const lastRunPill = config?.lastRunStatus === 'ok' ? 'active'
    : config?.lastRunStatus === 'error' ? 'past_due'
    : config?.lastRunStatus === 'running' ? 'open' : '';

  // Header: just the enable toggle + tier-locked badge. Title / subtitle
  // / icon are owned by the page-header outside the card now — the tile
  // grid uses them to render the master list, the detail view uses them
  // to render its page-header. Putting them inside the card too would
  // double them up.
  const header = (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      gap: 12, marginBottom: 14, flexWrap:'wrap',
    }}>
      <div style={{display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap'}}>
        {tierLocked && <TierBadge tier="advanced" />}
        {config?.enabled && meta.key !== 'download' && (
          <span className="pill pill-active" style={{fontSize: 10}}>{t('backup.enabledPill', 'on')}</span>
        )}
      </div>
      {meta.key !== 'download' && (
        <label style={{display:'flex', alignItems:'center', gap: 8, fontSize: 13, color:'var(--text-secondary)'}}>
          <input type="checkbox" checked={!!form.enabled} disabled={tierLocked}
                 onChange={(e) => onForm({ enabled: e.target.checked })} />
          <strong>{t('backup.enable.short', 'Enable scheduled runs')}</strong>
        </label>
      )}
    </div>
  );

  // Per-strategy field group.
  const fields = (
    <>
      {meta.key === 'download' && (
        <div style={{fontSize: 13, color:'var(--text-secondary)', padding: 12, background:'var(--bg-subtle)', borderRadius: 8}}>
          {t('backup.downloadHelp', 'Build a complete .zip (database + uploaded files + license info) and download it. Keep it somewhere safe — uploading it to a fresh install reconstructs the same state.')}
        </div>
      )}

      {meta.key === 's3' && <>
        <div>
          <label className="label">{t('backup.endpoint', 'Endpoint')}</label>
          <input className="input" placeholder="https://s3.eu-central.your-provider.com"
                 value={form.endpoint || ''} onChange={(e) => onForm({ endpoint: e.target.value })} />
          <div style={{fontSize: 12, color:'var(--text-muted)', marginTop: 4}}>
            {t('backup.endpointHelp', 'Leave blank for real AWS S3. Set for MinIO, Backblaze, Hetzner, etc.')}
          </div>
        </div>
        <div className="backup-row">
          <div>
            <label className="label">{t('backup.region', 'Region')}</label>
            <input className="input" value={form.region || ''}
                   onChange={(e) => onForm({ region: e.target.value })} />
          </div>
          <div>
            <label className="label">{t('backup.bucket', 'Bucket')}</label>
            <input className="input" value={form.bucket || ''}
                   onChange={(e) => onForm({ bucket: e.target.value })} />
          </div>
        </div>
        <div className="backup-row">
          <div>
            <label className="label">{t('backup.accessKey', 'Access key')}</label>
            <input className="input" type="password" autoComplete="new-password"
                   placeholder={config?.accessKeySet ? '•••• ' + t('backup.setBlank', '(leave blank to keep)') : ''}
                   value={form.accessKey || ''} onChange={(e) => onForm({ accessKey: e.target.value })} />
          </div>
          <div>
            <label className="label">{t('backup.secretKey', 'Secret key')}</label>
            <input className="input" type="password" autoComplete="new-password"
                   placeholder={config?.secretKeySet ? '•••• ' + t('backup.setBlank', '(leave blank to keep)') : ''}
                   value={form.secretKey || ''} onChange={(e) => onForm({ secretKey: e.target.value })} />
          </div>
        </div>
        <label style={{display:'flex', gap: 8, alignItems:'center'}}>
          <input type="checkbox" checked={!!form.forcePathStyle}
                 onChange={(e) => onForm({ forcePathStyle: e.target.checked })} />
          {t('backup.forcePathStyle', 'Force path-style URLs (required for MinIO and most S3-compatibles)')}
        </label>
      </>}

      {(meta.key === 'sftp' || meta.key === 'ftp' || meta.key === 'rsync') && <>
        <div className="backup-row">
          <div style={{flex: '2 1 200px'}}>
            <label className="label">{t('backup.host', 'Host')}</label>
            <input className="input" placeholder="backup.example.com"
                   value={form.host || ''} onChange={(e) => onForm({ host: e.target.value })} />
          </div>
          <div>
            <label className="label">{t('backup.port', 'Port')}</label>
            <input className="input" type="number" placeholder={meta.key === 'ftp' ? '21' : '22'}
                   value={form.port || ''} onChange={(e) => onForm({ port: e.target.value })} />
          </div>
        </div>
        <div className="backup-row">
          <div>
            <label className="label">{t('backup.username', 'Username')}</label>
            <input className="input" autoComplete="off"
                   value={form.username || ''} onChange={(e) => onForm({ username: e.target.value })} />
          </div>
          <div style={{flex: '2 1 200px'}}>
            <label className="label">{t('backup.remotePath', 'Remote directory')}</label>
            <input className="input" placeholder="/backups/pluma"
                   value={form.remotePath || ''} onChange={(e) => onForm({ remotePath: e.target.value })} />
          </div>
        </div>
        {meta.key !== 'rsync' && (
          <div>
            <label className="label">{t('backup.password', 'Password')}</label>
            <input className="input" type="password" autoComplete="new-password"
                   placeholder={config?.passwordSet ? '•••• ' + t('backup.setBlank', '(leave blank to keep)') : ''}
                   value={form.password || ''} onChange={(e) => onForm({ password: e.target.value })} />
            {meta.key === 'sftp' && (
              <div style={{fontSize: 12, color:'var(--text-muted)', marginTop: 4}}>
                {t('backup.sftpAuthHint', 'Password OR private key — paste a key below to use key-based auth.')}
              </div>
            )}
          </div>
        )}
        {(meta.key === 'sftp' || meta.key === 'rsync') && (
          <div>
            <label className="label">{t('backup.privateKey', 'Private key (PEM)')}</label>
            <textarea className="input" rows={5} style={{fontFamily:'monospace', fontSize: 12}}
                      placeholder={config?.privateKeySet ? '•••• ' + t('backup.setBlank', '(leave blank to keep)') : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                      value={form.privateKey || ''}
                      onChange={(e) => onForm({ privateKey: e.target.value })} />
            {meta.key === 'rsync' && (
              <div style={{fontSize: 12, color:'var(--text-muted)', marginTop: 4}}>
                {t('backup.rsyncKeyRequired', 'rsync is key-based only — paste a PEM key here, password is not supported.')}
              </div>
            )}
          </div>
        )}
      </>}
    </>
  );

  // Schedule row (hidden for download since it has no scheduled-run path).
  const schedule = meta.key === 'download' ? null : (
    <div>
      <label className="label">{t('backup.schedule', 'Schedule')}</label>
      <select className="input" value={form.schedule || 'daily'} disabled={tierLocked}
              onChange={(e) => onForm({ schedule: e.target.value })}>
        <option value="manual">{t('backup.schedule.manual', 'Manual only')}</option>
        <option value="hourly">{t('backup.schedule.hourly', 'Hourly')}</option>
        <option value="daily">{t('backup.schedule.daily', 'Daily')}</option>
        <option value="weekly">{t('backup.schedule.weekly', 'Weekly')}</option>
      </select>
    </div>
  );

  return (
    <div className={`card ${tierLocked ? 'tier-locked-inner' : ''}`} style={{padding: 18, display:'flex', flexDirection:'column'}}>
      {header}
      <div className="form-row" style={{flex: 1}}>
        {fields}
        {schedule}
      </div>

      <div style={{display:'flex', gap: 8, marginTop: 14, flexWrap:'wrap'}}>
        <button className="btn btn-primary" onClick={runBackup} disabled={running || !ready || tierLocked}>
          {running ? t('common.loading') : t('backup.runNow', 'Backup now')}
        </button>
        {meta.key !== 'download' && (
          <>
            <button className="btn" onClick={save} disabled={busy || tierLocked}>
              {busy ? t('common.saving', 'Saving…') : t('common.save')}
            </button>
            <button className="btn" onClick={test} disabled={testing || !ready || tierLocked}>
              {testing ? t('common.testing', 'Testing…') : t('backup.test', 'Test')}
            </button>
          </>
        )}
      </div>

      {testResult && (
        <div className="card" style={{
          marginTop: 12, padding: 10,
          borderColor: testResult.ok ? 'var(--accent)' : 'var(--danger)',
          background: testResult.ok ? 'rgba(108,92,231,0.06)' : 'rgba(255,71,87,0.06)',
        }}>
          <strong style={{fontSize: 13}}>
            {testResult.ok ? t('backup.testOk', 'Connection ok') : t('backup.testFailed', 'Connection failed')}
          </strong>
          {testResult.message && <div style={{fontSize: 12, marginTop: 4}}>{testResult.message}</div>}
        </div>
      )}

      <div style={{marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 12, color:'var(--text-muted)'}}>
        {config?.lastRunAt ? (
          <div style={{display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap'}}>
            <span className={`pill pill-${lastRunPill}`} style={{fontSize: 10}}>{config.lastRunStatus}</span>
            <span>{formatDate(config.lastRunAt)}</span>
            {config.lastRunBytes != null && <span>· {formatNum(config.lastRunBytes)} bytes</span>}
            {config.lastRunError && (
              <div style={{color:'var(--danger)', flexBasis:'100%', marginTop: 4}}>{config.lastRunError}</div>
            )}
          </div>
        ) : (
          <span>{t('backup.noRunsYet.card', 'No runs yet')}</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Help center (public-ish FAQ — visible to logged-in users; can also be
// rendered standalone without auth via /spa/faqs)
// ============================================================================

function HelpCenterPage() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState('');
  const t = useT();

  useEffect(() => {
    // A 429/500 body is {error}, not {categories} — rendering it crashed
    // the page on data.categories.map.
    fetch('/spa/faqs')
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) => setData(d && Array.isArray(d.categories) ? d : { categories: [] }))
      .catch(() => setData({ categories: [] }));
  }, []);

  if (!data) return <LoadingFrame label={t('common.loading')} />;

  const filterFaq = (f) => !q || f.question.toLowerCase().includes(q.toLowerCase()) || f.answer.toLowerCase().includes(q.toLowerCase());

  const visible = data.categories
    .map((c) => ({ ...c, items: c.items.filter(filterFaq) }))
    .filter((c) => c.items.length > 0);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('help.title')}</div>
          <div className="page-subtitle">{t('help.subtitle')}</div>
        </div>
      </div>
      <div className="admin-search">
        <input className="input" placeholder={t('help.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {visible.length === 0 && <div className="card empty">{t('help.noResults')}</div>}
      {visible.map((c) => (
        <div key={c.category} style={{marginBottom: 24}}>
          <div className="card-title" style={{textTransform: 'capitalize'}}>{c.category}</div>
          <div className="faq-list">
            {c.items.map((f) => (
              <div key={f.slug} className="faq-row">
                <button type="button" className="q" aria-expanded={!!open[f.slug]}
                        onClick={() => setOpen((o) => ({ ...o, [f.slug]: !o[f.slug] }))}>
                  <span aria-hidden="true">{open[f.slug] ? '▾' : '▸'}</span> {f.question}
                </button>
                {open[f.slug] && <div className="a">{f.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

// ============================================================================
// Tenant-side support — list my tickets, open new, view thread
// ============================================================================

function SupportPage() {
  const [tickets, setTickets] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null);
  const toast = useToast();
  const t = useT();
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    try {
      const d = await api.get('/spa/support/tickets');
      setTickets(d.tickets);
    } catch (e) { toast(e.message, 'error'); setTickets([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  if (!tickets) return <LoadingFrame label={t('common.loading')} />;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('support.title')}</div>
          <div className="page-subtitle">{t('support.subtitle')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>{t('support.newTicket')}</button>
      </div>
      <div className="card" style={{padding: 0, overflow: 'hidden'}}>
        {tickets.length === 0 ? (
          <div className="empty">{t('support.noTicketsYet')}</div>
        ) : isMobile ? (
          // Card layout — the 5-column table doesn't fit on a phone.
          // Subject + status at top, category + updated as meta, full
          // -width Open button at the bottom for an obvious tap target.
          <div className="ticket-card-list">
            {tickets.map((tk) => (
              <div key={tk.id} className="ticket-card">
                <div className="ticket-card-head">
                  <div className="ticket-card-subject">{tk.subject}</div>
                  <span className={`pill pill-${tk.status}`}>{t('ticketStatus.' + tk.status)}</span>
                </div>
                <div className="ticket-card-meta">
                  <span className="pill" style={{background: 'var(--bg-elevated)'}}>{t('category.' + tk.category)}</span>
                  <span>{formatDateOnly(new Date(tk.updatedAt))}</span>
                </div>
                <button className="btn" onClick={() => setActive(tk.id)}>{t('common.open')}</button>
              </div>
            ))}
          </div>
        ) : (
          <table className="table">
            <thead><tr><th>{t('support.colSubject')}</th><th>{t('support.colCategory')}</th><th>{t('support.colStatus')}</th><th>{t('support.colUpdated')}</th><th></th></tr></thead>
            <tbody>
              {tickets.map((tk) => (
                <tr key={tk.id}>
                  <td>{tk.subject}</td>
                  <td><span className="pill" style={{background: 'var(--bg-elevated)'}}>{t('category.' + tk.category)}</span></td>
                  <td><span className={`pill pill-${tk.status}`}>{t('ticketStatus.' + tk.status)}</span></td>
                  <td>{formatDateOnly(new Date(tk.updatedAt))}</td>
                  <td style={{textAlign: 'right'}}>
                    <button className="btn" onClick={() => setActive(tk.id)}>{t('common.open')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {active && <TicketDrawer ticketId={active} onClose={() => { setActive(null); load(); }} role="user" />}
    </>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('other');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const toast = useToast();
  const t = useT();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/spa/support/tickets', { subject, category, body });
      toast(t('support.ticketOpened'), 'success');
      onCreated();
    } catch (err) { toast(err.message, 'error'); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={busy || subject.trim() || body.trim() ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('support.openATicket')}</h2>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label">{t('support.subject')}</label>
            <input className="input" required value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
          </div>
          <div>
            <label className="label">{t('support.category')}</label>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="bug">{t('category.bug')}</option>
              <option value="billing">{t('category.billing')}</option>
              <option value="feature">{t('category.feature')}</option>
              <option value="abuse">{t('category.abuse')}</option>
              <option value="other">{t('category.other')}</option>
            </select>
          </div>
          <div>
            <label className="label">{t('support.describe')}</label>
            <textarea className="input" required rows={6} value={body} onChange={(e) => setBody(e.target.value)} style={{fontFamily: 'inherit'}} />
          </div>
          <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.submitting', 'Submitting…') : t('support.submit')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Shared ticket drawer — used by both tenant Support page and admin Tickets queue.
// `role` = "user" or "staff"; gates which endpoints we hit and which fields show.
function TicketDrawer({ ticketId, onClose, role, onChanged }) {
  useModalDismiss(onClose);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const t = useT();
  const basePath = role === 'staff' ? '/admin/tickets' : '/spa/support/tickets';

  const load = useCallback(async () => {
    try {
      const d = await api.get(`${basePath}/${ticketId}`);
      setTicket(d.ticket);
    } catch (e) { toast(e.message, 'error'); }
  }, [ticketId, basePath, toast]);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`${basePath}/${ticketId}/messages`, { body: reply });
      setReply('');
      await load();
      onChanged && onChanged();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`${basePath}/${ticketId}`, { status });
      await load();
      onChanged && onChanged();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  if (!ticket) return null;
  const closed = ticket.status === 'closed';

  return (
    <div className="drawer-overlay" onClick={busy || reply.trim() ? undefined : onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
          <div>
            <h2>{ticket.subject}</h2>
            <div className="sub">
              {ticket.tenant && role === 'staff' && <>{ticket.tenant.name} · </>}
              {ticket.reporter && role === 'staff' && <>{ticket.reporter.email} · </>}
              <span className={`pill pill-${ticket.status}`}>{ticket.status.replace('_', ' ')}</span>
              {' '}<span className="pill" style={{background: 'var(--bg-elevated)'}}>{ticket.category}</span>
            </div>
          </div>
          <button className="btn" onClick={onClose}>{t('common.close', 'Close')}</button>
        </div>

        {role === 'staff' && (
          <div style={{display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap'}}>
            {['in_progress', 'awaiting_user', 'resolved', 'closed'].map((s) => (
              <button key={s} className="btn" onClick={() => setStatus(s)} disabled={busy || ticket.status === s}>
                Mark {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        )}

        <div className="ticket-thread">
          {ticket.messages.map((m) => (
            <div key={m.id} className={`ticket-msg ${m.fromStaff ? 'staff' : 'user'}`}>
              <div className="meta">
                {m.fromStaff ? t('support.staff', 'Support team') : t('support.you', 'You')} · {m.author?.email} · {formatDateTime(m.createdAt)}
              </div>
              {m.body}
            </div>
          ))}
        </div>

        {!closed && (
          <div style={{marginTop: 16}}>
            <label className="label" htmlFor="ticket-reply">{t('support.reply', 'Reply')}</label>
            <textarea className="input" rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
                      id="ticket-reply" style={{fontFamily: 'inherit'}} placeholder={t('support.replyPlaceholder', 'Type your message…')} />
            <button className="btn btn-primary" onClick={post} disabled={busy || !reply.trim()} style={{marginTop: 8}}>
              {busy ? t('common.sending', 'Sending…') : t('support.sendReply', 'Send reply')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Webhooks page — configurable outbound HTTP requests fired on renderer
// outcomes (job.completed / job.failed, + publish.completed on self-hosted).
// ============================================================================

const WEBHOOK_METHODS = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE', 'HEAD'];
const WEBHOOK_AUTH_TYPES = ['none', 'basic', 'bearer', 'header', 'query'];

// Friendly labels for the renderer / publish trigger event keys. The keys
// are the canonical event types (also the webhook payload `type`); the
// labels are what the operator sees in the editor's trigger checkboxes.
const WEBHOOK_TRIGGER_LABELS = {
  'job.completed':       'Render succeeded',
  'job.failed':          'Render failed',
  'publish.completed':   'Publish completed',
  'signature.requested': 'Signature requested',
  'signature.signed':    'Signature signed',
  'signature.declined':  'Signature declined',
  'signature.expired':   'Signature expired',
};

// ----------------------------------------------------------------------------
// Font catalogue cache. /spa/fonts returns the built-in PDF base fonts +
// the tenant's uploaded customs. We cache module-side so opening 50 fields
// in the property panel doesn't fire 50 requests; FontsPage invalidates the
// cache after an upload / delete so the picker reflects fresh state.
// ----------------------------------------------------------------------------
let _fontsCache = null;
let _fontsPromise = null;
function loadFontsCatalogue() {
  if (_fontsCache) return Promise.resolve(_fontsCache);
  if (_fontsPromise) return _fontsPromise;
  _fontsPromise = api.get('/spa/fonts')
    .then((d) => {
      _fontsCache = d;
      _fontsPromise = null;
      // Side effect: inject @font-face rules so editor previews can
      // actually USE the catalogue's custom fonts. Idempotent — running
      // this on every load is cheap and keeps URLs fresh as their
      // 10-minute presigned TTL approaches expiry.
      registerCustomFonts(d?.custom || []);
      return d;
    })
    .catch(() => { _fontsPromise = null; return { builtin: [], custom: [], canUpload: false }; });
  return _fontsPromise;
}
function invalidateFontsCatalogue() { _fontsCache = null; _fontsPromise = null; }

// Tenant font management. Lists built-ins (read-only) and customs (delete-
// able). Upload UI shows only when the API said canUpload=true — that flag
// is the customFonts feature gate (enterprise-only). Non-enterprise tiers
// still see the catalogue + an upgrade hint.
function FontsPage() {
  const t = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', family: '', weight: '400', style: 'normal', file: null });
  const toast = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    invalidateFontsCatalogue();
    try { setData(await loadFontsCatalogue()); }
    catch (e) { toast(e.message, 'error'); setData({ builtin: [], custom: [], canUpload: false }); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast('Select a TTF or OTF file', 'error');
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', form.file);
      body.append('name', form.name);
      if (form.family) body.append('family', form.family);
      body.append('weight', form.weight);
      body.append('style', form.style);
      // api.upload sets X-Tenant-Id — raw fetch would 400 with tenant_required.
      await api.upload('/spa/fonts', body);
      toast('Font uploaded', 'success');
      setForm({ name: '', family: '', weight: '400', style: 'normal', file: null });
      load();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  const removeCustom = async (f) => {
    const ok = await confirm({
      title: 'Delete font?',
      body: `"${f.name}" will be removed. Existing renders are unaffected, but any field still referencing "${f.family}" will fall back to the template default.`,
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/spa/fonts/${f.id}`);
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!data) return <div className="settings-empty">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title" style={{display:'flex', alignItems:'center', gap: 10}}>
            Fonts
            {!data.canUpload && <TierBadge tier="enterprise" />}
          </div>
          <div className="page-subtitle">Built-in fonts + your custom uploads. Fields can reference any of these.</div>
        </div>
      </div>

      {/* Engine-dependency notice. Custom fonts are only embedded by the
          PLUMAv1 engine; Standard (pdfcpu) silently substitutes Helvetica.
          Surfaced here so a customer who uploads a custom font and then
          switches the template's renderer doesn't quietly get the wrong
          output. Localised — see fonts.rendererNotice in the catalogues. */}
      <div className="card" style={{marginBottom: 16, padding: 12, background: 'rgba(0,153,255,0.05)', border: '1px solid rgba(0,153,255,0.25)', fontSize: 13, lineHeight: 1.5}}>
        {t('fonts.rendererNotice')}
      </div>

      {!data.canUpload && (
        <div className="card" style={{marginBottom: 16, padding: 14, background: 'rgba(255,165,2,0.06)', border: '1px dashed var(--border)'}}>
          <div style={{display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap'}}>
            <TierBadge tier="enterprise" />
            <span style={{fontSize: 13, color: 'var(--text-secondary)'}}>
              Every plan can use the built-in fonts. Upgrade to Enterprise to upload your own TTF / OTF files.
            </span>
            <button className="btn btn-primary" style={{marginLeft: 'auto'}}
                    onClick={() => window.dispatchEvent(new CustomEvent('pluma:upgrade-required', {
                      detail: { code: 'feature_customFonts', requiredTier: 'enterprise' },
                    }))}>
              {t('upgrade.cta', 'See plans')}
            </button>
          </div>
        </div>
      )}

      <div className="card-title" style={{marginBottom: 8}}>Built-in</div>
      {/* overflow-x: auto so the table scrolls horizontally on narrow
          viewports instead of being clipped by `overflow: hidden`
          (which was the original setting, and is what hid the action
          column on the customs table just below). border-radius +
          contain belt for the rounded corners that overflow:hidden was
          providing. */}
      <div className="card" style={{padding: 0, overflowX: 'auto', marginBottom: 16, borderRadius: 'var(--radius-lg)'}}>
        <table className="table">
          <thead><tr><th>Name</th><th>Family</th><th>Weights</th><th>Encoding</th><th></th></tr></thead>
          <tbody>
            {data.builtin.map((f) => (
              <tr key={f.id}>
                <td><strong>{f.name}</strong></td>
                <td><code style={{fontSize: 12}}>{f.id}</code></td>
                <td style={{fontSize: 12, color: 'var(--text-muted)'}}>{f.weights.join(', ')}</td>
                <td><span className="pill" style={{fontSize: 11}}>{f.encoding}</span></td>
                <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                  {f.standard14 ? <span className="sub" style={{fontSize: 11}}>PDF base font</span> : <span className="sub" style={{fontSize: 11}}>bundled</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-title" style={{marginBottom: 8}}>Custom</div>
      <div className="card" style={{padding: 0, overflowX: 'auto', marginBottom: 16, borderRadius: 'var(--radius-lg)'}}>
        {data.custom.length === 0 ? (
          <div className="empty">No custom fonts uploaded yet.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Name</th><th>Family</th><th>Weight</th><th>Style</th><th>Format</th><th>Size</th><th></th></tr></thead>
            <tbody>
              {data.custom.map((f) => (
                <tr key={f.id}>
                  <td><strong>{f.name}</strong></td>
                  <td><code style={{fontSize: 12}}>{f.family}</code></td>
                  <td>{f.weight}</td>
                  <td>{f.style}</td>
                  <td><span className="pill" style={{fontSize: 11, textTransform: 'uppercase'}}>{f.format}</span></td>
                  <td style={{fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap'}}>{Math.round(f.fileSize / 1024)} KB</td>
                  <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                    {data.canUpload && (
                      <button className="btn btn-danger" onClick={() => removeCustom(f)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.canUpload && (
        <>
          <div className="card-title" style={{marginBottom: 8}}>Upload a font</div>
          <div style={{
            marginBottom: 12, padding: 12,
            background: 'var(--accent-glow)', color: 'var(--accent)',
            borderRadius: 8, fontSize: 13, lineHeight: 1.5,
          }}>
            <strong>Use TTF.</strong> .otf files only work if they carry
            TrueType outlines — .otf with PostScript (CFF) outlines is
            not supported.
          </div>
          <form className="card" onSubmit={submit}>
            <div className="form-row">
              <div>
                <label className="label">File (.ttf / .otf, max 5 MB)</label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                  <label className="btn" style={{
                    cursor: 'pointer', margin: 0,
                    // ≥44px tap target (iOS HIG, also Android Material).
                    minHeight: 44, padding: '0 18px',
                    display: 'inline-flex', alignItems: 'center',
                    touchAction: 'manipulation',  // disables 300ms tap delay on older WebKit
                  }}>
                    {form.file ? 'Choose another file' : 'Choose file…'}
                    <input type="file" accept=".ttf,.otf,font/ttf,font/otf"
                           style={{display: 'none'}}
                           onChange={(e) => {
                             const file = e.target.files?.[0] || null;
                             setForm((f) => ({
                               ...f, file,
                               name: f.name || (file?.name?.replace(/\.(ttf|otf)$/i, '') || ''),
                             }));
                             e.target.value = '';
                           }} />
                  </label>
                  {form.file ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '6px 12px', borderRadius: 999,
                      background: 'var(--accent-glow)', color: 'var(--accent)',
                      fontSize: 13, fontWeight: 600, maxWidth: '100%',
                      // Ensure the chip itself can shrink on narrow viewports —
                      // without min-width:0 a long filename pushes outside
                      // the flex container.
                      minWidth: 0,
                    }} title={form.file.name}>
                      <span style={{
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 240,
                      }}>{form.file.name}</span>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, file: null }))}
                              style={{
                                background: 'transparent', border: 'none', color: 'inherit',
                                cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 18,
                                // Tap target: invisible 32px hit area around the visible "×"
                                width: 32, height: 32,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                                touchAction: 'manipulation',
                              }}
                              title="Remove">×</button>
                    </span>
                  ) : (
                    <span style={{fontSize: 13, color: 'var(--text-muted)'}}>No file chosen</span>
                  )}
                </div>
              </div>
              <div className="prop-row-2">
                <div>
                  <label className="label">Display name</label>
                  <input className="input" required value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         placeholder="Acme Sans" />
                </div>
                <div>
                  <label className="label">Family slug (optional)</label>
                  <input className="input" value={form.family}
                         onChange={(e) => setForm({ ...form, family: e.target.value })}
                         placeholder="auto from name" />
                </div>
              </div>
              <div className="prop-row-2">
                <div>
                  <label className="label">Weight</label>
                  <select className="input" value={form.weight}
                          onChange={(e) => setForm({ ...form, weight: e.target.value })}>
                    {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => (
                      <option key={w} value={w}>{w}{w === 400 ? ' (regular)' : w === 700 ? ' (bold)' : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Style</label>
                  <select className="input" value={form.style}
                          onChange={(e) => setForm({ ...form, style: e.target.value })}>
                    <option value="normal">Normal</option>
                    <option value="italic">Italic</option>
                  </select>
                </div>
              </div>
              <button className="btn btn-primary" disabled={busy} type="submit">
                {busy ? 'Uploading…' : 'Upload font'}
              </button>
            </div>
          </form>
        </>
      )}
    </>
  );
}

function WebhooksPage() {
  const [webhooks, setWebhooks] = useState(null);
  const [triggerEvents, setTriggerEvents] = useState([]);
  // Rich per-event catalogue from /spa/webhooks. Each item is
  // { key, description, eligible, reason?, message? }. The editor
  // renders checkboxes from this list, grays + disables ones with
  // eligible=false, and surfaces the message as the gating reason.
  // Falls back to a synthesised list from `triggerEvents` (all
  // eligible) when an older server returns only the flat array, so
  // a partial deploy never silently hides the create form.
  const [eventCatalog, setEventCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [revealed, setRevealed] = useState(null); // {id, secret} after create/rotate
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();

  const load = useCallback(async () => {
    try {
      const d = await api.get('/spa/webhooks');
      setWebhooks(d.webhooks);
      setTriggerEvents(d.triggerEvents || []);
      // Prefer the rich `events` array; synthesise from `triggerEvents`
      // if the server is older and only returned the flat list.
      setEventCatalog(Array.isArray(d.events) && d.events.length > 0
        ? d.events
        : (d.triggerEvents || []).map((key) => ({ key, eligible: true })));
    } catch (e) { toast(e.message, 'error'); setWebhooks([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const sendTest = async (w) => {
    try { await api.post(`/spa/webhooks/${w.id}/test`); toast(t('webhooks.testSent'), 'success'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const toggleActive = async (w) => {
    try { await api.patch(`/spa/webhooks/${w.id}`, { active: !w.active }); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const remove = async (w) => {
    const ok = await confirm({
      title: t('webhooks.deleteConfirm.title'),
      body: t('webhooks.deleteConfirm.body', { url: w.name || w.url }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!ok) return;
    try { await api.del(`/spa/webhooks/${w.id}`); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  if (!webhooks) return <LoadingFrame label={t('common.loading')} />;

  return (
    <>
      <SettingsHeader
        title={t('webhooks.title')}
        subtitle={t('webhooks.subtitle')}
        action={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>{t('webhooks.create')}</button>
        }
      />

      {triggerEvents.length > 0 && (
        <div className="card" style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16}}>
          {t('webhooks.firesOn', { events: triggerEvents.join(', ') })}
        </div>
      )}

      {revealed && (
        <div className="card" style={{borderColor: 'var(--accent)', background: 'var(--accent-glow)', marginBottom: 16}}>
          <div style={{fontWeight:600, marginBottom:8}}>{t('webhooks.secretLabel')}</div>
          <div style={{fontSize: 13, color:'var(--text-secondary)', marginBottom: 8}}>{t('webhooks.secretCopy')}</div>
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <code style={{flex:1, fontSize:12, padding:'8px 12px', background:'var(--bg-elevated)', borderRadius:6, wordBreak:'break-all'}}>{revealed.secret}</code>
            <button className="btn" onClick={() => { navigator.clipboard.writeText(revealed.secret); toast(t('common.copied'), 'success'); }}>{t('common.copy')}</button>
            <button className="btn" onClick={() => setRevealed(null)}>{t('common.dismiss')}</button>
          </div>
        </div>
      )}

      {webhooks.length === 0 ? (
        <div className="settings-empty">
          <p>{t('webhooks.empty')}</p>
          <button className="btn" onClick={() => setEditing('new')}>{t('webhooks.create')}</button>
        </div>
      ) : webhooks.map((w) => (
        <div key={w.id} className="settings-card">
          <div className="settings-card-info">
            <div className="settings-card-title-row">
              <div className="settings-card-title">{w.name || w.url}</div>
              <span className="pill" style={{fontFamily:'var(--font-mono)', fontSize:11}}>{w.method}</span>
              <span className={`pill pill-${w.active ? 'active' : 'past_due'}`}>
                {w.active ? t('webhooks.statusActive') : t('webhooks.statusPaused')}
              </span>
            </div>
            <div className="settings-card-meta" style={{fontFamily:'var(--font-mono)', wordBreak:'break-all'}}>
              {w.url}
            </div>
            <div className="settings-card-meta">
              {t('webhooks.authLabel')}: {t('webhooks.auth.' + (w.authType || 'none'))}
              {' · '}{t('webhooks.colCreated')}: {formatDateOnly(new Date(w.createdAt))}
            </div>
          </div>
          <div className="settings-card-actions">
            <ActionMenu items={[
              { label: t('webhooks.test'), onClick: () => sendTest(w) },
              { label: t('common.edit'), onClick: () => setEditing(w.id) },
              { label: w.active ? t('webhooks.disable') : t('webhooks.enable'), onClick: () => toggleActive(w) },
              { label: t('common.delete'), onClick: () => remove(w), danger: true },
            ]} />
          </div>
        </div>
      ))}

      {editing && <WebhookEditor
        id={editing === 'new' ? null : editing}
        triggerEvents={triggerEvents}
        eventCatalog={eventCatalog}
        onClose={() => { setEditing(null); load(); }}
        onSecretRevealed={(secret, id) => setRevealed({ secret, id })}
      />}
    </>
  );
}

// Editable list of { name, value } rows — used for both custom headers and
// query parameters.
function WebhookPairList({ label, addLabel, pairs, onChange }) {
  const t = useT();
  const set = (i, key, val) => onChange(pairs.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  return (
    <div>
      <label className="label">{label}</label>
      {pairs.map((p, i) => (
        <div key={i} style={{display:'flex', gap:8, marginBottom:6}}>
          <input className="input" placeholder={t('webhooks.pairName')} value={p.name}
                 onChange={(e) => set(i, 'name', e.target.value)} style={{flex:1}} />
          <input className="input" placeholder={t('webhooks.pairValue')} value={p.value}
                 onChange={(e) => set(i, 'value', e.target.value)} style={{flex:1}} />
          <button className="btn icon-btn" aria-label="Remove" onClick={() => onChange(pairs.filter((_, idx) => idx !== i))}><Icon name="close" /></button>
        </div>
      ))}
      <button className="btn" onClick={() => onChange([...pairs, { name: '', value: '' }])}>{addLabel}</button>
    </div>
  );
}

function WebhookEditor({ id, triggerEvents = [], eventCatalog = [], onClose, onSecretRevealed }) {
  const isNew = !id;
  // Default subscription = every ELIGIBLE event. Ineligible events
  // would be rejected server-side anyway; pre-checking them only
  // confuses the operator. The catalog is the source of truth; we
  // fall through to triggerEvents (treating everything as eligible)
  // only when the server returned the legacy flat list.
  const eligibleKeys = eventCatalog.length > 0
    ? eventCatalog.filter((e) => e.eligible).map((e) => e.key)
    : triggerEvents;
  const [form, setForm] = useState({
    name: '', url: '', method: 'POST',
    authType: 'none', authConfig: {}, headers: [], queryParams: [],
    triggers: eligibleKeys, active: true,
  });
  const [authConfigured, setAuthConfigured] = useState(false);
  const [authTouched, setAuthTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const [deliveries, setDeliveries] = useState([]);
  const [redelivering, setRedelivering] = useState(null);
  const [expandedDelivery, setExpandedDelivery] = useState(null);
  const loadDeliveries = React.useCallback(() => {
    if (isNew || !id) return;
    api.get(`/spa/webhooks/${id}/deliveries`).then((d) => setDeliveries(d.deliveries)).catch(() => {});
  }, [id, isNew]);
  const redeliver = async (deliveryId) => {
    setRedelivering(deliveryId);
    try {
      await api.post(`/spa/webhooks/${id}/deliveries/${deliveryId}/redeliver`);
      toast(t('webhooks.redelivered', 'Queued for redelivery'), 'success');
      setTimeout(loadDeliveries, 1200); // give the dispatcher a beat
    } catch (e) { toast(e.message, 'error'); }
    finally { setRedelivering(null); }
  };
  const toast = useToast();
  const t = useT();
  const confirm = useConfirm();

  useEffect(() => {
    if (!isNew) {
      api.get(`/spa/webhooks/${id}`).then((d) => {
        const w = d.webhook;
        // authConfig credentials are never returned — start the section
        // blank; only re-send auth if the user actually edits it.
        // An empty `triggers` means "all" — surface that as every box ticked.
        setForm({
          name: w.name || '', url: w.url, method: w.method || 'POST',
          authType: w.authType || 'none', authConfig: {},
          headers: w.headers || [], queryParams: w.queryParams || [],
          triggers: (w.triggers && w.triggers.length) ? w.triggers : triggerEvents,
          active: w.active,
        });
        setAuthConfigured(!!w.authConfigured);
      }).catch((e) => toast(e.message, 'error'));
      loadDeliveries();
    }
  }, [id, isNew, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTrigger = (ev) => setForm((f) => ({
    ...f,
    triggers: f.triggers.includes(ev) ? f.triggers.filter((x) => x !== ev) : [...f.triggers, ev],
  }));

  const setAuth = (patch) => { setAuthTouched(true); setForm((f) => ({ ...f, ...patch })); };
  const setCfg = (patch) => setAuth({ authConfig: { ...form.authConfig, ...patch } });

  const save = async () => {
    // Events the current plan can't use (e.g. signature.* after a
    // downgrade) show ticked-but-disabled on an old webhook; sending them
    // made every save fail with 402 ineligible_triggers and the user
    // couldn't untick them. Drop them here.
    const ineligible = new Set(eventCatalog.filter((e) => e.eligible === false).map((e) => e.key));
    const triggers = form.triggers.filter((k) => !ineligible.has(k));
    if (triggerEvents.length > 0 && triggers.length === 0) {
      toast(t('webhooks.triggersRequired', 'Select at least one trigger'), 'error');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, url: form.url, method: form.method,
        headers: form.headers, queryParams: form.queryParams,
        triggers, active: form.active,
      };
      // Only send auth when creating, or when the user touched the auth
      // section — otherwise the server keeps the existing credentials.
      if (isNew || authTouched) {
        payload.authType = form.authType;
        payload.authConfig = form.authConfig;
      }
      if (isNew) {
        const r = await api.post('/spa/webhooks', payload);
        if (r.webhook?.secret) onSecretRevealed(r.webhook.secret, r.webhook.id);
      } else {
        await api.patch(`/spa/webhooks/${id}`, payload);
      }
      onClose();
    } catch (e) { toast(e.message, 'error'); setBusy(false); }
  };

  const rotate = async () => {
    const ok = await confirm({
      title: t('webhooks.rotateConfirm.title', 'Rotate signing secret?'),
      body: t('webhooks.rotateConfirm.body', 'Deliveries signed with the current secret stop verifying immediately. Update your endpoint with the new secret right after rotating.'),
      confirmLabel: t('webhooks.rotate', 'Rotate secret'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(`/spa/webhooks/${id}/rotate-secret`);
      if (r.webhook?.secret) onSecretRevealed(r.webhook.secret, r.webhook.id);
      toast(t('webhooks.rotated'), 'success');
      onClose();
    } catch (e) { toast(e.message, 'error'); setBusy(false); }
  };

  const at = form.authType;
  const cfg = form.authConfig;
  const credHint = (!isNew && authConfigured && !authTouched) ? t('webhooks.auth.keepHint') : '';

  return (
    <div className="drawer-overlay" onClick={busy ? undefined : (isNew && !form.url ? onClose : undefined)}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <h2>{isNew ? t('webhooks.newTitle') : t('webhooks.editTitle')}</h2>
        <div className="form-row" style={{marginTop: 16}}>
          <div>
            <label className="label">{t('webhooks.nameLabel')}</label>
            <input className="input" placeholder={t('webhooks.namePlaceholder')}
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label">{t('webhooks.urlLabel')}</label>
            <input className="input" placeholder="https://example.com/webhook"
                   value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div>
            <label className="label">{t('webhooks.methodLabel')}</label>
            <select className="select" value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}>
              {WEBHOOK_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t('webhooks.authLabel')}</label>
            <select className="select" value={at}
                    onChange={(e) => setAuth({ authType: e.target.value, authConfig: {} })}>
              {WEBHOOK_AUTH_TYPES.map((a) => <option key={a} value={a}>{t('webhooks.auth.' + a)}</option>)}
            </select>
            {credHint && <div style={{fontSize:12, color:'var(--text-secondary)', marginTop:4}}>{credHint}</div>}
          </div>

          {at === 'basic' && (
            <div style={{display:'flex', gap:8}}>
              <input className="input" placeholder={t('webhooks.auth.username')} value={cfg.username || ''}
                     onChange={(e) => setCfg({ username: e.target.value })} style={{flex:1}} />
              <input className="input" type="password" placeholder={t('webhooks.auth.password')} value={cfg.password || ''}
                     onChange={(e) => setCfg({ password: e.target.value })} style={{flex:1}} />
            </div>
          )}
          {at === 'bearer' && (
            <input className="input" type="password" placeholder={t('webhooks.auth.token')} value={cfg.token || ''}
                   onChange={(e) => setCfg({ token: e.target.value })} />
          )}
          {at === 'header' && (
            <div style={{display:'flex', gap:8}}>
              <input className="input" placeholder={t('webhooks.auth.headerName')} value={cfg.headerName || ''}
                     onChange={(e) => setCfg({ headerName: e.target.value })} style={{flex:1}} />
              <input className="input" type="password" placeholder={t('webhooks.auth.token')} value={cfg.token || ''}
                     onChange={(e) => setCfg({ token: e.target.value })} style={{flex:1}} />
            </div>
          )}
          {at === 'query' && (
            <div style={{display:'flex', gap:8}}>
              <input className="input" placeholder={t('webhooks.auth.paramName')} value={cfg.paramName || ''}
                     onChange={(e) => setCfg({ paramName: e.target.value })} style={{flex:1}} />
              <input className="input" type="password" placeholder={t('webhooks.auth.token')} value={cfg.token || ''}
                     onChange={(e) => setCfg({ token: e.target.value })} style={{flex:1}} />
            </div>
          )}

          <WebhookPairList label={t('webhooks.headersLabel')} addLabel={t('webhooks.addHeader')}
                           pairs={form.headers} onChange={(headers) => setForm({ ...form, headers })} />
          <WebhookPairList label={t('webhooks.paramsLabel')} addLabel={t('webhooks.addParam')}
                           pairs={form.queryParams} onChange={(queryParams) => setForm({ ...form, queryParams })} />

          {(eventCatalog.length > 0 || triggerEvents.length > 0) && (
            <div>
              <label className="label">{t('webhooks.triggersLabel', 'Triggers')}</label>
              <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom:6}}>
                {t('webhooks.triggersHint', 'Which events fire this webhook.')}
              </div>
              {(eventCatalog.length > 0 ? eventCatalog : triggerEvents.map((k) => ({ key: k, eligible: true }))).map((ev) => {
                const eligible = ev.eligible !== false;
                const checked = form.triggers.includes(ev.key);
                // Ineligible events: disabled + grayed + tooltip
                // explaining the gate. The gating message comes from
                // the server so a future tier rename doesn't require
                // a SPA update.
                return (
                  <label key={ev.key}
                         title={eligible ? (ev.description || '') : (ev.message || '')}
                         style={{
                           display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4,
                           opacity: eligible ? 1 : 0.5,
                           cursor: eligible ? 'pointer' : 'not-allowed',
                         }}>
                    <input type="checkbox"
                           checked={checked}
                           disabled={!eligible}
                           onChange={() => eligible && toggleTrigger(ev.key)} />
                    {WEBHOOK_TRIGGER_LABELS[ev.key] || ev.key}
                    <code style={{fontSize:11, color:'var(--text-muted)'}}>{ev.key}</code>
                    {!eligible && (
                      <span style={{fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic'}}>
                        — {ev.message || t('webhooks.eventLocked', 'Upgrade required')}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          <label style={{display:'flex', gap:8, alignItems:'center'}}>
            <input type="checkbox" checked={form.active}
                   onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            {t('webhooks.enabledLabel')}
          </label>

          {!isNew && (
            <div>
              <label className="label">{t('webhooks.secretLabel')}</label>
              <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom: 6}}>{t('webhooks.secretRotate')}</div>
              <button className="btn" onClick={rotate} disabled={busy}>{t('webhooks.rotate')}</button>
            </div>
          )}

          <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
            <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? t('common.saving', 'Saving…') : t('common.save')}</button>
          </div>
        </div>

        {!isNew && (
          <>
            <div className="card-title" style={{marginTop: 24}}>{t('webhooks.deliveries')}</div>
            <div className="card" style={{padding: 0}}>
              {deliveries.length === 0 ? <div className="empty">{t('webhooks.deliveriesEmpty')}</div> : (
                <table className="table">
                  <thead><tr>
                    <th>{t('webhooks.col.event', 'Event')}</th>
                    <th>{t('webhooks.col.status', 'Status')}</th>
                    <th>{t('webhooks.col.response', 'Response')}</th>
                    <th>{t('webhooks.col.time', 'Time')}</th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {deliveries.map((d) => {
                      const hasDetail = !!(d.responseBody || d.lastError);
                      const open = expandedDelivery === d.id;
                      return (
                      <React.Fragment key={d.id}>
                      <tr onClick={() => hasDetail && setExpandedDelivery(open ? null : d.id)}
                          style={hasDetail ? { cursor: 'pointer' } : undefined}>
                        <td>
                          {hasDetail && <span aria-hidden="true" style={{color: 'var(--text-muted)', marginRight: 6}}>{open ? '▾' : '▸'}</span>}
                          <code style={{fontSize:11}}>{d.eventType}</code>
                        </td>
                        <td><span className={`pill pill-${d.status === 'delivered' ? 'active' : d.status === 'failed' ? 'past_due' : 'open'}`}>{t('webhooks.deliveryStatus.' + d.status)}</span></td>
                        <td>
                          {d.responseStatus || (d.status === 'failed' ? t('webhooks.noResponse', 'No response') : '—')}
                          {d.lastError && (
                            <div style={{fontSize: 11, color: 'var(--danger)', marginTop: 2, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word'}}
                                 title={d.lastError}>{d.lastError}</div>
                          )}
                        </td>
                        <td style={{fontSize: 11, color:'var(--text-muted)', whiteSpace: 'nowrap'}}>{formatDateTime(d.createdAt)}</td>
                        <td style={{textAlign: 'right'}}>
                          <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); redeliver(d.id); }} disabled={redelivering === d.id}>
                            {redelivering === d.id ? t('common.sending', 'Sending…') : t('webhooks.redeliver', 'Redeliver')}
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={5} style={{background: 'var(--bg-elev)', padding: '10px 14px'}}>
                            <div style={{fontSize: 11, color: 'var(--text-muted)', marginBottom: 4}}>
                              {t('webhooks.responseBody', 'Response body')}
                              {d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
                            </div>
                            <pre style={{margin: 0, maxHeight: 200, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                              {d.responseBody || d.lastError || t('webhooks.noResponseBody', '(empty response)')}
                            </pre>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Members page — list, invite, role change, remove
// ============================================================================

function MembersPage() {
  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [ownerless, setOwnerless] = useState(false);
  const [claimableBy, setClaimableBy] = useState(null);
  // Per-row workspace editor state. Keyed by userId — at most one
  // member is being edited at a time; opening one closes the other.
  const [editingWsUserId, setEditingWsUserId] = useState(null);
  const [editingWsDraft, setEditingWsDraft] = useState([]);
  // The accept-link from the most recent invite create / re-send. On
  // self-hosted (no email) this is the operator's only delivery channel,
  // so we surface it prominently and let them copy it.
  const [inviteLink, setInviteLink] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();
  const { user } = useAuth();
  const { workspaces } = useWorkspace();
  const runtime = useRuntime();
  const localAuthOnly = !!(runtime.features && runtime.features.localAuthOnly);
  const [memberQuery, setMemberQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      // Members first — every role can read it. Invites are management
      // data (member:invite); fetch them only when this user can manage,
      // so an editor/viewer opening the page doesn't trip a 403 toast.
      const m = await api.get('/spa/members');
      setMembers(m.members);
      setOwnerless(!!m.ownerless);
      setClaimableBy(m.claimableBy || null);
      const me = user ? (m.members.find((x) => x.userId === user.id) || {}) : {};
      if (me.role === 'owner' || me.role === 'admin') {
        try {
          const i = await api.get('/spa/members/invites');
          setInvites(i.invites);
        } catch (_) { setInvites([]); }
      } else {
        setInvites([]);
      }
    } catch (e) { toast(e.message, 'error'); setMembers([]); }
  }, [toast, user]);
  useEffect(() => { load(); }, [load]);

  const myRole = members && user ? (members.find((m) => m.userId === user.id) || {}).role : null;
  const isOwner = myRole === 'owner';
  // Owners + admins manage the roster (invite, change roles). Everyone
  // else gets a read-only view — they can see their teammates but every
  // mutating control is hidden.
  const canManage = myRole === 'owner' || myRole === 'admin';

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast(t('members.linkCopied'), 'success'); }
    catch { /* clipboard blocked — the link is still visible to select manually */ }
  };

  const remove = async (m) => {
    const ok = await confirm({
      title: t('members.removeConfirm.title'),
      body: t('members.removeConfirm.body', { email: m.email }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!ok) return;
    try { await api.del(`/spa/members/${m.userId}`); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const changeRole = async (m, role) => {
    if (user && m.userId === user.id) {
      const ok = await confirm({
        title: t('members.selfRoleConfirm.title', 'Change your own role?'),
        body: t('members.selfRoleConfirm.body', 'You\u2019ll lose member-management access immediately if you drop below Admin.'),
        confirmLabel: t('members.selfRoleConfirm.cta', 'Change my role'),
        danger: true,
      });
      if (!ok) { load(); return; }
    }
    try {
      await api.patch(`/spa/members/${m.userId}`, { role });
      toast(t('members.roleUpdated', 'Role updated \u2014 {{email}} is now {{role}}', { email: m.email, role: t('members.role.' + role, role) }), 'success');
      load();
    }
    catch (e) { toast(e.message, 'error'); }
  };

  const startEditWorkspaces = (m) => {
    setEditingWsUserId(m.userId);
    setEditingWsDraft([...(m.workspaceIds || [])]);
  };
  const cancelEditWorkspaces = () => {
    setEditingWsUserId(null);
    setEditingWsDraft([]);
  };
  const toggleDraftWorkspace = (id) => {
    setEditingWsDraft((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };
  const saveWorkspaces = async (m) => {
    if (editingWsDraft.length === 0) {
      const ok = await confirm({
        title: t('members.zeroWorkspacesConfirm.title', 'Save with no workspaces?'),
        body: t('members.zeroWorkspacesConfirm.body', '{{email}} will stay a member of the tenant but won\'t see any workspace until granted.', { email: m.email }),
        confirmLabel: t('common.save') || 'Save',
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await api.patch(`/spa/members/${m.userId}/workspaces`, { workspaceIds: editingWsDraft });
      cancelEditWorkspaces();
      load();
    } catch (e) { toast(e.message, 'error'); }
  };
  const wsName = (id) => workspaces.find((w) => w.id === id)?.name || id;

  const transferOwnership = async (m) => {
    const ok = await confirm({
      title: t('members.transferConfirm.title'),
      body: t('members.transferConfirm.body', { email: m.email }),
      confirmLabel: t('members.transferOwnership'),
      danger: true,
    });
    if (!ok) return;
    try { await api.post('/spa/members/transfer-ownership', { userId: m.userId }); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const claimOwnership = async () => {
    const ok = await confirm({
      title: t('members.claimOwnership'),
      body: t('members.ownerlessNotice'),
      confirmLabel: t('members.claimOwnership'),
    });
    if (!ok) return;
    try { await api.post('/spa/members/claim-ownership'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const cancelInvite = async (i) => {
    try { await api.del(`/spa/members/invites/${i.id}`); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const resendInvite = async (i) => {
    // Tokens are stored hashed, so the old link can't be shown again —
    // "copy link" always mints a new one and the old link dies. Say so
    // before doing it (the label used to promise a plain copy).
    if (localAuthOnly) {
      const ok = await confirm({
        title: t('members.regenerateConfirmTitle', 'Create a new invite link?'),
        body: t('members.regenerateConfirmBody', 'Any link you shared before for {{email}} will stop working.', { email: i.email }),
        confirmLabel: t('members.regenerateLink', 'New invite link'),
      });
      if (!ok) return;
    }
    try {
      const r = await api.post(`/spa/members/invites/${i.id}/resend`);
      if (r.acceptUrl) setInviteLink(r.acceptUrl);
      if (!localAuthOnly) toast(t('members.invited'), 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!members) return <LoadingFrame label={t('common.loading')} />;

  // Roster search + role filter + a stable owner→admin→editor→viewer
  // sort so the list reads as a hierarchy, not raw insertion order.
  const ROLE_RANK = { owner: 0, admin: 1, editor: 2, viewer: 3 };
  const q = memberQuery.trim().toLowerCase();
  const visibleMembers = members
    .filter((m) => roleFilter === 'all' || m.role === roleFilter)
    .filter((m) => !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    .slice()
    .sort((a, b) => (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9)
      || (a.name || a.email || '').localeCompare(b.name || b.email || ''));

  return (
    <>
      <SettingsHeader
        title={t('members.title')}
        subtitle={t('members.subtitle')}
        action={canManage ? (
          <button className="btn btn-primary" onClick={() => setShowInvite(true)}>{t('members.invite')}</button>
        ) : null}
      />

      {ownerless && user && claimableBy === user.id && (
        <div className="settings-card" style={{borderColor: 'var(--warning, #d97706)'}}>
          <div className="settings-card-info">
            <div className="settings-card-title">{t('members.ownerlessNotice')}</div>
          </div>
          <div className="settings-card-actions">
            <button className="btn btn-primary" onClick={claimOwnership}>{t('members.claimOwnership')}</button>
          </div>
        </div>
      )}

      {inviteLink && (
        <div className="settings-card" style={{borderColor: 'var(--accent, #6366f1)'}}>
          <div className="settings-card-info" style={{minWidth: 0}}>
            <div className="settings-card-title">{t('members.inviteLink.title')}</div>
            <div className="settings-card-meta">{t('members.inviteLink.body')}</div>
            <input className="input" readOnly value={inviteLink}
                   onFocus={(e) => e.target.select()}
                   style={{marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 12}} />
          </div>
          <div className="settings-card-actions" style={{flexDirection: 'column', gap: 6}}>
            <button className="btn btn-primary" onClick={() => copyLink(inviteLink)}>{t('members.copyLink')}</button>
            <button className="btn" onClick={() => setInviteLink(null)}>{t('common.close')}</button>
          </div>
        </div>
      )}

      {members.length === 0 ? (
        <div className="settings-empty">
          <p>{t('members.empty')}</p>
          {canManage && (
            <button className="btn" onClick={() => setShowInvite(true)}>{t('members.invite')}</button>
          )}
        </div>
      ) : (<>
        {members.length > 4 && (
          <div style={{display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center'}}>
            <input className="input" style={{flex: 1, minWidth: 200}}
                   placeholder={t('members.searchPlaceholder', 'Search by name or email…')}
                   value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} />
            <select className="select" style={{maxWidth: 180}} value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">{t('members.filter.allRoles', 'All roles')}</option>
              <option value="owner">{t('members.role.owner')}</option>
              <option value="admin">{t('members.role.admin')}</option>
              <option value="editor">{t('members.role.editor')}</option>
              <option value="viewer">{t('members.role.viewer')}</option>
            </select>
          </div>
        )}
        {visibleMembers.length === 0 ? (
          <div className="settings-empty"><p>{t('members.noMatches', 'No members match these filters.')}</p></div>
        ) : visibleMembers.map((m) => {
        const editing = editingWsUserId === m.userId;
        const isFullAccess = m.role === 'owner' || m.role === 'admin';
        const grants = m.workspaceIds || [];
        return (
          <div key={m.userId} className="settings-card" style={{flexDirection: 'column', alignItems: 'stretch'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center'}}>
              <div className="settings-card-info">
                <div className="settings-card-title-row">
                  <div className="settings-card-title">{m.name || m.email}</div>
                  <span className="pill">{t('members.role.' + m.role)}</span>
                </div>
                <div className="settings-card-meta">
                  {m.email}{' · '}
                  {t('members.colJoined')}: {formatDateOnly(new Date(m.joinedAt))}
                </div>
              </div>
              <div className="settings-card-actions">
                {/* Role control is manager-only; read-only members already
                    see the role pill in the title row, so they get no
                    duplicate control here. The owner role is changed only
                    by an explicit transfer, never from this dropdown. */}
                {canManage && (m.role === 'owner' ? (
                  <span className="pill" style={{maxWidth: 130}}>{t('members.role.owner')}</span>
                ) : (
                  <select className="select" value={m.role}
                          onChange={(e) => changeRole(m, e.target.value)}
                          style={{maxWidth: 130}}>
                    <option value="admin">{t('members.role.admin')}</option>
                    <option value="editor">{t('members.role.editor')}</option>
                    <option value="viewer">{t('members.role.viewer')}</option>
                  </select>
                ))}
                {isOwner && m.userId !== user?.id && (
                  <ActionMenu items={[
                    ...(m.role !== 'owner' ? [{ label: t('members.transferOwnership'), onClick: () => transferOwnership(m) }] : []),
                    { label: t('members.remove', 'Remove member'), onClick: () => remove(m), danger: true },
                  ]} />
                )}
              </div>
            </div>

            {/* Workspace access row. Owner + admin always see every
                workspace (server returns workspaceIds=null for them);
                editor and viewer get an explicit grant list managed
                by the owner. Inline editor — opens on Edit, replaces
                grants on Save. */}
            <div style={{marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)',
                         display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap'}}>
              <span className="label" style={{margin: 0, alignSelf: 'center', minWidth: 110}}>
                {t('members.workspacesLabel', 'Workspaces')}
              </span>
              {isFullAccess ? (
                <span style={{color: 'var(--text-muted)', fontSize: 12, alignSelf: 'center'}}>
                  {t('members.allWorkspaces', 'All workspaces (role grants full access)')}
                </span>
              ) : editing ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 auto'}}>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                    {workspaces.map((w) => {
                      const on = editingWsDraft.includes(w.id);
                      return (
                        <button key={w.id} type="button"
                                className={'btn btn-sm' + (on ? ' btn-primary' : '')}
                                onClick={() => toggleDraftWorkspace(w.id)}>
                          {w.name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{display: 'flex', gap: 6}}>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => saveWorkspaces(m)}>
                      {t('common.save') || 'Save'}
                    </button>
                    <button type="button" className="btn btn-sm" onClick={cancelEditWorkspaces}>
                      {t('common.cancel') || 'Cancel'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, flex: '1 1 auto'}}>
                  {grants.length === 0 ? (
                    <span style={{color: 'var(--danger)', fontSize: 12}}>
                      {t('members.noWorkspaces', 'No workspace access')}
                    </span>
                  ) : grants.map((id) => (
                    <span key={id} className="badge badge-free" style={{fontSize: 11}}>{wsName(id)}</span>
                  ))}
                  {isOwner && (
                    <button className="btn btn-sm" onClick={() => startEditWorkspaces(m)}>
                      {t('common.edit') || 'Edit'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      </>)}

      {invites.length > 0 && (
        <>
          <div style={{marginTop: 32, marginBottom: 12, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em'}}>
            {t('members.pending')}
          </div>
          {invites.map((i) => {
            // Pending grants: server returns null for admin (role
            // grants tenant-wide access), array for editor/viewer.
            const grants = i.workspaceIds;
            return (
              <div key={i.id} className="settings-card" style={{flexDirection: 'column', alignItems: 'stretch'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center'}}>
                  <div className="settings-card-info">
                    <div className="settings-card-title-row">
                      <div className="settings-card-title">{i.email}</div>
                      <span className="pill pill-incomplete">{t('members.role.' + i.role)}</span>
                    </div>
                    <div className="settings-card-meta">
                      {t('members.invitedAt')}: {formatDateOnly(new Date(i.createdAt))}
                      {' · '}{t('members.expires')}: {formatDateOnly(new Date(i.expiresAt))}
                    </div>
                  </div>
                  <div className="settings-card-actions">
                    <ActionMenu items={[
                      { label: localAuthOnly ? t('members.regenerateLink', 'New invite link') : t('members.resendInvite'), onClick: () => resendInvite(i) },
                      { label: t('members.cancelInvite'), onClick: () => cancelInvite(i), danger: true },
                    ]} />
                  </div>
                </div>
                {/* Show the pending workspace grants under the
                    invite row so an admin can see what each invite
                    will land the user in. Owner/admin invites
                    short-circuit to "all workspaces" since the role
                    confers tenant-wide access. */}
                <div style={{marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)',
                             display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                  <span className="label" style={{margin: 0, minWidth: 110}}>
                    {t('members.workspacesLabel')}
                  </span>
                  {grants === null ? (
                    <span style={{color: 'var(--text-muted)', fontSize: 12}}>
                      {t('members.allWorkspaces')}
                    </span>
                  ) : grants.length === 0 ? (
                    <span style={{color: 'var(--danger)', fontSize: 12}}>
                      {t('members.noWorkspaces')}
                    </span>
                  ) : grants.map((id) => (
                    <span key={id} className="badge badge-free" style={{fontSize: 11}}>{wsName(id)}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {showInvite && <InviteMemberModal
        localAuthOnly={localAuthOnly}
        onClose={() => setShowInvite(false)}
        onCreated={(acceptUrl) => {
          setShowInvite(false);
          if (acceptUrl) setInviteLink(acceptUrl);
          if (!localAuthOnly) toast(t('members.invited'), 'success');
          load();
        }}
      />}
    </>
  );
}

function InviteMemberModal({ onClose, onCreated, localAuthOnly }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [workspaceIds, setWorkspaceIds] = useState([]);
  const [busy, setBusy] = useState(false);
  useModalDismiss(onClose, { busy });
  const toast = useToast();
  const t = useT();
  const { workspaces, current: currentWs } = useWorkspace();
  // Editor/viewer invites MUST grant at least one workspace so the
  // invitee lands with real access. Owner/admin invites get tenant-
  // wide access from their role alone; we hide the workspace picker
  // entirely for those roles to avoid implying it'd matter.
  const isFullAccess = role === 'admin';   // owner is never invited (transfer is its own flow)
  // Pre-fill with the current workspace once on mount — cuts a click
  // for the common "add a teammate to this workspace I'm in" case.
  useEffect(() => {
    if (workspaceIds.length === 0 && currentWs?.id) setWorkspaceIds([currentWs.id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWs?.id]);

  const toggleWorkspace = (id) => {
    setWorkspaceIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side guard mirrors the server's workspaces_required
    // 400. Surfacing it inline keeps a frustrating "submit → modal
    // closes? → 400 → modal stays open" cycle from happening.
    if (!isFullAccess && workspaceIds.length === 0) {
      toast(t('members.invite.workspacesRequired',
        'Pick at least one workspace this person should land in.'), 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/spa/members/invites', {
        email, role,
        // Always send the array — server ignores it for admin role.
        workspaceIds,
      });
      onCreated(r && r.acceptUrl);
    } catch (err) {
      if (err.detail?.max) {
        toast(t('members.cap.body', { max: err.detail.max }), 'error');
      } else {
        toast(err.message, 'error');
      }
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={busy || email.trim() ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('members.invite.title')}</h2>
        <p style={{fontSize:13, color:'var(--text-secondary)', marginBottom:16}}>
          {localAuthOnly ? t('members.invite.bodySelfhosted') : t('members.invite.body')}
        </p>
        <form className="form-row" onSubmit={submit}>
          <div>
            <label className="label">{t('auth.email')}</label>
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('members.colRole')}</label>
            <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="admin">{t('members.role.admin')}</option>
              <option value="editor">{t('members.role.editor')}</option>
              <option value="viewer">{t('members.role.viewer')}</option>
            </select>
          </div>
          {/* Workspaces — only meaningful for editor/viewer. Hidden
              for admin since the role itself grants tenant-wide
              access. Same chip-toggle pattern as the API key create
              form so the two configuration surfaces feel consistent. */}
          {!isFullAccess && (
            <div>
              <label className="label" style={{marginBottom: 6}}>
                {t('members.invite.workspacesLabel', 'Workspaces')}
                <span style={{color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, textTransform: 'none', letterSpacing: 0}}>
                  {t('members.invite.workspacesHint',
                    "The invitee lands with access to the workspaces you pick here. They can't see anything else until granted.")}
                </span>
              </label>
              {workspaces.length === 0 ? (
                <div style={{fontSize: 12, color: 'var(--text-muted)'}}>
                  {t('members.invite.noWorkspaces', 'No workspaces yet. Create one first.')}
                </div>
              ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                  {workspaces.map((w) => {
                    const on = workspaceIds.includes(w.id);
                    return (
                      <button key={w.id} type="button"
                              className={'btn btn-sm' + (on ? ' btn-primary' : '')}
                              onClick={() => toggleWorkspace(w.id)}>
                        {on && <Icons.Check />} {w.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {isFullAccess && (
            <div style={{fontSize: 12, color: 'var(--text-muted)'}}>
              {t('members.invite.adminAccessNote',
                'Admins have access to every workspace by role — no workspace selection needed.')}
            </div>
          )}
          <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary"
                    disabled={busy || (!isFullAccess && workspaceIds.length === 0)}>
              {busy ? t('common.sending', 'Sending…') : t('members.invite.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Account page — profile, email verification, GDPR delete + export
// ============================================================================

// Profile-page section that lists every organisation the user belongs to,
// with inline rename for the ones they own, a "switch to" action, and a
// shortcut to create a new one. Workspace-level management lives in the
// settings shell; this section is about ORGANISATIONS specifically.
function OrganisationsSection({ memberships, onChanged }) {
  const t = useT();
  const toast = useToast();
  const { current, switchTo, refresh: refreshTenants } = useTenant();
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  const startEdit = (m) => { setEditingId(m.tenantId); setEditName(m.tenant.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };

  const saveName = async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.patch(`/spa/tenants/${id}`, { name: trimmed });
      toast(t('account.orgRenamed', 'Organisation renamed'), 'success');
      setEditingId(null);
      refreshTenants();
      onChanged();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  // Switching organisation re-seeds every tenant-scoped cache (workspaces,
  // templates, jobs, schemas, …). A clean reload is more reliable than
  // refetching each piece by hand.
  const switchOrg = (m) => {
    if (m.tenantId === current?.id) return;
    switchTo({ id: m.tenantId, name: m.tenant.name, slug: m.tenant.slug });
    window.location.assign('/app');
  };

  // Owned first (mirrors the OrgSwitcher modal), then the rest.
  const sorted = [...(memberships || [])].sort((a, b) => {
    if (a.role === 'owner' && b.role !== 'owner') return -1;
    if (b.role === 'owner' && a.role !== 'owner') return 1;
    return (a.tenant.name || '').localeCompare(b.tenant.name || '');
  });
  // Business rule: a user can only own ONE organisation. If they already
  // do, hide the create button — the backend rejects a second create with
  // a 409 either way, but surfacing the rule in the UI is friendlier than
  // letting them type a name and discover it on submit.
  const ownsAny = (memberships || []).some((m) => m.role === 'owner');

  return (
    <>
      <div className="card" style={{marginBottom: 16}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap'}}>
          <div className="card-title" style={{marginBottom: 0}}>{t('account.organisations', 'Organisations')}</div>
          {!ownsAny && (
            <button className="btn" onClick={() => setShowCreate(true)}>+ {t('account.createOrg', 'New organisation')}</button>
          )}
        </div>
        {sorted.length === 0 && <div className="empty">{t('account.noOrganisations', 'You are not a member of any organisation yet.')}</div>}
        {sorted.map((m) => {
          const isCurrent = m.tenantId === current?.id;
          const isOwner = m.role === 'owner';
          const isEditing = editingId === m.tenantId;
          return (
            <div key={m.tenantId}
                 style={{display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center',
                         padding: '10px 0', borderBottom: '1px solid var(--border)'}}>
              <div style={{flex: 1, minWidth: 0}}>
                {isEditing ? (
                  <div style={{display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap'}}>
                    <input className="input" value={editName}
                           onChange={(e) => setEditName(e.target.value)}
                           autoFocus
                           style={{flex: '1 1 200px'}} />
                    <button className="btn btn-primary" onClick={() => saveName(m.tenantId)} disabled={busy}>{t('common.save')}</button>
                    <button className="btn" onClick={cancelEdit} disabled={busy}>{t('common.cancel')}</button>
                  </div>
                ) : (
                  <>
                    <div style={{fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{m.tenant.name}</div>
                    <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                      <span className={`badge badge-${m.tenant.plan}`}>{t('plan.' + m.tenant.plan)}</span>
                      <span className="pill">{t('members.role.' + m.role, m.role)}</span>
                      {isCurrent && <span className="pill pill-active">{t('orgSwitcher.current')}</span>}
                    </div>
                  </>
                )}
              </div>
              {!isEditing && (
                <div style={{display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap'}}>
                  {isOwner && <button className="btn" onClick={() => startEdit(m)}>{t('common.rename', 'Rename')}</button>}
                  {!isCurrent && <button className="btn btn-primary" onClick={() => switchOrg(m)}>{t('orgSwitcher.switchTo', 'Switch')}</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshTenants(); onChanged(); }} />
      )}
    </>
  );
}

// Two-factor authentication management card on the Account page.
// Self-contained: fetches its own status, drives the setup wizard
// (QR → confirm code → recovery codes) and the disable flow.
// Change the password. Password managers reach it through
// /.well-known/change-password, which lands on ?nav=account&section=password.
// Accounts that sign in with Google, Apple or an email code have none.
function PasswordCard({ hasPassword, email }) {
  const t = useT();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (readUrlParam('section') !== 'password' || !ref.current) return;
    ref.current.scrollIntoView({ block: 'start' });
    const first = ref.current.querySelector('input[type="password"]');
    if (first) first.focus();
    writeUrlParam('section', null);
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (next !== repeat) { toast(t('account.password.mismatch'), 'error'); return; }
    setBusy(true);
    try {
      await api.post('/spa/account/password', { currentPassword: current, newPassword: next });
      toast(t('account.password.changed'), 'success');
      setCurrent(''); setNext(''); setRepeat('');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const field = { marginBottom: 12, maxWidth: 360 };
  return (
    <div className="card" id="password" ref={ref} style={{marginBottom: 16}}>
      <div className="card-title">{t('account.password.title')}</div>
      {hasPassword === false ? (
        <p style={{fontSize: 13, color: 'var(--text-secondary)'}}>{t('account.password.none')}</p>
      ) : (
        <form onSubmit={submit}>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12}}>{t('account.password.intro')}</p>
          {/* Lets password managers attach the new password to this account's entry. */}
          <input type="text" name="username" autoComplete="username" value={email || ''} readOnly style={{display: 'none'}} />
          <div style={field}>
            <label className="label" htmlFor="pw-current">{t('account.password.current')}</label>
            <input id="pw-current" className="input" type="password" autoComplete="current-password" required
                   value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div style={field}>
            <label className="label" htmlFor="pw-new">{t('account.password.new')}</label>
            <input id="pw-new" className="input" type="password" autoComplete="new-password" required minLength={8}
                   value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div style={field}>
            <label className="label" htmlFor="pw-repeat">{t('account.password.repeat')}</label>
            <input id="pw-repeat" className="input" type="password" autoComplete="new-password" required minLength={8}
                   value={repeat} onChange={(e) => setRepeat(e.target.value)} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy || !current || next.length < 8 || !repeat}>
            {t('account.password.save')}
          </button>
        </form>
      )}
    </div>
  );
}

function TwoFactorCard() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);        // null = loading
  const [phase, setPhase] = useState('view');        // view | setup | recovery | disable
  const [setupData, setSetupData] = useState(null);  // { secret, otpauthUri }
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try { setStatus(await api.get('/auth/2fa/status')); }
    catch (e) { toast(e.message, 'error'); }
  }, [toast]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Draw the QR for the otpauth:// URI whenever we (re-)enter setup.
  useEffect(() => {
    if (phase === 'setup' && setupData && qrRef.current && window.QRCode) {
      qrRef.current.innerHTML = '';
      new window.QRCode(qrRef.current, {
        text: setupData.otpauthUri, width: 184, height: 184,
        colorDark: '#1a1a1a', colorLight: '#ffffff',
        correctLevel: window.QRCode.CorrectLevel.M,
      });
    }
  }, [phase, setupData]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const d = await api.post('/auth/2fa/setup');
      setSetupData(d); setCode(''); setPhase('setup');
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const confirmEnable = async () => {
    setBusy(true);
    try {
      const d = await api.post('/auth/2fa/enable', { code: code.trim() });
      setRecoveryCodes(d.recoveryCodes || []);
      setCode(''); setPhase('recovery');
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const finishRecovery = async () => {
    setRecoveryCodes([]); setSetupData(null); setPhase('view');
    await loadStatus();
  };
  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { code: code.trim() });
      toast(t('account.security.disabled'), 'success');
      setCode(''); setPhase('view');
      await loadStatus();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const downloadRecovery = () => {
    const blob = new Blob(
      ['Pluma — two-factor recovery codes\n\nEach code works once.\n\n' + recoveryCodes.join('\n') + '\n'],
      { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pluma-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!status) return null;

  return (
    <div className="card" style={{marginBottom: 16}}>
      <div className="card-title">{t('account.security.title')}</div>

      {phase === 'view' && (
        <>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12}}>
            {status.enabled ? t('account.security.statusOn') : t('account.security.intro')}
          </p>
          {status.enabled ? (
            <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
              <span className="pill" style={{background: 'rgba(46,213,115,0.15)', color: '#2ed573'}}>
                {t('account.security.statusOn')}
              </span>
              <span style={{fontSize: 12, color: 'var(--text-muted)'}}>
                {t('account.security.recoveryRemaining', { count: status.recoveryCodesRemaining })}
              </span>
              <button className="btn" disabled={busy}
                      onClick={() => { setCode(''); setPhase('disable'); }}>
                {t('account.security.disable')}
              </button>
            </div>
          ) : (
            <>
              <button className="btn btn-primary" disabled={busy} onClick={startSetup}>
                {t('account.security.enable')}
              </button>
              <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 8}}>
                {t('account.security.adminNote')}
              </div>
            </>
          )}
        </>
      )}

      {phase === 'setup' && setupData && (
        <>
          <div style={{fontWeight: 600, fontSize: 14, marginBottom: 6}}>{t('account.security.setupScanTitle')}</div>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10}}>{t('account.security.setupScanHelp')}</p>
          <div ref={qrRef} style={{
            display: 'inline-block', padding: 10, background: '#fff',
            borderRadius: 8, marginBottom: 10,
          }} />
          <div style={{fontSize: 12, color: 'var(--text-muted)', marginBottom: 4}}>{t('account.security.setupManual')}</div>
          <code style={{
            display: 'block', fontFamily: 'var(--font-mono)', fontSize: 13,
            wordBreak: 'break-all', background: 'var(--bg-elevated)',
            padding: '6px 10px', borderRadius: 6, marginBottom: 16,
          }}>{setupData.secret}</code>

          <div style={{fontWeight: 600, fontSize: 14, marginBottom: 6}}>{t('account.security.setupConfirmTitle')}</div>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8}}>{t('account.security.setupConfirmHelp')}</p>
          <input className="input" value={code} autoFocus
                 inputMode="numeric" maxLength={6}
                 style={{maxWidth: 200, letterSpacing: '0.3em', fontFamily: 'var(--font-mono)', fontSize: 18, textAlign: 'center'}}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          <div style={{display: 'flex', gap: 8, marginTop: 14}}>
            <button className="btn btn-primary" disabled={busy || code.length !== 6} onClick={confirmEnable}>
              {t('account.security.confirm')}
            </button>
            <button className="btn" disabled={busy}
                    onClick={() => { setPhase('view'); setSetupData(null); setCode(''); }}>
              {t('account.security.cancel')}
            </button>
          </div>
        </>
      )}

      {phase === 'recovery' && (
        <>
          <div style={{fontWeight: 600, fontSize: 14, marginBottom: 6}}>{t('account.security.recoveryTitle')}</div>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12}}>{t('account.security.recoveryIntro')}</p>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
            fontFamily: 'var(--font-mono)', fontSize: 14,
            background: 'var(--bg-elevated)', padding: 14, borderRadius: 8, marginBottom: 14,
          }}>
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <button className="btn" onClick={downloadRecovery}>{t('account.security.recoveryDownload')}</button>
            <button className="btn btn-primary" onClick={finishRecovery}>{t('account.security.recoveryDone')}</button>
          </div>
        </>
      )}

      {phase === 'disable' && (
        <>
          <div style={{fontWeight: 600, fontSize: 14, marginBottom: 6}}>{t('account.security.disableTitle')}</div>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8}}>{t('account.security.disableHelp')}</p>
          <input className="input" value={code} autoFocus
                 style={{maxWidth: 220, fontFamily: 'var(--font-mono)', fontSize: 16}}
                 onChange={(e) => setCode(e.target.value)} />
          <div style={{display: 'flex', gap: 8, marginTop: 14}}>
            <button className="btn" disabled={busy || !code.trim()}
                    style={{borderColor: 'var(--danger)', color: 'var(--danger)'}}
                    onClick={confirmDisable}>
              {t('account.security.disable')}
            </button>
            <button className="btn" disabled={busy}
                    onClick={() => { setPhase('view'); setCode(''); }}>
              {t('account.security.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AccountPage() {
  const { user, refresh, logout } = useAuth();
  const runtime = useRuntime();
  const [account, setAccount] = useState(null);
  // DB now stores structured parts (salutation/firstName/lastName) AND the
  // denormalised `name`. The server derives `name` from the parts via
  // composeName(); we only send the parts on save.
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const t = useT();
  const { locale, setLocale, locales } = useI18n();

  const load = useCallback(async () => {
    try {
      const d = await api.get('/spa/account/me');
      setAccount(d.user);
      setSalutation(d.user.salutation || '');
      setFirstName(d.user.firstName || '');
      setLastName(d.user.lastName || '');
    } catch (e) { toast(e.message, 'error'); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    setBusy(true);
    try {
      await api.patch('/spa/account/me', {
        salutation: salutation || null,           // '' → null (clears the column)
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      toast(t('common.saved', 'Changes saved'), 'success');
      refresh(); load();
    }
    catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const resendVerify = async () => {
    setResending(true);
    try { await api.post('/auth/verify-email/resend'); toast(t('account.verificationSent'), 'success'); }
    catch (e) { toast(e.message, 'error'); }
    setResending(false);
  };
  const requestDeletion = async (deleteOwnedTenants = false) => {
    if (!deleteOwnedTenants) {
      const ok = await confirm({
        title: t('account.confirmDeleteTitle'),
        body: t('account.confirmDeleteBody'),
        typeToConfirm: 'DELETE',
        confirmLabel: t('account.scheduleDeletion'),
        danger: true,
      });
      if (!ok) return;
    }
    try {
      const r = await api.post('/spa/account/request-deletion',
        deleteOwnedTenants ? { deleteOwnedTenants: true } : {});
      toast(t('account.deletionScheduled', { date: formatDateOnly(new Date(r.scheduledFor)) }), 'success');
      refresh();   // surfaces the app-wide deletion banner
      load();
    } catch (e) {
      // Sole owner of a workspace → server asks the user to choose:
      // transfer ownership (handled elsewhere) or delete the workspace too.
      if (e.code === 'sole_owner_of_tenants') {
        const names = (e.detail?.tenants || []).map((x) => x.name).join(', ');
        const ok = await confirm({
          title: t('account.deleteTenantsTitle'),
          body: t('account.deleteTenantsBody', { tenants: names }),
          typeToConfirm: 'DELETE',
          confirmLabel: t('account.deleteTenantsConfirm'),
          danger: true,
        });
        if (ok) return requestDeletion(true);
      } else {
        toast(e.message, 'error');
      }
    }
  };
  const cancelDeletion = async (resumeSubscription = false) => {
    try {
      await api.post('/spa/account/cancel-deletion',
        resumeSubscription ? { resumeSubscription: true } : {});
      toast(t('account.deletionCancelled'), 'success');
      refresh();
      load();
    } catch (e) { toast(e.message, 'error'); }
  };
  const exportData = () => {
    // Plain link rather than fetch — lets the browser handle the file download
    // including the Content-Disposition filename header.
    window.location.href = '/spa/account/export';
  };

  if (!account) return <LoadingFrame label={t('common.loading')} />;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{t('account.title')}</div>
          <div className="page-subtitle">{t('account.subtitle')}</div>
        </div>
      </div>

      {!account.emailVerified && runtime.mode !== 'selfhosted' && (
        <div className="card" style={{borderColor: 'var(--warning)', background: 'rgba(255, 165, 2, 0.06)', marginBottom: 16}}>
          <strong>{t('account.verifyBanner').split('.')[0]}.</strong> {t('account.verifyBanner').split('.').slice(1).join('.').trim()}
          <button className="btn" style={{marginLeft: 12}} onClick={resendVerify} disabled={resending}>
            {resending ? t('common.sending', 'Sending…') : t('account.resendVerification')}
          </button>
        </div>
      )}

      {/* The pending-deletion banner is rendered app-wide by
          <AccountDeletionBanner/> at the top of the shell — no page-level
          copy needed here. */}

      <div className="card" style={{marginBottom: 16}}>
        <div className="card-title">{t('account.profile')}</div>

        {/* Avatar upload — single click to pick a file, instant
            optimistic preview on selection, server-side presigning for
            display so a fresh upload appears without a refresh. */}
        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16}}>
          <div className="avatar-upload-preview">
            {account.avatarUrl
              ? <img src={account.avatarUrl} alt="" />
              : <span>{(account.name || account.email || '?')[0].toUpperCase()}</span>}
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <label className="btn" style={{cursor: 'pointer', margin: 0}}>
              {account.avatarUrl ? t('account.changePhoto', 'Change photo') : t('account.uploadPhoto', 'Upload photo')}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                     style={{display: 'none'}}
                     onChange={async (e) => {
                       const file = e.target.files?.[0];
                       if (!file) return;
                       if (file.size > 2 * 1024 * 1024) {
                         toast(t('account.photoTooLarge', 'Image too large (2 MB max)'), 'error');
                         return;
                       }
                       const fd = new FormData();
                       fd.append('image', file);
                       try {
                         const res = await fetch('/spa/account/avatar', {
                           method: 'POST', credentials: 'include', body: fd,
                         });
                         if (!res.ok) throw await errorFrom(res);
                         await load();
                         refresh();
                         toast(t('account.photoUpdated', 'Photo updated'), 'success');
                       } catch (err) {
                         toast(err.message || t('account.photoUploadFailed', 'Upload failed'), 'error');
                       } finally {
                         e.target.value = '';
                       }
                     }} />
            </label>
            {account.avatarUrl && (
              <button className="btn" type="button"
                      onClick={async () => {
                        try {
                          await api.del('/spa/account/avatar');
                          await load(); refresh();
                          toast(t('account.photoRemoved', 'Photo removed'));
                        } catch (e) { toast(e.message, 'error'); }
                      }}>
                {t('account.removePhoto', 'Remove')}
              </button>
            )}
          </div>
        </div>

        <div style={{marginBottom: 12}}>
          <label className="label">{t('auth.email')}</label>
          <input className="input" value={account.email} disabled />
        </div>
        {account.customerNumber && (
          <div style={{marginBottom: 12}}>
            <label className="label">{t('account.customerNumber', 'Customer number')}</label>
            <input className="input" value={account.customerNumber} readOnly onFocus={(e) => e.target.select()} style={{fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em'}} />
            <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 4}}>
              {t('account.customerNumberHint', 'Identifies you, for example on the online cancellation page — also if you sign in with a private relay address (Sign in with Apple).')}
            </div>
          </div>
        )}
        <div style={{marginBottom: 12}}>
          <label className="label">{t('auth.salutation', 'Salutation')}</label>
          <select className="select" value={salutation}
                  onChange={(e) => setSalutation(e.target.value)}>
            <option value="">{t('auth.salutation.unset', 'Prefer not to say')}</option>
            <option value="mr">{t('auth.salutation.mr', 'Mr')}</option>
            <option value="mrs">{t('auth.salutation.mrs', 'Mrs')}</option>
            <option value="diverse">{t('auth.salutation.diverse', 'Diverse')}</option>
          </select>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom: 12}}>
          <div>
            <label className="label">{t('auth.firstName', 'First name')}</label>
            <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('auth.lastName', 'Last name')}</label>
            <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div style={{marginBottom: 12}}>
          <label className="label">{t('account.language')}</label>
          <select className="select" value={locale} onChange={(e) => setLocale(e.target.value)}>
            {locales.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
          <div style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 6}}>{t('account.languageHelp')}</div>
        </div>
        <button className="btn btn-primary" onClick={saveName} disabled={busy}>{t('common.save')}</button>
      </div>

      <PasswordCard hasPassword={account.hasPassword} email={account.email} />

      {/* Two-factor authentication. Self-contained — fetches its own
          status and drives setup/disable. Required for the admin console. */}
      <TwoFactorCard />

      {/* Organisations the user belongs to — manage from the profile page.
          Workspace-level management is in the Settings shell, not here. */}
      <OrganisationsSection memberships={account.memberships} onChanged={load} />

      <div className="card" style={{marginBottom: 16}}>
        <div className="card-title">{t('account.gdprTitle')}</div>
        <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12}}>
          {t('account.gdprBody')}
        </p>
        <button className="btn" onClick={exportData}>{t('account.downloadData')}</button>
      </div>

      {/* Account deletion is hosted-only — a self-hosted box is the
          operator's own infrastructure (server returns 404 there). */}
      {runtime.mode !== 'selfhosted' && (
        <div className="card" style={{borderColor: 'var(--danger)'}}>
          <div className="card-title" style={{color: 'var(--danger)'}}>{t('account.dangerZone')}</div>
          <p style={{fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12}}>
            {t('account.deleteBody')}
          </p>
          {account.deletionRequestedAt
            ? <button className="btn" onClick={() => cancelDeletion(false)}>{t('account.cancelDeletion')}</button>
            : <button className="btn btn-danger" onClick={() => requestDeletion()}>{t('account.deleteAccount')}</button>}
        </div>
      )}
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
